/* PlanBoss — NZ Trade Intelligence */
// Mobile styles loaded via mobile.css
import { useState, useRef, useEffect, useLayoutEffect, useMemo, Component, Fragment } from "react";
import { fbSignup, fbLogin, fbResetPassword, fbErrorMessage, fbIdToken, fbSignOut, fbDeleteUser, fbReauthenticate, fbResendVerification, fbRefreshSignIn,
  fbApplyActionCode, fbVerifyPasswordResetCode, fbConfirmPasswordReset } from "./firebase";
// plancheck-v1-2026-10-13: is this plan safe to quote from? (shared with the server)
import PBC from "./pb_plancheck";

// One support address for the whole app (planboss-v1-2026-10-12).
const PB_SUPPORT_EMAIL = "hello@planboss.co.nz";

// ─── Server calls (security-v1-2026-10-12) ───────────────────────────────────
// Every call carries the user's Firebase sign-in; the server refuses scans
// without one and caps each user's scans per day.
const PB_API = (process.env.REACT_APP_API_URL || "https://measureup-app.onrender.com").replace(/\/+$/, "");
async function pbFetch(path, init) {
  const token = await fbIdToken();
  if (!token) {
    const e = new Error("Your sign-in has expired. Sign out, sign back in and try again.");
    e.code = "signed_out";
    throw e;
  }
  const opts = init || {};
  return fetch(PB_API + path, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts.headers || {}), Authorization: "Bearer " + token },
  });
}
// Plain-English message for a failed server call, plus the server's error
// code (e.g. "monthly_limit", "no_plan") so the screen can offer the fix.
async function pbErrorInfo(resp, fallback) {
  let body = null;
  try { body = await resp.json(); } catch (e) {}
  const code = (body && body.error) || null;
  if (body && body.message) return { message: body.message, code };
  if (resp.status === 401) return { message: "Your sign-in has expired. Sign out, sign back in and try again.", code };
  if (resp.status === 429) return { message: "You've hit the scan limit for now. Try again later.", code };
  if (resp.status === 413) return { message: "That PDF is too large to send. Try the floor-plan pages only.", code };
  return { message: fallback + (code ? " (" + String(code).slice(0, 120) + ")" : " (server error " + resp.status + ")"), code };
}
async function pbErrorText(resp, fallback) { return (await pbErrorInfo(resp, fallback)).message; }

// ─── Storage helpers (localStorage so data survives page refresh) ─────────────
const getCurrentUserEmail = () => {
  try {
    const u = JSON.parse(localStorage.getItem('to_user')||'null');
    if (u?.email) return u.email;
    const a = JSON.parse(localStorage.getItem('mu_admin')||'null');
    if (a?.email) return a.email;
  } catch(e) {}
  return 'guest';
};
// ——— Usage tracking helpers ———————————————————————
const getUsageKey = (email) => 'mu_usage_' + email + '_' + new Date().getFullYear() + '_' + (new Date().getMonth()+1);
const getAnalysisCount = (email) => { try { return parseInt(localStorage.getItem(getUsageKey(email))||'0'); } catch(e){ return 0; }};
const incrementAnalysis = (email) => { try { const k=getUsageKey(email); localStorage.setItem(k, (getAnalysisCount(email)+1).toString()); } catch(e){}};
// Plan scans per calendar month (Grant, 12 Oct 2026). The server enforces
// these once filing is on (pb_store.js PLAN_LIMITS); this copy only drives the
// on-screen count when it isn't.
// stripe-live-2 (Grant, 28 Oct): 5 / 10 / 20 scans a month (were 10 / 25 / 50).
const getPlanLimit = (plan) => { const limits={pro:5,proplus:10,business:20}; return limits[plan]||5; };
// The free trial (Grant, 28 Oct): 7 days on Pro, card taken up front, once per person.
// stripe-live-3 (Grant, 28 Oct): 3 scans in the trial (the server's
// PB_TRIAL_SCANS default); Pro's 5 a month start with the first payment.
const PB_TRIAL = { plan: "pro", days: 7, scans: 3 };
// stripe-live-2: why a signed-in account has no plan, from the server (filing
// on): "offer" = Pro's free trial is still on offer; "payment" = the trial
// ended and its first payment didn't go through (update the card); "refused"
// = a second free trial (same card or email) was cancelled; "trial" = a free
// trial has ended; "plan" = a paid plan has ended; "" = not known.
// deviceTrialOver: this device's own old trial ran out (filing off).
const pbNoPlanWhy = (account, deviceTrialOver) => {
  const ent = account && account.plan;
  if (!ent) return deviceTrialOver ? "trial" : "";
  if (ent.plan !== null) return "";
  if (account.trialOffer && account.trialOffer.eligible === true) return "offer";
  if (ent.firstPaymentFailed) return "payment";
  if (ent.trialRefused) return "refused";
  if (ent.trialEnded || (ent.source === "none" && ent.trialEndsAt)) return "trial";
  if (ent.hadPlan) return "plan";
  return "";
};
// What the app says for each of those, and the button that goes with it.
const PB_NO_PLAN_TEXT = {
  offer: "Start your 7-day free trial of Pro to scan your first plan.",
  payment: "Your free trial has ended, and the first payment didn't go through. Update your card to keep scanning.",
  refused: "This card or email has already had a PlanBoss free trial. Choose a plan to keep scanning.",
  trial: "Your free trial has ended. Choose a plan to keep scanning.",
  plan: "Your plan has ended. Choose a plan to keep scanning.",
  "": "Choose a plan to keep scanning.",
};

const LS = {
  get: (key, def) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; } },
  set: (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} },
};

// ─── Filing system: jobs saved to the user's account (store-v1-2026-10-12) ───
// With filing on (FIREBASE_SERVICE_ACCOUNT on Render) every scan is filed
// under the tradie's account with its plan PDF, so their jobs follow them to
// any phone or computer and the admin can see them. The per-job settings a
// tradie changes (confirmed stud and pitch, room corrections, price
// overrides) are still saved on the device first, then copied up to the job
// a moment later. Whole-blob, newest-wins: one tradie, a phone and a laptop.
const pbCloudIds = new Set();                 // job ids that live on the server
const pbDeletedIds = new Set();               // deleted here; a list loaded meanwhile mustn't bring them back
const pbSyncTimers = {};
const PB_JOB_KEYS = {
  stud:      (pid) => 'stud_confirm_' + pid,
  pitch:     (pid) => 'pitch_confirm_' + pid,
  overrides: (pid) => 'project_overrides_' + pid,
  prices:    (pid) => 'price_overrides_' + pid,
  // plancheck-v1-2026-10-13: the tradie's external-corner check (count agreed,
  // engine corners taken off, missed ones added). Follows the job like the stud.
  corners:   (pid) => 'corner_check_' + pid,
};
const PB_FIELDS = Object.keys(PB_JOB_KEYS);
// store-v2: each setting carries its own "changed at" time, and the server
// merges field by field (newest wins). So a stud confirmed on a phone with no
// signal survives the pitch being changed on the laptop meanwhile. A job is
// "dirty" from a change here until the server has taken it.
const pbAtKey = (pid) => 'pb_local_at_' + pid;
const pbDirtyKey = (pid) => 'pb_local_dirty_' + pid;
function pbAtMap(pid) {
  try {
    const raw = localStorage.getItem(pbAtKey(pid));
    if (raw === null) return null;                                   // never had any settings record
    const v = JSON.parse(raw);
    if (typeof v === 'number') { const m = {}; PB_FIELDS.forEach(f => { m[f] = v; }); return m; }   // first format
    return (v && typeof v === 'object') ? v : {};
  } catch (e) { return {}; }
}
function pbSetAtMap(pid, m) { try { localStorage.setItem(pbAtKey(pid), JSON.stringify(m || {})); } catch (e) {} }
// Newest change time on this device; -1 = this device never had the job's settings.
function pbLocalAt(pid) {
  const m = pbAtMap(pid);
  if (m === null) return -1;
  return PB_FIELDS.reduce((x, f) => Math.max(x, Number(m[f]) || 0), 0);
}
function pbIsDirty(pid) { try { return localStorage.getItem(pbDirtyKey(pid)) === '1'; } catch (e) { return false; } }
function pbSetDirty(pid, on) { try { if (on) localStorage.setItem(pbDirtyKey(pid), '1'); else localStorage.removeItem(pbDirtyKey(pid)); } catch (e) {} }
function pbReadField(pid, f) {
  try {
    const raw = localStorage.getItem(PB_JOB_KEYS[f](pid));
    if (raw === null || raw === '') return null;
    return (f === 'stud' || f === 'pitch') ? raw : JSON.parse(raw);
  } catch (e) { return null; }
}
function pbWriteField(pid, f, v) {
  try {
    const empty = v === undefined || v === null || v === '' || (typeof v === 'object' && !Object.keys(v).length);
    if (empty) localStorage.removeItem(PB_JOB_KEYS[f](pid));
    else localStorage.setItem(PB_JOB_KEYS[f](pid), typeof v === 'string' ? v : JSON.stringify(v));
  } catch (e) {}
}
// Room corrections (room -> field -> value) and price overrides (material ->
// price) carry a time per entry, so a room fixed here and a different room
// fixed on another device both survive.
const PB_SEP = '\u0001';
const pbIsMapField = (f) => f === 'overrides' || f === 'prices';
function pbEntries(f, v) {
  const out = [];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return out;
  if (f === 'overrides') {
    Object.keys(v).forEach(room => {
      const r = v[room];
      if (r && typeof r === 'object' && !Array.isArray(r)) Object.keys(r).forEach(fld => out.push([room + PB_SEP + fld, r[fld]]));
    });
  } else Object.keys(v).forEach(k => out.push([k, v[k]]));
  return out;
}
function pbHasEntry(f, v, key) { return pbEntries(f, v).some(([k]) => k === key); }
function pbPutEntry(f, v, key, val) {
  if (f === 'overrides') {
    const i = key.indexOf(PB_SEP);
    const room = key.slice(0, i), fld = key.slice(i + 1);
    if (!v[room] || typeof v[room] !== 'object') v[room] = {};
    v[room][fld] = val;
  } else v[key] = val;
}
// This device's settings for a job, in the server's shape.
function pbReadJobLocal(pid) {
  const at = pbAtMap(pid) || {};
  const out = { fmt: 2 };
  PB_FIELDS.forEach(f => {
    out[f] = { v: pbReadField(pid, f), at: Number(at[f]) || 0 };
    if (pbIsMapField(f) && at.e && at.e[f]) out[f].e = Object.assign({}, at.e[f]);
  });
  return out;
}
// Take what the server has newer than this device: field by field, and entry
// by entry for room corrections and prices. Returns true if anything here changed.
function pbAdoptLocal(pid, srv) {
  if (!srv || typeof srv !== 'object') return false;
  const fresh = pbAtMap(pid) === null;          // this device never had the job's settings
  const at = pbAtMap(pid) || {};
  at.e = (at.e && typeof at.e === 'object') ? at.e : {};
  let changed = false;
  PB_FIELDS.forEach(f => {
    const e = srv[f];
    if (!e || typeof e !== 'object') return;
    const sAt = Number(e.at) || 0;
    if (pbIsMapField(f) && !fresh && e.v && typeof e.v === 'object') {
      const cur = pbReadField(pid, f) || {};
      const devE = Object.assign({}, at.e[f] || {});
      const srvE = e.e || {};
      let any = false;
      pbEntries(f, e.v).forEach(([k, val]) => {
        const st = srvE[k] != null ? Number(srvE[k]) : sAt;
        const dt = devE[k] != null ? Number(devE[k]) : (pbHasEntry(f, cur, k) ? (Number(at[f]) || 0) : -1);
        if (st > dt) { pbPutEntry(f, cur, k, val); devE[k] = st; any = true; }
      });
      if (any) { pbWriteField(pid, f, cur); at.e[f] = devE; at[f] = Math.max(Number(at[f]) || 0, sAt); changed = true; }
      return;
    }
    if (fresh || sAt > (Number(at[f]) || 0)) {
      pbWriteField(pid, f, e.v);
      at[f] = sAt;
      if (pbIsMapField(f)) at.e[f] = Object.assign({}, e.e || {});
      changed = true;
    }
  });
  if (changed || fresh) pbSetAtMap(pid, at);
  return changed;
}
function pbMoveJobLocal(fromId, toId) {
  PB_FIELDS.forEach(f => {
    try {
      const v = localStorage.getItem(PB_JOB_KEYS[f](fromId));
      if (v !== null) { localStorage.setItem(PB_JOB_KEYS[f](toId), v); localStorage.removeItem(PB_JOB_KEYS[f](fromId)); }
    } catch (e) {}
  });
  try {
    const a = localStorage.getItem(pbAtKey(fromId));
    if (a !== null) { localStorage.setItem(pbAtKey(toId), a); localStorage.removeItem(pbAtKey(fromId)); }
    localStorage.removeItem(pbDirtyKey(fromId));
    const rough = localStorage.getItem(pbRoughKey(fromId));
    if (rough !== null) { localStorage.setItem(pbRoughKey(toId), rough); localStorage.removeItem(pbRoughKey(fromId)); }
  } catch (e) {}
  pbCheckImgGet(fromId).then(img => { if (img) { pbCheckImgPut(toId, img); pbCheckImgDrop(fromId); } });
}
function pbForgetJobLocal(pid) {
  PB_FIELDS.forEach(f => { try { localStorage.removeItem(PB_JOB_KEYS[f](pid)); } catch (e) {} });
  try { localStorage.removeItem(pbAtKey(pid)); localStorage.removeItem(pbDirtyKey(pid)); } catch (e) {}
  try { localStorage.removeItem(pbRoughKey(pid)); } catch (e) {}
  pbCacheDrop(pid);
  pbCheckImgDrop(pid);
}
// Send this job's settings shortly after the last change. The server merges
// and sends the result back; anything it has newer is taken here.
function pbQueueJobSync(pid, delay) {
  if (!pid || !pbCloudIds.has(pid)) return;
  clearTimeout(pbSyncTimers[pid]);
  pbSyncTimers[pid] = setTimeout(async () => {
    delete pbSyncTimers[pid];
    const sentAt = pbLocalAt(pid);
    try {
      const r = await pbFetch('/jobs/' + encodeURIComponent(pid),
        { method: 'PATCH', body: JSON.stringify({ local: pbReadJobLocal(pid) }) });
      if (r.ok) {
        const d = await r.json().catch(() => null);
        const changed = !!(d && d.local && pbAdoptLocal(pid, d.local));
        if (pbLocalAt(pid) <= sentAt) pbSetDirty(pid, false);   // nothing newer here since it was sent
        if (changed && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('pb-job-settings', { detail: { id: pid } }));
      } else if (r.status === 404) {
        pbSetDirty(pid, false);                      // the job is gone
      } else console.warn('[filing] job ' + pid + ' settings not saved (' + r.status + ')');
    } catch (e) { console.warn('[filing] job ' + pid + ' settings not saved: ' + (e && e.message)); }
  }, delay == null ? 1500 : delay);
}
// Every per-job setter calls this after saving on the device (with the
// entry it changed, for room corrections and prices).
function pbTouchLocal(pid, field, entryKey) {
  if (!pid) return;
  const at = pbAtMap(pid) || {};
  const now = Date.now();
  (field ? [field] : PB_FIELDS).forEach(f => {
    at[f] = Math.max(now, (Number(at[f]) || 0) + 1);
    if (entryKey != null && pbIsMapField(f)) {
      at.e = (at.e && typeof at.e === 'object') ? at.e : {};
      at.e[f] = Object.assign({}, at.e[f] || {}, { [entryKey]: at[f] });
    }
  });
  pbSetAtMap(pid, at);
  pbSetDirty(pid, true);
  pbQueueJobSync(pid);
}

// The last few opened jobs' results stay on the device, so they open
// instantly and still open with no signal on site. The results of a scan
// never change once filed; only the settings above do.
const PB_CACHE_MAX = 8;
const pbCacheKey = (pid) => 'pb_job_' + pid;
function pbCacheIndex() { const v = LS.get('pb_job_cache_index', []); return Array.isArray(v) ? v : []; }
function pbCacheGet(pid) {
  try { const v = localStorage.getItem(pbCacheKey(pid)); return v ? JSON.parse(v) : null; } catch (e) { return null; }
}
function pbCachePut(pid, analysis) {
  if (!pid || !analysis) return false;
  const idx = pbCacheIndex().filter(x => x !== pid);
  idx.unshift(pid);
  let json;
  try { json = JSON.stringify(analysis); } catch (e) { return false; }
  for (let attempt = 0; attempt < PB_CACHE_MAX + 1; attempt++) {
    while (idx.length > PB_CACHE_MAX) { const old = idx.pop(); try { localStorage.removeItem(pbCacheKey(old)); } catch (e) {} }
    try { localStorage.setItem(pbCacheKey(pid), json); LS.set('pb_job_cache_index', idx); return true; }
    catch (e) {                                   // storage full: drop the oldest and retry
      if (idx.length <= 1) break;
      const old = idx.pop(); try { localStorage.removeItem(pbCacheKey(old)); } catch (e2) {}
    }
  }
  LS.set('pb_job_cache_index', idx.filter(x => x !== pid));
  return false;
}
function pbCacheDrop(pid) {
  try { localStorage.removeItem(pbCacheKey(pid)); } catch (e) {}
  LS.set('pb_job_cache_index', pbCacheIndex().filter(x => x !== pid));
}

// Tradie corrections (measurements and prices) on filed jobs go to the server
// as well: they are the best evidence there is of where the engine is wrong.
// Held in an outbox until the server has them, so a correction made with no
// signal still arrives.
// One outbox per account, so another person signing in on the same phone
// never sends (or loses) someone else's corrections.
const pbOutboxKey = (email) => 'pb_corr_outbox_' + (email || getCurrentUserEmail());
function pbQueueCorrection(pid, entry, email) {
  if (!pid || !entry || !pbCloudIds.has(pid)) return;
  const key = pbOutboxKey(email);
  const box = LS.get(key, []);
  const list = Array.isArray(box) ? box : [];
  list.push({ k: Date.now() + '-' + Math.random().toString(36).slice(2, 8), jobId: pid, entry: entry });
  LS.set(key, list.slice(-300));
  clearTimeout(pbSyncTimers.__corr);
  pbSyncTimers.__corr = setTimeout(() => pbFlushCorrections(email), 2000);
}
let pbFlushing = false;
async function pbFlushCorrections(email) {
  if (pbFlushing) return;
  const key = pbOutboxKey(email);
  const box = LS.get(key, []);
  if (!Array.isArray(box) || !box.length) return;
  pbFlushing = true;
  const sent = new Set();
  try {
    const byJob = {};
    box.forEach(x => { if (x && x.jobId) (byJob[x.jobId] = byJob[x.jobId] || []).push(x); });
    for (const jobId of Object.keys(byJob)) {
      const chunk = byJob[jobId].slice(0, 50);
      try {
        const r = await pbFetch('/jobs/' + encodeURIComponent(jobId) + '/corrections',
          { method: 'POST', body: JSON.stringify({ corrections: chunk.map(x => x.entry) }) });
        // 404 = the job has been deleted, so its corrections can go too
        if (r.ok || r.status === 404) chunk.forEach(x => sent.add(x.k));
      } catch (e) { /* no signal: keep them for next time */ }
    }
  } finally {
    const left = LS.get(key, []);
    LS.set(key, (Array.isArray(left) ? left : []).filter(x => x && !sent.has(x.k)));
    pbFlushing = false;
  }
}

// One shape for the corrections log: an array. The price logger used to
// write an object and the measurement logger an array under the same key, so
// whichever ran second silently lost its entries.
function pbCorrectionsList(email) {
  try {
    const v = JSON.parse(localStorage.getItem('to_corrections_' + email) || '[]');
    if (Array.isArray(v)) return v;
    if (v && typeof v === 'object') return Object.values(v);
  } catch (e) {}
  return [];
}

// ─── Plan verdict + external-corner check (plancheck-v1-2026-10-13) ─────────
// Grant, 13 Oct 2026: "if the plan is wrong, it needs to say it's wrong, and
// not just put in numbers." Every scan now ends in a verdict
// (src/pb_plancheck.js, shared with the server):
//   stop  - can't read it: NO numbers anywhere, says why and what to upload.
//   hold  - not measured from the drawing (angled walls, a scan, a photo),
//           or only part of the house was: numbers hidden until the tradie
//           taps "show them anyway", then marked rough everywhere.
//   check - measured, with the specific things to check listed.
//   ok    - measured from the drawing.
// Jobs scanned before this get the same verdict worked out on the device.
let pbFilingOn = false;              // from /me: plan cards word "saved jobs" by it
function pbVerdictFor(project) {
  const a = project && project.aiAnalysis;
  if (!a || typeof a !== 'object') {
    const s = project && project.summary;       // a filed job whose results aren't on this device yet
    // alter-v2 (review #11): a measured alteration reads the same on the list as when opened
    return (s && s.verdict) ? { verdict: s.verdict, kind: s.verdictKind || null, measured: s.verdictMeasured === true, reasons: [], headline: '', fromSummary: true } : null;
  }
  const v = a.planVerdict;
  if (v && typeof v === 'object' && ['stop', 'hold', 'check', 'ok'].includes(v.verdict) && Array.isArray(v.reasons)) return v;
  return PBC.planVerdict({ analysis: a, geometry: a.geometry, fileName: (project && project.file) || '', legacy: true });
}
// macritchie-v1 / review #5 (23 Oct): the engine's own doubt about its
// external corner count (no doors found, too many corners in a room or for
// the floor area). Shown on the corner check and kept off the green dot.
function pbCornersSuspect(project) {
  const f = project && project.aiAnalysis && project.aiAnalysis.geometry && project.aiAnalysis.geometry.fields &&
    project.aiAnalysis.geometry.fields.external_corners_lm;
  const s = f && typeof f === 'object' ? f.suspect : null;
  return typeof s === 'string' && s ? s : null;
}
// "Show me the rough numbers anyway", remembered per job on this device.
const pbRoughKey = (pid) => 'pb_rough_ok_' + pid;
function pbRoughOk(pid) { try { return localStorage.getItem(pbRoughKey(pid)) === '1'; } catch (e) { return false; } }
function pbSetRoughOk(pid) { try { localStorage.setItem(pbRoughKey(pid), '1'); } catch (e) {} }

// The tradie's external-corner check for a job: { v, ai, count, removed,
// added, img, engine, at } or null if they haven't checked it.
function pbGetCornerCheck(pid) {
  const v = pbReadField(pid, 'corners');
  return (v && typeof v === 'object' && typeof v.count === 'number' && v.count >= 0) ? v : null;
}
function pbSetCornerCheck(pid, v) { pbWriteField(pid, 'corners', v); pbTouchLocal(pid, 'corners'); }
// The engine's corner sites saved with the job (no picture): [{x, y, kind, count, room}]
function pbCornerSites(project) {
  const g = project && project.aiAnalysis && project.aiAnalysis.geometry;
  const e = g && g.fields && g.fields.external_corners_lm;
  return (e && Array.isArray(e.sites) && e.sites.length) ? e.sites : null;
}
// every-storey-v1-2026-10-14: the check pictures of a job, one per measured
// floor ([img] for a one-floor job; the cache keeps {levels: [...]}).
function pbOkPic(i) {
  return !!(i && typeof i === 'object' && typeof i.b64 === 'string' && i.b64.length && Array.isArray(i.sites) && i.sites.length);
}
function pbPicList(v) {
  if (!v || typeof v !== 'object') return null;
  if (Array.isArray(v.levels)) { const l = v.levels.filter(pbOkPic).slice(0, 8); return l.length ? l : null; }
  return pbOkPic(v) ? [v] : null;
}
// The floors the engine measured on a multi-storey job: [{key, name, stud}]
// (empty for a one-floor job).
function pbFloors(project) {
  const g = project && project.aiAnalysis && project.aiAnalysis.geometry;
  const lr = g && Array.isArray(g.level_results) ? g.level_results : [];
  return lr.filter(x => x && typeof x === 'object' && x.measured).map(x => ({
    key: x.key || null, name: pbTxt(x.name, 40) || 'Floor', stud: Number(x.stud_m && x.stud_m.value) || null,
    studAssumed: !!(x.stud_m && x.stud_m.source === 'assumed'),
    count: typeof x.external_count === 'number' ? x.external_count : null }));
}
// "the first floor", but "Level 1": a numbered level keeps its capital and
// takes no "the".
function pbFloorRef(name) {
  const n = pbTxt(name, 40) || 'the floor';
  return /^level\s*\d/i.test(n) ? n.replace(/^level/i, 'Level') : 'the ' + n.toLowerCase();
}
// Do the floors' stud heights differ? Then walls and corners are worked out
// floor by floor (each floor's square stop or corners x its own stud).
function pbStudsDiffer(project) {
  const f = pbFloors(project).map(x => x.stud).filter(Boolean);
  return f.length >= 2 && f.some(st => Math.abs(st - f[0]) > 0.005);
}
// Corner lineal metres from a corner check: one stud the tradie confirmed for
// every floor, else each checked floor's count x that floor's stud.
function pbCornerLm(cc, confirmedStud, fallbackStud) {
  if (!cc) return null;
  if (confirmedStud) return cc.count * confirmedStud;
  if (Array.isArray(cc.levels) && cc.levels.length) {
    return cc.levels.reduce((t, l) => t + (Number(l && l.count) || 0) * (Number(l && l.stud) || fallbackStud || 0), 0);
  }
  return fallbackStud ? cc.count * fallbackStud : null;
}
// every-storey round 2 (review, 14 Oct): a multi-storey job's walls and
// external corners, floor by floor — each floor's square stop and corners x
// that floor's own stud. A stud the tradie confirms replaces only the floors
// whose stud the plans don't state (every floor when none is stated), never
// a stud a plan states. A floor the corner check didn't cover (no picture)
// keeps PlanBoss's count. A floor with no figure makes the total unknown.
// null for a one-floor job.
function pbFloorTotals(project, confirmedStud, cc) {
  const g = project && project.aiAnalysis && project.aiAnalysis.geometry;
  const lr = g && Array.isArray(g.level_results) ? g.level_results.filter(x => x && typeof x === 'object' && x.measured) : [];
  if (lr.length < 2) return null;
  const isAssumed = (x) => !(x.stud_m && x.stud_m.source && x.stud_m.source !== 'assumed');
  const allAssumed = lr.every(isAssumed);
  const ccLevels = cc && Array.isArray(cc.levels) ? cc.levels : null;
  const rows = lr.map(x => {
    const own = Number(x.stud_m && x.stud_m.value) || null;
    const assumed = isAssumed(x);
    const stud = (confirmedStud && (assumed || allAssumed)) ? confirmedStud : (own || confirmedStud || null);
    const f = x.fields && x.fields.square_stop_lm;
    const sq = f && f.value != null && isFinite(Number(f.value)) ? Number(f.value) : null;
    const chk = ccLevels ? ccLevels.find(l => l && l.key && l.key === x.key) || null : null;
    const engineCount = typeof x.external_count === 'number' ? x.external_count : null;
    return { key: x.key || null, name: pbTxt(x.name, 40) || 'Floor', stud, studAssumed: assumed && !confirmedStud, sq,
      count: chk && typeof chk.count === 'number' ? chk.count : engineCount, checked: !!chk, engineCount };
  });
  const sum = (fn) => { const v = rows.map(fn); return v.some(x => x == null || !isFinite(x)) ? null : v.reduce((t, x) => t + x, 0); };
  const gross = sum(r => (r.sq != null && r.stud) ? r.sq * r.stud : null);
  return {
    rows,
    walls: gross == null ? null : gross - pbOpeningsM2(project),
    extCount: sum(r => r.count),
    extLm: sum(r => (r.count != null && r.stud) ? r.count * r.stud : null),
  };
}
// A changed corner count is a correction: the best evidence of where the
// engine is wrong, with the positions so it can be graded corner by corner.
function logCornerCorrection(email, project, cc) {
  if (!email || !project || !cc) return;
  const list = pbCorrectionsList(email);
  const entry = {
    type: 'corners', projectId: project.id, fieldKey: 'external_corners', label: 'External corners', unit: 'corners',
    aiValue: cc.ai, realValue: cc.count, reason: 'Corner check', planFileName: project.file || '',
    detail: JSON.stringify(Array.isArray(cc.levels)
      ? { floors: cc.levels.map(l => ({ floor: l.name || l.key, ai: l.ai, count: l.count, removed: l.removed || [],
          added: (l.added || []).map(a => ({ x: a.x, y: a.y, c: a.c })) })), engine: cc.engine || null }
      : { removed: cc.removed || [], added: (cc.added || []).map(a => ({ x: a.x, y: a.y, c: a.c })),
          engine: cc.engine || null }).slice(0, 2000),
    correctedBy: email, correctedAt: new Date().toISOString(),
  };
  list.push(entry);
  try { localStorage.setItem('to_corrections_' + email, JSON.stringify(list.slice(-2000))); } catch (e) {}
  pbQueueCorrection(project.id, entry, email);
}

// The check pictures (~150 KB each) live in IndexedDB, never in localStorage,
// which has ~5 MB for everything. The newest 20 are kept, so a job's corners
// can be re-checked later on this device. Without IndexedDB (a private
// window, an old WebView) they're kept for this session only.
const PB_IMG_MAX = 20;
const pbImgMem = new Map();
let pbImgDbP = null;
function pbImgDb() {
  if (pbImgDbP) return pbImgDbP;
  pbImgDbP = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined' || !indexedDB) { resolve(null); return; }
      const req = indexedDB.open('planboss', 1);
      req.onupgradeneeded = () => { try { req.result.createObjectStore('checkimg'); } catch (e) {} };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch (e) { resolve(null); }
  });
  return pbImgDbP;
}
function pbImgRun(db, mode, fn) {
  return new Promise((resolve) => {
    try {
      const tx = db.transaction('checkimg', mode);
      const out = fn(tx.objectStore('checkimg'));
      tx.oncomplete = () => resolve(out && typeof out === 'object' && 'result' in out ? out.result : out);
      tx.onerror = () => resolve(null);
      tx.onabort = () => resolve(null);
    } catch (e) { resolve(null); }
  });
}
async function pbCheckImgPut(pid, img) {
  if (!pid || !img) return;
  // a cached multi-storey record ({levels:[...]}, e.g. when a job moves to
  // its account id) is stored as its floors (review, 14 Oct)
  if (!Array.isArray(img) && Array.isArray(img.levels)) img = img.levels;
  let rec;
  if (Array.isArray(img)) {                        // every-storey: one per floor
    const l = img.filter(pbOkPic).slice(0, 8);
    if (!l.length) return;
    rec = (l.length === 1 && !l[0].level) ? { ...l[0], savedAt: Date.now() } : { levels: l, savedAt: Date.now() };
  } else {
    if (typeof img.b64 !== 'string') return;
    rec = { ...img, savedAt: Date.now() };
  }
  pbImgMem.delete(pid);
  pbImgMem.set(pid, rec);
  while (pbImgMem.size > PB_IMG_MAX) pbImgMem.delete(pbImgMem.keys().next().value);
  const db = await pbImgDb();
  if (!db) return;
  await pbImgRun(db, 'readwrite', st => { st.put(rec, pid); });
  const keys = await pbImgRun(db, 'readonly', st => {
    const out = [];
    const cur = st.openCursor();
    cur.onsuccess = () => { const c = cur.result; if (c) { out.push([c.key, (c.value && c.value.savedAt) || 0]); c.continue(); } };
    return out;
  });
  if (Array.isArray(keys) && keys.length > PB_IMG_MAX) {
    keys.sort((a, b) => b[1] - a[1]);
    const drop = keys.slice(PB_IMG_MAX).map(k => k[0]);
    await pbImgRun(db, 'readwrite', st => { drop.forEach(k => st.delete(k)); });
  }
}
async function pbCheckImgGet(pid) {
  if (!pid) return null;
  if (pbImgMem.has(pid)) return pbImgMem.get(pid);
  const db = await pbImgDb();
  if (!db) return null;
  const v = await pbImgRun(db, 'readonly', st => st.get(pid));
  if (v && typeof v === 'object' && (typeof v.b64 === 'string' || Array.isArray(v.levels))) { pbImgMem.set(pid, v); return v; }
  return null;
}
function pbCheckImgDrop(pid) {
  pbImgMem.delete(pid);
  pbImgDb().then(db => { if (db) pbImgRun(db, 'readwrite', st => { st.delete(pid); }); });
}
function pbCheckImgClearAll() {
  pbImgMem.clear();
  pbImgDb().then(db => { if (db) pbImgRun(db, 'readwrite', st => { st.clear(); }); });
}
// The quote as an email the tradie sends from their own mail app. A rough or
// partial take-off says so in the subject and at the top of the body.
function pbQuoteMailto(q) {
  const caveat = q.rough ? "NOTE: quantities are a rough read of the plan, not measured from the drawing. Check before relying on this quote."
    : q.partial ? "NOTE: part of the house wasn't measured, so these quantities are short. Check before relying on this quote."
    : q.extraFloors ? "NOTE: more floors were measured than the drawings show, so these quantities may be too big. Check before relying on this quote."
    // alter-v1-2026-10-23: an alteration measured as the whole building
    : q.wholeBldg ? "NOTE: this plan is an alteration and these quantities are the whole building, not just the new work. Check before relying on this quote." : null;
  const lines = [
    "Quote — " + q.name + " · " + q.tradeLabel, "",
    ...(caveat ? [caveat, ""] : []),
    "Materials (excl. GST): " + q.matText,
    "Labour (" + q.labourHours + " hrs @ " + fmt(q.labourRate) + "/hr): " + fmt(q.labourTotal),
    "Markup (" + q.markupPct + "%): " + fmt(q.markupTotal),
    "Subtotal: " + fmt(q.subtotal),
    "GST (15%): " + fmt(q.subtotal * 0.15),
    "Total (incl. GST): " + fmt(q.subtotal * 1.15), "",
    "Prepared with PlanBoss",
  ];
  return "mailto:?subject=" + encodeURIComponent("Quote — " + q.name + (caveat ? (q.rough ? " (rough estimate)" : q.partial ? " (partial)" : q.wholeBldg ? " (alteration — whole building)" : " (check the floors)") : "")) +
    "&body=" + encodeURIComponent(lines.join("\n"));
}
// Plan cards: where saved jobs live depends on whether filing is on.
function pbFeatureText(f) {
  if (f === "Jobs saved to your account") return pbFilingOn ? "Jobs saved to your account (any device)" : "Jobs saved on your device";
  return f;
}

// plancheck-v1-2026-10-13: what the scan can take, checked before anything is
// sent (the file picker AND drag-and-drop). The AI reads at most ~32 MB per
// request and the file goes up as base64 (a third bigger), so a PDF over
// 20 MB used to upload for a minute and then fail with a code.
const PB_MAX_PDF_MB = 20;
const PB_MAX_IMG_MB = 7;     // the AI takes images up to 10 MB once base64'd (a third bigger)
// What the scan will send the file as: a PDF, a PNG or a JPEG, decided from
// the name as well as the type (some phones label a PDF
// "application/octet-stream"). null = not something the scan can read.
function pbUploadType(f) {
  const n = String((f && f.name) || ""), t = String((f && f.type) || "").toLowerCase();
  if (/\.pdf$/i.test(n) || /pdf/.test(t)) return "application/pdf";
  if (/\.png$/i.test(n) || t === "image/png") return "image/png";
  if (/\.jpe?g$/i.test(n) || t === "image/jpeg" || t === "image/jpg") return "image/jpeg";
  return null;
}
function pbMB(bytes) { return (Math.ceil(bytes / 1048576 * 10) / 10).toLocaleString("en-NZ") + " MB"; }
function pbPlanFileProblem(f) {
  if (!f) return "No file chosen.";
  const kind = pbUploadType(f);
  if (!kind) return "PlanBoss reads PDF, PNG or JPG files. Export the plan as a PDF from the designer's software.";
  if (kind === "application/pdf" && f.size > PB_MAX_PDF_MB * 1048576) {
    return "This PDF is " + pbMB(f.size) + " — PlanBoss can take up to " + PB_MAX_PDF_MB + " MB. " +
      "Make a smaller PDF with just the plans: every floor's plan page, plus the sections and the window schedule.";
  }
  return null;
}
// Phone photos are often 4–14 MB and up to 8,000 px across. The AI takes
// images up to 10 MB (base64) and 8,000 px a side, and scales them down to
// about 1,500 px anyway. Shrink a big photo on the device first (long side
// 2,400 px, JPEG). If anything goes wrong the original is sent as it was.
async function pbPrepareImage(file) {
  try {
    if (!file || !/^image\/(png|jpeg)$/.test(file.type || "") || file.size <= 3.5 * 1048576) return file;
    if (typeof document === "undefined" || typeof URL === "undefined" || !URL.createObjectURL) return file;
    const url = URL.createObjectURL(file);
    let im;
    try {
      im = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
    } finally { setTimeout(() => URL.revokeObjectURL(url), 0); }
    const iw = im.naturalWidth || im.width, ih = im.naturalHeight || im.height;
    if (!iw || !ih) return file;
    const k = Math.min(1, 2400 / Math.max(iw, ih));
    const w = Math.max(1, Math.round(iw * k)), h = Math.max(1, Math.round(ih * k));
    const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
    const cx = cv.getContext && cv.getContext("2d");
    if (!cx) return file;
    cx.fillStyle = "#fff"; cx.fillRect(0, 0, w, h);   // a see-through PNG comes out white, not black
    cx.drawImage(im, 0, 0, w, h);
    const blob = await new Promise(res => { try { cv.toBlob(res, "image/jpeg", 0.85); } catch (e) { res(null); } });
    if (!blob || !blob.size || blob.size >= file.size) return file;
    return new File([blob], String(file.name || "plan").replace(/\.(png|jpe?g)$/i, "") + ".jpg", { type: "image/jpeg" });
  } catch (e) { return file; }
}

// Stripe's own billing page: change card, switch plan or cancel. Plan
// changes happen there, never on the device alone (the old "Downgrade"
// button only rewrote this phone's copy while Stripe kept billing).
async function pbOpenBillingPortal() {
  try {
    const r = await pbFetch("/billing-portal", { method: "POST", body: "{}" });
    const d = await r.json().catch(() => ({}));
    if (r.ok && d.url) { window.location.href = d.url; return true; }
    window.alert(d.message || ("Couldn't open billing. Email " + PB_SUPPORT_EMAIL + " and we'll sort it."));
  } catch (e) {
    window.alert((e && e.code === "signed_out") ? e.message : ("Couldn't open billing. Email " + PB_SUPPORT_EMAIL + " and we'll sort it."));
  }
  return false;
}

// What the app's feature gates treat a server plan as. The server decides how
// many scans; these only pick which screens show.
function pbFeaturePlan(ent) {
  if (!ent) return undefined;
  if (ent.plan === 'admin') return 'business';
  if (ent.plan === 'beta') return 'proplus';    // beta testers: Pro+ features, 25 scans
  if (ent.plan === 'trial') return 'pro';       // the 7-day trial is on Pro
  return PLANS[ent.plan] ? ent.plan : null;     // null = trial over, no plan
}
const pbPlanLabel = (p) => ({ pro: 'Pro', proplus: 'Pro+', business: 'Business', trial: 'Free trial',
  beta: 'Beta tester', admin: 'Admin' }[p] || (p ? String(p) : 'No plan'));
const pbDate = (ms, opts) => {
  if (!ms) return '—';
  try { return new Date(ms).toLocaleDateString('en-NZ', opts || { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch (e) { return '—'; }
};
// "2026-11-01" -> "1 Nov"
const pbDay = (iso) => { const t = Date.parse(iso + 'T00:00:00'); return Number.isNaN(t) ? iso : pbDate(t, { day: 'numeric', month: 'short' }); };
// A job row from the server -> the shape the job list uses. The scan results
// are fetched when the job is opened (or come from the device cache).
function pbShellFromRow(r, prev) {
  const s = r.summary || {};
  return {
    id: r.id, cloud: true, name: r.name || 'Untitled job', trades: Array.isArray(r.trades) ? r.trades : [],
    councilId: r.councilId || null, accuracy: r.accuracy || null, tuning: r.tuning || null,
    area: r.area || s.floor_m2 || null, createdAt: r.createdAt || null,
    date: pbDate(r.createdAt), status: r.status || 'complete',
    file: (r.file && r.file.name) || null, hasPdf: !!(r.file && r.file.stored),
    summary: r.summary || null, localAt: r.localAt || 0,
    aiAnalysis: prev && prev.aiAnalysis ? prev.aiAnalysis : undefined,
  };
}

// ─── Colour tokens ────────────────────────────────────────────────────────────
const C = {
  // PlanBoss brand colours
  ink:"#111827",inkMid:"#4B5563",inkLight:"#9CA3AF",
  border:"#E5E7EB",borderMid:"#D1D5DB",bg:"#F8FAFC",card:"#FFFFFF",
  // Primary — deep slate blue (authority, trust, professional)
  teal:"#1B3A5C",tealMid:"#1E4D7B",tealLight:"#DBEAFE",tealFaint:"#EFF6FF",
  // Accent — construction orange (energy, tools, action)
  amber:"#C2530A",amberLight:"#FED7AA",amberFaint:"#FFF7ED",
  // Status colours
  red:"#DC2626",redLight:"#FEE2E2",
  blue:"#1B3A5C",blueLight:"#DBEAFE",
  purple:"#6D28D9",purpleLight:"#EDE9FE",
  green:"#1A9E6B",greenLight:"#D1FAE5",
  yellow:"#D97706",yellowLight:"#FEF3C7",
  coral:"#EA580C",coralLight:"#FFEDD5",
  // PlanBoss orange — primary accent
  orange:"#F06400",orangeLight:"#FFEDD5",orangeFaint:"#FFF7ED",
  orangeMid:"#EA580C",
};

const PLANS = {
  // planboss-v1-2026-10-12: scans are 10 / 25 / 50 a month (Business was
  // "unlimited", Pro+ said 15 here and 25 elsewhere). "NZBC compliance" is
  // gone from every plan: the code check is a sample, not a real check.
  // PDF export is marked coming soon until it exists.
  // stripe-live-2 (Grant, 28 Oct): $50 / $100 / $200 a month + GST for 5 / 10 / 20 scans.
  // Trades per scan (Grant, 28 Oct): Pro 1, Pro+ 3, Business all. The server
  // enforces the same (pb_store tradeLimit) when filing is on.
  pro:      { id:"pro",      name:"Pro",      price:50,  analyses:5,  trades:1, compliance:true,  color:C.teal,   light:C.tealLight,
    features:["5 plan scans a month","1 trade per scan","Tradie take-off with the key metrics for your trade (plasterers live now)","Materials list","Jobs saved to your account","PDF export (coming soon)"],
    missing:["Supplier pricing comparison","Quote builder","Sense check","Team access"],
    persona:"Single-trade tradie — measurements & materials" },
  proplus:  { id:"proplus",  name:"Pro+",     price:100, analyses:10, trades:3, compliance:true,  color:C.orange, light:C.orangeLight, popular:true,
    features:["10 plan scans a month","3 trades per scan","Tradie take-off with the key metrics for your trade (plasterers live now)","Materials list","Jobs saved to your account","Supplier pricing comparison (coming soon)","Quote builder","Labour & markup","Sense check","Regional council notes","PDF export (coming soon)","1 team member"],
    missing:["Full job quote","Send to subie","Xero/MYOB export"],
    persona:"Single-trade tradie — full quoting" },
  business: { id:"business", name:"Business", price:200, analyses:20, trades:99,compliance:true,  color:C.purple, light:C.purpleLight,
    features:["20 plan scans a month","All trades","Tradie take-off with the key metrics for your trade (plasterers live now)","Jobs saved to your account","Full job quote (coming soon)","Send to subie (coming soon)","Sense check","Regional council notes","Xero/MYOB export (coming soon)","5 team members (coming soon)","Priority support"],
    missing:[],
    persona:"Project manager / subbies" },
};

const SUPPLIERS = [
  // Multipliers are relative to PlaceMakers trade account pricing (base = 1.00)
  // Full job cost · Based on NZ Commerce Commission building supplies market study + industry knowledge
  // Sorted by price LOW→HIGH for display (preferred supplier always shown first regardless)
  { id:"bunnings",    name:"Bunnings",    color:"#E8291C", mult:0.94, note:"Everyday low price — no account needed. Best for hardware and general materials.", coverage:"full", type:"retail" },
  { id:"mitre10",     name:"Mitre 10",    color:"#F07800", mult:0.97, note:"Trade discounts available. Good range across all categories.", coverage:"full", type:"retail" },
  { id:"placemakers", name:"PlaceMakers", color:"#006B3C", mult:1.00, note:"NZ's largest trade supplier. Best pricing with trade account.", coverage:"full", type:"trade" },
  { id:"itm",         name:"ITM",         color:"#1B5299", mult:1.02, note:"Co-op of 95 independent stores. Pricing varies by branch — negotiate locally.", coverage:"full", type:"trade" },
  { id:"carters",     name:"Carters",     color:"#2C2C2C", mult:1.06, note:"Trade specialist. Highest list price but best trade account discounts for loyal customers.", coverage:"full", type:"trade" },
];

// Sort order for display: preferred first, then lowest→highest price
// ─── REGIONAL SUPPLIER COVERAGE ──────────────────────────────────────────────
// Based on real NZ supplier branch presence as of 2025/2026.
// Sources: supplier store finders, Commerce Commission building supplies study.
// freightMult: additional cost multiplier on top of base price for remote areas.
// unavailable: suppliers with no branch presence in this region.
// limited: suppliers present but with reduced range or single branch only.

const REGIONAL_SUPPLIER_DATA = {
  // Auckland — full coverage all suppliers
  auckland_council:       { freightMult:1.00, unavailable:[], limited:[], note:null },

  // Northland
  far_north:              { freightMult:1.06, unavailable:["carters"], limited:["bunnings","mitre10"], note:"Bunnings/Mitre 10 limited to Whangarei — allow freight time to Far North." },
  whangarei:              { freightMult:1.02, unavailable:[], limited:["carters"], note:null },
  kaipara:                { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Limited retail presence — PlaceMakers and ITM are primary suppliers." },

  // Waikato
  thames_coromandel:      { freightMult:1.04, unavailable:["carters"], limited:["bunnings"], note:"Bunnings nearest in Hamilton — freight to Coromandel adds cost." },
  hauraki:                { freightMult:1.03, unavailable:["carters"], limited:["bunnings"], note:null },
  waikato_district:       { freightMult:1.01, unavailable:[], limited:[], note:null },
  matamata_piako:         { freightMult:1.02, unavailable:["carters"], limited:["bunnings"], note:null },
  hamilton:               { freightMult:1.00, unavailable:[], limited:[], note:null },
  waipa:                  { freightMult:1.01, unavailable:["carters"], limited:[], note:null },
  otorohanga:             { freightMult:1.04, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Primary suppliers: PlaceMakers and ITM. Retail options limited." },
  south_waikato:          { freightMult:1.03, unavailable:["carters"], limited:["bunnings"], note:null },
  waitomo:                { freightMult:1.06, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Rural — allow extra freight lead times." },
  taupo:                  { freightMult:1.03, unavailable:["carters"], limited:["bunnings"], note:null },

  // Bay of Plenty
  western_bay_plenty:     { freightMult:1.01, unavailable:[], limited:["carters"], note:null },
  tauranga:               { freightMult:1.00, unavailable:[], limited:[], note:null },
  rotorua:                { freightMult:1.02, unavailable:["carters"], limited:[], note:null },
  whakatane:              { freightMult:1.03, unavailable:["carters"], limited:["bunnings"], note:null },
  kawerau:                { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Small town — ITM and PlaceMakers primary options." },
  opotiki:                { freightMult:1.07, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Remote eastern Bay — freight premium applies." },

  // Gisborne
  gisborne:               { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Gisborne is isolated — PlaceMakers and ITM are well-established here." },

  // Hawke's Bay
  wairoa:                 { freightMult:1.08, unavailable:["carters","bunnings","mitre10"], limited:["itm"], note:"Very limited supplier presence. PlaceMakers primary — allow significant freight." },
  hastings:               { freightMult:1.01, unavailable:[], limited:["carters"], note:null },
  napier:                 { freightMult:1.01, unavailable:[], limited:["carters"], note:null },
  central_hawkes_bay:     { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:null },

  // Taranaki
  new_plymouth:           { freightMult:1.02, unavailable:[], limited:["carters"], note:null },
  stratford:              { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:null },
  south_taranaki:         { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:null },

  // Manawatū-Whanganui
  ruapehu:                { freightMult:1.09, unavailable:["carters","bunnings"], limited:["mitre10","itm"], note:"Very remote — PlaceMakers Taumarunui. High freight premium." },
  whanganui:              { freightMult:1.02, unavailable:["carters"], limited:["bunnings"], note:null },
  rangitikei:             { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:null },
  manawatu:               { freightMult:1.01, unavailable:[], limited:["carters"], note:null },
  palmerston_north:       { freightMult:1.00, unavailable:[], limited:[], note:null },
  tararua:                { freightMult:1.06, unavailable:["carters","bunnings"], limited:["mitre10"], note:null },
  horowhenua:             { freightMult:1.02, unavailable:["carters"], limited:[], note:null },

  // Wellington
  kapiti_coast:           { freightMult:1.01, unavailable:[], limited:[], note:null },
  porirua:                { freightMult:1.00, unavailable:[], limited:[], note:null },
  upper_hutt:             { freightMult:1.00, unavailable:[], limited:[], note:null },
  lower_hutt:             { freightMult:1.00, unavailable:[], limited:[], note:null },
  wellington_city:        { freightMult:1.00, unavailable:[], limited:[], note:null },
  masterton:              { freightMult:1.02, unavailable:["carters"], limited:[], note:null },
  carterton:              { freightMult:1.03, unavailable:["carters","bunnings"], limited:[], note:null },
  south_wairarapa:        { freightMult:1.04, unavailable:["carters","bunnings"], limited:["mitre10"], note:null },

  // Nelson-Marlborough
  nelson:                 { freightMult:1.02, unavailable:["carters"], limited:[], note:null },
  tasman:                 { freightMult:1.03, unavailable:["carters"], limited:["bunnings"], note:null },
  marlborough:            { freightMult:1.03, unavailable:["carters"], limited:["bunnings"], note:"Bunnings and Mitre 10 nearest in Nelson for some items." },

  // West Coast
  buller:                 { freightMult:1.12, unavailable:["carters","bunnings","mitre10"], limited:["itm"], note:"West Coast — very limited retail. PlaceMakers and ITM primary. Freight premium is significant." },
  grey:                   { freightMult:1.10, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Greymouth — freight costs are a real factor here." },
  westland:               { freightMult:1.15, unavailable:["carters","bunnings","mitre10"], limited:["itm"], note:"Most remote mainland district. PlaceMakers Hokitika. Allow 15% freight premium and longer lead times." },

  // Canterbury
  kaikoura:               { freightMult:1.06, unavailable:["carters","bunnings"], limited:["mitre10","itm"], note:"Small town — primary supply from Christchurch or Blenheim." },
  hurunui:                { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:null },
  waimakariri:            { freightMult:1.01, unavailable:[], limited:["carters"], note:"Christchurch metro area — full coverage." },
  christchurch:           { freightMult:1.00, unavailable:[], limited:[], note:null },
  selwyn:                 { freightMult:1.01, unavailable:[], limited:["carters"], note:null },
  ashburton:              { freightMult:1.03, unavailable:["carters"], limited:["bunnings"], note:null },
  timaru:                 { freightMult:1.02, unavailable:["carters"], limited:[], note:null },
  mackenzie:              { freightMult:1.10, unavailable:["carters","bunnings","mitre10"], limited:["itm"], note:"Mackenzie Basin — Tekapo/Twizel. Very limited local supply. Source from Christchurch or Timaru." },
  waimate:                { freightMult:1.05, unavailable:["carters","bunnings"], limited:["mitre10"], note:null },

  // Otago
  waitaki:                { freightMult:1.04, unavailable:["carters"], limited:["bunnings"], note:null },
  central_otago:          { freightMult:1.06, unavailable:["carters"], limited:["bunnings","mitre10"], note:"Alexandra has PlaceMakers and ITM. Cromwell growing. Bunnings nearest in Dunedin." },
  queenstown_lakes:       { freightMult:1.14, unavailable:["carters"], limited:["bunnings"], note:"Queenstown premium — highest labour and material costs in South Island. Bunnings Queenstown opened 2023. Allow 10-15% freight on non-stocked items." },
  dunedin:                { freightMult:1.00, unavailable:[], limited:[], note:null },
  clutha:                 { freightMult:1.06, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Balclutha — PlaceMakers and ITM primary suppliers." },

  // Southland
  southland_district:     { freightMult:1.07, unavailable:["carters","bunnings"], limited:["mitre10"], note:"Rural Southland — Winton/Riverton. Allow freight time from Invercargill." },
  gore:                   { freightMult:1.04, unavailable:["carters"], limited:["bunnings"], note:null },
  invercargill:           { freightMult:1.02, unavailable:["carters"], limited:[], note:null },
  stewart_island:         { freightMult:1.45, unavailable:["carters","bunnings","mitre10","itm"], limited:["placemakers"], note:"All materials must be shipped to Oban. Massive freight premium — allow 40-50% on top of mainland prices and 2-4 week lead times." },

  // Chatham Islands
  chatham_islands:        { freightMult:1.65, unavailable:["carters","bunnings","mitre10","itm"], limited:["placemakers"], note:"Extremely remote. All materials air or sea freighted. Allow 60-70% freight premium and 4-6 week lead times. Source everything before starting." },
};

// Get regional data for a council, fallback to no restrictions
function getRegionalData(councilId) {
  return REGIONAL_SUPPLIER_DATA[councilId] || { freightMult:1.02, unavailable:[], limited:[], note:null };
}

// Get suppliers available in a region, sorted preferred-first then low→high price
function getSortedSuppliers(preferredId, councilId) {
  const regional = getRegionalData(councilId);
  const available = SUPPLIERS.map(s => ({
    ...s,
    // Apply freight multiplier on top of base price mult
    effectiveMult: s.mult * regional.freightMult,
    isUnavailable: regional.unavailable.includes(s.id),
    isLimited: regional.limited.includes(s.id),
  }));
  const preferred = available.find(s=>s.id===preferredId);
  const rest = available.filter(s=>s.id!==preferredId).sort((a,b)=>a.effectiveMult-b.effectiveMult);
  return preferred ? [preferred,...rest] : [...available].sort((a,b)=>a.effectiveMult-b.effectiveMult);
}


const TRADES = [
  { id:"builder",    label:"Builder / Framer",      icon:"🏗", color:C.teal,   light:C.tealLight,   rateMin:85,  rateMax:130, totalMin:18000, totalMax:45000, unit:"m²",
    materials:[
      {name:"90×45 H1.2 framing timber",       qty:312, unit:"lm",     price:4.20,  cat:"Framing"},
      {name:"140×45 LVL bearer",                qty:24,  unit:"lm",     price:18.50, cat:"Framing"},
      {name:"Posi-strut floor joists",          qty:18,  unit:"lm",     price:22.00, cat:"Framing"},
      {name:"10mm structural ply",              qty:22,  unit:"sheets", price:68.00, cat:"Flooring"},
      {name:"Weatherboard cladding (primed)",   qty:148, unit:"lm",     price:12.40, cat:"Cladding"},
      {name:"Rigid air barrier (Wrap)",         qty:3,   unit:"rolls",  price:185.00,cat:"Cladding"},
      {name:"Cavity battens 45×18mm",           qty:120, unit:"lm",     price:1.85,  cat:"Cladding"},
      {name:"Joist hanger 90mm",                qty:48,  unit:"each",   price:4.80,  cat:"Hardware"},
      {name:"Framing nails 90mm (3kg)",         qty:6,   unit:"boxes",  price:28.00, cat:"Hardware"},
      {name:"Galv joist tie strap",             qty:24,  unit:"each",   price:3.20,  cat:"Hardware"},
      {name:"H3.2 piles 150×150",               qty:16,  unit:"each",   price:38.00, cat:"Foundation"},
      {name:"Scaffolding (weekly hire est.)",   qty:4,   unit:"weeks",  price:480.00,cat:"Temporary works"},
    ]},
  { id:"concrete",   label:"Concrete & Foundation",  icon:"🏛", color:"#7C6C55", light:"#F5F0E8",    rateMin:80,  rateMax:140, totalMin:12000, totalMax:40000, unit:"m²",
    materials:[
      {name:"Concrete 25MPa pump mix",          qty:18,  unit:"m³",     price:280.00,cat:"Concrete"},
      {name:"Concrete 30MPa (engineer spec)",   qty:4,   unit:"m³",     price:310.00,cat:"Concrete"},
      {name:"D12 deformed bar (6m lengths)",    qty:48,  unit:"lengths",price:28.50, cat:"Reinforcing"},
      {name:"D10 deformed bar (6m lengths)",    qty:36,  unit:"lengths",price:18.50, cat:"Reinforcing"},
      {name:"665 mesh sheet 6×2.4m",            qty:22,  unit:"sheets", price:95.00, cat:"Reinforcing"},
      {name:"Bar chairs 65mm (100pk)",          qty:3,   unit:"packs",  price:42.00, cat:"Reinforcing"},
      {name:"H4 treated timber formwork 200×50",qty:80,  unit:"lm",     price:8.40,  cat:"Formwork"},
      {name:"Form release oil 20L",             qty:2,   unit:"drums",  price:68.00, cat:"Formwork"},
      {name:"FormTie wire (1kg roll)",          qty:4,   unit:"rolls",  price:18.00, cat:"Formwork"},
      {name:"Polythene vapour barrier 0.25mm",  qty:180, unit:"m²",     price:1.20,  cat:"Damp proofing"},
      {name:"DPC 150mm (30m roll)",             qty:4,   unit:"rolls",  price:28.00, cat:"Damp proofing"},
      {name:"Drainage cell membrane",           qty:40,  unit:"m²",     price:12.50, cat:"Drainage"},
      {name:"100mm perforated ag-pipe",         qty:24,  unit:"lm",     price:4.80,  cat:"Drainage"},
      {name:"AP20 crusher dust (compacted)",    qty:12,  unit:"m³",     price:75.00, cat:"Sub-base"},
      {name:"AP40 hardfill base course",        qty:18,  unit:"m³",     price:68.00, cat:"Sub-base"},
    ]},
  { id:"insulation", label:"Insulation",              icon:"🧱", color:"#D4820A", light:"#FEF3DC",    rateMin:18,  rateMax:38,  totalMin:4500,  totalMax:14000, unit:"m²",
    materials:[
      {name:"Pink Batts R2.2 ceiling 140mm",    qty:160, unit:"m²",     price:14.50, cat:"Ceiling"},
      {name:"Pink Batts R2.9 ceiling 190mm",    qty:20,  unit:"m²",     price:19.50, cat:"Ceiling"},
      {name:"Pink Batts R1.8 wall 90mm",        qty:180, unit:"m²",     price:9.80,  cat:"Walls"},
      {name:"Pink Batts R2.2 wall 140mm",       qty:20,  unit:"m²",     price:12.50, cat:"Walls"},
      {name:"Underfloor Pink Batts R1.3",       qty:145, unit:"m²",     price:8.40,  cat:"Underfloor"},
      {name:"Underfloor support netting",       qty:145, unit:"m²",     price:2.20,  cat:"Underfloor"},
      {name:"Rigid foam board EPS 50mm",        qty:40,  unit:"m²",     price:22.00, cat:"Rigid board"},
      {name:"Rigid foam board XPS 25mm",        qty:20,  unit:"m²",     price:28.00, cat:"Rigid board"},
      {name:"Reflective foil underlay",         qty:160, unit:"m²",     price:3.80,  cat:"Foil"},
      {name:"Proprietary wall wrap (H1)",       qty:180, unit:"m²",     price:4.20,  cat:"Foil"},
      {name:"Acoustic insulation R1.8 90mm",    qty:40,  unit:"m²",     price:14.00, cat:"Acoustic"},
      {name:"Acoustic sealant 600ml",           qty:6,   unit:"tubes",  price:22.00, cat:"Acoustic"},
      {name:"Staples 35mm (5000pk)",            qty:3,   unit:"boxes",  price:18.00, cat:"Fixings"},
    ]},
  { id:"plasterer",  label:"Plasterer (GIB)",        icon:"🪣", color:C.blue,   light:C.blueLight,   rateMin:38,  rateMax:65,  totalMin:4500,  totalMax:12000, unit:"m²",
    materials:[
      {name:"GIB Standard 10mm 2400×1200",      qty:48,  unit:"sheets", price:24.50, cat:"Board"},
      {name:"GIB Aqualine 10mm (wet areas)",     qty:12,  unit:"sheets", price:38.00, cat:"Board"},
      {name:"GIB Braceline 10mm",               qty:8,   unit:"sheets", price:32.00, cat:"Board"},
      {name:"Tradeset stopping compound 20kg",  qty:8,   unit:"bags",   price:42.00, cat:"Stopping"},
      {name:"GIB jointing tape 150m",           qty:4,   unit:"rolls",  price:18.00, cat:"Stopping"},
      {name:"GIB finishing compound 15L",       qty:5,   unit:"pails",  price:36.00, cat:"Stopping"},
      {name:"L-bead 3000mm",                    qty:36,  unit:"lengths",price:3.80,  cat:"Beads"},
      {name:"Corner bead 3000mm",               qty:24,  unit:"lengths",price:3.20,  cat:"Beads"},
      {name:"GIB screws 32mm (500pk)",          qty:6,   unit:"boxes",  price:14.50, cat:"Fixings"},
    ]},
  { id:"landscaper", label:"Landscaper",              icon:"🌿", color:C.green,  light:C.greenLight,  rateMin:45,  rateMax:85,  totalMin:3500,  totalMax:18000, unit:"m²",
    materials:[
      {name:"Topsoil screened (bulk)",          qty:12,  unit:"m³",     price:95.00, cat:"Fill"},
      {name:"AP40 aggregate base",              qty:8,   unit:"m³",     price:85.00, cat:"Fill"},
      {name:"Concrete 25MPa pump mix",          qty:3.5, unit:"m³",     price:280.00,cat:"Concrete"},
      {name:"Weedmat 3.66m wide",               qty:2,   unit:"rolls",  price:145.00,cat:"Ground"},
      {name:"Bark mulch fine (bulk)",           qty:5,   unit:"m³",     price:68.00, cat:"Ground"},
      {name:"Concrete edging 1800mm",           qty:20,  unit:"lengths",price:18.50, cat:"Edging"},
      {name:"Treated retaining timber 200×50",  qty:36,  unit:"lm",     price:8.40,  cat:"Edging"},
      {name:"Geotextile drainage fabric",       qty:40,  unit:"m²",     price:4.20,  cat:"Drainage"},
    ]},
  { id:"plumber",    label:"Plumber",                 icon:"🔧", color:C.coral,  light:C.coralLight,  rateMin:95,  rateMax:145, totalMin:8000,  totalMax:28000, unit:"fixture",
    materials:[
      {name:"25mm copper pipe",                 qty:18,  unit:"lm",     price:18.50, cat:"Pipe"},
      {name:"100mm PVC waste pipe",             qty:12,  unit:"lm",     price:8.40,  cat:"Pipe"},
      {name:"50mm PVC waste pipe",              qty:20,  unit:"lm",     price:5.20,  cat:"Pipe"},
      {name:"Pressure-reducing valve",          qty:1,   unit:"each",   price:185.00,cat:"Valves"},
      {name:"Isolation valve 20mm",             qty:8,   unit:"each",   price:28.00, cat:"Valves"},
      {name:"Brass elbow 25mm",                 qty:12,  unit:"each",   price:9.50,  cat:"Fittings"},
      {name:"P-trap 40mm",                      qty:4,   unit:"each",   price:18.00, cat:"Fittings"},
      {name:"Silicone sealant 300ml",           qty:3,   unit:"tubes",  price:14.00, cat:"Sealing"},
    ]},
  { id:"electrician",label:"Electrician",             icon:"⚡", color:C.yellow, light:C.yellowLight, rateMin:90,  rateMax:135, totalMin:6000,  totalMax:22000, unit:"m²",
    materials:[
      {name:"2.5mm² TPS twin+earth cable",      qty:80,  unit:"lm",     price:2.80,  cat:"Cable"},
      {name:"4mm² TPS twin+earth cable",        qty:20,  unit:"lm",     price:4.20,  cat:"Cable"},
      {name:"16-way switchboard (main)",        qty:1,   unit:"each",   price:380.00,cat:"Switchboard"},
      {name:"RCD protected MCB 16A",            qty:6,   unit:"each",   price:48.00, cat:"Switchboard"},
      {name:"Double GPO with USB",              qty:14,  unit:"each",   price:28.00, cat:"Outlets"},
      {name:"LED downlight dimmable 10W",       qty:16,  unit:"each",   price:32.00, cat:"Lighting"},
      {name:"Smoke alarm photoelectric",        qty:3,   unit:"each",   price:68.00, cat:"Safety"},
      {name:"Exhaust fan 250mm",                qty:2,   unit:"each",   price:95.00, cat:"Ventilation"},
    ]},
  { id:"roofer",     label:"Roofer",                  icon:"🏠", color:C.purple, light:C.purpleLight, rateMin:55,  rateMax:95,  totalMin:6000,  totalMax:32000, unit:"m²",
    materials:[
      {name:"Colorsteel Endura 0.55mm",         qty:68,  unit:"m²",     price:28.50, cat:"Roofing"},
      {name:"Ridge capping",                    qty:12,  unit:"lm",     price:22.00, cat:"Roofing"},
      {name:"Barge flashing",                   qty:18,  unit:"lm",     price:18.50, cat:"Roofing"},
      {name:"Butyl tape flashing",              qty:4,   unit:"rolls",  price:45.00, cat:"Flashing"},
      {name:"Self-drilling tek screws 250pk",   qty:4,   unit:"boxes",  price:22.00, cat:"Fixings"},
      {name:"Roofing underlay breathable",      qty:2,   unit:"rolls",  price:195.00,cat:"Underlay"},
      {name:"PVC spouting 100mm",               qty:18,  unit:"lm",     price:12.00, cat:"Guttering"},
      {name:"Downpipe 80mm PVC",                qty:6,   unit:"lm",     price:14.50, cat:"Guttering"},
    ]},
  { id:"drainlayer", label:"Drainlayer",                icon:"🌊", color:"#2980B9", light:"#EBF5FB",    rateMin:85,  rateMax:130, totalMin:8000,  totalMax:25000, unit:"connection",
    materials:[
      {name:"100mm PVC stormwater pipe",        qty:28,  unit:"lm",     price:9.80,  cat:"Stormwater"},
      {name:"100mm PVC fittings (elbows/tees)", qty:12,  unit:"each",   price:8.50,  cat:"Stormwater"},
      {name:"Stormwater sump/grate 300mm",      qty:4,   unit:"each",   price:185.00,cat:"Stormwater"},
      {name:"150mm PVC sewer pipe",             qty:18,  unit:"lm",     price:14.50, cat:"Sewer"},
      {name:"150mm PVC bends/tees",             qty:8,   unit:"each",   price:22.00, cat:"Sewer"},
      {name:"Inspection chamber 300mm",         qty:2,   unit:"each",   price:380.00,cat:"Sewer"},
      {name:"Soakage pit precast 1200mm",       qty:2,   unit:"each",   price:650.00,cat:"Soakage"},
      {name:"Soakage aggregate 20mm",           qty:4,   unit:"m³",     price:85.00, cat:"Soakage"},
      {name:"Geotextile filter fabric",         qty:30,  unit:"m²",     price:4.80,  cat:"Soakage"},
      {name:"100mm perforated ag-pipe",         qty:20,  unit:"lm",     price:4.80,  cat:"Soakage"},
      {name:"AP20 backfill aggregate",          qty:6,   unit:"m³",     price:75.00, cat:"Backfill"},
      {name:"Compactable gravel backfill",      qty:4,   unit:"m³",     price:68.00, cat:"Backfill"},
      {name:"Tracer wire 500m",                 qty:1,   unit:"roll",   price:85.00, cat:"Compliance"},
      {name:"Warning tape (buried services)",   qty:2,   unit:"rolls",  price:28.00, cat:"Compliance"},
    ]},
  { id:"gasfitter",  label:"Gasfitter",                icon:"🔥", color:"#E74C3C", light:"#FDEDEC",    rateMin:95,  rateMax:145, totalMin:2500,  totalMax:12000, unit:"point",
    materials:[
      {name:"20mm copper gas pipe",             qty:18,  unit:"lm",     price:16.80, cat:"Pipework"},
      {name:"25mm copper gas pipe",             qty:8,   unit:"lm",     price:22.50, cat:"Pipework"},
      {name:"Gas ball valve 20mm",              qty:4,   unit:"each",   price:48.00, cat:"Valves"},
      {name:"Gas ball valve 25mm (main)",       qty:1,   unit:"each",   price:85.00, cat:"Valves"},
      {name:"Flare fittings 20mm (pk10)",       qty:2,   unit:"packs",  price:65.00, cat:"Fittings"},
      {name:"Gas flex connector 600mm",         qty:4,   unit:"each",   price:38.00, cat:"Fittings"},
      {name:"Gas meter box recessed",           qty:1,   unit:"each",   price:285.00,cat:"Metering"},
      {name:"Pressure test gauge kit",          qty:1,   unit:"each",   price:145.00,cat:"Testing"},
      {name:"Gas pipe clips 20mm (50pk)",       qty:2,   unit:"packs",  price:18.00, cat:"Fixings"},
      {name:"Pipe lagging foam 20mm",           qty:12,  unit:"lm",     price:3.20,  cat:"Fixings"},
      {name:"Flashband gas penetration seal",   qty:2,   unit:"each",   price:28.00, cat:"Sealing"},
      {name:"LPG regulator (if bottled gas)",   qty:1,   unit:"each",   price:95.00, cat:"Connections"},
    ]},
];

// ─── TRADE METRICS CONFIG ─────────────────────────────────────────────────────
// Each trade has specific measurements tradies use to price their work.
// These drive both the results display and the account preferences panel.
const TRADE_METRICS = {
  builder: {
    label: "Builder / Framer",
    metrics: [
      { id:"floor_area",     label:"Floor area",        unit:"m²",  description:"Total floor area including all levels" },
      { id:"wall_area",      label:"Wall framing",       unit:"lm",  description:"Total lineal metres of wall framing" },
      { id:"roof_area",      label:"Roof area",          unit:"m²",  description:"Total roof area (raked measurement)" },
      { id:"ext_cladding",   label:"External cladding",  unit:"m²",  description:"External wall area for cladding" },
    ],
    deductions: [
      { id:"deduct_windows", label:"Auto-deduct windows & doors from wall area", defaultOn:true },
    ],
    rateFields: [
      { id:"rate_m2",        label:"Rate per m² (floor area)",   unit:"$/m²",  default:85 },
    ]
  },
  concrete: {
    label: "Concrete & Foundation",
    metrics: [
      { id:"slab_area",      label:"Slab area",          unit:"m²",  description:"Total concrete slab area" },
      { id:"slab_volume",    label:"Concrete volume",    unit:"m³",  description:"Total concrete required" },
      { id:"reinf_length",   label:"Reinforcing",        unit:"lm",  description:"Total reinforcing bar length" },
    ],
    deductions: [],
    rateFields: [
      { id:"rate_m2",        label:"Rate per m² (slab)",          unit:"$/m²",  default:110 },
      { id:"rate_m3",        label:"Rate per m³ (concrete)",      unit:"$/m³",  default:280 },
    ]
  },
  insulation: {
    label: "Insulation",
    metrics: [
      { id:"ceiling_area",   label:"Ceiling area",       unit:"m²",  description:"Total ceiling insulation area" },
      { id:"wall_area",      label:"Wall area",          unit:"m²",  description:"Total wall insulation area (after deductions)" },
      { id:"underfloor",     label:"Underfloor area",    unit:"m²",  description:"Total underfloor insulation area" },
    ],
    deductions: [
      { id:"deduct_windows", label:"Auto-deduct windows & doors from wall area", defaultOn:true },
    ],
    rateFields: [
      { id:"rate_ceiling",   label:"Rate per m² (ceiling)",       unit:"$/m²",  default:22 },
      { id:"rate_wall",      label:"Rate per m² (walls)",         unit:"$/m²",  default:18 },
      { id:"rate_floor",     label:"Rate per m² (underfloor)",    unit:"$/m²",  default:14 },
    ]
  },
  plasterer: {
    label: "Plasterer (GIB)",
    metrics: [
      { id:"ceiling_area",    label:"Ceiling",  unit:"m²", description:"Total GIB ceiling area" },
      { id:"wall_area",       label:"Walls",    unit:"m²", description:"Total GIB wall area (windows & doors deducted)" },
      { id:"square_stop",     label:"Square",   unit:"lm", description:"Square stop / square set lineal metres" },
      { id:"external_corner", label:"External", unit:"lm", description:"External (convex) corners" },
    ],
    deductions: [
      { id:"deduct_windows", label:"Auto-deduct windows & doors from wall area", defaultOn:true },
      { id:"deduct_doors",   label:"Auto-deduct door openings",                  defaultOn:true },
    ],
    rateFields: [
      { id:"rate_walls",     label:"Rate per m² (walls)",         unit:"$/m²",  default:9.50 },
      { id:"rate_ceiling",   label:"Rate per m² (ceiling)",       unit:"$/m²",  default:12.00 },
      { id:"rate_coving",    label:"Rate per lm (coving)",        unit:"$/lm",  default:8.50 },
    ]
  },
  landscaper: {
    label: "Landscaper",
    metrics: [
      { id:"garden_area",    label:"Garden / lawn area", unit:"m²",  description:"Total landscaping area" },
      { id:"paving_area",    label:"Paving / concrete",  unit:"m²",  description:"Hard surface area" },
      { id:"retaining_lm",   label:"Retaining walls",    unit:"lm",  description:"Total lineal metres of retaining" },
    ],
    deductions: [],
    rateFields: [
      { id:"rate_garden",    label:"Rate per m² (soft landscaping)", unit:"$/m²",  default:55 },
      { id:"rate_paving",    label:"Rate per m² (hard surfaces)",    unit:"$/m²",  default:120 },
    ]
  },
  plumber: {
    label: "Plumber",
    metrics: [
      { id:"fixtures",       label:"Fixture count",      unit:"each", description:"Total plumbing fixtures (taps, toilets, basins etc)" },
      { id:"pipe_lm",        label:"Pipe runs",          unit:"lm",   description:"Total lineal metres of pipework" },
      { id:"hot_cold",       label:"Hot & cold points",  unit:"each", description:"Number of hot/cold connection points" },
    ],
    deductions: [],
    rateFields: [
      { id:"rate_fixture",   label:"Rate per fixture",               unit:"$/fixture", default:850 },
      { id:"rate_pipe",      label:"Rate per lm (pipework)",         unit:"$/lm",      default:65 },
    ]
  },
  electrician: {
    label: "Electrician",
    metrics: [
      { id:"floor_area",     label:"Floor area",         unit:"m²",  description:"Total floor area (for cable run estimates)" },
      { id:"light_points",   label:"Light points",       unit:"each", description:"Number of lighting outlets" },
      { id:"power_points",   label:"Power points",       unit:"each", description:"Number of GPOs / double outlets" },
      { id:"circuits",       label:"Circuit count",      unit:"each", description:"Number of separate circuits" },
    ],
    deductions: [],
    rateFields: [
      { id:"rate_light",     label:"Rate per light point",           unit:"$/point",  default:180 },
      { id:"rate_power",     label:"Rate per power point",           unit:"$/point",  default:160 },
      { id:"rate_m2",        label:"Rate per m² (floor area)",       unit:"$/m²",     default:65 },
    ]
  },
  roofer: {
    label: "Roofer",
    metrics: [
      { id:"roof_area",      label:"Roof area",          unit:"m²",  description:"Total raked roof area" },
      { id:"ridge_lm",       label:"Ridge / hip",        unit:"lm",  description:"Total lineal metres of ridge and hip" },
      { id:"gutter_lm",      label:"Guttering",          unit:"lm",  description:"Total lineal metres of spouting & downpipes" },
      { id:"flashing_lm",    label:"Flashings",          unit:"lm",  description:"Total lineal metres of flashing" },
    ],
    deductions: [],
    rateFields: [
      { id:"rate_m2",        label:"Rate per m² (roofing)",          unit:"$/m²",  default:75 },
      { id:"rate_gutter",    label:"Rate per lm (guttering)",        unit:"$/lm",  default:45 },
    ]
  },
  drainlayer: {
    label: "Drainlayer",
    metrics: [
      { id:"stormwater_lm",  label:"Stormwater runs",    unit:"lm",  description:"Total lineal metres of stormwater pipe" },
      { id:"sewer_lm",       label:"Sewer / waste runs", unit:"lm",  description:"Total lineal metres of sewer pipe" },
      { id:"connections",    label:"Connections",        unit:"each", description:"Number of inspection chambers / connections" },
    ],
    deductions: [],
    rateFields: [
      { id:"rate_pipe",      label:"Rate per lm (drainage pipe)",    unit:"$/lm",  default:95 },
    ]
  },
  gasfitter: {
    label: "Gasfitter",
    metrics: [
      { id:"pipe_lm",        label:"Gas pipe runs",      unit:"lm",  description:"Total lineal metres of gas pipework" },
      { id:"points",         label:"Gas points",         unit:"each", description:"Number of gas outlet points" },
      { id:"appliances",     label:"Appliance connections", unit:"each", description:"Number of appliances to connect" },
    ],
    deductions: [],
    rateFields: [
      { id:"rate_point",     label:"Rate per gas point",             unit:"$/point", default:450 },
      { id:"rate_pipe",      label:"Rate per lm (gas pipe)",         unit:"$/lm",    default:85 },
    ]
  },
};

// Get trade metrics preferences for a user (falls back to defaults)
function getTradeMetricPrefs(prefs, tradeId) {
  return prefs.tradeMetrics?.[tradeId] || {};
}

// ─── COMING SOON TRADES ───────────────────────────────────────────────────────
const COMING_SOON_TRADES = [
  { id:"windows",   label:"Windows & Doors",     icon:"🪟", color:"#5B8FA8", eta:"Stage 2",
    teaser:"Joinery supply and install. Huge cost variance — wizard guides you through glazing type, frame material and opening count.",
    reason:"High price variance requires a guided wizard to quote accurately. Coming in Stage 2." },
  { id:"painter",   label:"Painter",              icon:"🖌️", color:"#E8A838", eta:"Stage 2",
    teaser:"Interior and exterior painting. Includes primer, undercoat and topcoats. Separate options for windows/doors.",
    reason:"Window/door inclusion varies significantly by contractor. Wizard coming in Stage 2." },
  { id:"kitchen",   label:"Kitchen & Bathrooms",  icon:"🍳", color:"#C0392B", eta:"Stage 2",
    teaser:"Cabinetry, benchtops, fixtures and tiling. Flat-pack through to full custom — massive price range.",
    reason:"Spec variance (flat-pack to custom) is too large without a guided wizard. Coming in Stage 2." },
  { id:"flooring",  label:"Floor Coverings",      icon:"🪵", color:"#8B6914", eta:"Stage 2",
    teaser:"Carpet, timber, vinyl plank and tiles. Priced by room type and grade.",
    reason:"Material mix (carpet vs timber vs tile) varies too much without room-by-room input. Coming in Stage 2." },
  { id:"hvac",      label:"HVAC & Ventilation",   icon:"❄️", color:"#16A085", eta:"Stage 3",
    teaser:"Heat pumps, HRV/DVS ventilation systems and ducting.",
    reason:"Coming in Stage 3." },
];


const COMPLIANCE = [
  {rule:"E2/AS1 — External moisture",      status:"pass",detail:"Cladding overlap and flashing details appear compliant."},
  {rule:"H1/AS1 — Energy efficiency",      status:"warn",detail:"Roof insulation R-value not specified. Minimum R2.9 for Climate Zone 2."},
  {rule:"G6 — Airborne & impact sound",    status:"pass",detail:"Internal wall construction meets minimum STC requirements."},
  {rule:"F4 — Safety from falling",        status:"fail",detail:"Deck balustrade appears below 1000mm minimum. Update plan before consent."},
  {rule:"G7 — Natural light",              status:"pass",detail:"Window-to-floor ratio meets 10% minimum in all habitable rooms."},
  {rule:"G4 — Ventilation",               status:"warn",detail:"Bathroom mechanical extract not shown. Required per G4/AS1."},
  {rule:"B1 — Structure",                  status:"pass",detail:"Bracing layout appears adequate — confirm with LBP engineer."},
  {rule:"C — Fire protection",             status:"pass",detail:"Smoke alarm positions and separations appear compliant."},
];

const planOrder = ["pro","proplus","business"];
// ─── ADMIN CONFIG ─────────────────────────────────────────────────────────────
// security-v1-2026-10-12: the hardcoded admin email/password that lived here
// is gone. Admins sign in with their own Firebase account and the server's
// /me answer (PB_ADMIN_EMAILS on Render) decides. Change that old password
// anywhere it was reused: it was public in the app's JavaScript.
const DEFAULT_FLAGS = {
  showComingSoon:    { label:"Show coming soon trades",   value:true  },
  allowNewSignups:   { label:"Allow new signups",         value:true  },
  trialActive:       { label:"7-day free trial active",   value:true  },
  showProPlus:       { label:"Show Pro+ plan",            value:true  },
  maintenanceMode:   { label:"Maintenance mode",          value:false },
  showRoomBreakdown: { label:"Room breakdown tab",        value:true  },
};


// Plan capability flags — single source of truth for feature gating

// ─── ADMIN GATING ─────────────────────────────────────────────────────────────
// Single source of truth for "is this user admin or admin testing?"
// Admin = real admin login (role: "admin") OR admin testing as a regular user
// (isAdminTest: true). Both paths set the user object in localStorage.
//
// USAGE: const u = LS.get("to_user", null); if (isAdminUser(u)) { ... }
function isAdminUser(user) {
  return !!(user && (user.role === "admin" || user.isAdminTest === true));
}

// Trades currently live in the product. Add a trade ID here when its AI prompt,
// materials list, sense check rates, and end-to-end testing are complete.
// Non-admin users see other trades as "Coming soon" with click-to-register interest.
// When a trade is added here, it becomes immediately available to all users.
const LIVE_TRADES = ["plasterer"];

// Helper: is a given trade live for a given user?
// Admin sees all trades as live. Non-admin only sees trades in LIVE_TRADES.
function isTradeLive(tradeId, user) {
  if (isAdminUser(user)) return true;
  return LIVE_TRADES.includes(tradeId);
}

// Capture clicks on Coming Soon trades for analytics — tells us which trade
// to ship next based on actual user demand, not guesses.
function logComingSoonInterest(tradeId, user) {
  try {
    const key = "to_coming_soon_interest";
    const existing = LS.get(key, {}) || {};
    const userKey = (user && user.email) || "anonymous";
    if (!existing[tradeId]) existing[tradeId] = { totalClicks: 0, users: {} };
    existing[tradeId].totalClicks = (existing[tradeId].totalClicks || 0) + 1;
    existing[tradeId].users[userKey] = (existing[tradeId].users[userKey] || 0) + 1;
    existing[tradeId].lastClick = new Date().toISOString();
    LS.set(key, existing);
  } catch (e) { /* silent — analytics shouldn't break UX */ }
}

const PLAN_CAN = {
  supplierPricing: (pid) => ["proplus","business"].includes(pid),
  quoteBuilder:    (pid) => ["proplus","business"].includes(pid),
  senseCheck:      (pid) => ["proplus","business"].includes(pid),
  fullJobQuote:    (pid) => pid === "business",
  sendToSubie:     (pid) => pid === "business",
  xeroExport:      (pid) => pid === "business",
  multiTrade:      (pid) => true, // all plans get 3 trades
  nzbc:            (pid) => true, // all plans get NZBC
};

const DEFAULT_PREFS = { labourRate:95, labourHours:12, markupPct:15, preferredSupplier:"placemakers", preferredTrade:"plasterer", region:"auckland_council", tradeMetrics:{}, wastagePercent:10 };

// ─── MATERIAL PRICES (NZ trade rates) ─────────────────────────────────────────
// Last updated: 30 Apr 2026
// Update monthly — pull current trade prices from PlaceMakers/ITM/Mitre 10
// AI returns material names; app looks up prices here.
// If a material isn't in this list, price shows as $— and tradie can correct.
//
// Match keys: lowercase, trimmed. Use partial match (includes).
const MATERIAL_PRICES_NZ = {
  // Boards
  "gib standard 10mm": 25,
  "gib aqualine 10mm": 38,
  "gib aqualine 13mm ceiling": 42,
  "gib aqualine 13mm": 42,
  "gib ceiling 13mm": 32,
  "gib braceline 10mm": 32,
  "gib fyreline 13mm": 48,
  "gib fyreline 16mm": 58,
  "gib noiseline": 45,
  "gib toughline": 38,

  // Stopping & jointing
  "tradeset stopping compound 20kg": 42,
  "trade-set 45 base coat": 38,
  "gib plus 4 finishing compound 15l": 36,
  "gib plus 4": 36,
  "gib finishing compound": 36,
  "gib paper tape 75m": 18,
  "gib paper tape": 18,
  "gib jointing tape 150m": 22,
  "gib mesh tape": 24,

  // Beads & trims
  "l-bead 3000mm": 4,
  "l-bead": 4,
  "corner bead 3000mm": 3,
  "corner bead": 3,
  "gib goldline external corner": 5,
  "goldline external corner": 5,
  "gib casing bead": 4,
  "casing bead": 4,
  "gib aluminium l-trim": 6,
  "crack stopping bead": 4,

  // Fasteners
  "gib grabber drywall screws 32mm 1000pk": 24,
  "gib grabber drywall screws 41mm 1000pk": 28,
  "gib screws 32mm": 15,
  "gib screws 41mm": 18,

  // Bracing accessories
  "gib handibrac": 12,
  "handibrac hold-down": 12,

  // Wet area extras
  "acrylic sealant for wet area junctions 300ml": 8,
  "acrylic sealant": 8,
  "silicone sealant": 9,

  // Ceiling/structural
  "rondo metal ceiling battens 3.6m": 14,
  "ceiling battens": 14,
  "coving": 18,
};

// ============================================================
// TRADE_CORRECTIONS_CONFIG
//
// Smart-split corrections system config (added by patch-corrections-foundation-v1).
// Plasterer is fully populated; other trades are stubs that future patches will fill.
//
// Each trade defines:
//   - rootMeasurements:   fields that AI captures directly (m², lm, counts)
//   - derivedMaterials:   fields computed FROM root measurements (sheet counts etc.)
//   - reasons.config:     reasons that route to direct edit (supplier/spec)
//   - reasons.measurement: reasons that route upstream (AI was wrong)
//
// When user picks a measurement reason, MeasurementEditModal flips to ask for
// the upstream root value. Downstream materials auto-recalc.
// When user picks a config reason, direct edit + global by default.
// ============================================================

const TRADE_CORRECTIONS_CONFIG = {
  plasterer: {
    rootMeasurements: [
      { key: 'wallM2',     label: 'Wall area',     unit: 'm²' },
      { key: 'ceilingM2',  label: 'Ceiling area',  unit: 'm²' },
      { key: 'floorM2',    label: 'Floor area',    unit: 'm²' },
      { key: 'studHeight', label: 'Stud height',   unit: 'm'  },
    ],
    derivedMaterials: [
      'gibStandard', 'gibBraceline', 'gibAqualine', 'gibFyreline',
      'gibCeiling13mm', 'plasterBag', 'tape', 'cornice', 'screws',
      'casing-bead', 'goldline', 'l-bead', 'acrylic-sealant',
    ],
    reasons: {
      config: [
        { value: 'sheet-width',       label: 'Different sheet width' },
        { value: 'sheet-length',      label: 'Different sheet length' },
        { value: 'supplier-waste',    label: 'Different supplier waste %' },
        { value: 'brand',             label: 'Different brand' },
      ],
      measurement: [
        { value: 'missed-wall',       label: 'AI missed a wall', upstreamField: 'wallM2' },
        { value: 'stud-height',       label: 'Different stud height', upstreamField: 'studHeight' },
        { value: 'scale-wrong',       label: 'Scale on plan was wrong', upstreamField: null },
        { value: 'wall-area-wrong',   label: 'Wall area is wrong', upstreamField: 'wallM2' },
        { value: 'window-door',       label: 'Window/door deduction wrong', upstreamField: 'wallM2' },
        { value: 'plan-unclear',      label: 'Plan was unclear', upstreamField: null },
      ],
    },
  },

  // Stubs — future patches fill these in trade-by-trade.
  builder:  { rootMeasurements: [], derivedMaterials: [], reasons: { config: [], measurement: [] } },
  roofer:   { rootMeasurements: [], derivedMaterials: [], reasons: { config: [], measurement: [] } },
  concrete: { rootMeasurements: [], derivedMaterials: [], reasons: { config: [], measurement: [] } },
  plumber:  { rootMeasurements: [], derivedMaterials: [], reasons: { config: [], measurement: [] } },
  sparkie:  { rootMeasurements: [], derivedMaterials: [], reasons: { config: [], measurement: [] } },
};

// Helper: get the corrections config for a trade, with a safe fallback.
function getTradeCorrectionsConfig(trade) {
  return TRADE_CORRECTIONS_CONFIG[trade] || {
    rootMeasurements: [],
    derivedMaterials: [],
    reasons: { config: [], measurement: [] },
  };
}

// One-time migration: wipe legacy correction keys on first load after this patch.
// CORRECTIONS_SCHEMA_VERSION bumps on every breaking schema change.
// Self-disabling: only runs when the stored version is missing or older than current.
const CORRECTIONS_SCHEMA_VERSION = 2;
function migrateCorrectionsSchemaIfNeeded() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    const stored = parseInt(localStorage.getItem('to_corrections_schema_version') || '1', 10);
    if (stored >= CORRECTIONS_SCHEMA_VERSION) return;
    console.log('[corrections] schema migration: wiping legacy keys v' + stored + ' → v' + CORRECTIONS_SCHEMA_VERSION);
    const prefixes = ['to_corrections_', 'price_overrides_', 'user_price_overrides_', 'to_unknown_materials_'];
    const toDelete = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (prefixes.some(p => key.startsWith(p))) toDelete.push(key);
    }
    toDelete.forEach(k => localStorage.removeItem(k));
    localStorage.setItem('to_corrections_schema_version', String(CORRECTIONS_SCHEMA_VERSION));
    console.log('[corrections] migration done. Wiped ' + toDelete.length + ' keys.');
  } catch (e) {
    console.warn('[corrections] migration failed (non-fatal):', e);
  }
}

// Run the migration once at module load.
migrateCorrectionsSchemaIfNeeded();

// isWetCapable: returns true for room names where wet-area measurements
// plausibly apply. Used by RoomCard (Option C) to decide whether to show
// the wet-area pill at all.
function isWetCapable(roomName) {
  if (!roomName) return false;
  const s = String(roomName).toLowerCase();
  return /\b(bath|ensuite|laundry|wc|toilet|kitchen|scullery|butler|mudroom|powder)\b/.test(s);
}

// classifyMaterial: route a material to the right scaling axis.
// Returns one of: 'wall' | 'ceiling' | 'perimeter' | 'sheetDerived' | 'floor'
// Used by the Patch 4 recalc engine to scale qty by the right ratio when
// a root measurement is corrected.
function classifyMaterial(name, cat, unit) {
  const n = (name || '').toLowerCase();
  const c = (cat || '').toLowerCase();
  const u = (unit || '').toLowerCase();

  // Ceiling materials
  if (n.includes('ceiling') || n.includes('rondo') || c.includes('ceiling')) return 'ceiling';

  // Cornice / coving / perimeter trim — scales with perimeter
  if (n.includes('cornice') || n.includes('coving') || n.includes('skirting')) return 'perimeter';

  // Wall sheets — GIB Standard, Aqualine, Braceline, Fyreline (when not ceiling)
  if ((n.includes('gib') || n.includes('plasterboard') || n.includes('board')) && u.includes('sheet')) return 'wall';

  // Wall finishing — beads, l-bead, casing, goldline
  if (n.includes('bead') || n.includes('goldline') || n.includes('casing') || n.includes('l-bead')) return 'wall';

  // Sheet-derived items — screws, fixings, tape, compound, plaster bags
  // (these track the total sheet count, which itself is wall+ceiling derived)
  if (n.includes('screw') || n.includes('fix') || n.includes('tape') || n.includes('plaster') || n.includes('compound') || n.includes('bag') || n.includes('pail')) return 'sheetDerived';

  // Sealant, glue — fallback to floor area
  if (n.includes('sealant') || n.includes('adhesive') || n.includes('glue')) return 'floor';

  // Default: floor area
  return 'floor';
}



// Look up a material's price by name (case-insensitive partial match).
// Returns 0 if not found — display as "$—".
// ─── PRICE OVERRIDES ──────────────────────────────────────────────────────────
function getProjectPriceOverrides(projectId) {
  if (!projectId) return {};
  try {
    const raw = localStorage.getItem('price_overrides_' + projectId);
    return raw ? (JSON.parse(raw) || {}) : {};
  } catch (e) { return {}; }
}

function getUserPriceOverrides(email) {
  if (!email) return {};
  try {
    const raw = localStorage.getItem('user_price_overrides_' + email);
    return raw ? (JSON.parse(raw) || {}) : {};
  } catch (e) { return {}; }
}

function setProjectPriceOverride(projectId, name, price) {
  if (!projectId || !name) return;
  const current = getProjectPriceOverrides(projectId);
  current[name] = price;
  try { localStorage.setItem('price_overrides_' + projectId, JSON.stringify(current)); } catch (e) {}
  pbTouchLocal(projectId, 'prices', name);   // store-v1: follows the job to other devices
}

function setUserPriceOverride(email, name, price) {
  if (!email || !name) return;
  const current = getUserPriceOverrides(email);
  current[name] = price;
  try { localStorage.setItem('user_price_overrides_' + email, JSON.stringify(current)); } catch (e) {}
}

function logPriceCorrection(email, projectId, materialName, oldPrice, newPrice, scope, reason, note) {
  if (!email) return;
  const key = 'to_corrections_' + email;
  // store-v1: one array log shared with logMeasurementCorrection (see
  // pbCorrectionsList), capped so it can't fill the phone's storage.
  const list = pbCorrectionsList(email);
  const entry = {
    type: 'price',
    projectId,
    materialName,
    oldPrice,
    newPrice,
    scope,
    reason: reason || '',
    note: note || '',
    correctedBy: email,
    correctedAt: new Date().toISOString(),
  };
  list.push(entry);
  try { localStorage.setItem(key, JSON.stringify(list.slice(-2000))); } catch (e) {}
  pbQueueCorrection(projectId, entry, email);
}


// ─── LIVE MATERIALS TOTAL (single source of truth) ────────────────────────────
// Returns the canonical live materials total for a given project + trade.
// Mirrors mapAIRoomToCardShape's pricing logic so header, sense check,
// Materials tab subtotal and quote builder all show the same number.
//
// Applies in this order:
//   1. Read AI per-trade per-room materials (project.aiAnalysis.trades[trade.id].rooms)
//   2. Apply wastage to sheet quantities (Math.ceil — can't order half a sheet)
//   3. Resolve price via lookup chain: project override → user override → default
//   4. Sum qty × price across all rooms
//
// Falls back to legacy trade.materials if AI data is missing or sums to zero.
// Wrapped in try/catch — cannot throw, cannot white-screen.
//
// USAGE: computeLiveMaterialsTotal({ project, trade, prefs })
//   - project: full project object (must have .aiAnalysis to be live)
//   - trade:   the trade object (e.g. TRADES.find(t=>t.id==='plasterer'))
//   - prefs:   user prefs object (for wastagePercent — defaults to 10)
// Returns: a number (dollars, ex GST). Returns 0 if no trade or no project.
function computeLiveMaterialsTotal({ project, trade, prefs }) {
  // plancheck-v1-2026-10-13: null when the scan gave no priced materials. It
  // used to fall back to the trade's built-in example list (48 sheets of GIB
  // and so on), which put a made-up dollar figure in the job header.
  if (!trade || !project) return null;
  const legacy = () => null;
  try {
    const aiTradeRooms = project?.aiAnalysis?.trades?.[trade.id]?.rooms;
    if (!Array.isArray(aiTradeRooms) || aiTradeRooms.length === 0) {
      return legacy();
    }
    const wastagePct = (prefs?.wastagePercent ?? 10) / 100;
    const email = (LS && typeof LS.get === "function") ? ((LS.get("to_user", null) || {}).email) : undefined;
    let sum = 0;
    for (const room of aiTradeRooms) {
      const mats = (room && room.materials) || [];
      for (const m of mats) {
        const isSheet = (m && m.unit ? String(m.unit).toLowerCase().includes("sheet") : false);
        const baseQty = Number(m?.qty) || 0;
        const finalQty = (isSheet && wastagePct > 0) ? Math.ceil(baseQty * (1 + wastagePct)) : baseQty;
        const basePrice = (typeof lookupPrice === "function") ? (lookupPrice(m?.name) || 0) : 0;
        const price = (typeof resolvePrice === "function")
          ? (resolvePrice(m?.name, project?.id, email, basePrice) || 0)
          : basePrice;
        sum += finalQty * price;
      }
    }
    return sum > 0 ? sum : legacy();
  } catch (e) {
    console.warn("computeLiveMaterialsTotal failed, falling back to legacy", e);
    return legacy();
  }
}

function resolvePrice(name, projectId, email, defaultPrice) {
  if (!name) return defaultPrice || 0;
  if (projectId) {
    const proj = getProjectPriceOverrides(projectId);
    if (proj[name] !== undefined) return proj[name];
  }
  if (email) {
    const user = getUserPriceOverrides(email);
    if (user[name] !== undefined) return user[name];
  }
  return defaultPrice || 0;
}


// ─── MATERIALS-ONLY $/m² RATES BY TRADE (NZ) ──────────────────────────────────
// MATERIALS-ONLY rates per square metre of floor area, used to size the
// sensitivity bar's "typical range". NOT all-in trade quote rates (no labour/margin).
// Last updated: 30 Apr 2026, review monthly.
const MATERIALS_RATES_NZ = {
  plasterer:    { min: 22, max: 42 },
  builder:      { min: 180, max: 320 },
  electrician:  { min: 28, max: 55 },
  plumber:      { min: 32, max: 65 },
  roofer:       { min: 45, max: 95 },
  insulation:   { min: 12, max: 28 },
  concrete:     { min: 65, max: 120 },
  tiler:        { min: 35, max: 85 },
  painter:      { min: 8, max: 18 },
  flooring:     { min: 45, max: 110 },
  _default:     { min: 20, max: 80 },
};

// ─── useIsMobile hook — for mobile-conditional logic in components ──────────
// Returns true when window width < breakpoint (default 768px = standard mobile/tablet boundary)
// Use sparingly; prefer CSS classes (mobile.css) when possible. This hook is for cases
// where inline styles need to differ between mobile and desktop.
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

function lookupPrice(name) {
  if (!name) return 0;
  const lower = name.toLowerCase().trim();
  // First try exact match
  if (MATERIAL_PRICES_NZ[lower] !== undefined) return MATERIAL_PRICES_NZ[lower];
  // Then try partial match — find any key that's contained in the name
  for (const [key, price] of Object.entries(MATERIAL_PRICES_NZ)) {
    if (lower.includes(key)) return price;
  }
  return 0;
}

// ─── AI MATERIALS AGGREGATION ─────────────────────────────────────────────────
// Sum per-room materials from AI analysis into a job-level totals list.
// Returns array shaped like trade.materials: [{name, qty, unit, price, cat}]
// Returns empty array if no AI data — caller should fall back to trade.materials.
function aggregateAiMaterials(project, tradeId) {
  const aiRooms = project?.aiAnalysis?.trades?.[tradeId]?.rooms;
  if (!Array.isArray(aiRooms) || aiRooms.length === 0) return [];

  // Sum quantities by material name (preserve unit)
  const totals = {};
  aiRooms.forEach(room => {
    const mats = room?.roomMaterials || [];
    mats.forEach(m => {
      if (!m.name) return;
      const key = m.name;
      if (!totals[key]) {
        totals[key] = { name: m.name, qty: 0, unit: m.unit || '', cat: m.cat || categoriseMaterial(m.name) };
      }
      totals[key].qty += (m.qty || 0);
    });
  });

  // Convert to array, add prices via lookup
  return Object.values(totals).map(m => ({
    name: m.name,
    qty: Math.round(m.qty), // whole numbers for display
    unit: m.unit,
    price: lookupPrice(m.name),
    cat: m.cat || categoriseMaterial(m.name),
  })).filter(m => m.qty > 0);
}

// ─── AUTO-CATEGORISE BY NAME ──────────────────────────────────────────────────
// If AI doesn't provide a cat, infer from material name.
function categoriseMaterial(name) {
  if (!name) return 'Other';
  const lower = name.toLowerCase();
  if (/gib (standard|aqualine|braceline|fyreline|noiseline|toughline|ceiling)/.test(lower)) return 'Board';
  if (/stopping|finishing|tradeset|trade-set|tape|jointing/.test(lower)) return 'Stopping';
  if (/bead|trim/.test(lower)) return 'Beads';
  if (/screw|nail|fastener|grabber|handibrac/.test(lower)) return 'Fixings';
  if (/sealant|silicone|membrane|waterproof/.test(lower)) return 'Wet Area';
  if (/baten|coving|cornice|rondo/.test(lower)) return 'Ceiling';
  return 'Other';
}

// ─── CAPTURE-AND-FLAG: log unknown materials for monthly review ───────────────
// Saves to localStorage so admin can see what's missing from MATERIAL_PRICES_NZ.
function captureUnknownMaterials(materials, userEmail) {
  if (!Array.isArray(materials) || !userEmail) return;
  const key = 'to_unknown_materials_' + userEmail;
  let store = {};
  try {
    const raw = localStorage.getItem(key);
    if (raw) store = JSON.parse(raw) || {};
  } catch (e) { store = {}; }

  const now = new Date().toISOString();
  let changed = false;

  materials.forEach(m => {
    if (!m.name || (m.price && m.price > 0)) return; // only log when price unknown
    const id = m.name.toLowerCase().trim();
    if (!store[id]) {
      store[id] = { name: m.name, firstSeen: now, lastSeen: now, occurrenceCount: 1 };
    } else {
      store[id].lastSeen = now;
      store[id].occurrenceCount = (store[id].occurrenceCount || 0) + 1;
    }
    changed = true;
  });

  if (changed) {
    try { localStorage.setItem(key, JSON.stringify(store)); } catch (e) {}
  }
}

// ─── All 67 NZ Territorial Authorities with regional cost data ────────────────
const NZ_COUNCILS = [
  // Auckland
  { id:"auckland_council",        name:"Auckland Council",                    region:"Auckland",        costMult:1.18, climateZone:1, notes:"Flood zone overlays apply in some areas. Check IPS maps." },
  // Northland
  { id:"far_north",               name:"Far North District",                  region:"Northland",       costMult:1.08, climateZone:1, notes:"Coastal corrosion zone considerations for materials." },
  { id:"whangarei",               name:"Whangārei District",                  region:"Northland",       costMult:1.06, climateZone:1, notes:"" },
  { id:"kaipara",                 name:"Kaipara District",                    region:"Northland",       costMult:1.04, climateZone:1, notes:"" },
  // Waikato
  { id:"thames_coromandel",       name:"Thames-Coromandel District",          region:"Waikato",         costMult:1.10, climateZone:1, notes:"Coastal overlay areas. Check TCDC hazard maps." },
  { id:"hauraki",                 name:"Hauraki District",                    region:"Waikato",         costMult:1.00, climateZone:1, notes:"" },
  { id:"waikato_district",        name:"Waikato District",                    region:"Waikato",         costMult:1.02, climateZone:1, notes:"" },
  { id:"matamata_piako",          name:"Matamata-Piako District",             region:"Waikato",         costMult:0.98, climateZone:1, notes:"" },
  { id:"hamilton",                name:"Hamilton City",                       region:"Waikato",         costMult:1.04, climateZone:1, notes:"" },
  { id:"waipa",                   name:"Waipā District",                      region:"Waikato",         costMult:1.00, climateZone:1, notes:"" },
  { id:"otorohanga",              name:"Ōtorohanga District",                 region:"Waikato",         costMult:0.94, climateZone:2, notes:"" },
  { id:"south_waikato",           name:"South Waikato District",              region:"Waikato",         costMult:0.93, climateZone:2, notes:"" },
  { id:"waitomo",                 name:"Waitomo District",                    region:"Waikato",         costMult:0.92, climateZone:2, notes:"" },
  { id:"taupo",                   name:"Taupō District",                      region:"Waikato",         costMult:0.97, climateZone:2, notes:"High altitude areas may require additional insulation R-values." },
  // Bay of Plenty
  { id:"western_bay_plenty",      name:"Western Bay of Plenty District",      region:"Bay of Plenty",   costMult:1.06, climateZone:1, notes:"" },
  { id:"tauranga",                name:"Tauranga City",                       region:"Bay of Plenty",   costMult:1.12, climateZone:1, notes:"" },
  { id:"rotorua",                 name:"Rotorua District",                    region:"Bay of Plenty",   costMult:0.98, climateZone:2, notes:"Geothermal ground conditions may affect foundations." },
  { id:"whakatane",               name:"Whakatāne District",                  region:"Bay of Plenty",   costMult:0.98, climateZone:1, notes:"" },
  { id:"kawerau",                 name:"Kawerau District",                    region:"Bay of Plenty",   costMult:0.93, climateZone:1, notes:"" },
  { id:"opotiki",                 name:"Ōpōtiki District",                    region:"Bay of Plenty",   costMult:0.92, climateZone:1, notes:"" },
  // Gisborne
  { id:"gisborne",                name:"Gisborne District",                   region:"Gisborne",        costMult:0.96, climateZone:1, notes:"Earthquake-prone district. Check NZBC B1 seismic requirements carefully." },
  // Hawke's Bay
  { id:"wairoa",                  name:"Wairoa District",                     region:"Hawke's Bay",     costMult:0.90, climateZone:2, notes:"" },
  { id:"hastings",                name:"Hastings District",                   region:"Hawke's Bay",     costMult:1.00, climateZone:2, notes:"Post-cyclone Gabrielle: additional floor level requirements in flood areas." },
  { id:"napier",                  name:"Napier City",                         region:"Hawke's Bay",     costMult:1.02, climateZone:2, notes:"Reclaimed land in some areas. Check ground conditions." },
  { id:"central_hawkes_bay",      name:"Central Hawke's Bay District",        region:"Hawke's Bay",     costMult:0.91, climateZone:2, notes:"" },
  // Taranaki
  { id:"new_plymouth",            name:"New Plymouth District",               region:"Taranaki",        costMult:1.02, climateZone:2, notes:"" },
  { id:"stratford",               name:"Stratford District",                  region:"Taranaki",        costMult:0.93, climateZone:2, notes:"" },
  { id:"south_taranaki",          name:"South Taranaki District",             region:"Taranaki",        costMult:0.91, climateZone:2, notes:"" },
  // Manawatū-Whanganui
  { id:"ruapehu",                 name:"Ruapehu District",                    region:"Manawatū-Whanganui", costMult:0.89, climateZone:3, notes:"High altitude. Minimum R3.3 roof insulation required (Climate Zone 3)." },
  { id:"whanganui",               name:"Whanganui District",                  region:"Manawatū-Whanganui", costMult:0.95, climateZone:2, notes:"" },
  { id:"rangitikei",              name:"Rangitīkei District",                 region:"Manawatū-Whanganui", costMult:0.90, climateZone:2, notes:"" },
  { id:"manawatu",                name:"Manawatū District",                   region:"Manawatū-Whanganui", costMult:0.96, climateZone:2, notes:"" },
  { id:"palmerston_north",        name:"Palmerston North City",               region:"Manawatū-Whanganui", costMult:1.00, climateZone:2, notes:"" },
  { id:"tararua",                 name:"Tararua District",                    region:"Manawatū-Whanganui", costMult:0.88, climateZone:2, notes:"" },
  { id:"horowhenua",              name:"Horowhenua District",                 region:"Manawatū-Whanganui", costMult:0.95, climateZone:2, notes:"" },
  // Wellington
  { id:"kapiti_coast",            name:"Kāpiti Coast District",               region:"Wellington",      costMult:1.06, climateZone:2, notes:"Coastal hazard areas. Check managed retreat zones." },
  { id:"porirua",                 name:"Porirua City",                        region:"Wellington",      costMult:1.08, climateZone:2, notes:"" },
  { id:"upper_hutt",              name:"Upper Hutt City",                     region:"Wellington",      costMult:1.06, climateZone:2, notes:"" },
  { id:"lower_hutt",              name:"Lower Hutt City (Hutt City)",         region:"Wellington",      costMult:1.08, climateZone:2, notes:"" },
  { id:"wellington_city",         name:"Wellington City",                     region:"Wellington",      costMult:1.15, climateZone:2, notes:"High wind zone. Bracing requirements often exceed minimums." },
  { id:"masterton",               name:"Masterton District",                  region:"Wellington",      costMult:0.95, climateZone:2, notes:"" },
  { id:"carterton",               name:"Carterton District",                  region:"Wellington",      costMult:0.93, climateZone:2, notes:"" },
  { id:"south_wairarapa",         name:"South Wairarapa District",            region:"Wellington",      costMult:0.92, climateZone:2, notes:"" },
  // Nelson-Marlborough
  { id:"nelson",                  name:"Nelson City",                         region:"Nelson-Marlborough", costMult:1.04, climateZone:2, notes:"" },
  { id:"tasman",                  name:"Tasman District",                     region:"Nelson-Marlborough", costMult:1.00, climateZone:2, notes:"" },
  { id:"marlborough",             name:"Marlborough District",                region:"Nelson-Marlborough", costMult:0.98, climateZone:2, notes:"" },
  // West Coast
  { id:"buller",                  name:"Buller District",                     region:"West Coast",      costMult:1.05, climateZone:3, notes:"Very high rainfall. Enhanced weathertightness detailing required. Min R3.3 roof." },
  { id:"grey",                    name:"Grey District",                       region:"West Coast",      costMult:1.02, climateZone:3, notes:"High rainfall zone. Enhanced weathertightness." },
  { id:"westland",                name:"Westland District",                   region:"West Coast",      costMult:1.08, climateZone:3, notes:"Highest rainfall in NZ. Remote location adds material costs." },
  // Canterbury
  { id:"kaikoura",                name:"Kaikōura District",                   region:"Canterbury",      costMult:1.02, climateZone:2, notes:"Earthquake-prone. Enhanced seismic bracing likely required." },
  { id:"hurunui",                 name:"Hurunui District",                    region:"Canterbury",      costMult:0.94, climateZone:2, notes:"" },
  { id:"waimakariri",             name:"Waimakariri District",                region:"Canterbury",      costMult:1.04, climateZone:2, notes:"Post-earthquake: check land zoning before consenting." },
  { id:"christchurch",            name:"Christchurch City",                   region:"Canterbury",      costMult:1.08, climateZone:2, notes:"Post-earthquake rebuild rules apply. Check TC1/TC2/TC3 land classification." },
  { id:"selwyn",                  name:"Selwyn District",                     region:"Canterbury",      costMult:1.03, climateZone:2, notes:"Fast-growing area. Check plan change implications." },
  { id:"ashburton",               name:"Ashburton District",                  region:"Canterbury",      costMult:0.95, climateZone:2, notes:"" },
  { id:"timaru",                  name:"Timaru District",                     region:"Canterbury",      costMult:0.94, climateZone:2, notes:"" },
  { id:"mackenzie",               name:"Mackenzie District",                  region:"Canterbury",      costMult:0.96, climateZone:3, notes:"High altitude — min R3.3 roof insulation (Climate Zone 3)." },
  { id:"waimate",                 name:"Waimate District",                    region:"Canterbury",      costMult:0.90, climateZone:2, notes:"" },
  // Otago
  { id:"waitaki",                 name:"Waitaki District",                    region:"Otago",           costMult:0.92, climateZone:3, notes:"" },
  { id:"central_otago",           name:"Central Otago District",              region:"Otago",           costMult:0.98, climateZone:3, notes:"Extreme temperature range. Enhanced insulation recommended beyond code minimum." },
  { id:"queenstown_lakes",        name:"Queenstown-Lakes District",           region:"Otago",           costMult:1.22, climateZone:3, notes:"Highest build costs in South Island. Min R3.3 roof. Steep site premiums common." },
  { id:"dunedin",                 name:"Dunedin City",                        region:"Otago",           costMult:1.02, climateZone:3, notes:"Hilly terrain. Steep site premiums apply. Min R3.3 roof insulation." },
  { id:"clutha",                  name:"Clutha District",                     region:"Otago",           costMult:0.88, climateZone:3, notes:"" },
  // Southland
  { id:"southland_district",      name:"Southland District",                  region:"Southland",       costMult:0.90, climateZone:3, notes:"Coldest climate zone. Min R3.3 roof, R2.0 walls. Enhanced insulation strongly recommended." },
  { id:"gore",                    name:"Gore District",                       region:"Southland",       costMult:0.88, climateZone:3, notes:"Climate Zone 3 — enhanced insulation requirements." },
  { id:"invercargill",            name:"Invercargill City",                   region:"Southland",       costMult:0.92, climateZone:3, notes:"Climate Zone 3. Min R3.3 roof insulation required." },
  { id:"stewart_island",          name:"Rakiura/Stewart Island",              region:"Southland",       costMult:1.35, climateZone:3, notes:"Remote island. All materials must be shipped. Significant cost premium." },
  // Chatham Islands
  { id:"chatham_islands",         name:"Chatham Islands",                     region:"Chatham Islands", costMult:1.60, climateZone:1, notes:"Remote. Very high freight costs. All materials shipped from mainland." },
];

// Group councils by region for dropdown display
const COUNCIL_REGIONS = [...new Set(NZ_COUNCILS.map(c=>c.region))];

// Regional $/m² multipliers applied to base sense-check ranges
// Base = NZ average. Auckland is ~18% above, Southland ~10% below etc.


const fmt  = n => "$" + Math.round(n).toLocaleString("en-NZ");
const fmtQ = n => Number.isInteger(n) ? n : n.toFixed(1);
const uid  = () => Math.random().toString(36).slice(2, 10);

// ─── Shared atoms ─────────────────────────────────────────────────────────────
function Logo({ onClick, dark, title }) {
  const textColor = dark ? "#FFFFFF" : C.ink;
  return (
    <div onClick={onClick} title={title} data-pb-logo={onClick ? "1" : undefined} style={{display:"flex",alignItems:"center",gap:10,cursor:onClick?"pointer":"default"}}>
      <div style={{width:38,height:38,background:"linear-gradient(135deg, #1B3A5C 0%, #F06400 100%)",borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 2px 8px rgba(240,100,0,0.3)"}}>
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <rect x="2" y="15" width="18" height="3" rx="1.5" fill="white" opacity="0.4"/>
          <path d="M4 13 L11 4 L18 13" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          <circle cx="11" cy="4" r="2.2" fill="#F06400"/>
        </svg>
      </div>
      <div>
        <div style={{fontSize:18,fontWeight:800,color:textColor,letterSpacing:"-0.8px",fontFamily:"Inter,system-ui,-apple-system,sans-serif",lineHeight:1}}>PlanBoss</div>
        <div style={{fontSize:9,fontWeight:600,color:"#F06400",letterSpacing:"0.12em",textTransform:"uppercase",fontFamily:"Inter,system-ui,-apple-system,sans-serif"}}>NZ Trade Intelligence</div>
      </div>
    </div>
  );
}

function Pill({label,color,bg,small}) {
  return <span style={{fontSize:small?10:11,fontWeight:600,padding:small?"1px 6px":"3px 9px",borderRadius:20,background:bg,color,whiteSpace:"nowrap"}}>{label}</span>;
}

function Btn({children,onClick,variant="primary",color,disabled,full,small,danger}) {
  const bg = disabled?C.border : danger?C.redLight : variant==="primary"?(color||C.orange):"transparent";
  const fg = disabled?C.inkLight : danger?C.red : variant==="primary"?"#fff":(color||C.teal);
  return <button onClick={disabled?undefined:onClick} disabled={!!disabled} style={{display:"block",width:full?"100%":"auto",padding:small?"8px 16px":"13px 22px",background:bg,color:fg,border:variant==="outline"?`1.5px solid ${color||C.teal}`:danger?`1px solid ${C.red}44`:"none",borderRadius:10,fontSize:small?13:15,fontWeight:700,cursor:disabled?"not-allowed":"pointer"}}>{children}</button>;
}

function NumInput({label,value,onChange,min,max,prefix,suffix}) {
  return (
    <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 14px"}}>
      <div style={{fontSize:11,color:C.inkLight,marginBottom:4}}>{label}</div>
      <div style={{display:"flex",alignItems:"center",gap:6}}>
        {prefix&&<span style={{fontSize:13,color:C.inkMid}}>{prefix}</span>}
        <input type="number" value={value} min={min} max={max}
          onChange={e=>onChange(Number(e.target.value))}
          style={{flex:1,border:"none",background:"transparent",fontSize:18,fontWeight:700,color:C.ink,outline:"none",width:0}}/>
        {suffix&&<span style={{fontSize:13,color:C.inkMid}}>{suffix}</span>}
      </div>
    </div>
  );
}

function StatusDot({s}) {
  return <span style={{width:8,height:8,borderRadius:"50%",background:{pass:C.tealMid,warn:C.amber,fail:C.red}[s]||C.border,display:"inline-block",flexShrink:0}}/>;
}

function LockBanner({requiredPlan,onUpgrade}) {
  const p=PLANS[requiredPlan];
  return (
    <div style={{background:p.light,border:`1.5px solid ${p.color}44`,borderRadius:14,padding:24,textAlign:"center",margin:"16px 0"}}>
      <div style={{fontSize:32,marginBottom:10}}>🔒</div>
      <div style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:6}}>{p.name} feature</div>
      <div style={{fontSize:13,color:C.inkMid,marginBottom:18,lineHeight:1.6}}>Unlock with <strong>{p.name}</strong> at {fmt(p.price)}/month.</div>
      <Btn onClick={onUpgrade} color={p.color}>Upgrade to {p.name} →</Btn>
    </div>
  );
}

// ─── CONFIRM DIALOG ───────────────────────────────────────────────────────────
function ConfirmDialog({message,onConfirm,onCancel}) {
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
      <div style={{background:C.card,borderRadius:16,padding:28,maxWidth:360,width:"90%",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
        <div style={{fontSize:15,fontWeight:700,color:C.ink,marginBottom:8}}>Are you sure?</div>
        <div style={{fontSize:13,color:C.inkMid,marginBottom:24,lineHeight:1.6}}>{message}</div>
        <div style={{display:"flex",gap:10}}>
          <Btn full onClick={onCancel} variant="outline">Cancel</Btn>
          <Btn full onClick={onConfirm} danger>Yes, delete</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── EMAIL LINKS (auth-action-2026-10-21) ─────────────────────────────────────
// Grant: the links in the sign-in emails opened Firebase's own page at
// measure-up-nz.firebaseapp.com. With Firebase console → Authentication →
// Templates → "Customize action URL" set to https://www.planboss.co.nz/ they
// land here instead, as ?mode=…&oobCode=…. Nothing is used up until the person
// taps the button: mail scanners open links on their own.
const PB_AUTH_MODES = ["verifyEmail", "resetPassword", "recoverEmail", "verifyAndChangeEmail"];
function pbReadAuthAction() {
  try {
    const q = new URLSearchParams(window.location.search);
    const mode = q.get("mode"), oobCode = q.get("oobCode");
    return mode && oobCode ? { mode, oobCode } : null;
  } catch (e) { return null; }
}
function pbAuthActionError(e, mode) {
  const c = e && e.code;
  const again = mode === "resetPassword" ? " Go to sign in and tap “Forgot password?” to get a new link."
    : mode === "verifyEmail" ? " Sign in and tap “Send the link again” on your Account page." : "";
  if (c === "auth/expired-action-code") return { text: "This link has expired." + again, stage: "error" };
  if (c === "auth/invalid-action-code") return { text: "This link has already been used, or it isn't valid." + again, stage: "error" };
  // stripe-live-1: new passwords are at least 8 characters (Firebase's
  // password policy can enforce it: auth/password-does-not-meet-requirements).
  if (c === "auth/weak-password" || c === "auth/password-does-not-meet-requirements") return { text: "Use a password of at least 8 characters.", stage: "ready" };
  if (c === "auth/network-request-failed") return { text: "Couldn't reach PlanBoss. Check your connection and try again.", stage: "ready" };
  return { text: fbErrorMessage(e), stage: "error" };
}
function PbAuthAction({ action, onDone }) {
  const { mode, oobCode } = action;
  const known = PB_AUTH_MODES.includes(mode);
  const [stage, setStage] = useState("ready");      // ready | busy | done | error
  const [err, setErr] = useState("");
  const [forEmail, setForEmail] = useState(null);   // resetPassword: whose password
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  // A reset link shows whose password it is first, and catches a dead link early.
  useEffect(() => {
    if (mode !== "resetPassword") return undefined;
    let live = true;
    fbVerifyPasswordResetCode(oobCode)
      .then((e) => { if (live) setForEmail(e); })
      .catch((e) => { if (live) { const r = pbAuthActionError(e, mode); setErr(r.text); setStage(r.stage); } });
    return () => { live = false; };
  }, [mode, oobCode]);
  const go = async () => {
    setErr("");
    if (mode === "resetPassword" && pw.length < 8) { setErr("Use a password of at least 8 characters."); return; }
    setStage("busy");
    try {
      if (mode === "resetPassword") await fbConfirmPasswordReset(oobCode, pw);
      else await fbApplyActionCode(oobCode);
      setStage("done");
    } catch (e) { const r = pbAuthActionError(e, mode); setErr(r.text); setStage(r.stage); }
  };
  const T = {
    verifyEmail:          { title: "Verify your email", text: "Tap the button to finish verifying your email for PlanBoss.", btn: "Verify my email", busy: "Verifying…",
                            done: "Email verified ✓", doneText: "You're all set. You can now subscribe and use everything in PlanBoss.", next: "Continue to PlanBoss" },
    resetPassword:        { title: "Choose a new password", text: forEmail ? "For " + forEmail : "Checking the link…", btn: "Save new password", busy: "Saving…",
                            done: "Password changed ✓", doneText: "Sign in with your new password.", next: "Sign in" },
    recoverEmail:         { title: "Restore your email address", text: "Tap the button to change your PlanBoss sign-in email back.", btn: "Restore my email", busy: "Restoring…",
                            done: "Email address restored ✓", doneText: "If you didn't ask for the change, reset your password too.", next: "Continue to PlanBoss" },
    verifyAndChangeEmail: { title: "Confirm your new email", text: "Tap the button to finish changing your PlanBoss sign-in email.", btn: "Confirm", busy: "Confirming…",
                            done: "Email address updated ✓", doneText: "Sign in with your new email address from now on.", next: "Continue to PlanBoss" },
  }[mode];
  const finish = () => onDone(stage === "done" && (mode === "verifyEmail" || mode === "verifyAndChangeEmail"));
  const shell = (title, body) => (
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg, #EFF6FF 0%, #F8FAFC 50%, #FFF7ED 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:24,boxSizing:"border-box"}}>
      <div style={{width:"100%",maxWidth:420}}>
        <div style={{display:"flex",justifyContent:"center",marginBottom:28}}><Logo/></div>
        <div data-pb-auth-action={mode} style={{background:C.card,borderRadius:16,border:`1px solid ${C.border}`,padding:28,boxShadow:"0 4px 32px rgba(27,58,92,0.10)"}}>
          <h1 style={{fontSize:21,fontWeight:700,color:C.ink,margin:"0 0 10px"}}>{title}</h1>
          {body}
        </div>
      </div>
    </div>
  );
  const note = (s, col) => <div style={{fontSize:14,color:col||C.inkMid,lineHeight:1.6,marginBottom:18}}>{s}</div>;
  if (!known) return shell("This link isn't one PlanBoss recognises", <>
    {note("If you were expecting an email from PlanBoss, sign in and try again from the app.")}
    <Btn full onClick={() => onDone(false)}>Go to PlanBoss</Btn></>);
  if (stage === "error") return shell("That link didn't work", <>
    {note(err, C.red)}
    <Btn full onClick={() => onDone(false)}>{mode === "resetPassword" ? "Go to sign in" : "Go to PlanBoss"}</Btn></>);
  if (stage === "done") return shell(T.done, <>
    {note(T.doneText)}
    <Btn full onClick={finish}>{T.next}</Btn></>);
  return shell(T.title, <>
    {note(T.text)}
    {mode === "resetPassword" && (
      <div style={{position:"relative",marginBottom:16}}>
        <input type={showPw?"text":"password"} placeholder="New password (at least 8 characters)" value={pw} autoComplete="new-password"
          onChange={(e)=>setPw(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&forEmail&&go()}
          style={{width:"100%",padding:"11px 40px 11px 14px",border:`1.5px solid ${err?C.red:C.border}`,borderRadius:10,fontSize:16,outline:"none",boxSizing:"border-box",background:C.bg,color:C.ink}}/>
        <button type="button" onClick={()=>setShowPw(!showPw)} title={showPw?"Hide password":"Show password"}
          style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",cursor:"pointer",fontSize:18,padding:4,lineHeight:1}}>{showPw?"🙈":"👁"}</button>
      </div>
    )}
    {err && <div role="alert" style={{background:C.redLight,color:C.red,borderRadius:8,padding:"10px 14px",fontSize:13,marginBottom:14}}>{err}</div>}
    <Btn full onClick={go} disabled={stage==="busy" || (mode==="resetPassword" && !forEmail)}>{stage==="busy" ? T.busy : T.btn}</Btn></>);
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen({onLogin,onSignup,onAdminLogin}) {
  const [email,setEmail]=useState("");
  const [pass,setPass]=useState("");
  const [showPass,setShowPass]=useState(false);
  const [mode,setMode]=useState("login");
  const [err,setErr]=useState("");
  const [busy,setBusy]=useState(false);

  const submit = async (choiceParam) => {
    setErr("");
    if(mode==="reset"){if(!email){setErr("Please enter your email.");return;}}else if(!email||!pass){setErr("Please fill in all fields.");return;}
    // stripe-live-1: at least 8 characters for a NEW password. Signing in has
    // no length check, so a password made under the old 6-character rule still works.
    if(mode==="signup" && pass.length<8){setErr("Use a password of at least 8 characters.");return;}

    setBusy(true);
    setTimeout(async()=>{
      setBusy(false);
      if(mode==="reset"){
        try{ await fbResetPassword(email); setMode("login"); setErr(""); alert("Password reset email sent. Check your inbox (and spam folder)."); }catch(fbErr){ setErr(fbErrorMessage(fbErr)); }
        return;
      }
      if(mode==="signup"){
      try{
        await fbSignup(email,pass);
      }catch(fbErr){
        setErr(fbErrorMessage(fbErr));setBusy(false);return;
      }
      const accounts = LS.get("to_accounts",{});
      const createdAt = new Date().toISOString();
      // stripe-live-2 (Grant, 28 Oct): no free trial without a card. A new
      // account has no plan; it goes to the plans, where Pro's 7-day free
      // trial starts at Stripe's checkout with the card (after the email is
      // verified). Nothing is charged until the trial ends.
      accounts[email] = { plan: null, trialStarted: null, trialActive: false, createdAt, needsVerification:true };
      LS.set("to_accounts", accounts);
      onSignup({email, plan: null, trialStarted: null, trialActive: false, needsVerification:true, signupChoice: "plans"});
    } else {
        // security-v1-2026-10-12: no demo login and no admin password in the
        // app's code any more (both were readable by anyone in the live
        // JavaScript). Everyone signs in through Firebase; the server says
        // who is an admin (PB_ADMIN_EMAILS on Render, verified email only).
        setBusy(true);   // keep the button busy while we talk to Firebase and the server
        try { await fbLogin(email, pass); } catch(fbErr) { setErr(fbErrorMessage(fbErr));setBusy(false);return; }
        // Ask the server two things at once: is this an admin (PB_ADMIN_EMAILS),
        // and does Stripe have a paid plan for them (so a payment that wasn't
        // confirmed when checkout returned is picked up here). Don't hold the
        // login up if the server is still waking: after 8 s carry on with what
        // this device knows (an admin can just sign in again).
        const pbAsk = async (path) => {
          const ctl = new AbortController();
          const tmr = setTimeout(() => ctl.abort(), 8000);
          try { const r = await pbFetch(path, { signal: ctl.signal }); return r.ok ? await r.json() : null; }
          catch (e) { return null; }
          finally { clearTimeout(tmr); }
        };
        const [me, sub] = await Promise.all([pbAsk("/me"), pbAsk("/subscription-status")]);
        if (me && me.admin) {
          onAdminLogin({email,role:"admin",loginTime:new Date().toISOString()});
          return;
        }
        const paidPlan = (sub && sub.plan && PLANS[sub.plan]) ? sub.plan : null;
        const accounts2 = LS.get("to_accounts",{});
        if(!accounts2[email]){
          // stripe-live-2: no trial on the device any more. The plan comes
          // from the server or Stripe; with none, the plans screen offers
          // Pro's free trial (card up front, once per person).
          accounts2[email] = { plan:null, trialStarted:null, trialActive:false, createdAt: new Date().toISOString(), needsVerification:true };
          LS.set("to_accounts", accounts2);
        }
        if (paidPlan) {
          accounts2[email] = { ...accounts2[email], plan: paidPlan, paidPlan, trialActive: false, trialExpired: false };
          LS.set("to_accounts", accounts2);
        }
        const acct = accounts2[email];
        // Check if trial has expired (7 days)
        let plan = acct.plan||"starter";
        let trialActive = acct.trialActive||false;
        if(trialActive && acct.trialStarted) {
          const daysSince = (Date.now()-new Date(acct.trialStarted).getTime())/(1000*60*60*24);
          if(daysSince>7) { trialActive=false; accounts2[email].trialActive=false; accounts2[email].trialExpired=true; LS.set("to_accounts",accounts2); }
        }
        // stripe-live-2: no plan on this device (and none from Stripe): the
        // plans screen, where Pro's free trial starts.
        // (Not when the server has a plan for them, e.g. a tester signing in
        // on a new phone, nor for an admin email still waiting on
        // verification: the home screen's card says how to finish that.)
        const serverPlan = !!(me && me.plan && me.plan.plan);
        onLogin({email, plan, trialStarted:acct.trialStarted, trialActive,
          signupChoice: (PLANS[plan] || serverPlan || (me && me.adminWaiting)) ? undefined : "plans"});
      }
    },700);
  };

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg, #EFF6FF 0%, #F8FAFC 50%, #FFF7ED 100%)",display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
      <div style={{width:"100%",maxWidth:420}}>
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{display:"flex",justifyContent:"center",marginBottom:14}}><Logo/></div>
          <p style={{fontSize:15,color:C.inkLight,margin:0,letterSpacing:"0.01em"}}>Measure faster. Quote smarter. NZ made.</p>
        </div>
        <div style={{background:C.card,borderRadius:16,border:`1px solid ${C.border}`,padding:36,boxShadow:"0 4px 32px rgba(27,58,92,0.10)"}}>
          <div style={{display:"flex",background:C.bg,borderRadius:10,padding:4,marginBottom:28}}>
            {["login","signup"].map(m=>(
              <button key={m} onClick={()=>{setMode(m);setErr("");}} style={{flex:1,padding:"8px",border:"none",borderRadius:8,cursor:"pointer",background:mode===m?C.card:"transparent",color:mode===m?C.ink:C.inkLight,fontWeight:mode===m?700:400,fontSize:14,boxShadow:mode===m?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>
                {m==="login"?"Sign in":"Create account"}
              </button>
            ))}
          </div>
          {err&&<div style={{background:C.redLight,color:C.red,borderRadius:8,padding:"10px 14px",fontSize:13,marginBottom:16}}>{err}</div>}
          {(mode==="reset"?["Email"]:["Email","Password"]).map(f=>(
            <div key={f} style={{marginBottom:16}}>
              <label style={{fontSize:13,fontWeight:600,color:C.inkMid,display:"block",marginBottom:6}}>{f}</label>
              <div style={{position:"relative"}}>{f==="Password"&&<button type="button" onClick={()=>setShowPass(!showPass)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"transparent",border:"none",cursor:"pointer",fontSize:18,padding:4,lineHeight:1,zIndex:2}} title={showPass?"Hide password":"Show password"}>{showPass?"🙈":"👁"}</button>}<input type={f==="Password"?(showPass?"text":"password"):"email"} placeholder={f==="Email"?"you@yourcompany.co.nz":(mode==="signup"?"At least 8 characters":"Your password")} value={f==="Email"?email:pass} onChange={e=>f==="Email"?setEmail(e.target.value):setPass(e.target.value)} onKeyDown={e=>e.key==="Enter"&&submit()}
                style={{width:"100%",padding:f==="Password"?"11px 40px 11px 14px":"11px 14px",border:`1.5px solid ${err&&f==="Password"?C.red:C.border}`,borderRadius:10,fontSize:14,outline:"none",boxSizing:"border-box",background:C.bg,color:C.ink}}/></div>
            </div>
          ))}
          {mode==="login" && <div style={{textAlign:"right",marginTop:-8,marginBottom:14}}><span onClick={()=>{setMode("reset");setErr("");}} style={{color:C.teal,cursor:"pointer",fontSize:12,fontWeight:600}}>Forgot password?</span></div>}
          {mode==="signup"&&<div data-pb-trial-terms style={{background:C.tealFaint,border:`1px solid ${C.tealLight}`,borderRadius:10,padding:"12px 14px",marginBottom:16,fontSize:13,color:C.teal,lineHeight:1.5}}>✓ <strong>Pro is free for 7 days, with {PB_TRIAL.scans} scans.</strong> Create your account, verify your email, then start the trial with your card. Cancel before the 7 days are up and you pay nothing.</div>}
          
          {mode!=="signup" ?
            <Btn full onClick={submit}>{busy?(mode==="reset"?"Sending…":"Signing in…"):(mode==="reset"?"Send reset email":"Sign in")}</Btn>
          :
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              <button type="button" onClick={()=>submit("plans")} disabled={busy}
                style={{display:"block",width:"100%",padding:"15px 22px",background:C.tealMid,color:"#fff",border:"none",borderRadius:11,fontSize:16,fontWeight:800,cursor:busy?"not-allowed":"pointer",boxShadow:"0 2px 8px rgba(10,123,90,0.25)"}}>
                {busy?"Creating account…":"Create my account"}
              </button>
            </div>
          }
          {mode==="reset"&&<p style={{textAlign:"center",marginTop:14,fontSize:13,color:C.inkLight}}>Remember your password? <span onClick={()=>{setMode("login");setErr("");}} style={{color:C.teal,cursor:"pointer",fontWeight:600}}>Back to login</span></p>}
          {mode==="login"&&<p style={{textAlign:"center",marginTop:14,fontSize:13,color:C.inkLight}}>No account? <span onClick={()=>setMode("signup")} style={{color:C.teal,cursor:"pointer",fontWeight:600}}>Create one free</span></p>}
        </div>
        <p style={{textAlign:"center",marginTop:20,fontSize:12,color:C.inkLight}}>By continuing you agree to PlanBoss's Terms of Service and Privacy Policy.</p>
      </div>
    </div>
  );
}

// ─── PRICING ──────────────────────────────────────────────────────────────────
// stripe-live-2: trialOffer = this account may still start Pro's 7-day free
// trial (card up front, once per person); the server has the final say.
function PricingScreen({currentPlan,onSelect,onBack,lockout,onSignOut,onAccount,trialOffer,why,sellBusiness}) {
  // stripe-live-2 (review): monthly first while the free trial is on offer,
  // so the trial most people start turns into NZ$50 a month, not a year up front.
  const [billing,setBilling]=useState(trialOffer?"monthly":"annual");
  return (
    <div style={{minHeight:"100vh",background:C.bg,padding:"40px 24px"}}>
      <div style={{maxWidth:900,margin:"0 auto"}}>
        {onBack&&<button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",color:C.inkLight,fontSize:13,marginBottom:24,padding:0}}>← Back</button>}
        {/* store-v1: a locked-out user can still reach their account (to
            delete it — Apple 5.1.1(v)) and sign out */}
        {lockout&&<div style={{textAlign:"center",background:C.amberFaint,border:`1px solid ${C.amberLight}`,borderRadius:12,padding:"12px 16px",marginBottom:20,fontSize:13,color:C.amber}}>
          {trialOffer
            ?<span data-pb-start-trial>Welcome to PlanBoss! Start your <strong>7-day free trial of Pro</strong> ({PB_TRIAL.scans} scans) to start scanning. Your card is taken at checkout and nothing is charged for 7 days.</span>
            :why==="payment"?<span data-pb-payment-failed>Your free trial has ended, and the first payment didn't go through. Update your card to keep scanning — your saved jobs are kept.</span>
            :why==="refused"?<span>This card or email has already had a PlanBoss free trial. Choose a plan to keep scanning — your saved jobs are kept.</span>
            :why==="trial"?<span>Your free trial has ended. Choose a plan to keep scanning — your saved jobs are kept.</span>
            :why==="plan"?<span>Your plan has ended. Choose a plan to keep scanning — your saved jobs are kept.</span>
            :<span>Choose a plan to keep scanning. Your saved jobs are kept.</span>}
          {why==="payment"&&<div style={{marginTop:10}}><Btn small onClick={()=>pbOpenBillingPortal()}>Update your card →</Btn></div>}
          <div style={{marginTop:8,display:"flex",gap:16,justifyContent:"center",flexWrap:"wrap"}}>
            {onAccount&&<span onClick={onAccount} style={{cursor:"pointer",fontWeight:700,textDecoration:"underline"}}>Account settings</span>}
            {onSignOut&&<span onClick={onSignOut} style={{cursor:"pointer",fontWeight:700,textDecoration:"underline"}}>Sign out</span>}
          </div>
        </div>}
        <div style={{textAlign:"center",marginBottom:40}}>
          <div style={{display:"inline-block",background:"linear-gradient(135deg,#FFF7ED,#FEF3C7)",border:"1px solid #F06400",borderRadius:20,padding:"6px 18px",fontSize:13,fontWeight:700,color:C.orange,marginBottom:16}}>
            {trialOffer?`🎉 Pro is free for 7 days, with ${PB_TRIAL.scans} scans. Card up front; cancel within the 7 days and pay nothing.`:"🎉 Plans for every size of job. Cancel any time."}
          </div>
          <h1 style={{fontSize:32,fontWeight:700,color:C.ink,fontFamily:"Inter,system-ui,-apple-system,sans-serif",marginBottom:8}}>Simple, honest pricing</h1>
          <p style={{fontSize:15,color:C.inkMid}}>Start free. Cancel any time. NZ-built for NZ tradies.</p>
          <div style={{display:"inline-flex",background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:4,marginTop:20,gap:4}}>
            {["monthly","annual"].map(b=>(
              <button key={b} onClick={()=>setBilling(b)} style={{padding:"8px 20px",border:"none",borderRadius:8,cursor:"pointer",background:billing===b?C.tealMid:"transparent",color:billing===b?"#fff":C.inkMid,fontWeight:billing===b?700:400,fontSize:14}}>{b==="annual"?"Annual (save 20%)":"Monthly"}</button>
            ))}
          </div>
        </div>
        <div className="mu-pricing-grid" style={{display:"grid",gridTemplateColumns:"repeat(3,minmax(0,1fr))",gap:14}}>
          {Object.values(PLANS).map(p=>{
            const price=billing==="annual"&&p.price>0?Math.round(p.price*0.8):p.price;
            const isCurrent=currentPlan===p.id;
            // Gating: non-admin sees Business as "Coming soon" — visible but
            // disabled — until the server sells it (PB_SELL_BUSINESS).
            const _user_ps = LS.get("to_user", null);
            const isComingSoon = !isAdminUser(_user_ps) && p.id === "business" && !sellBusiness;
            return (
              <div key={p.id} style={{background:C.card,border:p.popular?`2px solid ${p.color}`:`1px solid ${C.border}`,borderRadius:16,padding:"24px 20px",position:"relative",boxShadow:p.popular?"0 4px 24px rgba(10,123,90,0.12)":"none"}}>
                {p.popular&&<div style={{position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",background:p.color,color:"#fff",fontSize:11,fontWeight:700,padding:"3px 12px",borderRadius:20,whiteSpace:"nowrap"}}>Most popular</div>}
                {p.persona&&<div style={{fontSize:11,color:C.inkLight,marginBottom:6,fontStyle:"italic"}}>{p.persona}</div>}
                <div style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:4}}>{p.name}</div>
                {billing==="annual"&&p.price>0 ? (
                  <>
                    <div style={{marginBottom:2}}>
                      <span style={{fontSize:30,fontWeight:800,color:p.color}}>${price*12}</span>
                      <span style={{fontSize:13,color:C.inkLight}}>/year</span>
                    </div>
                    <div style={{fontSize:11,color:C.inkLight,marginBottom:8}}>${price}/mo · <span style={{color:C.green,fontWeight:600}}>save ${p.price*12-price*12}</span></div>
                  </>
                ) : (
                  <div style={{marginBottom:8}}>
                    <span style={{fontSize:30,fontWeight:800,color:p.color}}>${price}</span>
                    <span style={{fontSize:13,color:C.inkLight}}>/month</span>
                  </div>
                )}
                {p.id === PB_TRIAL.plan && trialOffer && (
                  <div data-pb-trial-badge style={{fontSize:12,color:C.teal,fontWeight:600,background:C.tealFaint,borderRadius:6,padding:"5px 10px",marginBottom:16,textAlign:"center"}}>
                    ✓ 7 days free ({PB_TRIAL.scans} scans), then ${billing==="annual"?price*12+"/year":price+"/month"} + GST
                  </div>
                )}
                <div style={{marginBottom:20}}>
                  {p.features.filter(f=>f!=="7-day free trial").map(f=><div key={f} style={{display:"flex",gap:8,fontSize:12,color:C.inkMid,marginBottom:7,lineHeight:1.4}}><span style={{color:p.color,fontWeight:700,flexShrink:0}}>✓</span>{pbFeatureText(f)}</div>)}
                  {p.missing.map(f=><div key={f} style={{display:"flex",gap:8,fontSize:12,color:C.borderMid,marginBottom:7,lineHeight:1.4}}><span style={{flexShrink:0}}>✗</span>{f}</div>)}
                </div>
                <button onClick={()=>{ if(isComingSoon||isCurrent) return; onSelect(p.id, billing); }} disabled={isComingSoon} style={{width:"100%",padding:"11px",border:isCurrent?`1.5px solid ${C.border}`:"none",borderRadius:10,background:isComingSoon?C.amber:isCurrent?"transparent":p.popular?p.color:C.ink,color:isComingSoon?"#fff":isCurrent?C.inkLight:"#fff",fontWeight:700,fontSize:13,cursor:(isComingSoon||isCurrent)?"default":"pointer",opacity:1}}>
                  {isComingSoon?"🚧 Coming soon":isCurrent?"✓ Current plan":(p.id===PB_TRIAL.plan&&trialOffer)?"Start 7-day free trial →":!currentPlan||currentPlan==="starter"&&p.id!=="starter"?"Upgrade →":planOrder.indexOf(p.id)<planOrder.indexOf(currentPlan)?"Downgrade":"Upgrade →"}
                </button>
              </div>
            );
          })}
        </div>
        <div style={{textAlign:"center",marginTop:32,fontSize:13,color:C.inkLight}}>All prices in NZD · GST not included · Cancel any time</div>
      </div>
    </div>
  );
}

// ─── CHECKOUT ─────────────────────────────────────────────────────────────────
// stripe-live-2 (Grant, 28 Oct): Pro's 7-day free trial starts here, with the
// card taken on Stripe's page and nothing charged until the trial ends. The
// email must be verified first (the server only takes a card from a verified
// email), so this screen offers the verification step in place.
function CheckoutScreen({plan,onComplete,onBack,period="monthly",trialOffer,emailVerified,email,onCheckVerified}) {
  const p=PLANS[plan];
  // Display price reflects the chosen billing period (annual = 20% off)
  const displayPrice = period==="annual" && p && p.price>0 ? Math.round(p.price*0.8) : (p?p.price:0);
  const annualTotal  = period==="annual" && p && p.price>0 ? Math.round(p.price*0.8*12) : 0;
  const isTrial = !!trialOffer && plan===PB_TRIAL.plan;
  const firstCharge = Date.now() + PB_TRIAL.days*86400000;
  const firstChargeText = new Date(firstCharge).toLocaleDateString("en-NZ",{weekday:"long",day:"numeric",month:"long"});
  const amountText = period==="annual" ? fmt(annualTotal)+" a year + GST" : fmt(p.price)+" a month + GST";
  const [busy,setBusy]=useState(false);
  const [needVerify,setNeedVerify]=useState(false);
  const verifyFirst = emailVerified===false || needVerify;
  const pay=async()=>{
    if(verifyFirst) return;
    setBusy(true);
    try {
      // stripe-live-1-2026-10-28: the app sends the plan and the period; the
      // server picks the Stripe price (its lookup key, e.g. "pro_monthly"), so
      // the same app works with test and live Stripe. (The six test-mode price
      // ids used to be written in here, which would have failed in live mode.)
      // security-v1-2026-10-12: signed in, and the server uses the signed-in
      // email for Stripe (this used to send test@measureup.co.nz for everyone).
      const res=await pbFetch("/create-checkout-session",{method:"POST",
        body:JSON.stringify({planId:plan,billingPeriod:period})});
      const data=await res.json();
      // stripe-live-2 (review): the server has the final say on the trial
      // (it also checks Stripe's history). If this screen promised one and
      // there isn't one, say so before Stripe takes the money.
      if(data.url&&isTrial&&data.trial===false&&!window.confirm("This account or email has had a PlanBoss free trial before, so there's no free trial this time: Pro starts today at "+amountText+".\n\nContinue to Stripe?")){
        setBusy(false); return;
      }
      if(data.url){window.location.href=data.url;}
      else if(data.error==="already_subscribed"){
        // store-v1: a second checkout would start a second subscription.
        setBusy(false);
        if(window.confirm((data.message||"You already have a subscription.")+"\n\nOpen the billing page now?")) pbOpenBillingPortal();
      }
      else if(data.error==="email_not_verified"){ setBusy(false); setNeedVerify(true); }
      else{setBusy(false);alert("Couldn't start the checkout: "+(data.message||data.error||"unknown error"));}
    }catch(err){setBusy(false);alert(err&&err.code==="signed_out"?err.message:"Couldn't reach the payment server. Check your connection and try again.");}
  };
  const checkVerified=async()=>{
    const me=onCheckVerified?await onCheckVerified():null;
    if(me&&me.emailVerified) setNeedVerify(false);
    return me;
  };
  const tick=(t,last)=>(<div style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:last?0:6}}><span style={{color:p.color,fontWeight:700}}>✓</span><span>{t}</span></div>);
  return (
    <div style={{minHeight:"100vh",background:C.bg,padding:"40px 24px"}}>
      <div style={{maxWidth:480,margin:"0 auto"}}>
        <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",color:C.inkLight,fontSize:13,marginBottom:24,padding:0}}>← Back to plans</button>
        <h2 style={{fontSize:26,fontWeight:700,color:C.ink,fontFamily:"Inter,system-ui,-apple-system,sans-serif",marginBottom:8}}>{isTrial?`Start your 7-day free trial of PlanBoss ${p.name}`:`You're subscribing to PlanBoss ${p.name}`}</h2>
        <p style={{fontSize:14,color:C.inkMid,marginBottom:24}}>{p.persona}</p>
        <div data-pb-checkout-price style={{background:p.light,border:`1px solid ${p.color}44`,borderRadius:14,padding:"20px 24px",marginBottom:20}}>
          <div style={{fontSize:13,fontWeight:700,color:p.color,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:8}}>{period==="annual"?"Annual plan":"Monthly plan"}</div>
          {isTrial
            ? <>
                <div style={{fontSize:32,fontWeight:800,color:C.ink,lineHeight:1}}>Free for 7 days</div>
                <div style={{fontSize:13,color:C.inkMid,marginTop:6}}>then {amountText}, from {firstChargeText}</div>
              </>
            : period==="annual"
            ? <>
                <div style={{fontSize:32,fontWeight:800,color:C.ink,lineHeight:1}}>{fmt(annualTotal)}<span style={{fontSize:14,fontWeight:500,color:C.inkMid}}> / year + GST</span></div>
                <div style={{fontSize:13,color:C.inkMid,marginTop:6}}>{fmt(displayPrice)}/month + GST, billed annually · save 20%</div>
              </>
            : <>
                <div style={{fontSize:32,fontWeight:800,color:C.ink,lineHeight:1}}>{fmt(p.price)}<span style={{fontSize:14,fontWeight:500,color:C.inkMid}}> / month + GST</span></div>
                <div style={{fontSize:13,color:C.inkMid,marginTop:6}}>Billed monthly · cancel any time</div>
              </>
          }
        </div>
        <div data-pb-checkout-terms style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"16px 20px",marginBottom:20,fontSize:13,color:C.inkMid,lineHeight:1.6}}>
          {isTrial
            ? <>
                {tick(<>You enter your card on Stripe now, but <strong>nothing is charged until {firstChargeText}</strong>.</>)}
                {tick(<>The trial includes <strong>{PB_TRIAL.scans} plan scans</strong>. {p.name}'s {getPlanLimit(plan)} scans a month start from {firstChargeText}.</>)}
                {tick(<>Cancel any time before then (Account → Cancel subscription) and you pay nothing.</>)}
                {tick(<>If you keep it, it's {amountText} from {firstChargeText}. GST (15%) is shown on your receipt.</>)}
                {tick("Card details entered securely on Stripe",true)}
              </>
            : <>
                {tick("You will be charged today via Stripe. GST (15%) is added at checkout and shown on your receipt.")}
                {tick("Cancel any time from your account")}
                {tick("Card details entered securely on Stripe",true)}
              </>}
        </div>
        {verifyFirst&&(
          <div data-pb-verify-first role="status" style={{background:C.amberFaint,border:`1px solid ${C.amberLight}`,borderRadius:14,padding:"14px 18px",marginBottom:16,fontSize:13,color:C.amber,lineHeight:1.5}}>
            <strong>First, verify your email.</strong> We sent a link to {email||"your email"}. Click it (check junk too), then come back and tap "I've verified".
            <PbVerifyEmail email={email||"your email"} onCheck={checkVerified} checkLabel="I've verified"/>
          </div>
        )}
        <Btn full onClick={pay} color={p.color} disabled={verifyFirst||busy}>{busy?"Redirecting to Stripe…":verifyFirst?"Verify your email to continue":isTrial?"Start free trial: add card →":`Continue to secure checkout →`}</Btn>
        <p style={{textAlign:"center",fontSize:12,color:C.inkLight,marginTop:12}}>🔒 Payments processed securely by Stripe</p>
      </div>
    </div>
  );
}

// ─── SENSE CHECK ──────────────────────────────────────────────────────────────
function SenseCheck({trade,floorArea,council,liveMatTotal}) {
  const matTotal = (typeof liveMatTotal === "number" && liveMatTotal > 0) ? liveMatTotal : trade.materials.reduce((s,m)=>s+m.qty*m.price,0);
  const rateActual=matTotal/floorArea;
  const mult=council?.costMult||1.0;
  // Materials-only regional range — derived from per-trade materials-only $/m² rates
  // × floor area × council multiplier. Apples-to-apples with our materials total.
  // Defensive: optional chaining + final fallback so this can never crash.
  const matRates = (typeof MATERIALS_RATES_NZ !== "undefined" && MATERIALS_RATES_NZ)
    ? (MATERIALS_RATES_NZ[trade?.id] || MATERIALS_RATES_NZ._default || { min: 20, max: 80 })
    : { min: 20, max: 80 };
  const regRateMin = Math.round(matRates.min * mult);
  const regRateMax = Math.round(matRates.max * mult);
  const regMin = Math.round(regRateMin * (floorArea || 1));
  const regMax = Math.round(regRateMax * (floorArea || 1));
  const regAvg=(regMin+regMax)/2;
  const regAvgRate=regAvg/floorArea;
  const ok = rateActual >= matRates.min*mult*0.7 && rateActual <= matRates.max*mult*1.4;
  const rawPct=((matTotal-regMin)/(regMax-regMin))*100;
  const barPct=Math.max(3,Math.min(97,rawPct));
  const isLow=rawPct<0; const isHigh=rawPct>100;
  const regionLabel=council?council.name:"NZ average";
  const diff=matTotal-regAvg;
  const diffPct=Math.abs(Math.round((diff/regAvg)*100));

  return (
    <div style={{background:ok?trade.light:C.amberLight,border:`1px solid ${ok?trade.color+"44":C.amber+"44"}`,borderRadius:14,padding:"20px 20px 16px",marginBottom:22}}>

      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <span style={{fontWeight:700,fontSize:13,color:ok?trade.color:C.amber}}>
          {isLow?"⚠ Below typical range — double check quantities":isHigh?"⚠ Above typical range — review estimate":ok?"✓ Sense check — looks right":"⚠ Sense check — review this estimate"}
        </span>
        <Pill label={`${fmt(regRateMin)}–${fmt(regRateMax)} / ${trade.unit}`} color={trade.color} bg={ok?trade.light:C.amberLight} small/>
      </div>

      {/* Stats — 4 columns */}
      <div className="mu-sense-stats" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:20}}>
        {[
          ["Your materials",         fmt(matTotal),                       ok?trade.color:C.amber],
          ["NZ typical materials",   fmt(Math.round(regAvg)),             C.ink],
          ["Your materials $/m²",    `${fmt(Math.round(rateActual))}/m²`, ok?trade.color:C.amber],
          ["Typical materials $/m²", `${fmt(Math.round(regAvgRate))}/m²`, C.ink],
        ].map(([l,v,col])=>(
          <div key={l} style={{background:"rgba(255,255,255,0.5)",borderRadius:8,padding:"10px 12px"}}>
            <div style={{fontSize:11,color:C.inkLight,marginBottom:3}}>{l}</div>
            <div style={{fontSize:15,fontWeight:700,color:col}}>{v}</div>
          </div>
        ))}
      </div>

      {/* Difference callout */}
      <div style={{fontSize:12,color:ok?trade.color:C.amber,marginBottom:16,fontWeight:600}}>
        {diff>0
          ? `Your materials are ${fmt(Math.abs(Math.round(diff)))} (${diffPct}%) above typical NZ materials cost`
          : diff<0
            ? `Your materials are ${fmt(Math.abs(Math.round(diff)))} (${diffPct}%) below typical NZ materials cost`
            : "Your materials are right on the typical NZ materials cost"
        }
      </div>

      {/* Bar — with enough top padding so the label bubble clears the stats */}
      <div style={{paddingTop:36,position:"relative",marginBottom:8}}>
        {/* Label bubble — floats above bar */}
        <div style={{
          position:"absolute",top:0,
          left:`${barPct}%`,
          transform:"translateX(-50%)",
          background:ok?trade.color:C.amber,
          color:"#fff",fontSize:11,fontWeight:700,
          padding:"3px 8px",borderRadius:6,whiteSpace:"nowrap",
          boxShadow:"0 1px 4px rgba(0,0,0,0.15)",
        }}>
          {fmt(matTotal)}
          {/* Small triangle pointer */}
          <div style={{position:"absolute",bottom:-5,left:"50%",transform:"translateX(-50%)",width:0,height:0,borderLeft:"5px solid transparent",borderRight:"5px solid transparent",borderTop:`5px solid ${ok?trade.color:C.amber}`}}/>
        </div>

        {/* Track */}
        <div style={{background:"rgba(0,0,0,0.1)",borderRadius:999,height:10,position:"relative",overflow:"visible"}}>
          {/* Filled bar */}
          <div style={{width:`${Math.max(0,Math.min(100,rawPct))}%`,height:"100%",background:ok?trade.color:C.amber,borderRadius:999,transition:"width 0.4s"}}/>
          {/* Typical (regional average) tick mark — short tick poking below the bar */}
          {regMax>regMin && (
            <div style={{position:"absolute",top:0,left:"50%",transform:"translateX(-50%)",width:2,height:18,background:C.inkMid,borderRadius:1,zIndex:1,opacity:0.6}}/>
          )}
          {/* Dot marker */}
          <div style={{position:"absolute",top:"50%",left:`${barPct}%`,transform:"translate(-50%,-50%)",width:18,height:18,borderRadius:"50%",background:ok?trade.color:C.amber,border:"3px solid #fff",boxShadow:"0 1px 5px rgba(0,0,0,0.25)",zIndex:2}}/>
        </div>
      </div>

      {/* Typical row — own line so it never overlaps the user bubble or scale labels */}
      {regMax>regMin && (
        <div style={{position:"relative",height:14,marginTop:6,marginBottom:4}}>
          <span style={{position:"absolute",left:"50%",transform:"translateX(-50%)",fontSize:10,fontWeight:600,color:C.inkMid,whiteSpace:"nowrap",letterSpacing:"0.02em",opacity:0.85}}>Typical</span>
        </div>
      )}

      {/* Scale labels */}
      <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.inkLight,marginBottom:council?.notes?12:0}}>
        <span>Low {fmt(regMin)}</span>
        <span style={{fontStyle:"italic"}}>Based on NZ rates{regionLabel && regionLabel !== "NZ average" ? ` · adjusted for ${regionLabel}` : ""}</span>
        <span>High {fmt(regMax)}</span>
      </div>

      {/* Council notes */}
      {council?.notes&&(
        <div style={{padding:"8px 12px",background:"rgba(0,0,0,0.04)",borderRadius:8,fontSize:11,color:C.inkMid,marginTop:12}}>
          📍 <strong>{council.name}:</strong> {council.notes}
        </div>
      )}
    </div>
  );
}

// ─── SUPPLIER TABLE ───────────────────────────────────────────────────────────
function SupplierPricingTable({trade,preferredSupplier,councilId}) {
  const cats=[...new Set(trade.materials.map(m=>m.cat))];
  const regional=getRegionalData(councilId);
  const sorted=getSortedSuppliers(preferredSupplier,councilId);
  // Use effectiveMult for pricing (includes freight)
  const totals=sorted.map(s=>trade.materials.reduce((sum,m)=>sum+m.qty*m.price*(s.effectiveMult||s.mult),0));
  // Only count available suppliers for cheapest
  const availableTotals=sorted.map((s,i)=>s.isUnavailable?Infinity:totals[i]);
  const overallCheapestIdx=availableTotals.indexOf(Math.min(...availableTotals));
  const fmt2=n=>"$"+Math.round(n).toLocaleString("en-NZ");
  const fmtQ2=n=>Number.isInteger(n)?n:n.toFixed(1);

  return (
    <div>
      {/* Regional context banner */}
      {regional.freightMult>1.01&&(
        <div style={{background:"rgba(27,58,92,0.06)",border:"1px solid rgba(27,58,92,0.15)",borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12,color:C.teal,display:"flex",alignItems:"flex-start",gap:8}}>
          <span style={{flexShrink:0}}>📍</span>
          <div>
            <strong>Regional pricing applied</strong> — {regional.freightMult>1.10?"significant":"standard"} freight premium of {Math.round((regional.freightMult-1)*100)}% added for {NZ_COUNCILS.find(c=>c.id===councilId)?.name||"this region"}.
            {regional.note&&<span style={{color:C.inkMid}}> {regional.note}</span>}
          </div>
        </div>
      )}
      {regional.unavailable.length>0&&(
        <div style={{background:C.redLight,border:`1px solid ${C.red}33`,borderRadius:10,padding:"10px 14px",marginBottom:12,fontSize:12,color:C.red}}>
          ⚠ <strong>{regional.unavailable.map(id=>SUPPLIERS.find(s=>s.id===id)?.name).join(", ")}</strong> {regional.unavailable.length===1?"does":"do"} not have branches in this region. {regional.unavailable.length>0?"Prices shown are indicative only — freight and availability will vary significantly.":""}
        </div>
      )}
      <div style={{background:C.amberFaint,border:`1px solid ${C.amberLight}`,borderRadius:10,padding:"8px 14px",marginBottom:12,fontSize:11,color:C.amber}}>
        Indicative NZ market rates. ♥ = your preferred (shown first) · ★ = cheapest available · columns sorted low→high · prices include regional freight estimate
      </div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr>
              <th style={{textAlign:"left",padding:"8px 12px",background:C.bg,borderBottom:`2px solid ${C.border}`,fontSize:11,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.05em",minWidth:160}}>Material</th>
              <th style={{textAlign:"right",padding:"8px 12px",background:C.bg,borderBottom:`2px solid ${C.border}`,fontSize:11,color:C.inkLight,textTransform:"uppercase"}}>Qty</th>
              {sorted.map((s,i)=>{
                const isPref=i===0; const isCheapest=i===overallCheapestIdx;
                const colColor=s.isUnavailable?C.inkLight:isPref?C.orange:isCheapest?"#1A9E6B":s.color;
                return (
                  <th key={s.id} style={{textAlign:"right",padding:"8px 10px",borderBottom:`2px solid ${colColor}`,fontSize:10,color:colColor,background:s.isUnavailable?"#f9f9f9":isPref?"rgba(240,100,0,0.05)":isCheapest?"rgba(26,158,107,0.05)":C.bg,whiteSpace:"nowrap",opacity:s.isUnavailable?0.5:1}}>
                    <div style={{fontWeight:700}}>{s.name}</div>
                    <div style={{fontSize:9,fontWeight:400,marginTop:1,color:s.isUnavailable?C.red:C.inkLight}}>
                      {s.isUnavailable?"✕ not in region":s.isLimited?"⚠ limited branch":isPref?"♥ preferred":isCheapest?"★ cheapest":s.type}
                    </div>
                    {regional.freightMult>1.01&&!s.isUnavailable&&<div style={{fontSize:8,color:C.inkLight,marginTop:1}}>+{Math.round((regional.freightMult-1)*100)}% freight</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {cats.map(cat=>(
              <>
                <tr key={`cat-${cat}`}><td colSpan={2+sorted.length} style={{padding:"8px 12px",background:C.bg,fontWeight:700,fontSize:11,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.06em"}}>{cat}</td></tr>
                {trade.materials.filter(m=>m.cat===cat).map((m,i)=>(
                  <tr key={m.name} style={{background:i%2===0?C.card:"#fafaf8"}}>
                    <td style={{padding:"8px 12px",borderBottom:`1px solid ${C.border}`,color:C.ink}}>{m.name}</td>
                    <td style={{padding:"8px 12px",borderBottom:`1px solid ${C.border}`,color:C.inkMid,textAlign:"right",whiteSpace:"nowrap"}}>{fmtQ2(m.qty)} {m.unit}</td>
                    {sorted.map((s,si)=>{
                      const lineTotal=m.qty*m.price*(s.effectiveMult||s.mult);
                      const isPref=si===0; const isCheapest=si===overallCheapestIdx;
                      if(s.isUnavailable) return <td key={s.id} style={{padding:"8px 10px",borderBottom:`1px solid ${C.border}`,textAlign:"right",color:C.inkLight,fontSize:11,opacity:0.5}}>N/A</td>;
                      return (
                        <td key={s.id} style={{padding:"8px 10px",borderBottom:`1px solid ${C.border}`,textAlign:"right",fontWeight:isPref||isCheapest?700:400,color:isCheapest?"#1A9E6B":isPref?C.orange:C.ink,background:isPref?"rgba(240,100,0,0.03)":isCheapest?"rgba(26,158,107,0.03)":"transparent"}}>
                          {fmt2(lineTotal)}
                          {s.isLimited&&<span style={{fontSize:8,color:C.amber,display:"block"}}>⚠ limited</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} style={{padding:"12px",fontWeight:700,fontSize:13,color:C.ink,borderTop:`2px solid ${C.border}`}}>Total excl. GST</td>
              {sorted.map((s,i)=>{
                const total=totals[i];
                const isPref=i===0; const isCheapest=i===overallCheapestIdx;
                const saving=total-totals[overallCheapestIdx];
                if(s.isUnavailable) return <td key={s.id} style={{padding:"12px",textAlign:"right",borderTop:`2px solid ${C.border}`,color:C.inkLight,opacity:0.5,fontSize:12}}>Not available<br/>in this region</td>;
                return (
                  <td key={s.id} style={{padding:"12px",fontWeight:700,fontSize:13,textAlign:"right",borderTop:`2px solid ${C.border}`,color:isCheapest?"#1A9E6B":isPref?C.orange:C.ink,background:isCheapest?"rgba(26,158,107,0.06)":isPref?"rgba(240,100,0,0.06)":"transparent"}}>
                    {fmt2(total)}
                    {!isCheapest&&saving>0&&<div style={{fontSize:10,color:C.red,fontWeight:400}}>+{fmt2(Math.round(saving))} vs cheapest</div>}
                    {isCheapest&&<div style={{fontSize:10,color:"#1A9E6B",fontWeight:400}}>★ lowest in region</div>}
                    {s.isLimited&&<div style={{fontSize:9,color:C.amber,fontWeight:400}}>⚠ limited local stock</div>}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td colSpan={2} style={{padding:"8px 12px",fontSize:12,color:C.inkLight}}>Total incl. GST (15%)</td>
              {sorted.map((s,i)=>{
                const total=totals[i]*1.15;
                const isPref=i===0; const isCheapest=i===overallCheapestIdx;
                if(s.isUnavailable) return <td key={s.id} style={{padding:"8px 12px",textAlign:"right",color:C.inkLight,opacity:0.4}}>—</td>;
                return <td key={s.id} style={{padding:"8px 12px",fontSize:12,textAlign:"right",color:isCheapest?"#1A9E6B":isPref?C.orange:C.inkMid,background:isCheapest?"rgba(26,158,107,0.06)":isPref?"rgba(240,100,0,0.06)":"transparent"}}>{fmt2(total)}</td>;
              })}
            </tr>
          </tfoot>
        </table>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:8,marginTop:14}}>
        {sorted.filter(s=>!s.isUnavailable).map((s,i)=>(
          <div key={s.id} style={{padding:"8px 10px",background:i===0?"rgba(240,100,0,0.05)":C.bg,borderRadius:8,border:`1px solid ${i===0?C.orange+"44":C.border}`}}>
            <div style={{fontSize:11,fontWeight:700,color:i===0?C.orange:s.color,marginBottom:2}}>
              {s.name}{i===0?" ♥":""}{i===overallCheapestIdx?" ★":""}
              {s.isLimited&&<span style={{fontSize:9,color:C.amber,marginLeft:4}}>⚠ limited</span>}
            </div>
            <div style={{fontSize:10,color:C.inkLight,lineHeight:1.4}}>{s.note}</div>
          </div>
        ))}
        {sorted.some(s=>s.isUnavailable)&&(
          <div style={{padding:"8px 10px",background:"#fafafa",borderRadius:8,border:`1px solid ${C.red}33`}}>
            <div style={{fontSize:11,fontWeight:700,color:C.red,marginBottom:2}}>Not in region</div>
            <div style={{fontSize:10,color:C.inkLight,lineHeight:1.4}}>{sorted.filter(s=>s.isUnavailable).map(s=>s.name).join(", ")} — nearest branches may be in another region.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ACCURACY DETECTION ───────────────────────────────────────────────────────
function detectAccuracy(file, tuning) {
  if(!file) return null;
  const name = file.name.toLowerCase();
  const isPDF = name.endsWith(".pdf");
  const isDWG = name.endsWith(".dwg");
  const highQualityWords = ["consent","consented","working","construction","building","permit","architectural","arch","final"];
  const medQualityWords  = ["developed","design","dd","preliminary","prelim","scheme","sd"];
  const lowQualityWords  = ["sketch","concept","rough","idea","draft","scan","scanned","old"];
  const hasHigh = highQualityWords.some(w=>name.includes(w));
  const hasMed  = medQualityWords.some(w=>name.includes(w));
  const hasLow  = lowQualityWords.some(w=>name.includes(w));
  let baseScore, level, description, tip;
  // plancheck-v1-2026-10-13: a hint from the FILE NAME only, before the
  // scan. It used to promise "85-95% accurate" from words in the name; the
  // scan's own verdict (measured / not measured / can't read) is the real
  // answer now, so this just says what kind of file it looks like.
  if(isDWG||(isPDF&&hasHigh))      { baseScore=88; level="high";   description="Looks like working drawings — the best kind of file. PlanBoss measures these from the drawing, and tells you if it can't."; tip=null; }
  else if(isPDF&&hasMed)            { baseScore=72; level="medium"; description="Looks like developed design drawings. Some dimensions may be missing; PlanBoss says so if it can't measure something."; tip="Consented working drawings give the most complete take-off."; }
  else if(hasLow)                   { baseScore=45; level="low";    description="Looks like a sketch, concept or scan. These often can't be measured from the drawing — PlanBoss will tell you if it can't."; tip="Ask the designer for the PDF straight from their drawing software."; }
  else if(isPDF)                    { baseScore=68; level="medium"; description="A PDF. If it's the drawing file from the designer, PlanBoss can measure it; if it's a scan, it will tell you."; tip="Working drawings straight from the designer's software work best."; }
  else                              { baseScore=40; level="low";    description="A photo or image. It can only be read by eye, not measured from the drawing."; tip="Upload the PDF from the designer for measured numbers."; }
  const boosts=[];
  if(tuning) {
    if(tuning.floorAreaOverride)                      boosts.push({label:"Floor area given",boost:0,reason:"The AI uses it as a cross-check on the plan's floor area"});
    if(tuning.jobType)                                boosts.push({label:"Job type given",boost:0,reason:"Passed to the AI with the plan"});
    if(tuning.constructionMethod)                     boosts.push({label:"Construction method given",boost:0,reason:"Passed to the AI with the plan"});
    if(tuning.specLevel)                              boosts.push({label:"Spec level given",boost:0,reason:"Passed to the AI with the plan"});
  }
  const totalBoost=boosts.reduce((s,b)=>s+b.boost,0);
  const finalScore=Math.min(97,baseScore+totalBoost);
  const finalLevel=finalScore>=85?"high":finalScore>=65?"medium":"low";
  const levelImproved=finalLevel!==level&&totalBoost>0;
  const colorMap={high:C.teal,medium:C.amber,low:C.red};
  const bgMap={high:C.tealLight,medium:C.amberLight,low:C.redLight};
  const iconMap={high:"🟢",medium:"🟡",low:"🔴"};
  return {level:finalLevel,score:finalScore,baseScore,label:level==="high"?"Good file":level==="medium"?"OK file":"Rough file",color:colorMap[level],bg:bgMap[level],icon:iconMap[level],description,tip:level==="high"?null:tip,boosts,totalBoost,levelImproved:false};
}

// plancheck-v1-2026-10-13: what kind of file this looks like, before the
// scan. No percentages: the old "88% — High confidence", "+8% from tuning"
// and "Working drawings 85-95%" were invented from the file's name.
function AccuracyBadge({accuracy,compact}) {
  if(!accuracy) return null;
  const a=accuracy;
  if(compact) return (
    <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"3px 10px",background:a.bg,borderRadius:20,border:`1px solid ${a.color}44`}}>
      <span style={{fontSize:11}}>{a.icon}</span>
      <span style={{fontSize:11,fontWeight:700,color:a.color}}>{a.label}</span>
    </div>
  );
  return (
    <div style={{background:a.bg,border:`1.5px solid ${a.color}44`,borderRadius:14,padding:"14px 18px",marginBottom:20}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
        <span style={{fontSize:18}}>{a.icon}</span>
        <span style={{fontSize:14,fontWeight:700,color:a.color}}>{a.label}</span>
      </div>
      <p style={{fontSize:13,color:C.inkMid,margin:0,lineHeight:1.5}}>{a.description}</p>
      {a.boosts&&a.boosts.length>0&&(
        <div style={{fontSize:11,color:C.teal,marginTop:8,lineHeight:1.5}}>✓ {a.boosts.map(b=>b.label).join(" · ")} — passed to the AI with the plan.</div>
      )}
      {a.tip&&(
        <div style={{display:"flex",gap:8,padding:"10px 12px",background:"rgba(255,255,255,0.5)",borderRadius:8,marginTop:10}}>
          <span style={{fontSize:14,flexShrink:0}}>💡</span>
          <div style={{fontSize:12,color:C.inkMid,lineHeight:1.5}}>{a.tip}</div>
        </div>
      )}
    </div>
  );
}

// ─── COMING SOON TILE ─────────────────────────────────────────────────────────
function ComingSoonTile({trade}) {
  const [notified,setNotified]=useState(()=>{
    const saved=LS.get("to_notify_interest",[]);
    return saved.includes(trade.id);
  });
  const [showInfo,setShowInfo]=useState(false);
  const notify=(e)=>{
    e.stopPropagation();
    const saved=LS.get("to_notify_interest",[]);
    if(!saved.includes(trade.id)) LS.set("to_notify_interest",[...saved,trade.id]);
    setNotified(true);
  };
  return (
    <div style={{position:"relative"}}>
      <button onClick={()=>setShowInfo(s=>!s)} style={{padding:"14px 10px",border:`1px dashed ${C.borderMid}`,borderRadius:12,background:"#fafafa",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,width:"100%",opacity:0.75,position:"relative"}}>
        <div style={{position:"absolute",top:4,right:6,fontSize:9,color:C.inkLight,fontWeight:700,background:C.bg,borderRadius:4,padding:"1px 5px"}}>{trade.eta}</div>
        <span style={{fontSize:22,filter:"grayscale(0.6)"}}>{trade.icon}</span>
        <span style={{fontSize:12,fontWeight:400,color:C.inkLight,textAlign:"center",lineHeight:1.3}}>{trade.label}</span>
        <span style={{fontSize:10,color:C.borderMid}}>Coming soon</span>
      </button>
      {showInfo&&(
        <div style={{position:"absolute",zIndex:10,top:"calc(100% + 6px)",left:"50%",transform:"translateX(-50%)",width:220,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 14px",boxShadow:"0 4px 16px rgba(0,0,0,0.12)"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
            <span style={{fontSize:16}}>{trade.icon}</span>
            <span style={{fontSize:13,fontWeight:700,color:C.ink}}>{trade.label}</span>
          </div>
          <p style={{fontSize:11,color:C.inkMid,lineHeight:1.5,margin:"0 0 10px"}}>{trade.teaser}</p>
          <div style={{fontSize:10,color:C.inkLight,marginBottom:10,fontStyle:"italic"}}>{trade.reason}</div>
          {notified
            ?<div style={{fontSize:12,color:C.teal,fontWeight:600,textAlign:"center"}}>✓ We'll notify you when it's ready!</div>
            :<button onClick={notify} style={{width:"100%",padding:"7px",background:C.tealMid,color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontSize:12,fontWeight:700}}>🔔 Notify me when ready</button>
          }
          <button onClick={e=>{e.stopPropagation();setShowInfo(false);}} style={{position:"absolute",top:6,right:8,background:"none",border:"none",cursor:"pointer",color:C.inkLight,fontSize:14}}>✕</button>
        </div>
      )}
    </div>
  );
}

// ─── TUNING CONFIG ────────────────────────────────────────────────────────────
const JOB_TYPES = [
  {id:"new_single",  label:"New build — single storey", icon:"🏠", note:"Standard timber frame, full materials takeoff"},
  {id:"new_double",  label:"New build — two storey",    icon:"🏢", note:"Double quantities for upper floor framing"},
  {id:"extension",   label:"Extension / addition",      icon:"🔨", note:"Partial materials — connection to existing structure"},
  {id:"renovation",  label:"Renovation",                icon:"🔧", note:"Reduced framing, focus on linings and fit-out"},
  {id:"deck",        label:"Deck / outbuilding",         icon:"🌿", note:"Simplified structure, outdoor-grade materials"},
  {id:"commercial",  label:"Light commercial",          icon:"🏗",  note:"Higher spec fittings, fire-rated materials"},
];
const CONSTRUCTION_METHODS = [
  {id:"timber",  label:"Timber framing",    icon:"🪵", note:"Standard NZ — H1.2 framing, GIB lining"},
  {id:"steel",   label:"Steel framing",     icon:"⚙️",  note:"Light steel — no timber framing, different fixings"},
  {id:"brick",   label:"Brick / block",     icon:"🧱", note:"Masonry — higher foundation and wall costs"},
  {id:"concrete",label:"Concrete tilt slab",icon:"🏗",  note:"Commercial-style — high upfront, low cladding cost"},
  {id:"sip",     label:"SIP panels",        icon:"🪟", note:"Structural insulated panels — fast build, premium cost"},
];
const SPEC_LEVELS = [
  {id:"standard",label:"Standard spec",icon:"⭐",  color:C.inkLight,note:"GIB Standard, basic fittings, entry-level fixtures"},
  {id:"mid",     label:"Mid spec",    icon:"⭐⭐", color:C.blue,    note:"Feature walls, mid-range fittings, some upgrades"},
  {id:"high",    label:"High spec",   icon:"⭐⭐⭐",color:C.amber,   note:"Architectural detail, premium materials throughout"},
];

// ─── NEW PROJECT SCREEN ───────────────────────────────────────────────────────
function NewProjectScreen({user,prefs,account,onComplete,onUpgrade}) {
  const plan=PLANS[user.plan]||PLANS.pro;
  const [name,setName]=useState("New project");
  const [editingName,setEditingName]=useState(false);
  const [file,setFile]=useState(null);
  const [trades,setTrades]=useState(
    prefs.preferredTrade==="fulljob"&&plan.id==="business"?TRADES.map(t=>t.id)
    :prefs.preferredTrade&&prefs.preferredTrade!=="fulljob"?
      (isAdminUser(user)||LIVE_TRADES.includes(prefs.preferredTrade)?[prefs.preferredTrade]:[LIVE_TRADES[0]])
      :(LIVE_TRADES[0]?[LIVE_TRADES[0]]:[])
  );
  const [dragging,setDragging]=useState(false);
  const [busy,setBusy]=useState(false);
  const [progress,setProgress]=useState(0);
  const [councilId,setCouncilId]=useState(prefs.region||"auckland_council");
  const council=NZ_COUNCILS.find(c=>c.id===councilId)||NZ_COUNCILS[0];
  const [jobType,setJobType]=useState("");
  const [constructionMethod,setConstructionMethod]=useState("");
  const [specLevel,setSpecLevel]=useState("");
  const [floorAreaOverride,setFloorAreaOverride]=useState("");
  const [showTuning,setShowTuning]=useState(false);
  const fileRef=useRef();
  const maxTrades=plan.trades;
  const toggleTrade=id=>{if(!trades.includes(id)&&trades.length>=maxTrades)return;setTrades(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);};
  // security-v1-2026-10-12: a failed scan now says so and saves nothing. It
  // used to save the job anyway with no analysis, and the tiles then showed
  // made-up figures (walls = floor area x 2.8) as if they had been measured.
  const [scanError,setScanError]=useState("");
  const [fileErr,setFileErr]=useState("");   // a picked file the scan can't take: shown right under the drop zone
  const [scanErrorCode,setScanErrorCode]=useState(null);
  // store-v1-2026-10-12: with filing on, the server's count is the real one
  // ({ plan, used, limit, resetsOn }); the device's own count is the fallback.
  const ent=account&&account.plan?account.plan:null;
  // stripe-live-2: with filing off the device decides: an admin has no limit,
  // and a signed-in account with no plan (no device trial any more) has none.
  const devAdmin=!ent&&!!(user&&user.role==="admin");
  const scanLimit=ent?ent.limit:devAdmin?null:getPlanLimit(user?.plan);
  const scansUsed=ent?ent.used:getAnalysisCount(user?.email);
  const noPlan=ent?ent.plan===null:!devAdmin&&!(user&&user.plan&&PLANS[user.plan]);
  const atLimit=noPlan||(scanLimit!==null&&scanLimit!==undefined&&scansUsed>=scanLimit);
  // stripe-live-2: with no plan, a new account starts Pro's free trial (card
  // up front); anyone else is told why they have no plan.
  // (Filing off: the server can't say, so the trial is offered, as on the
  // plans; Stripe's history has the final word at checkout.)
  const why=noPlan?(ent?pbNoPlanWhy(account):"offer"):"";
  const trialFree=why==="offer";
  const noPlanText=PB_NO_PLAN_TEXT[why]||PB_NO_PLAN_TEXT[""];
  const analyse=async()=>{
    setBusy(true);setProgress(10);setScanError("");setScanErrorCode(null);
    const tuning={jobType,constructionMethod,specLevel,floorAreaOverride:floorAreaOverride?Number(floorAreaOverride):null};
    const accuracy=detectAccuracy(file,tuning);
    const fail=(msg,code)=>{setBusy(false);setProgress(0);setScanError(msg);setScanErrorCode(code||null);};
    try{
      const toB64=(f)=>new Promise((res,rej)=>{const r=new FileReader();r.onload=()=>res(r.result.split(",")[1]);r.onerror=rej;r.readAsDataURL(f);});
      const send=await pbPrepareImage(file);   // big phone photos shrunk first; PDFs as they are
      const mediaType=pbUploadType(send)||"application/pdf";
      if(mediaType!=="application/pdf"&&send.size>PB_MAX_IMG_MB*1048576){
        fail("This photo is "+pbMB(send.size)+" and couldn't be made smaller on this phone, so it's too big to send. Upload the plan as a PDF, or take the photo at a lower resolution.");
        return;
      }
      const imageBase64=await toB64(send);
      setProgress(30);
      // store-v1: the job's details ride along so the server can file it
      // (name, council, file name) under the tradie's account. clientId fixes
      // the job's id on the server: if filing is slow and this copy is sent
      // up again later, it lands on the same job instead of a second one.
      const clientId=uid()+uid();
      const job={name,councilId,accuracy,tuning,fileName:file?.name||null,clientId};
      const resp=await pbFetch("/analyse-plan",{method:"POST",body:JSON.stringify({imageBase64,mediaType,trades,floorArea:tuning.floorAreaOverride,jobType,constructionMethod,specLevel,job})});
      setProgress(80);
      if(!resp.ok){ const info=await pbErrorInfo(resp,"The scan didn't finish. Nothing was saved — please try again."); fail(info.message,info.code); return; }
      let data=null; try{ data=await resp.json(); }catch(e){}
      if(!data||!data.analysis){ fail("The scan came back empty. Nothing was saved — please try again."); return; }
      setProgress(100);
      // plancheck-v1-2026-10-13: a plan that couldn't be read isn't counted
      // against the plan on this device; the corner check picture comes along.
      const vv=data.analysis.planVerdict&&data.analysis.planVerdict.verdict;
      const vk=data.analysis.planVerdict&&data.analysis.planVerdict.kind;
      if(vv!=="stop"&&!(vv==="hold"&&vk==="engine_down")) incrementAnalysis(user?.email);
      setTimeout(()=>onComplete({name,trades,file:file?.name,councilId,accuracy,tuning,aiAnalysis:data.analysis,jobId:data.jobId||null,clientId,checkImage:data.checkImage||null,checkImages:Array.isArray(data.checkImages)?data.checkImages:null}),400);
    }catch(err){
      console.error("AI analysis failed:",err);
      fail(err&&err.code==="signed_out"?err.message:"Couldn't reach the PlanBoss server. Check your connection and try again — nothing was saved.");
    }
  };
  // A picked or dropped file: only what the scan can take (a dropped DWG used
  // to be called "the best kind of file", then fail; a 40 MB PDF uploaded for
  // a minute, then failed with a code).
  const takeFile=(f)=>{
    if(!f)return;
    const bad=pbPlanFileProblem(f);
    if(bad){setFileErr(bad);return;}
    setFileErr("");setScanError("");setFile(f);
    const clean=String(f.name||"").replace(/\.[^/.]+$/,"").replace(/[-_]/g," ").replace(/\b\w/g,c=>c.toUpperCase()).trim();
    if(clean)setName(clean);
  };
  const jt=JOB_TYPES.find(j=>j.id===jobType);
  const cm=CONSTRUCTION_METHODS.find(c=>c.id===constructionMethod);
  const sl=SPEC_LEVELS.find(s=>s.id===specLevel);
  const tuning={jobType,constructionMethod,specLevel,floorAreaOverride:floorAreaOverride?Number(floorAreaOverride):null};
  const COUNCIL_REGIONS=[...new Set(NZ_COUNCILS.map(c=>c.region))];

  return (
    <div style={{padding:32,maxWidth:700,margin:"0 auto"}}>
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:28}}>
        <div style={{flex:1}}>
          {editingName
            ?<input value={name} onChange={e=>setName(e.target.value)} onBlur={()=>setEditingName(false)} onKeyDown={e=>e.key==="Enter"&&setEditingName(false)} autoFocus style={{fontSize:24,fontWeight:700,fontFamily:"Inter,system-ui,sans-serif",color:C.ink,border:`2px solid ${C.tealMid}`,borderRadius:8,padding:"4px 10px",background:C.bg,outline:"none",width:"90%",marginBottom:6}}/>
            :<div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
              <h1 style={{fontSize:24,fontWeight:700,color:C.ink,margin:0}}>{name}</h1>
              <button onClick={()=>setEditingName(true)} style={{background:"none",border:"none",cursor:"pointer",color:C.inkLight,fontSize:14,padding:4}}>✏️</button>
            </div>
          }
          <p style={{fontSize:14,color:C.inkLight}}>Upload a building plan — AI extracts measurements instantly.</p>
        </div>
        <Pill label={plan.name} color={plan.color} bg={plan.light}/>
      </div>

      {/* Upload zone */}
      <div className="mu-dropzone" onClick={()=>fileRef.current.click()} onDragOver={e=>{e.preventDefault();setDragging(true);}} onDragLeave={()=>setDragging(false)}
        onDrop={e=>{e.preventDefault();setDragging(false);takeFile(e.dataTransfer.files[0]);}}
        style={{border:`2px dashed ${dragging?C.tealMid:file?C.tealMid:C.borderMid}`,borderRadius:16,padding:"36px 32px",textAlign:"center",cursor:"pointer",background:file?C.tealFaint:C.bg,marginBottom:16}}>
        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];try{e.target.value="";}catch(x){}takeFile(f);}}/>
        <div className="mu-dropzone-icon" style={{fontSize:36,marginBottom:10}}>{file?"✅":"📐"}</div>
        {file?<><div style={{fontSize:16,fontWeight:700,color:C.teal}}>{file.name}</div><div style={{fontSize:13,color:C.inkLight,marginTop:4}}>Click to replace</div></>
          :<><div className="mu-dropzone-text" style={{fontSize:16,fontWeight:600,color:C.ink}}>Drop your building plan here</div><div className="mu-dropzone-caption" style={{fontSize:13,color:C.inkLight,marginTop:6}}>PDF (best — measured from the drawing), PNG or JPG · PDF up to 20 MB</div><button type="button" className="mu-browse-button" onClick={(e)=>{e.stopPropagation();fileRef.current.click();}} style={{marginTop:14,padding:"10px 20px",background:C.tealMid,color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:8}}>📁 Browse files</button></>}
      </div>

      {fileErr&&<div role="alert" style={{background:C.redLight,color:C.red,border:`1px solid ${C.red}33`,borderRadius:10,padding:"12px 14px",fontSize:13,fontWeight:600,marginTop:-6,marginBottom:16}}>{fileErr}</div>}
      {file&&<AccuracyBadge accuracy={detectAccuracy(file,tuning)}/>}

      {/* District council */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:"14px 16px",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div><div style={{fontSize:13,fontWeight:700,color:C.ink}}>District council</div><div style={{fontSize:11,color:C.inkLight,marginTop:2}}>Sets regional cost benchmarks and supplier coverage</div></div>
          {council&&<span style={{fontSize:11,color:C.inkLight}}>Climate Zone {council.climateZone}</span>}
        </div>
        <select value={councilId} onChange={e=>setCouncilId(e.target.value)} style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.ink,background:C.bg,outline:"none",cursor:"pointer"}}>
          {COUNCIL_REGIONS.map(region=>(
            <optgroup key={region} label={region}>
              {NZ_COUNCILS.filter(c=>c.region===region).map(c=>(
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </optgroup>
          ))}
        </select>
        {council?.notes&&<div style={{marginTop:8,fontSize:11,color:C.amber,background:C.amberFaint,borderRadius:6,padding:"6px 10px"}}>📍 {council.notes}</div>}
      </div>

      {/* Accuracy tuning */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,marginBottom:16,overflow:"hidden"}}>
        <button onClick={()=>setShowTuning(t=>!t)} style={{width:"100%",padding:"14px 16px",border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",textAlign:"left"}}>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:18}}>🎯</span>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <div style={{fontSize:13,fontWeight:700,color:C.ink}}>About the job</div>
                {(!jobType&&!constructionMethod&&!specLevel&&!floorAreaOverride)
                  ?<span style={{fontSize:10,color:C.amber,fontWeight:600,background:C.amberFaint,borderRadius:10,padding:"1px 7px"}}>Optional — tells the AI more about the job</span>
                  :<span style={{fontSize:10,color:C.teal,fontWeight:600,background:C.tealFaint,borderRadius:10,padding:"1px 7px"}}>✓ Set</span>}
              </div>
              <div style={{fontSize:11,color:C.inkLight,marginTop:1}}>
                {(!jobType&&!constructionMethod&&!specLevel&&!floorAreaOverride)
                  ?"Click to set job type, construction method and spec level"
                  :[jt&&`${jt.icon} ${jt.label}`,cm&&`${cm.icon} ${cm.label}`,sl&&`${sl.icon} ${sl.label}`,floorAreaOverride&&`${floorAreaOverride} m²`].filter(Boolean).join(" · ")}
              </div>
            </div>
          </div>
          <span style={{color:C.inkLight,fontSize:13}}>{showTuning?"▲":"▼"}</span>
        </button>
        {showTuning&&(
          <div style={{borderTop:`1px solid ${C.border}`,padding:"16px"}}>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:700,color:C.ink,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Job type</div>
              <div className="mu-tuning-options" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {JOB_TYPES.map(j=>{const sel=jobType===j.id;return(
                  <button key={j.id} onClick={()=>setJobType(sel?"":j.id)} style={{padding:"10px 8px",border:`2px solid ${sel?C.teal:C.border}`,borderRadius:10,background:sel?C.tealFaint:C.bg,cursor:"pointer",textAlign:"center"}}>
                    <div style={{fontSize:18,marginBottom:3}}>{j.icon}</div>
                    <div style={{fontSize:11,fontWeight:sel?700:400,color:sel?C.teal:C.inkMid,lineHeight:1.3}}>{j.label}</div>
                  </button>
                );})}
              </div>
              {jt&&<div style={{marginTop:8,fontSize:11,color:C.inkLight,padding:"6px 10px",background:C.tealFaint,borderRadius:6}}>💡 {jt.note}</div>}
            </div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:700,color:C.ink,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Construction method</div>
              <div className="mu-tuning-options" style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
                {CONSTRUCTION_METHODS.map(m=>{const sel=constructionMethod===m.id;return(
                  <button key={m.id} onClick={()=>setConstructionMethod(sel?"":m.id)} style={{padding:"10px 6px",border:`2px solid ${sel?C.blue:C.border}`,borderRadius:10,background:sel?C.blueLight:C.bg,cursor:"pointer",textAlign:"center"}}>
                    <div style={{fontSize:18,marginBottom:3}}>{m.icon}</div>
                    <div style={{fontSize:10,fontWeight:sel?700:400,color:sel?C.blue:C.inkMid,lineHeight:1.3}}>{m.label}</div>
                  </button>
                );})}
              </div>
              {cm&&<div style={{marginTop:8,fontSize:11,color:C.inkLight,padding:"6px 10px",background:C.blueLight,borderRadius:6}}>💡 {cm.note}</div>}
            </div>
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:700,color:C.ink,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Floor area confirmation</div>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{flex:1,background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:8}}>
                  <input type="number" value={floorAreaOverride} onChange={e=>setFloorAreaOverride(e.target.value)} placeholder="e.g. 145" style={{flex:1,border:"none",background:"transparent",fontSize:18,fontWeight:700,color:C.ink,outline:"none",width:0}}/>
                  <span style={{fontSize:13,color:C.inkMid}}>m²</span>
                </div>
                <div style={{fontSize:12,color:C.inkLight,flex:1,lineHeight:1.5}}>{floorAreaOverride?`✓ The AI checks the plan against ${floorAreaOverride} m²`:"Leave blank to read it from the plan. If you enter it, the AI uses it as a cross-check."}</div>
              </div>
            </div>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:C.ink,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Specification level</div>
              <div className="mu-tuning-options" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {SPEC_LEVELS.map(s=>{const sel=specLevel===s.id;return(
                  <button key={s.id} onClick={()=>setSpecLevel(sel?"":s.id)} style={{padding:"12px 8px",border:`2px solid ${sel?s.color:C.border}`,borderRadius:10,background:sel?`${s.color}18`:C.bg,cursor:"pointer",textAlign:"center"}}>
                    <div style={{fontSize:16,marginBottom:4}}>{s.icon}</div>
                    <div style={{fontSize:12,fontWeight:sel?700:500,color:sel?s.color:C.inkMid}}>{s.label}</div>
                    {sel&&<div style={{fontSize:10,color:s.color,marginTop:4,lineHeight:1.3}}>{s.note}</div>}
                  </button>
                );})}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trade selection */}
      <div style={{marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div><div style={{fontSize:13,fontWeight:700,color:C.ink,textTransform:"uppercase",letterSpacing:"0.06em"}}>Select trades to quote</div></div>
          {maxTrades<10&&<div style={{fontSize:12,color:C.inkLight}}>{trades.length}/{maxTrades} · <span onClick={onUpgrade} style={{color:C.teal,cursor:"pointer",fontWeight:600}}>Upgrade →</span></div>}
        </div>
        <div className="mu-trade-grid" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
          {isAdminUser(user) && (()=>{
            const allSel=trades.length===TRADES.length;const locked=plan.id!=="business";
            return(
              <button onClick={()=>locked?onUpgrade():allSel?setTrades([]):setTrades(TRADES.map(t=>t.id))}
                style={{padding:"14px 10px",border:`2px solid ${allSel&&!locked?C.purple:C.border}`,borderRadius:12,background:allSel&&!locked?C.purpleLight:C.card,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,opacity:locked?0.55:1,position:"relative"}}>
                {locked&&<div style={{position:"absolute",top:4,right:6,fontSize:9,color:C.purple,fontWeight:700}}>Business</div>}
                <span style={{fontSize:22}}>👷</span>
                <span style={{fontSize:12,fontWeight:allSel&&!locked?700:500,color:allSel&&!locked?C.purple:C.inkMid,textAlign:"center",lineHeight:1.3}}>Full job quote</span>
                {locked?<span style={{fontSize:10,color:C.borderMid}}>🔒 upgrade</span>:<span style={{fontSize:10,color:allSel?C.purple:C.inkLight}}>{allSel?"✓ all trades":"all trades"}</span>}
              </button>
            );
          })()}
          {TRADES.filter(t=>isTradeLive(t.id,user)).map(t=>{
            const sel=trades.includes(t.id);const locked=!sel&&trades.length>=maxTrades;const isPref=t.id===prefs.preferredTrade;
            return(
              <button key={t.id} onClick={()=>!locked&&toggleTrade(t.id)} style={{padding:"14px 10px",border:`2px solid ${sel?t.color:C.border}`,borderRadius:12,background:sel?t.light:C.card,cursor:locked?"not-allowed":"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,opacity:locked?0.45:1,position:"relative"}}>
                {isPref&&<div style={{position:"absolute",top:4,right:6,fontSize:9,color:t.color,fontWeight:700}}>preferred</div>}
                <span style={{fontSize:22}}>{t.icon}</span>
                <span style={{fontSize:12,fontWeight:sel?700:500,color:sel?t.color:C.inkMid,textAlign:"center",lineHeight:1.3}}>{t.label}</span>
                {locked&&<span style={{fontSize:10,color:C.borderMid}}>🔒 upgrade</span>}
              </button>
            );
          })}
          {/* Non-live trades shown as Coming Soon for non-admin */}
          {TRADES.filter(t=>!isTradeLive(t.id,user)).map(t=>(
            <button key={t.id} onClick={()=>logComingSoonInterest(t.id,user)} style={{padding:"14px 10px",border:`2px dashed ${C.borderMid}`,borderRadius:12,background:C.bg,cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:4,opacity:0.55,position:"relative"}} title="Coming soon — click to register your interest">
              <div style={{position:"absolute",top:4,right:6,fontSize:8,color:C.inkLight,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>Soon</div>
              <span style={{fontSize:22,filter:"grayscale(0.6)"}}>{t.icon}</span>
              <span style={{fontSize:12,fontWeight:500,color:C.inkLight,textAlign:"center",lineHeight:1.3}}>{t.label}</span>
              <span style={{fontSize:10,color:C.inkLight,fontStyle:"italic"}}>Coming soon</span>
            </button>
          ))}
          {COMING_SOON_TRADES.map(t=>(<ComingSoonTile key={t.id} trade={t}/>))}
        </div>
        {maxTrades<10&&<div data-pb-trade-limit style={{marginTop:12,padding:"10px 14px",background:C.amberFaint,border:`1px solid ${C.amberLight}`,borderRadius:10,fontSize:12,color:C.amber}}>Your <strong>{plan.name}</strong> plan includes {maxTrades} trade{maxTrades>1?"s":""} per scan. <span onClick={onUpgrade} style={{fontWeight:700,cursor:"pointer"}}>Upgrade →</span></div>}
      </div>

      {scanError&&!busy&&<div role="alert" style={{background:C.redLight,color:C.red,border:`1px solid ${C.red}33`,borderRadius:10,padding:"12px 14px",fontSize:13,fontWeight:600,marginBottom:12}}>
        {scanError}
        {scanErrorCode==="payment_failed"&&<div style={{marginTop:10}}><Btn small onClick={()=>pbOpenBillingPortal()}>Update your card →</Btn></div>}
        {(scanErrorCode==="monthly_limit"||scanErrorCode==="no_plan"||scanErrorCode==="trial_limit"||scanErrorCode==="too_many_trades")&&<div style={{marginTop:10}}><Btn small onClick={onUpgrade}>{scanErrorCode==="monthly_limit"||scanErrorCode==="too_many_trades"?"See plans →":(scanErrorCode==="no_plan"&&trialFree)?"Start your free trial →":"Choose a plan →"}</Btn></div>}
      </div>}
      {/* store-v1: this month's scans, from the server when filing is on */}
      {!busy&&((ent&&ent.limit===null)||devAdmin
        ?<div style={{fontSize:12,color:C.inkLight,marginBottom:10,textAlign:"center"}}>Admin — no scan limit</div>
        :<div style={{fontSize:12,color:atLimit?C.red:C.inkLight,marginBottom:10,textAlign:"center"}}>
          {noPlan?noPlanText
            :ent&&ent.plan==="trial"
              ?<>{scansUsed} of {scanLimit} free-trial scans used{ent.trialEndsAt?<> · trial ends {pbDate(ent.trialEndsAt,{day:"numeric",month:"short"})}</>:null}</>
              :ent&&ent.trialing
                ?<>{scansUsed} of {scanLimit} free-trial scans used · trial until {pbDate(ent.trialEndsAt,{day:"numeric",month:"short"})}</>
                :<>{scansUsed} of {scanLimit} scans used this month{ent&&ent.resetsOn?<> · resets {pbDay(ent.resetsOn)}</>:null}</>}
        </div>)}
      {busy
        ?<div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:24}}>
          <div style={{fontSize:14,fontWeight:600,color:C.ink,marginBottom:12}}>Analysing plan with AI…</div>
          <div style={{background:C.border,borderRadius:999,height:8,overflow:"hidden",marginBottom:10}}><div style={{width:`${Math.round(progress)}%`,height:"100%",background:C.tealMid,borderRadius:999,transition:"width 0.2s"}}/></div>
          <div style={{fontSize:12,color:C.inkLight,marginBottom:16}}>{progress<20?"Detecting drawing type and scale…":progress<40?"Reading plan dimensions…":progress<60?"Extracting wall areas and openings…":progress<80?"Calculating material quantities…":"Finishing up…"}</div>
          {progress>25&&<AccuracyBadge accuracy={detectAccuracy(file,tuning)}/>}
        </div>
        :(atLimit
          ?(noPlan&&why==="payment"
            ?<Btn full onClick={()=>pbOpenBillingPortal()}>Update your card →</Btn>
            :<Btn full onClick={onUpgrade}>{noPlan?(trialFree?"Start your 7-day free trial →":"Choose a plan to keep scanning →"):ent&&ent.plan==="trial"?"Free-trial scans used up — choose a plan →"
              :ent&&ent.trialing?"All "+scanLimit+" free-trial scans used — more when Pro starts "+pbDate(ent.trialEndsAt,{day:"numeric",month:"short"})
              :"Monthly limit reached ("+scansUsed+"/"+scanLimit+") — see plans →"}</Btn>)
          :<Btn full onClick={(!file||trades.length===0)?undefined:analyse} disabled={!file||trades.length===0}>{!file?"Upload a plan first":trades.length===0?"Select at least one trade":"Analyse plan →"}</Btn>)
      }
    </div>
  );
}

// ─── HOME (projects list) ─────────────────────────────────────────────────────
function HomeScreen({projects,setProjects,onOpen,onNewProject,opening,filing}) {
  const [editingId,setEditingId]=useState(null);
  const [editName,setEditName]=useState("");
  const [deletingId,setDeletingId]=useState(null);
  const saveName=(p)=>{const n=(editName||"").trim();if(n&&n!==p.name)setProjects(ps=>ps.map(x=>x.id===p.id?{...x,name:n}:x));setEditingId(null);};
  return (
    <div style={{padding:32,maxWidth:680,margin:"0 auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:filing?10:28}}>
        <h1 style={{fontSize:26,fontWeight:700,color:C.ink,margin:0}}>Projects</h1>
        <Btn onClick={onNewProject}>+ New project</Btn>
      </div>
      {filing&&<div style={{fontSize:12,color:C.inkLight,marginBottom:18}}>☁ Your jobs and plans are saved to your PlanBoss account — sign in on any phone or computer to see them.</div>}
      {projects.length===0
        ?<div style={{textAlign:"center",padding:"60px 20px",color:C.inkLight}}>
          <div style={{fontSize:48,marginBottom:16}}>📐</div>
          <div style={{fontSize:18,fontWeight:600,color:C.inkMid,marginBottom:8}}>No projects yet</div>
          <div style={{fontSize:14,marginBottom:24}}>Upload a building plan to get your first materials quote</div>
          <Btn onClick={onNewProject}>+ Start your first project</Btn>
        </div>
        :<div style={{display:"flex",flexDirection:"column",gap:10}}>
          {projects.map(p=>{ const pv=pbVerdictFor(p); const vv=pv&&pv.verdict; return (
            <div key={p.id} data-pb-job={p.id} style={{background:C.card,border:`1px solid ${p.viewOnly?C.orange+"66":vv==="stop"?C.red+"55":C.border}`,borderRadius:14,padding:"16px 20px",cursor:opening?"progress":"pointer",transition:"box-shadow 0.15s",opacity:opening&&opening!==p.id?0.6:1}}
              onClick={()=>!editingId&&!deletingId&&!opening&&onOpen(p)}
              onMouseEnter={e=>e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.08)"}
              onMouseLeave={e=>e.currentTarget.style.boxShadow="none"}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <div style={{flex:1,minWidth:0}}>
                  {editingId===p.id
                    ?<input value={editName} onChange={e=>setEditName(e.target.value)} onBlur={()=>saveName(p)} onKeyDown={e=>{if(e.key==="Enter")saveName(p);}} autoFocus onClick={e=>e.stopPropagation()} style={{fontSize:16,fontWeight:700,color:C.ink,border:`2px solid ${C.tealMid}`,borderRadius:6,padding:"2px 8px",background:C.bg,outline:"none",width:"80%"}}/>
                    :<div style={{fontSize:16,fontWeight:700,color:C.ink,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{p.name}</div>
                  }
                  <div style={{fontSize:12,color:C.inkLight,marginTop:3,display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                    {opening===p.id?<span style={{color:C.tealMid,fontWeight:600}}>Opening…</span>:<>{p.date}{vv!=="stop"?<> · {p.area?((vv==="hold"&&pv.kind!=="partial"&&pv.kind!=="levels"&&!(pv.kind==="alteration"&&pv.measured)?"~":"")+p.area):"—"} m²</>:null} · {p.trades?.length||0} trade{p.trades?.length!==1?"s":""}</>}
                    {/* plancheck-v1-2026-10-13: say so on the list too */}
                    {vv==="stop"&&<span style={{fontSize:10,fontWeight:700,color:C.red,background:C.redLight,borderRadius:10,padding:"1px 8px"}}>Couldn't read this plan</span>}
                    {vv==="hold"&&<span style={{fontSize:10,fontWeight:700,color:C.amber,background:C.amberFaint,borderRadius:10,padding:"1px 8px"}}>{pv.kind==="partial"?"Part not measured":pv.kind==="levels"?"Check the floors":pv.kind==="alteration"?"Alteration":"Not measured — rough"}</span>}
                    {p.viewOnly&&<span style={{fontSize:10,fontWeight:700,color:C.orange,background:C.orangeFaint,borderRadius:10,padding:"1px 8px"}}>View only · {p.viewOf||"customer"}</span>}
                    {filing&&!p.viewOnly&&(p.cloud
                      ?<span title="Saved to your PlanBoss account" style={{fontSize:11,color:C.tealMid}}>☁ saved</span>
                      :<span title="Not saved to your account yet" style={{fontSize:11,color:C.amber}}>this device only</span>)}
                  </div>
                </div>
                <div style={{display:"flex",gap:6,marginLeft:12,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                  <button onClick={()=>{setEditingId(p.id);setEditName(p.name);}} style={{padding:"5px 8px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,cursor:"pointer",fontSize:12,color:C.inkLight}}>✏️</button>
                  {deletingId===p.id
                    ?<div style={{display:"flex",gap:4}}>
                      <button onClick={()=>{setProjects(ps=>ps.filter(x=>x.id!==p.id));setDeletingId(null);}} style={{padding:"5px 10px",background:C.red,color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700}}>Delete</button>
                      <button onClick={()=>setDeletingId(null)} style={{padding:"5px 8px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:6,cursor:"pointer",fontSize:12}}>Cancel</button>
                    </div>
                    :<button onClick={()=>setDeletingId(p.id)} style={{padding:"5px 8px",background:C.bg,border:`1px solid ${C.red}44`,borderRadius:6,cursor:"pointer",fontSize:12,color:C.red}}>🗑</button>
                  }
                </div>
              </div>
            </div>
          ); })}
        </div>
      }
    </div>
  );
}

// ─── MATERIALS TAB WITH TRADIE CORRECTIONS (Option 6) ────────────────────────
// Corrections are logged locally (and will sync to Supabase when connected)
// to build a training dataset that improves AI accuracy over time.
function MaterialsTabWithCorrections({trade,project,fmt,fmtQ,showPrices=true,prefs,rough}) {
  const [corrections,setCorrections]=useState(()=>({...LS.get(`corrections_${project.id}_${trade.id}`,{})}));
  const [showCorrectMode,setShowCorrectMode]=useState(false);
  const [correctionsSaved,setCorrectionsSaved]=useState(false);
  const [viewMode,setViewMode]=useState("rooms");

  const [editingPrice,setEditingPrice]=useState(null);
  const [editingMeasurement,setEditingMeasurement]=useState(null);
  const [measurementUpdateNonce,setMeasurementUpdateNonce]=useState(0);

  const [priceUpdateNonce,setPriceUpdateNonce]=useState(0);

  // What metrics has the user ticked in preferences?
  const metricPrefs = prefs?.tradeMetrics?.[trade.id] || {};
  const hiddenMetrics = metricPrefs.hiddenMetrics || [];
  const tm = TRADE_METRICS[trade.id];
  const isVisible = (id) => !hiddenMetrics.includes(id);
  const showM2 = !tm || isVisible("wall_area")||isVisible("ceiling_area")||isVisible("floor_area")||isVisible("slab_area")||isVisible("roof_area")||isVisible("garden_area");
  const showSheets = trade.id==="plasterer" && (isVisible("wall_area")||isVisible("ceiling_area")||isVisible("wet_area"));

  // Build per-room notes from visible metrics only
  const buildNotes = (fallback, vals={}) => {
    const parts=[];
    if(vals.wall_area    !==undefined&&isVisible("wall_area"))    parts.push(`Walls ${vals.wall_area} m²`);
    if(vals.ceiling_area !==undefined&&isVisible("ceiling_area")) parts.push(`Ceiling ${vals.ceiling_area} m²`);
    if(vals.coving_lm    !==undefined&&isVisible("coving_lm"))    parts.push(`Coving ${vals.coving_lm} lm`);
    if(vals.wet_area     !==undefined&&isVisible("wet_area"))      parts.push(`Wet area ${vals.wet_area} m²`);
    if(vals.floor_area   !==undefined&&isVisible("floor_area"))   parts.push(`Floor ${vals.floor_area} m²`);
    if(vals.roof_area    !==undefined&&isVisible("roof_area"))     parts.push(`Roof ${vals.roof_area} m²`);
    if(vals.light_points !==undefined&&isVisible("light_points")) parts.push(`${vals.light_points} light pts`);
    if(vals.power_points !==undefined&&isVisible("power_points")) parts.push(`${vals.power_points} power pts`);
    return parts.length>0 ? parts.join(" · ") : fallback;
  };

  // plancheck-v1-2026-10-13: the made-up room breakdown is gone. With no
  // AI-read rooms it split the floor area (or a default 145 m2) into a
  // "Living / Dining 28%"-style list with sheet counts, which read like a
  // take-off. No rooms read now means no room list, said plainly.
  // ─── ROOMS: the AI-read rooms (nothing made up when there are none) ───
  const mapAIRoomToCardShape = (ai) => {
    // Find this room's per-trade data inside aiAnalysis.trades[trade.id].rooms[]
    const tradeRooms = project.aiAnalysis?.trades?.[trade.id]?.rooms || [];
    const tradeRoom = tradeRooms.find(r => r.room === ai.room);
    const tradeMats = tradeRoom?.materials || [];

    // Build exclusions from AI-read windows + doors
    const exclusions = [
      ...(ai.windows || []).map(w => ({
        type: 'window',
        count: 1,
        width: w.width || 1.2,
        height: w.height || 1.2,
        area: +(((w.width || 1.2) * (w.height || 1.2)).toFixed(2)),
        desc: w.ref ? `Window ${w.ref} (${w.width}×${w.height}m)` : `Window (${w.width}×${w.height}m)`,
        reason: 'Window — wall area deducted',
      })),
      ...(ai.doors || []).map(d => ({
        type: 'door',
        count: 1,
        width: d.width || 0.9,
        height: d.height || 2.0,
        area: +(((d.width || 0.9) * (d.height || 2.0)).toFixed(2)),
        desc: d.ref ? `${d.type || 'Door'} ${d.ref} (${d.width}×${d.height}m)` : `${d.type || 'Door'} (${d.width}×${d.height}m)`,
        reason: `${d.type || 'Door'} — wall area deducted`,
      })),
    ];

    // Per-room materials with prices looked up from trade.materials master list
    // Wastage multiplier applied to sheet quantities only (rounds UP — you can't order half a sheet)
    const wastagePct = (prefs?.wastagePercent ?? 10) / 100;
    const roomMaterials = tradeMats.map(m => {
      const isSheet = (m.unit || '').toLowerCase().includes('sheet');
      const baseQty = m.qty || 0;
      const finalQty = isSheet && wastagePct > 0
        ? Math.ceil(baseQty * (1 + wastagePct))
        : baseQty;
      return {
        name: m.name,
        qty: finalQty,
        baseQty,
        unit: m.unit || '',
        price: (typeof resolvePrice === 'function')
          ? resolvePrice(m.name, project?.id, (LS.get('to_user',null)||{}).email, lookupPrice(m.name))
          : lookupPrice(m.name),
        cat: m.cat || '',
        wastageApplied: isSheet && wastagePct > 0 ? Math.round(wastagePct * 100) : 0,
      };
    }).filter(m => m.qty > 0);

    return {
      room: ai.room,
      m2: ai.floorM2,
      wallM2: ai.wallM2Net,
      ceilingM2: ai.ceilingM2,
      perimeterLm: ai.perimeterLm,
      studHeight: ai.studHeight,
      walls: ai.walls || [],
      isWet: !!ai.isWet,
      wetM2: ai.isWet ? (ai.wallM2Net || 0) : 0,
      dryM2: ai.isWet ? 0 : (ai.wallM2Net || 0),
      sheets: roomMaterials.filter(m => (m.unit || '').includes('sheet')).reduce((s, m) => s + (m.qty || 0), 0),
      boardType: ai.isWet ? 'GIB Aqualine 10mm' : 'GIB Standard 10mm',
      exclusions,
      roomMaterials,
      notes: ai.notes || '',
      metricVals: {
        wall_area: ai.wallM2Net,
        ceiling_area: ai.ceilingM2,
        floor_area: ai.floorM2,
        coving_lm: ai.perimeterLm,
        wet_area: ai.isWet ? ai.wallM2Net : 0,
      },
      _aiSource: true,  // flag so UI can show "Read from plan" badge
    };
  };

  const aiRooms = project.aiAnalysis?.rooms;
  const rooms = (Array.isArray(aiRooms) && aiRooms.length > 0)
    ? aiRooms.map(mapAIRoomToCardShape)
    : [];
  const usingAIRooms = rooms.length > 0 && rooms[0]?._aiSource === true;

  const getQty=(m)=>corrections[m.name]!==undefined?corrections[m.name]:m.qty;
  const hasChanges=trade.materials.some(m=>corrections[m.name]!==undefined&&corrections[m.name]!==m.qty);

  const saveCorrections=()=>{
    LS.set(`corrections_${project.id}_${trade.id}`,corrections);
    // In production: POST corrections to Supabase for model training
    setCorrectionsSaved(true);
    setTimeout(()=>setCorrectionsSaved(false),2500);
    setShowCorrectMode(false);
  };

  // ─── MATERIALS TAB = SUM OF BY ROOM (single source of truth) ──────────────
  // Aggregate per-room materials from the rooms array (computed above at line ~2159).
  // This means Materials tab AUTOMATICALLY reflects:
  //   - Wastage toggle changes (already applied per-room)
  //   - Price lookups (already done in mapAIRoomToCardShape via lookupPrice)
  //   - Future per-room corrections by tradies
  // No second calculation, no chance of disagreement with By Room.
  const aggregatedFromRooms = {};
  (rooms || []).forEach(r => {
    (r.roomMaterials || []).forEach(m => {
      if (!m.name) return;
      const key = m.name;
      if (!aggregatedFromRooms[key]) {
        aggregatedFromRooms[key] = {
          name: m.name,
          unit: m.unit || '',
          price: m.price || 0,
          cat: m.cat || (typeof categoriseMaterial === 'function' ? categoriseMaterial(m.name) : 'Other'),
          qty: 0,
        };
      }
      aggregatedFromRooms[key].qty += (m.qty || 0);
    });
  });
  const aiAggregated = Object.values(aggregatedFromRooms).filter(m => m.qty > 0);
  // plancheck-v1: no AI materials -> no list (it used to show the trade's
  // built-in example list, e.g. 48 sheets of GIB, as if read off the plan).
  const matsForTab = (aiAggregated.length > 0 && usingAIRooms) ? aiAggregated : [];
  const usingAiMats = aiAggregated.length > 0 && usingAIRooms;

  // Capture unknown materials for monthly review (silent — no UI impact)
  useEffect(() => {
    if (usingAiMats && typeof captureUnknownMaterials === 'function') {
      const email = (typeof currentEmail !== 'undefined' && currentEmail)
        || (typeof userEmail !== 'undefined' && userEmail)
        || 'unknown';
      captureUnknownMaterials(matsForTab, email);
    }
  }, [project?.id, usingAiMats]);

  const correctedTotal=matsForTab.reduce((s,m)=>s+getQty(m)*m.price,0);
  const originalTotal=matsForTab.reduce((s,m)=>s+m.qty*m.price,0);
  const cats=[...new Set(matsForTab.map(m=>m.cat))];

  return (
    <>
      {/* View toggle + correction toolbar */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
        {/* Left: view toggle */}
        <div style={{display:"inline-flex",background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:2,gap:2}}>
          {[{id:"rooms",label:"🏠 By room"},{id:"materials",label:"📋 Materials"}].map(v=>(
            <button key={v.id} onClick={()=>setViewMode(v.id)}
              style={{padding:"5px 12px",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:viewMode===v.id?700:400,
                background:viewMode===v.id?C.card:C.bg,color:viewMode===v.id?C.ink:C.inkLight,
                boxShadow:viewMode===v.id?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>
              {v.label}
            </button>
          ))}
        </div>
        {/* Right: correction controls — only in materials view */}
        {viewMode==="materials"&&(
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            {correctionsSaved&&<span style={{fontSize:12,color:C.teal,fontWeight:600}}>✓ Corrections saved!</span>}
            {hasChanges&&!showCorrectMode&&<button onClick={saveCorrections} style={{padding:"5px 12px",background:C.tealMid,color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:700}}>Save corrections</button>}
            <button onClick={()=>setShowCorrectMode(m=>!m)} style={{padding:"5px 12px",background:showCorrectMode?C.amberLight:"transparent",color:showCorrectMode?C.amber:C.inkLight,border:`1px solid ${showCorrectMode?C.amber:C.border}`,borderRadius:6,cursor:"pointer",fontSize:12,fontWeight:600}}>
              {showCorrectMode?"✓ Done correcting":"✏️ Correct quantities"}
            </button>
          </div>
        )}
        {viewMode==="rooms"&&(
          <div style={{fontSize:11,color:C.inkLight,fontStyle:"italic"}}>AI estimates · verify on plan</div>
        )}
      </div>

      {showCorrectMode&&viewMode==="materials"&&(
        <div style={{background:C.amberFaint,border:`1px solid ${C.amberLight}`,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:C.amber}}>
          ✏️ Edit any quantity below. Your corrections help improve AI accuracy for future NZ jobs.
        </div>
      )}

      {/* ── ROOM BREAKDOWN VIEW ── */}
      {viewMode==="rooms"&&(
        <>
          <div style={{background:usingAIRooms&&!rough?C.tealFaint:'#FFFBEB',border:`1px solid ${usingAIRooms&&!rough?C.tealLight:'#FCD34D'}`,borderRadius:10,padding:"10px 14px",marginBottom:16,fontSize:12,color:usingAIRooms&&!rough?C.teal:'#92400E'}}>
            {!usingAIRooms ? "The AI couldn't list this plan's rooms, so there's no room-by-room breakdown for this scan."
              : rough ? `⚠ ${rooms.length} rooms as the AI read them by eye — check each against the plan.`
              : `✅ Read directly from your plan — ${rooms.length} rooms, ${rooms.reduce((s,r)=>s+(r.walls?.length||0),0)} walls measured`}
          </div>
          {(()=>{
            // Patch 5: corrections summary count. Hidden when zero.
            // Re-evaluates on measurementUpdateNonce.
            void measurementUpdateNonce;
            const pid = project?.id;
            if (!pid || !rooms || rooms.length === 0) return null;
            let correctedRoomCount = 0;
            try {
              const data = JSON.parse(localStorage.getItem('project_overrides_'+pid) || '{}');
              for (const r of rooms) {
                const roomOver = data[r.room];
                if (roomOver && Object.keys(roomOver).length > 0) correctedRoomCount++;
              }
            } catch (e) { return null; }
            if (correctedRoomCount === 0) return null;
            return (
              <div style={{background:C.amberFaint,border:'1px solid '+C.amberLight,borderRadius:8,padding:'8px 14px',marginBottom:12,fontSize:12,color:C.amber,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                <span style={{fontSize:13}}>✏️</span>
                <span>{correctedRoomCount} of {rooms.length} room{rooms.length === 1 ? '' : 's'} ha{correctedRoomCount === 1 ? 's' : 've'} corrections</span>
              </div>
            );
          })()}
          {rooms.map((room,i)=>(
            <RoomCard key={i} room={room} trade={trade} showM2={showM2} showSheets={showSheets} showPrices={showPrices} fmt={fmt} onEditPrice={(m)=>setEditingPrice(m)} onEditMeasurement={(req)=>setEditingMeasurement(req)} projectId={project?.id} currentEmail={(LS.get('to_user',null)||{}).email} planFileName={project?.fileName||project?.planFileName||null} measurementUpdateNonce={measurementUpdateNonce}/>
          ))}
          {/* Room totals */}
          <div style={{background:trade.light,border:`1px solid ${trade.color}33`,borderRadius:10,padding:"12px 18px",marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <span style={{fontSize:13,fontWeight:700,color:trade.color}}>{trade.label} working area</span>
              {showM2&&(()=>{
                const workingAreaM2 = rooms.reduce((s,r)=>s+(Number(r.m2)||0),0);
                const totalFloorArea = Number(project?.aiAnalysis?.floorArea) || Number(project?.area) || 0;
                const showTotalLine = totalFloorArea > 0 && Math.abs(totalFloorArea - workingAreaM2) >= 1;
                return (
                  <>
                    <div style={{fontSize:11,color:trade.color,opacity:0.7,marginTop:2}}>{Math.round(workingAreaM2*10)/10} m² · {rooms.length} rooms</div>
                    {showTotalLine && (
                      <div style={{fontSize:11,color:trade.color,opacity:0.55,marginTop:1,fontStyle:"italic"}}>Total floor area: {totalFloorArea} m² (incl. wardrobes & sub-spaces)</div>
                    )}
                  </>
                );
              })()}
            </div>
            <div style={{textAlign:"right"}}>
              {showSheets&&rooms.some(r=>r.sheets>0)&&<div style={{fontSize:12,color:trade.color,marginBottom:2}}>{rooms.reduce((s,r)=>s+r.sheets,0)} sheets total</div>}
              {showPrices&&fmt&&(()=>{
                // Use the canonical helper so this matches MaterialsScreen header + sense check.
                const grandTotal = computeLiveMaterialsTotal({ project, trade, prefs });
                return grandTotal>0?(
                  <div>
                    <div style={{fontSize:16,fontWeight:800,color:trade.color}}>{fmt(grandTotal)}</div>
                    <div style={{fontSize:11,color:trade.color,opacity:0.7}}>excl. GST · all rooms</div>
                  </div>
                ):null;
              })()}
            </div>
          </div>
        </>
      )}

      {/* PRICE EDIT MODAL */}
      {editingPrice && (
        <PriceEditModal
          material={editingPrice}
          projectId={project?.id}
          currentEmail={(LS.get('to_user',null)||{}).email}
          onClose={()=>setEditingPrice(null)}
          onSaved={()=>setPriceUpdateNonce(n=>n+1)}
        />
      )}
      {/* MEASUREMENT EDIT MODAL */}
      {editingMeasurement && (
        <MeasurementEditModal
          fieldKey={editingMeasurement.fieldKey}
          fieldLabel={editingMeasurement.fieldLabel}
          fieldUnit={editingMeasurement.fieldUnit}
          currentValue={editingMeasurement.currentValue}
          trade={trade?.id}
          roomName={editingMeasurement.roomName}
          projectId={project?.id}
          currentEmail={(LS.get('to_user',null)||{}).email}
          planFileName={project?.fileName||project?.planFileName||null}
          onClose={()=>setEditingMeasurement(null)}
          onSaved={()=>setMeasurementUpdateNonce(n=>n+1)}
        />
      )}
      {/* ── MATERIALS LIST VIEW ── */}
      {viewMode==="materials"&&(
        <>
      {matsForTab.length===0&&(
        <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:12,padding:"16px 18px",fontSize:13,color:C.inkMid,marginBottom:16}}>
          No materials list was read from this plan for this trade, so there's nothing to price here. Scan the plan again, or work the materials up from the measurements above.
        </div>
      )}
      {cats.map(cat=>(
        <div key={cat} style={{marginBottom:20}}>
          <div style={{fontSize:11,fontWeight:700,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:8}}>{cat}</div>
          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden"}}>
            {matsForTab.filter(m=>m.cat===cat).map((m,i,arr)=>{
              const corrected=corrections[m.name]!==undefined;
              const qty=getQty(m);
              return (
                <div key={m.name} style={{display:"grid",gridTemplateColumns:showPrices?"1fr 130px 100px 90px":"1fr 130px",padding:"10px 16px",borderBottom:i<arr.length-1?`1px solid ${C.border}`:"none",alignItems:"center",background:corrected?"#fffbf0":"transparent"}}>
                  <div style={{fontSize:13,color:C.ink,display:"flex",alignItems:"center",gap:6}}>
                    {corrected&&<span style={{fontSize:10,color:C.amber,fontWeight:700,background:C.amberLight,borderRadius:4,padding:"1px 5px"}}>edited</span>}
                    {m.name}
                  </div>
                  {showCorrectMode?(
                    <div style={{display:"flex",alignItems:"center",gap:4,justifyContent:"flex-end"}}>
                      <input type="number" value={qty} min={0} step={0.5}
                        onChange={e=>setCorrections(c=>({...c,[m.name]:Number(e.target.value)}))}
                        style={{width:60,padding:"3px 6px",border:`1px solid ${C.amber}`,borderRadius:6,fontSize:13,fontWeight:700,color:C.ink,outline:"none",textAlign:"right"}}/>
                      <span style={{fontSize:11,color:C.inkMid}}>{m.unit}</span>
                      {corrected&&<button onClick={()=>setCorrections(c=>{const n={...c};delete n[m.name];return n;})} style={{background:"none",border:"none",cursor:"pointer",color:C.borderMid,fontSize:12}}>↩</button>}
                    </div>
                  ):(
                    <div style={{fontSize:13,color:corrected?C.amber:C.inkMid,textAlign:"right",fontWeight:corrected?700:400}}>{fmtQ(qty)} {m.unit}</div>
                  )}
                  {showPrices&&<div style={{fontSize:13,color:C.inkMid,textAlign:"right"}}>{fmt(m.price)}</div>}
                  {showPrices&&<div style={{fontSize:13,fontWeight:600,color:corrected?C.amber:C.ink,textAlign:"right"}}>{fmt(qty*m.price)}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end",marginTop:8}}>
        {hasChanges&&showPrices&&<div style={{fontSize:12,color:C.amber,padding:"6px 16px"}}>Original AI estimate: {fmt(originalTotal)} · Your corrected total: {fmt(correctedTotal)}</div>}
        {showPrices&&<div style={{padding:"12px 20px",background:trade.light,borderRadius:10,fontSize:14,fontWeight:700,color:trade.color}}>Subtotal excl. GST: {fmt(correctedTotal)}</div>}
        {showPrices&&<div style={{padding:"10px 20px",background:C.bg,borderRadius:10,fontSize:13,fontWeight:600,color:C.ink}}>Subtotal incl. GST (15%): {fmt(correctedTotal*1.15)}</div>}
      </div>

      {/* Option 6 — Historical comparison prompt */}
      {(()=>{
        const savedProjects=LS.get("to_projects_"+getCurrentUserEmail(),[]);
        const similar=savedProjects.filter(p=>p.id!==project.id&&p.trades?.includes(trade.id)&&p.accuracy?.level==="high").slice(0,2);
        if(similar.length===0) return null;
        return (
          <div style={{marginTop:16,background:C.tealFaint,border:`1px solid ${C.tealLight}`,borderRadius:12,padding:"12px 16px"}}>
            <div style={{fontSize:12,fontWeight:700,color:C.teal,marginBottom:6}}>📂 Similar past jobs found</div>
            <div style={{fontSize:12,color:C.inkMid,marginBottom:10}}>You've quoted {trade.label} on similar projects. Compare to improve accuracy:</div>
            {similar.map(p=>(
              <div key={p.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderTop:`1px solid ${C.tealLight}`}}>
                <span style={{fontSize:12,color:C.ink}}>{p.name}</span>
                <span style={{fontSize:11,color:C.inkLight}}>{p.area}m² · {p.date}</span>
              </div>
            ))}
          </div>
        );
      })()}
        </>
      )}
    </>
  );
}

// ─── MATERIALS + QUOTE ────────────────────────────────────────────────────────
function MaterialsScreen({user,prefs,project,onBack,onUpgrade,onRenameProject,onPlanUrl,onNewScan,onDeleteJob,onCheckCorners}) {
  const plan=PLANS[user.plan]||PLANS.pro;
  // plancheck-v1-2026-10-13: can this plan be quoted from? A "stop" shows no
  // numbers at all; a "hold" shows none until the tradie asks to see them.
  const verdict=pbVerdictFor(project);
  const [roughShown,setRoughShown]=useState(()=>pbRoughOk(project.id));
  const hideNumbers=!!(verdict&&(verdict.verdict==="stop"||(verdict.verdict==="hold"&&!roughShown)));
  // alter-v1-2026-10-23: an alteration the engine measured is the WHOLE
  // building (measured, but not the job); one it couldn't measure is rough.
  const wholeBldg=!!(verdict&&verdict.verdict==="hold"&&verdict.kind==="alteration"&&verdict.measured===true);
  // third review (23 Oct): "the building it could trace" when part may be missing
  const wholeDoubt=wholeBldg&&Array.isArray(verdict.reasons)&&verdict.reasons.some(r=>r&&r.code==="partial_maybe");
  const rough=!!(verdict&&verdict.verdict==="hold"&&verdict.kind!=="partial"&&verdict.kind!=="levels"&&!wholeBldg);
  const partial=!!(verdict&&verdict.verdict==="hold"&&verdict.kind==="partial");
  // every-storey round 2: measured, but more floors than the drawings show
  const extraFloors=!!(verdict&&verdict.verdict==="hold"&&verdict.kind==="levels");
  const showAnyway=()=>{pbSetRoughOk(project.id);setRoughShown(true);};
  // the corner check picture, if this device still has it
  const [hasPic,setHasPic]=useState(false);
  useEffect(()=>{let live=true;pbCheckImgGet(project.id).then(v=>{if(live)setHasPic(!!v);});return()=>{live=false;};},[project.id]);
  // store-v1: the original plan PDF, kept with the job. Links are signed and
  // expire after 15 minutes, so a fresh one is fetched when it's asked for.
  const [planLink,setPlanLink]=useState(null);
  const [planLinkBusy,setPlanLinkBusy]=useState(false);
  const canViewPlan=!!(onPlanUrl&&project.hasPdf&&(project.cloud||project.viewOnly));
  const getPlanLink=async()=>{
    setPlanLinkBusy(true);
    try{ const url=await onPlanUrl(project); if(url) setPlanLink({url,at:Date.now()}); else window.alert("This job's plan PDF isn't available."); }
    catch(e){ window.alert((e&&e.message)||"Couldn't fetch the plan PDF. Check your connection."); }
    finally{ setPlanLinkBusy(false); }
  };
  const planLinkFresh=planLink&&Date.now()-planLink.at<14*60000;
  // plancheck-v1: the full job view is admin-only (its button is); a Business
  // customer used to land on it with fake "Sent!" buttons.
  const [activeTrade,setActiveTrade]=useState(
    isAdminUser(user)&&plan.id==="business" ? "fulljob" : project.trades?.[0]
  );
  const [activeTab,setActiveTab]=useState("materials");
  const [labourHours,setLabourHours]=useState(prefs.labourHours);
  const [labourRate,setLabourRate]=useState(prefs.labourRate);
  const [markupPct,setMarkupPct]=useState(prefs.markupPct);
  const [projectName,setProjectNameLocal]=useState(project.name);
  const council=NZ_COUNCILS.find(c=>c.id===(project.councilId||prefs.region))||NZ_COUNCILS[0];
  const [editingName,setEditingName]=useState(false);

  const handleRename=(name)=>{
    setProjectNameLocal(name);
    if(onRenameProject) onRenameProject(project.id, name);
  };
  // ---- floorsource-v1-2026-07-27 — same source as the Floor tile ----
  // This one feeds the job header, SenseCheck ($/m2 vs typical NZ cost)
  // and FullJobQuote. It has to agree with the tile or the screen shows
  // two different floor areas within a couple of centimetres of each other.
  const _pbGeoFloor = (p) => { const f = p && p.aiAnalysis && p.aiAnalysis.geometry && p.aiAnalysis.geometry.reconciled && p.aiAnalysis.geometry.reconciled.all && p.aiAnalysis.geometry.reconciled.all.floor_m2; return (f && f.value != null && f.conf !== 'flag') ? Math.round(f.value * 100) / 100 : null; };
  const floorArea = _pbGeoFloor(project) || project.area || null;    // plancheck-v1: no made-up 145 m2
  const isFullJob=activeTrade==="fulljob";
  const trade=isFullJob?null:TRADES.find(t=>t.id===activeTrade);
  // Live materials total — single source of truth via computeLiveMaterialsTotal.
  // Same calc used by Materials tab subtotal and sense check, so all three agree.
  const matTotal = computeLiveMaterialsTotal({ project, trade, prefs });   // null = no priced materials read
  const matKnown = matTotal != null;
  const labourTotal=labourHours*labourRate;
  const markupTotal=((matTotal||0)+labourTotal)*markupPct/100;
  const subtotal=(matTotal||0)+labourTotal+markupTotal;
  const matText = matKnown ? fmt(matTotal) : "not read from this plan";

  return (
    <div className="mu-materials" style={{display:"flex",height:"100%",minHeight:600}}>
      {/* Trade sidebar */}
      <div style={{width:180,borderRight:`1px solid ${C.border}`,padding:"16px 10px",flexShrink:0,display:"flex",flexDirection:"column"}} className="mu-trade-sidebar">
        <button onClick={onBack} style={{display:"flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",color:C.inkLight,fontSize:13,padding:"6px 8px",borderRadius:8,marginBottom:12,textAlign:"left"}}>
          ← Home
        </button>

        {/* Full job quote button — Business only, sits at top. Hidden entirely for non-admin. */}
        {isAdminUser(user) && (plan.id==="business"?(
          <button onClick={()=>{setActiveTrade("fulljob");setActiveTab("materials");}}
            style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"9px 10px",border:"none",borderRadius:8,cursor:"pointer",marginBottom:8,
              background:isFullJob?C.purpleLight:"transparent",
              color:isFullJob?C.purple:C.inkMid,
              fontWeight:isFullJob?700:500,fontSize:13,textAlign:"left"}}>
            <span>👷</span>Full job
            {isFullJob&&<span style={{marginLeft:"auto",fontSize:9,color:C.purple}}>★</span>}
          </button>
        ):(
          <button onClick={onUpgrade}
            style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"9px 10px",border:`1px dashed ${C.border}`,borderRadius:8,cursor:"pointer",marginBottom:8,background:"transparent",color:C.inkLight,fontSize:12,textAlign:"left",opacity:0.7}}>
            <span style={{fontSize:13}}>👷</span>Full job 🔒
          </button>
        ))}

        <div style={{height:1,background:C.border,marginBottom:8}}/>
        <div style={{fontSize:11,fontWeight:700,color:C.inkLight,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:10,padding:"0 4px",color:"#6B7280"}}>Trades</div>
        {TRADES.filter(t=>project.trades?.includes(t.id)).map(t=>(
          <button key={t.id} onClick={()=>{setActiveTrade(t.id);setActiveTab("materials");}} style={{display:"flex",alignItems:"center",gap:8,width:"100%",padding:"9px 10px",border:"none",borderRadius:8,cursor:"pointer",marginBottom:2,background:activeTrade===t.id?t.light:"transparent",color:activeTrade===t.id?t.color:C.inkMid,fontWeight:activeTrade===t.id?700:400,fontSize:13,textAlign:"left"}}>
            <span>{t.icon}</span>{t.label.split(" ")[0]}
            {t.id===prefs.preferredTrade&&<span style={{marginLeft:"auto",fontSize:9,color:t.color}}>★</span>}
          </button>
        ))}
        <div style={{marginTop:"auto",borderTop:`1px solid ${C.border}`,paddingTop:12}}>
          <div style={{padding:"8px 10px",background:"rgba(255,255,255,0.08)",borderRadius:8}}>
            <div style={{fontSize:12,fontWeight:700,color:plan.color}}>{plan.name}</div>
            {plan.id!=="business"&&<button onClick={onUpgrade} style={{marginTop:8,fontSize:11,color:plan.color,background:"none",border:"none",cursor:"pointer",padding:0,fontWeight:700}}>Upgrade →</button>}
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{flex:1,overflow:"auto",padding:28}}>

        {/* store-v1: admin viewing a customer's job, and the plan PDF */}
        {project.viewOnly&&(
          <div style={{background:C.orangeFaint,border:`1px solid ${C.orange}55`,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:C.amber,fontWeight:600}}>
            Admin view of {project.viewOf||"a customer"}'s job — what they see. Changes you make here stay on this device and never reach their account.
          </div>
        )}
        {canViewPlan&&(
          <div style={{display:"flex",justifyContent:"flex-end",marginBottom:10}}>
            {planLinkFresh
              ?<a href={planLink.url} target="_blank" rel="noopener noreferrer" onClick={e=>{if(Date.now()-planLink.at>14*60000){e.preventDefault();setPlanLink(null);getPlanLink();}}} style={{fontSize:12,fontWeight:700,color:C.tealMid,textDecoration:"none",border:`1px solid ${C.tealMid}55`,borderRadius:8,padding:"6px 12px"}}>📄 Open plan PDF ↗</a>
              :<button onClick={getPlanLink} disabled={planLinkBusy} style={{fontSize:12,fontWeight:700,color:C.tealMid,background:"transparent",border:`1px solid ${C.tealMid}55`,borderRadius:8,padding:"6px 12px",cursor:planLinkBusy?"progress":"pointer"}}>{planLinkBusy?"Fetching plan…":"📄 View plan PDF"}</button>}
          </div>
        )}

        {/* ── Full job view ── */}
        {isFullJob&&<PlanVerdictPanel verdict={verdict} roughShown={roughShown} onShowAnyway={showAnyway} onNewScan={onNewScan} onDeleteJob={onDeleteJob} onLookAtPicture={hasPic&&onCheckCorners?()=>onCheckCorners(project,true):undefined}/>}
        {isFullJob&&!hideNumbers&&(
          <FullJobQuote project={project} prefs={prefs} floorArea={floorArea} council={council}/>
        )}

        {/* ── Individual trade view ── */}
        {!isFullJob&&trade&&(<>
        <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{fontSize:32}}>{trade.icon}</span>
            <div>
              {editingName
                ?<input value={projectName} onChange={e=>setProjectNameLocal(e.target.value)}
                    onBlur={()=>{setEditingName(false);handleRename(projectName);}}
                    onKeyDown={e=>e.key==="Enter"&&(setEditingName(false),handleRename(projectName))}
                    autoFocus style={{fontSize:20,fontWeight:700,fontFamily:"Inter,system-ui,-apple-system,sans-serif",color:C.ink,border:`2px solid ${C.tealMid}`,borderRadius:8,padding:"3px 10px",background:C.bg,outline:"none",minWidth:200}}/>
                :<div style={{display:"flex",alignItems:"center",gap:6}}>
                  <h2 style={{fontSize:20,fontWeight:700,color:C.ink,fontFamily:"Inter,system-ui,-apple-system,sans-serif",margin:0}}>{projectName}</h2>
                  <button onClick={()=>setEditingName(true)} title="Rename project" style={{background:"none",border:"none",cursor:"pointer",color:C.inkLight,fontSize:14,padding:"2px 4px"}}>✏️</button>
                </div>
              }
              <div style={{fontSize:12,color:C.inkLight,marginTop:2}}>{trade.label}{!hideNumbers&&floorArea!=null?<> · {rough?"~":""}{floorArea} m²{partial?" measured (part of the house)":wholeBldg?(wholeDoubt?" measured (building — check it's all there)":" measured (whole building)"):""}</>:null}</div>
              {project.tuning&&(()=>{
                const jt=JOB_TYPES.find(j=>j.id===project.tuning.jobType);
                const cm=CONSTRUCTION_METHODS.find(c=>c.id===project.tuning.constructionMethod);
                const sl=SPEC_LEVELS.find(s=>s.id===project.tuning.specLevel);
                return (
                  <div style={{display:"flex",gap:6,marginTop:6,flexWrap:"wrap"}}>
                    {jt&&<span style={{fontSize:10,color:C.inkLight,background:C.bg,borderRadius:12,padding:"2px 8px",border:`1px solid ${C.border}`}}>{jt.icon} {jt.label}</span>}
                    {cm&&<span style={{fontSize:10,color:C.inkLight,background:C.bg,borderRadius:12,padding:"2px 8px",border:`1px solid ${C.border}`}}>{cm.icon} {cm.label}</span>}
                    {sl&&<span style={{fontSize:10,color:C.inkLight,background:C.bg,borderRadius:12,padding:"2px 8px",border:`1px solid ${C.border}`}}>{sl.icon} {sl.label}</span>}
                    {project.tuning.floorAreaOverride&&<span style={{fontSize:10,color:C.teal,background:C.tealFaint,borderRadius:12,padding:"2px 8px",border:`1px solid ${C.tealLight}`}}>📐 {project.tuning.floorAreaOverride} m² (confirmed)</span>}
                  </div>
                );
              })()}
            </div>
          </div>
          {!hideNumbers&&<div style={{textAlign:"right"}}>
            <div style={{fontSize:22,fontWeight:700,color:rough?C.inkMid:C.ink}}>{matKnown?(rough?"~":"")+fmt(matTotal):"—"}</div>
            <div style={{fontSize:12,color:C.inkLight}}>materials excl. GST{rough?" · rough":partial?" · part of house":wholeBldg?" · whole building":""}</div>
          </div>}
        </div>

        <PlanVerdictPanel verdict={verdict} roughShown={roughShown} onShowAnyway={showAnyway} onNewScan={onNewScan} onDeleteJob={onDeleteJob}
          onLookAtPicture={hasPic&&onCheckCorners?()=>onCheckCorners(project,true):undefined}/>

        {!hideNumbers&&<>
        {PLAN_CAN.senseCheck(plan.id)&&floorArea!=null&&matKnown&&(
          <SenseCheck trade={trade} floorArea={floorArea} council={council} liveMatTotal={matTotal}/>
        )}

        <div style={{display:"flex",borderBottom:`1px solid ${C.border}`,marginBottom:22,overflowX:"auto"}}>
          {[{id:"materials",label:"Materials list"},{id:"suppliers",label:"Compare suppliers",locked:!PLAN_CAN.supplierPricing(plan.id)},{id:"compliance",label:"NZ code check (sample)",adminOnly:true},{id:"quote",label:"Quote builder",locked:!PLAN_CAN.quoteBuilder(plan.id)}].filter(tab=>!tab.adminOnly||isAdminUser(user)).map(tab=>(
            <button key={tab.id} onClick={()=>setActiveTab(tab.id)} style={{padding:"10px 14px",border:"none",background:"transparent",cursor:"pointer",fontSize:12,fontWeight:activeTab===tab.id?700:400,color:activeTab===tab.id?trade.color:C.inkMid,borderBottom:`2px solid ${activeTab===tab.id?trade.color:"transparent"}`,marginBottom:-1,whiteSpace:"nowrap"}}>
              {tab.label}{tab.locked?" 🔒":""}
            </button>
          ))}
        </div>

        {activeTab==="materials"&&(
          <>
            <TradeMetricsDisplay trade={trade} project={project} prefs={prefs} rough={rough} partial={partial} extraFloors={extraFloors} wholeBldg={wholeBldg}
              canCheckCorners={hasPic&&!project.viewOnly} onCheckCorners={onCheckCorners?()=>onCheckCorners(project,false):undefined}/>
            <MaterialsTabWithCorrections trade={trade} project={project} fmt={fmt} fmtQ={fmtQ} showPrices={PLAN_CAN.quoteBuilder(plan.id)} prefs={prefs} rough={rough}/>
          </>
        )}

        {activeTab==="suppliers"&&(
          <>
            {!isAdminUser(user) ? (
              <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"40px 24px",textAlign:"center"}}>
                <div style={{fontSize:36,marginBottom:12}}>🚧</div>
                <div style={{fontSize:18,fontWeight:700,color:C.ink,marginBottom:6}}>Coming soon</div>
                <div style={{fontSize:13,color:C.inkMid,maxWidth:380,margin:"0 auto",lineHeight:1.5}}>Live supplier comparison across PlaceMakers, Mitre 10 and Bunnings is in development. We'll notify you when it's ready.</div>
              </div>
            ) : !PLAN_CAN.supplierPricing(plan.id)
              ? <LockBanner requiredPlan="proplus" onUpgrade={onUpgrade}/>
              : <SupplierPricingTable trade={trade} preferredSupplier={prefs.preferredSupplier} councilId={project.councilId||prefs.region}/>
            }
            {isAdminUser(user) && PLAN_CAN.supplierPricing(plan.id)&&(
              <div style={{marginTop:20,background:C.tealFaint,border:`1px solid ${C.tealLight}`,borderRadius:12,padding:"14px 18px",display:"flex",alignItems:"center",gap:14}}>
                <span style={{fontSize:24}}>🔴</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:C.teal,marginBottom:2}}>Get live supplier prices</div>
                  <div style={{fontSize:12,color:C.inkMid}}>Connect to PlaceMakers, Mitre 10 and Bunnings trade portals for real current pricing — removes the "indicative" disclaimer entirely.</div>
                </div>
                <button style={{padding:"9px 16px",background:C.tealMid,color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:700,whiteSpace:"nowrap",opacity:0.6}} title="Coming soon">
                  Coming soon
                </button>
              </div>
            )}
          </>
        )}

        {/* security-v1-2026-10-12: the code check is the same hard-coded list on every plan
            (e.g. "deck balustrade below 1000mm"), so it is admin-only and marked sample
            until a real check exists. A tradie must never read it as a result. */}
        {activeTab==="compliance"&&isAdminUser(user)&&<div style={{background:C.amberFaint,border:`1px solid ${C.amberLight}`,borderRadius:10,padding:"10px 14px",fontSize:12,color:C.amber,fontWeight:600,marginBottom:10}}>SAMPLE ONLY — these lines are the same on every plan. Not a check of this plan.</div>}
        {activeTab==="compliance"&&isAdminUser(user)&&((
          <>{COMPLIANCE.map(c=>(
            <div key={c.rule} style={{display:"flex",gap:12,padding:"14px 16px",background:C.card,border:`1px solid ${c.status==="fail"?C.red+"55":c.status==="warn"?C.amber+"44":C.border}`,borderRadius:12,marginBottom:10,alignItems:"flex-start"}}>
              <StatusDot s={c.status}/>
              <div style={{flex:1}}><div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:3}}>{c.rule}</div><div style={{fontSize:12,color:C.inkMid,lineHeight:1.5}}>{c.detail}</div></div>
              <Pill label={c.status==="pass"?"Pass":c.status==="warn"?"Review":"Fail"} color={c.status==="pass"?C.teal:c.status==="warn"?C.amber:C.red} bg={c.status==="pass"?C.tealLight:c.status==="warn"?C.amberLight:C.redLight}/>
            </div>
          ))}</>
        ))}

        {activeTab==="quote"&&(
          <>
            {!PLAN_CAN.quoteBuilder(plan.id)
              ? <LockBanner requiredPlan="proplus" onUpgrade={onUpgrade}/>
              : (
              <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"18px 20px",marginBottom:20}}>
                <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:14}}>Adjust quote inputs</div>
                <div className="mu-quote-inputs" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                  <NumInput label="Labour hours" value={labourHours} onChange={setLabourHours} min={0} max={500} suffix="hrs"/>
                  <NumInput label="Hourly rate" value={labourRate} onChange={setLabourRate} min={0} max={500} prefix="$" suffix="/hr"/>
                  <NumInput label="Markup" value={markupPct} onChange={setMarkupPct} min={0} max={100} suffix="%"/>
                </div>
                <div style={{fontSize:11,color:C.inkLight,marginTop:10}}>Pre-filled from your preferences. Changes here only affect this quote.</div>
              </div>
            )}
            {(rough||partial||extraFloors||wholeBldg)&&<div style={{background:C.amberFaint,border:`1px solid ${C.amber}66`,color:"#7C2D12",borderRadius:10,padding:"10px 14px",fontSize:13,fontWeight:600,marginBottom:12}}>
              ⚠ {rough ? "These quantities are a rough AI read, not measured from the drawing." : partial ? "Part of this house wasn't measured, so these quantities are short." : wholeBldg ? "This plan is an alteration, and these quantities are the whole building, not just the new work." : "More floors were measured than the drawings show, so these quantities may be too big."} Check them against the plan before this quote goes anywhere.
            </div>}
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",marginBottom:20}}>
              <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,fontWeight:700,fontSize:14,color:C.ink}}>Quote — {project.name} · {trade.label}</div>
              {[
                ["Materials (excl. GST)", matText, false],
                ["Labour ("+labourHours+" hrs @ "+fmt(labourRate)+"/hr)", fmt(labourTotal), false],
                ["Markup ("+markupPct+"%)", fmt(markupTotal), false],
                ["Subtotal", fmt(subtotal), false],
                ["GST (15%)", fmt(subtotal*0.15), false],
                ["Total (incl. GST)", fmt(subtotal*1.15), true],
              ].filter(Boolean).map(([l,v,bold])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"11px 20px",borderBottom:`1px solid ${C.border}`,fontSize:bold?15:13,background:bold?trade.light:"transparent"}}>
                  <span style={{color:bold?trade.color:C.inkMid,fontWeight:bold?700:400}}>{l}</span>
                  <span style={{fontWeight:bold?700:600,color:bold?trade.color:C.ink}}>{v}</span>
                </div>
              ))}
            </div>
            {/* planboss-v1-2026-10-12: both buttons used to do nothing. Email now
                opens the tradie's mail app with the quote filled in; PDF says
                plainly that it isn't built yet. */}
            <div style={{display:"flex",gap:10}}>
              <Btn full disabled color={trade.color}>PDF export — coming soon</Btn>
              <Btn full onClick={()=>{
                window.location.href=pbQuoteMailto({name:project.name,tradeLabel:trade.label,matText,labourHours,labourRate,labourTotal,markupPct,markupTotal,subtotal,rough,partial,extraFloors,wholeBldg});
              }} variant="outline" color={trade.color}>Share via email</Btn>
            </div>
          </>
        )}
        </>}
        </>)}
      </div>
    </div>
  );
}


// ─── FULL JOB QUOTE ───────────────────────────────────────────────────────────
function FullJobQuote({project,prefs,floorArea,council}) {
  const [labourRate,setLabourRate]=useState(prefs.labourRate);
  const [markupPct,setMarkupPct]=useState(prefs.markupPct);
  const [view,setView]=useState("trades"); // "trades" | "combined"

  // Ensure trades is always an array of IDs
  const tradeIds = Array.isArray(project.trades)
    ? project.trades
    : (project.trades ? [project.trades] : TRADES.map(t=>t.id).slice(0,3));

  const [hoursPerTrade,setHoursPerTrade]=useState(()=>{
    const init={};
    tradeIds.forEach(id=>{ init[id]=prefs.labourHours; });
    return init;
  });
  const [expandedTrade,setExpandedTrade]=useState(null);
  // plancheck-v1: no floor area read -> "—", not "$Infinity/m²"
  const fmtL=n=>Number.isFinite(n)?"$"+Math.round(n).toLocaleString("en-NZ"):"—";
  const fmtQ=n=>Number.isInteger(n)?n:n.toFixed(1);

  const tradeSummaries=tradeIds.map(id=>{
    const t=TRADES.find(x=>x.id===id); if(!t)return null;
    const matTotal=t.materials.reduce((s,m)=>s+m.qty*m.price,0);
    const hrs=hoursPerTrade[id]||prefs.labourHours;
    const labour=hrs*labourRate;
    const markup=(matTotal+labour)*markupPct/100;
    const subtotal=matTotal+labour+markup;
    // Sense check per trade
    const mult=council?.costMult||1.0;
    const regMin=Math.round(t.totalMin*mult);
    const regMax=Math.round(t.totalMax*mult);
    const regAvg=(regMin+regMax)/2;
    const rawPct=((matTotal-regMin)/(regMax-regMin))*100;
    const barPct=Math.max(3,Math.min(97,rawPct));
    const scOk=matTotal>=regMin*0.7&&matTotal<=regMax*1.4;
    const diff=matTotal-regAvg;
    const diffPct=Math.abs(Math.round((diff/regAvg)*100));
    return {t,matTotal,labour,markup,subtotal,hrs,regMin,regMax,regAvg,rawPct,barPct,scOk,diff,diffPct};
  }).filter(Boolean);

  const grandMat=tradeSummaries.reduce((s,x)=>s+x.matTotal,0);
  const grandLabour=tradeSummaries.reduce((s,x)=>s+x.labour,0);
  const grandMarkup=tradeSummaries.reduce((s,x)=>s+x.markup,0);
  const grandSub=tradeSummaries.reduce((s,x)=>s+x.subtotal,0);
  const grandTotal=grandSub*1.15;
  // Overall project sense check — sum of all trade averages vs actual
  const grandRegAvg=tradeSummaries.reduce((s,x)=>s+x.regAvg,0);
  const grandRegMin=tradeSummaries.reduce((s,x)=>s+x.regMin,0);
  const grandRegMax=tradeSummaries.reduce((s,x)=>s+x.regMax,0);
  const grandRawPct=((grandSub-grandRegMin)/(grandRegMax-grandRegMin))*100;
  const grandBarPct=Math.max(3,Math.min(97,grandRawPct));
  const grandScOk=grandSub>=grandRegMin*0.7&&grandSub<=grandRegMax*1.4;
  const grandDiff=grandMat-grandRegAvg;
  const grandDiffPct=Math.abs(Math.round((grandDiff/grandRegAvg)*100));
  // plancheck-v1: "Send to subie" used to flash "✓ Sent!" and send nothing.
  // This admin-only view still totals the trades' built-in example lists, so
  // nothing here is sent anywhere until it's built on real take-offs.

  return (
    <div>
      <div style={{background:"#F3EDFC",border:"1px solid #6B3FA044",borderRadius:12,padding:"12px 18px",marginBottom:20,display:"flex",alignItems:"center",gap:12}}>
        <span style={{fontSize:20}}>👷</span>
        <div>
          <div style={{fontSize:13,fontWeight:700,color:C.purple}}>Project Manager view — {project.name}</div>
          <div style={{fontSize:12,color:C.inkMid,marginTop:2}}>Full job quote across all trades. Click any trade to expand and send to that subie.</div>
        </div>
      </div>

      {/* View toggle */}
      <div style={{display:"flex",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:3,gap:3,marginBottom:20,width:"fit-content"}}>
        {[["trades","By trade"],["combined","All materials combined"]].map(([v,label])=>(
          <button key={v} onClick={()=>setView(v)} style={{padding:"7px 16px",border:"none",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:view===v?700:400,background:view===v?C.purpleLight:"transparent",color:view===v?C.purple:C.inkMid}}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Overall project sense check ── */}
      <div style={{background:grandScOk?C.tealFaint:C.amberFaint,border:`1px solid ${grandScOk?C.tealMid+"44":C.amber+"44"}`,borderRadius:14,padding:"20px 20px 16px",marginBottom:20}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <span style={{fontWeight:700,fontSize:13,color:grandScOk?C.teal:C.amber}}>
            {grandRawPct<0?"⚠ Total materials below typical range (excl. labour)":grandRawPct>100?"⚠ Total materials above typical range (excl. labour)":grandScOk?"✓ Materials sense check — looks right":"⚠ Materials sense check — review overall estimate"}
          </span>
          <span style={{fontSize:11,color:C.inkLight}}>{council?.name||"NZ average"}</span>
        </div>
        <div className="mu-sense-stats" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:12,marginBottom:20}}>
          {[
            ["All materials (excl. GST)", fmtL(Math.round(grandMat)),     grandScOk?C.teal:C.amber],
            ["Regional avg (all trades)", fmtL(Math.round(grandRegAvg)),   C.ink],
            ["Your total $/m²",           `${fmtL(Math.round(grandMat/floorArea))}/m²`, grandScOk?C.teal:C.amber],
            ["Regional avg $/m²",         `${fmtL(Math.round(grandRegAvg/floorArea))}/m²`, C.ink],
          ].map(([l,v,col])=>(
            <div key={l} style={{background:"rgba(255,255,255,0.6)",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:11,color:C.inkLight,marginBottom:3}}>{l}</div>
              <div style={{fontSize:14,fontWeight:700,color:col}}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{fontSize:12,color:grandScOk?C.teal:C.amber,fontWeight:600,marginBottom:14}}>
          {grandDiff>0
            ?`Total is ${fmtL(Math.abs(Math.round(grandDiff)))} (${grandDiffPct}%) above combined regional average`
            :grandDiff<0
              ?`Total is ${fmtL(Math.abs(Math.round(grandDiff)))} (${grandDiffPct}%) below combined regional average`
              :"Total is right on the combined regional average"
          }
        </div>
        <div style={{paddingTop:36,position:"relative",marginBottom:8}}>
          <div style={{position:"absolute",top:0,left:`${grandBarPct}%`,transform:"translateX(-50%)",background:grandScOk?C.tealMid:C.amber,color:"#fff",fontSize:11,fontWeight:700,padding:"3px 8px",borderRadius:6,whiteSpace:"nowrap",boxShadow:"0 1px 4px rgba(0,0,0,0.15)"}}>
            {fmtL(Math.round(grandMat))}
            <div style={{position:"absolute",bottom:-5,left:"50%",transform:"translateX(-50%)",width:0,height:0,borderLeft:"5px solid transparent",borderRight:"5px solid transparent",borderTop:`5px solid ${grandScOk?C.tealMid:C.amber}`}}/>
          </div>
          <div style={{background:"rgba(0,0,0,0.1)",borderRadius:999,height:10,position:"relative"}}>
            <div style={{width:`${Math.max(0,Math.min(100,grandRawPct))}%`,height:"100%",background:grandScOk?C.tealMid:C.amber,borderRadius:999,transition:"width 0.4s"}}/>
            <div style={{position:"absolute",top:"50%",left:`${grandBarPct}%`,transform:"translate(-50%,-50%)",width:18,height:18,borderRadius:"50%",background:grandScOk?C.tealMid:C.amber,border:"3px solid #fff",boxShadow:"0 1px 5px rgba(0,0,0,0.25)",zIndex:2}}/>
          </div>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.inkLight}}>
          <span>Low {fmtL(Math.round(grandRegMin))}</span>
          <span style={{fontStyle:"italic"}}>Combined regional range — all trades</span>
          <span>High {fmtL(Math.round(grandRegMax))}</span>
        </div>
      </div>

      {/* ── Combined all-materials view ── */}
      {view==="combined"&&(()=>{
        // Merge all trade materials into one flat list with trade label
        const allMats=tradeIds.flatMap(id=>{
          const t=TRADES.find(x=>x.id===id); if(!t) return [];
          return t.materials.map(m=>({...m,tradeLabel:t.label,tradeIcon:t.icon,tradeColor:t.color}));
        });
        const allMatTotal=allMats.reduce((s,m)=>s+m.qty*m.price,0);
        // All labour+markup across trades
        const allLabour=tradeIds.reduce((s,id)=>{
          const hrs=hoursPerTrade[id]||prefs.labourHours;
          return s+hrs*labourRate;
        },0);
        const allMarkup=(allMatTotal+allLabour)*markupPct/100;
        const allSub=allMatTotal+allLabour+allMarkup;

        return (
          <div style={{marginBottom:20}}>
            <div style={{background:C.amberFaint,border:`1px solid ${C.amberLight}`,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:12,color:C.amber}}>
              ⚠ Combined view shows all materials from all trades in one list. Use "By trade" to generate individual subie quotes.
            </div>
            <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,overflow:"hidden",marginBottom:16}}>
              <div style={{padding:"12px 18px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10,background:C.bg}}>
                <span style={{fontSize:16}}>📦</span>
                <div style={{fontWeight:700,fontSize:14,color:C.ink}}>All materials — {project.name}</div>
                <span style={{marginLeft:"auto",fontSize:12,color:C.inkLight}}>{allMats.length} line items across {tradeIds.length} trades</span>
              </div>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:C.bg}}>
                    {["Trade","Material","Qty","Unit price","Total"].map((h,i)=>(
                      <th key={h} style={{textAlign:i>1?"right":"left",padding:"8px 14px",borderBottom:`2px solid ${C.border}`,fontSize:11,color:C.inkLight,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.04em"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allMats.map((m,i)=>(
                    <tr key={`${m.tradeLabel}-${m.name}`} style={{background:i%2===0?C.card:"#fafaf8"}}>
                      <td style={{padding:"8px 14px",borderBottom:`1px solid ${C.border}`,whiteSpace:"nowrap"}}>
                        <span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:11,fontWeight:600,color:m.tradeColor,background:m.tradeColor+"18",borderRadius:6,padding:"2px 7px"}}>
                          {m.tradeIcon} {m.tradeLabel.split(" ")[0]}
                        </span>
                      </td>
                      <td style={{padding:"8px 14px",borderBottom:`1px solid ${C.border}`,color:C.ink}}>{m.name}</td>
                      <td style={{padding:"8px 14px",borderBottom:`1px solid ${C.border}`,color:C.inkMid,textAlign:"right"}}>{Number.isInteger(m.qty)?m.qty:m.qty.toFixed(1)} {m.unit}</td>
                      <td style={{padding:"8px 14px",borderBottom:`1px solid ${C.border}`,color:C.inkMid,textAlign:"right"}}>{fmtL(m.price)}</td>
                      <td style={{padding:"8px 14px",borderBottom:`1px solid ${C.border}`,fontWeight:600,color:C.ink,textAlign:"right"}}>{fmtL(m.qty*m.price)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={5} style={{padding:"10px 14px",borderTop:`2px solid ${C.border}`,background:C.bg,fontSize:11,color:C.inkLight,fontWeight:700,textTransform:"uppercase"}}>Totals</td></tr>
                  {[["Materials (excl. GST)",fmtL(allMatTotal)],["Labour (all trades)",fmtL(allLabour)],[`Markup (${markupPct}%)`,fmtL(allMarkup)],["Subtotal",fmtL(allSub)],["GST (15%)",fmtL(allSub*0.15)]].map(([l,v])=>(
                    <tr key={l}><td colSpan={4} style={{padding:"6px 14px",color:C.inkMid,fontSize:13}}>{l}</td><td style={{padding:"6px 14px",fontWeight:600,color:C.ink,textAlign:"right",fontSize:13}}>{v}</td></tr>
                  ))}
                  <tr style={{background:C.purpleLight}}>
                    <td colSpan={4} style={{padding:"12px 14px",fontWeight:700,fontSize:15,color:C.purple}}>Total project (incl. GST)</td>
                    <td style={{padding:"12px 14px",fontWeight:700,fontSize:18,color:C.purple,textAlign:"right"}}>{fmtL(allSub*1.15)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div style={{display:"flex",gap:10}}>
              <button style={{flex:1,padding:"12px",background:C.purple,color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer"}}>Export full materials PDF</button>
              <button style={{flex:1,padding:"12px",background:"transparent",color:C.purple,border:`1.5px solid ${C.purple}`,borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer"}}>Print combined list</button>
            </div>
          </div>
        );
      })()}

      {/* ── By-trade view ── */}
      {view==="trades"&&(
        <div>
          <div style={{fontSize:13,fontWeight:700,color:C.purple}}>Project Manager view — {project.name}</div>
          <div style={{fontSize:12,color:C.inkMid,marginTop:2,marginBottom:16}}>Click any trade row to expand the full breakdown and send to that subie.</div>

          <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,padding:"18px 20px",marginBottom:20}}>
            <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:14}}>Job-wide settings</div>
            <div className="mu-quote-inputs" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <NumInput label="Labour rate (all trades)" value={labourRate} onChange={setLabourRate} min={0} max={500} prefix="$" suffix="/hr"/>
              <NumInput label="Markup (all trades)" value={markupPct} onChange={setMarkupPct} min={0} max={100} suffix="%"/>
            </div>
            <div style={{fontSize:11,color:C.inkLight,marginTop:10}}>Adjust hours per trade individually below.</div>
          </div>

          <div style={{marginBottom:20}}>
            {tradeSummaries.map(({t,matTotal,labour,markup,subtotal,hrs})=>{
              const isExp=expandedTrade===t.id;
            const scOk = t.senseCheck?.ok ?? true;
            const rawPct = typeof t.senseCheck?.rawPct === 'number' ? t.senseCheck.rawPct : (markup || 0);
            const regAvg = t.senseCheck?.regAvg ?? subtotal;
            const regMin = t.senseCheck?.regMin ?? subtotal * 0.7;
            const regMax = t.senseCheck?.regMax ?? subtotal * 1.3;
            const diff = subtotal - regAvg;
            const diffPct = regAvg !== 0 ? ((diff / regAvg) * 100).toFixed(0) : 0;
            const barPct = Math.max(0, Math.min(100, rawPct));
              return (
                <div key={t.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:14,marginBottom:10,overflow:"hidden"}}>
              <div style={{display:"flex",alignItems:"center",gap:12,padding:"14px 18px",cursor:"pointer",flexWrap:"wrap"}} onClick={()=>setExpandedTrade(isExp?null:t.id)}>
                <span style={{fontSize:24}}>{t.icon}</span>
                <div style={{flex:1,minWidth:120}}>
                  <div style={{fontSize:14,fontWeight:700,color:C.ink}}>{t.label}</div>
                  <div style={{fontSize:12,color:C.inkLight,marginTop:2}}>{fmtL(matTotal)} materials · {hrs} hrs · {markupPct}% markup</div>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}} onClick={e=>e.stopPropagation()}>
                  <span style={{fontSize:11,color:C.inkLight}}>hrs</span>
                  <input type="number" value={hrs} min={0} max={500} onChange={e=>setHoursPerTrade(h=>({...h,[t.id]:Number(e.target.value)}))} style={{width:52,padding:"4px 8px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,fontWeight:700,color:C.ink,outline:"none",textAlign:"center"}}/>
                </div>
                <div style={{textAlign:"right",minWidth:90}}>
                  <div style={{fontSize:15,fontWeight:700,color:t.color}}>{fmtL(subtotal*1.15)}</div>
                  <div style={{fontSize:10,color:C.inkLight}}>incl. GST</div>
                </div>
                <button disabled onClick={e=>e.stopPropagation()} title="Coming soon" style={{padding:"7px 12px",background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,cursor:"not-allowed",fontSize:12,fontWeight:700,color:C.inkLight,whiteSpace:"nowrap",flexShrink:0}}>
                  Send to subie — soon
                </button>
                <span style={{color:C.inkLight,fontSize:12,flexShrink:0}}>{isExp?"▲":"▼"}</span>
              </div>
              {isExp&&(
                <div style={{borderTop:`1px solid ${C.border}`,padding:"16px 18px",background:C.bg}}>

                  {/* Per-trade sense check */}
                  <div style={{background:scOk?t.light:C.amberLight,border:`1px solid ${scOk?t.color+"44":C.amber+"44"}`,borderRadius:12,padding:"14px 16px",marginBottom:16}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
                      <span style={{fontWeight:700,fontSize:12,color:scOk?t.color:C.amber}}>
                        {rawPct<0?"⚠ Below typical range":rawPct>100?"⚠ Above typical range":scOk?"✓ Sense check — looks right":"⚠ Review this estimate"}
                      </span>
                      <span style={{fontSize:10,color:C.inkLight}}>{council?.name||"NZ average"}</span>
                    </div>
                    <div className="mu-sense-stats" style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:8,marginBottom:14}}>
                      {[
                        ["Your quote",   fmtL(Math.round(matTotal)), scOk?t.color:C.amber],
                        ["Regional avg", fmtL(Math.round(regAvg)),   C.ink],
                        ["Diff",         (diff>=0?"+":"")+fmtL(Math.round(diff))+" ("+diffPct+"%)", scOk?t.color:C.amber],
                        ["Range",        fmtL(regMin)+"–"+fmtL(regMax), C.inkLight],
                      ].map(([l,v,col])=>(
                        <div key={l} style={{background:"rgba(255,255,255,0.6)",borderRadius:6,padding:"7px 10px"}}>
                          <div style={{fontSize:10,color:C.inkLight,marginBottom:2}}>{l}</div>
                          <div style={{fontSize:13,fontWeight:700,color:col}}>{v}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{paddingTop:28,position:"relative",marginBottom:6}}>
                      <div style={{position:"absolute",top:0,left:`${barPct}%`,transform:"translateX(-50%)",background:scOk?t.color:C.amber,color:"#fff",fontSize:10,fontWeight:700,padding:"2px 6px",borderRadius:5,whiteSpace:"nowrap"}}>
                        {fmtL(Math.round(matTotal))}
                        <div style={{position:"absolute",bottom:-4,left:"50%",transform:"translateX(-50%)",width:0,height:0,borderLeft:"4px solid transparent",borderRight:"4px solid transparent",borderTop:`4px solid ${scOk?t.color:C.amber}`}}/>
                      </div>
                      <div style={{background:"rgba(0,0,0,0.1)",borderRadius:999,height:8,position:"relative"}}>
                        <div style={{width:`${Math.max(0,Math.min(100,rawPct))}%`,height:"100%",background:scOk?t.color:C.amber,borderRadius:999}}/>
                        <div style={{position:"absolute",top:"50%",left:`${barPct}%`,transform:"translate(-50%,-50%)",width:14,height:14,borderRadius:"50%",background:scOk?t.color:C.amber,border:"2px solid #fff",boxShadow:"0 1px 4px rgba(0,0,0,0.2)",zIndex:2}}/>
                      </div>
                    </div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:C.inkLight}}>
                      <span>Low {fmtL(regMin)}</span><span>High {fmtL(regMax)}</span>
                    </div>
                  </div>

                  <div style={{fontSize:12,fontWeight:700,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:10}}>Subie quote breakdown</div>
                  <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,marginBottom:14}}>
                    <thead>
                      <tr>{["Material","Qty","Unit price","Total"].map((h,i)=><th key={h} style={{textAlign:i>0?"right":"left",padding:"6px 10px",borderBottom:`1px solid ${C.border}`,fontSize:11,color:C.inkLight,fontWeight:600,textTransform:"uppercase"}}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {t.materials.map((m,i)=>(
                        <tr key={m.name} style={{background:i%2===0?"transparent":"#fafaf8"}}>
                          <td style={{padding:"7px 10px",color:C.ink,borderBottom:`1px solid ${C.border}`}}>{m.name}</td>
                          <td style={{padding:"7px 10px",color:C.inkMid,textAlign:"right",borderBottom:`1px solid ${C.border}`}}>{fmtQ(m.qty)} {m.unit}</td>
                          <td style={{padding:"7px 10px",color:C.inkMid,textAlign:"right",borderBottom:`1px solid ${C.border}`}}>{fmtL(m.price)}</td>
                          <td style={{padding:"7px 10px",fontWeight:600,color:C.ink,textAlign:"right",borderBottom:`1px solid ${C.border}`}}>{fmtL(m.qty*m.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{borderTop:`2px solid ${C.border}`,paddingTop:12}}>
                    {[[`Materials (excl. GST)`,fmtL(matTotal)],[`Labour (${hrs} hrs @ ${fmtL(labourRate)}/hr)`,fmtL(labour)],[`Markup (${markupPct}%)`,fmtL(markup)],["Subtotal",fmtL(subtotal)],["GST (15%)",fmtL(subtotal*0.15)]].map(([l,v])=>(
                      <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:12,padding:"4px 0",color:C.inkMid}}><span>{l}</span><span style={{fontWeight:600,color:C.ink}}>{v}</span></div>
                    ))}
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:14,fontWeight:700,padding:"10px 12px",background:t.light,borderRadius:8,marginTop:8,color:t.color}}>
                      <span>Total (incl. GST)</span><span>{fmtL(subtotal*1.15)}</span>
                    </div>
                  </div>
                  <div style={{display:"flex",gap:8,marginTop:14}}>
                    <button disabled style={{flex:1,padding:"10px",background:C.border,color:C.inkLight,border:"none",borderRadius:8,cursor:"not-allowed",fontSize:13,fontWeight:700}}>
                      Send to {t.label.split(" ")[0]} — coming soon
                    </button>
                    <button disabled style={{padding:"10px 14px",background:"transparent",color:C.inkLight,border:`1.5px solid ${C.border}`,borderRadius:8,cursor:"not-allowed",fontSize:13,fontWeight:700}}>PDF — soon</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div style={{background:C.card,border:`2px solid ${C.purple}`,borderRadius:14,overflow:"hidden",marginBottom:20}}>
        <div style={{padding:"14px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:18}}>📋</span>
          <div style={{fontWeight:700,fontSize:15,color:C.ink}}>Full project summary — {project.name}</div>
        </div>
        {tradeSummaries.map(({t,subtotal})=>(
          <div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 20px",borderBottom:`1px solid ${C.border}`,fontSize:13}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}><span>{t.icon}</span><span style={{color:C.inkMid}}>{t.label}</span></div>
            <div style={{display:"flex",gap:20,alignItems:"center"}}>
              <span style={{fontSize:11,color:C.inkLight}}>{fmtL(subtotal/floorArea)}/m²</span>
              <span style={{fontWeight:600,color:C.ink,minWidth:80,textAlign:"right"}}>{fmtL(subtotal*1.15)}</span>
            </div>
          </div>
        ))}
        <div style={{background:C.bg,padding:"16px 20px",borderTop:`1px solid ${C.border}`}}>
          {[["All materials (excl. GST)",fmtL(grandMat)],["All labour",fmtL(grandLabour)],["All markup",fmtL(grandMarkup)],["Subtotal",fmtL(grandSub)],["GST (15%)",fmtL(grandSub*0.15)]].map(([l,v])=>(
            <div key={l} style={{display:"flex",justifyContent:"space-between",fontSize:13,padding:"5px 0",color:C.inkMid}}><span>{l}</span><span style={{fontWeight:600,color:C.ink}}>{v}</span></div>
          ))}
          <div style={{display:"flex",justifyContent:"space-between",padding:"12px 16px",background:C.purpleLight,borderRadius:10,marginTop:10}}>
            <span style={{fontSize:16,fontWeight:700,color:C.purple}}>Total project (incl. GST)</span>
            <span style={{fontSize:20,fontWeight:700,color:C.purple}}>{fmtL(grandTotal)}</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between",padding:"6px 16px",fontSize:12,color:C.inkLight}}>
            <span>Effective rate across {floorArea} m²</span>
            <span style={{fontWeight:600,color:C.ink}}>{fmtL(grandTotal/floorArea)}/m²</span>
          </div>
        </div>
      </div>

      <div style={{display:"flex",gap:10}}>
        <button disabled style={{flex:1,padding:"13px",background:C.border,color:C.inkLight,border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:"not-allowed"}}>PDF export — coming soon</button>
        <button disabled style={{flex:1,padding:"13px",background:"transparent",color:C.inkLight,border:`1.5px solid ${C.border}`,borderRadius:10,fontSize:15,fontWeight:700,cursor:"not-allowed"}}>Email all subies — coming soon</button>
      </div>
        </div>
      )}
    </div>
  );
}


// ─── ACCOUNT ──────────────────────────────────────────────────────────────────
// ─── TRADE METRICS PREFERENCES PANEL ─────────────────────────────────────────
function TradeMetricsPrefsPanel({prefs, onUpdate, inlineTrade, planId}) {
  const canQuote = PLAN_CAN.quoteBuilder(planId||"pro");
  // inlineTrade: if set, only show that one trade (used inside account trade buttons)
  const [openTrade, setOpenTrade] = useState(inlineTrade||null);

  const tradesToShow = inlineTrade
    ? TRADES.filter(t=>t.id===inlineTrade)
    : TRADES;

  return (
    <div>
      {tradesToShow.map(t=>{
        const tm=TRADE_METRICS[t.id]; if(!tm) return null;
        const isOpen=inlineTrade ? true : openTrade===t.id;
        const saved=prefs.tradeMetrics?.[t.id]||{};

        const toggleMetric=(metricId)=>{
          const current=saved.hiddenMetrics||[];
          const updated=current.includes(metricId)
            ?current.filter(x=>x!==metricId)
            :[...current,metricId];
          onUpdate(t.id,{...saved,hiddenMetrics:updated});
        };

        const setRate=(rateId,val)=>{
          onUpdate(t.id,{...saved,[rateId]:val});
        };

        if(inlineTrade) {
          // Inline mode — no toggle button, just the content
          return (
            <div key={t.id} style={{marginTop:12,borderTop:`1px solid ${t.color}22`,paddingTop:14}}>

              {/* Metrics to show — tickable */}
              <div style={{marginBottom:14}}>
                <div style={{fontSize:11,fontWeight:700,color:C.ink,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:8}}>
                  Metrics to display
                </div>
                <div style={{fontSize:11,color:C.inkLight,marginBottom:10}}>
                  Tick the measurements you want shown for this trade. Untick any you don't use.
                </div>
                {tm.metrics.map(m=>{
                  const hidden=(saved.hiddenMetrics||[]).includes(m.id);
                  return (
                    <div key={m.id} onClick={()=>toggleMetric(m.id)}
                      style={{display:"flex",alignItems:"center",gap:10,marginBottom:8,padding:"8px 12px",
                        background:!hidden?t.light:C.bg,borderRadius:8,
                        border:`1px solid ${!hidden?t.color+"44":C.border}`,cursor:"pointer"}}>
                      <div style={{width:20,height:20,borderRadius:4,background:!hidden?t.color:"#fff",
                        border:`2px solid ${!hidden?t.color:C.borderMid}`,display:"flex",alignItems:"center",
                        justifyContent:"center",flexShrink:0}}>
                        {!hidden&&<span style={{color:"#fff",fontSize:12,fontWeight:700}}>✓</span>}
                      </div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:600,color:C.ink}}>{m.label}</div>
                        <div style={{fontSize:10,color:C.inkLight,marginTop:1}}>{m.description} · <strong>{m.unit}</strong></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Rate fields — Pro+ and Business only */}
              {tm.rateFields.length>0&&(
                <div>
                  <div style={{fontSize:11,fontWeight:700,color:C.ink,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>
                    Your rates
                  </div>
                  {!canQuote
                    ? <div style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 14px",display:"flex",alignItems:"center",gap:10}}>
                        <span style={{fontSize:16}}>🔒</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:600,color:C.inkMid}}>Rate fields — Pro+ feature</div>
                          <div style={{fontSize:11,color:C.inkLight,marginTop:2}}>Upgrade to Pro+ to set your rates and use the quote builder.</div>
                        </div>
                        <span style={{fontSize:11,color:C.orange,fontWeight:700,background:C.orangeLight,padding:"4px 10px",borderRadius:20}}>Pro+</span>
                      </div>
                    : <div className="mu-rate-inputs" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
                        {tm.rateFields.map(rf=>{
                          const val=saved[rf.id]!==undefined?saved[rf.id]:rf.default;
                          return (
                            <div key={rf.id} style={{background:C.bg,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 12px"}}>
                              <div style={{fontSize:10,color:C.inkLight,marginBottom:4}}>{rf.label}</div>
                              <div style={{display:"flex",alignItems:"center",gap:4}}>
                                <span style={{fontSize:12,color:C.inkMid}}>$</span>
                                <input type="number" value={val} min={0} step={0.5}
                                  onChange={e=>setRate(rf.id,Number(e.target.value))}
                                  onClick={e=>e.stopPropagation()}
                                  style={{flex:1,border:"none",background:"transparent",fontSize:15,fontWeight:700,color:t.color,outline:"none",width:0}}/>
                                <span style={{fontSize:11,color:C.inkLight}}>{rf.unit.replace('$','')}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                  }
                </div>
              )}
            </div>
          );
        }

        // Standalone mode (not used currently but kept for flexibility)
        return null;
      })}
    </div>
  );
}


// ─── TRADE METRICS DISPLAY (shown in Materials results) ───────────────────────
// ─── PRICE EDIT MODAL ─────────────────────────────────────────────────────────
function PriceEditModal({ material, projectId, currentEmail, onClose, onSaved }) {
  const [newPrice, setNewPrice] = useState(material?.price || 0);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  if (!material) return null;

  const REASONS = [
    'Different supplier',
    'Trade discount applied',
    'Supplier price changed',
    'AI default was wrong',
    'Other',
  ];

  const save = (scope) => {
    const priceNum = Number(newPrice);
    if (isNaN(priceNum) || priceNum < 0) return;
    if (scope === 'project') {
      setProjectPriceOverride(projectId, material.name, priceNum);
    } else if (scope === 'user') {
      setUserPriceOverride(currentEmail, material.name, priceNum);
    }
    logPriceCorrection(currentEmail, projectId, material.name, material.price, priceNum, scope, reason, note);
    onSaved && onSaved(scope, priceNum);
    onClose && onClose();
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
      onClick={onClose}>
      <div style={{background:C.card,borderRadius:14,maxWidth:440,width:'100%',padding:24,boxShadow:'0 8px 32px rgba(0,0,0,0.2)'}}
        onClick={(e)=>e.stopPropagation()}>
        <div style={{fontSize:11,fontWeight:700,color:C.inkLight,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>Update price</div>
        <div style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:14}}>{material.name}</div>

        <div style={{display:'flex',gap:14,alignItems:'center',marginBottom:16}}>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:C.inkLight,marginBottom:4}}>Current</div>
            <div style={{fontSize:18,color:C.inkMid,textDecoration:'line-through'}}>${material.price || 0}</div>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:11,color:C.inkLight,marginBottom:4}}>New price</div>
            <div style={{display:'flex',alignItems:'center',gap:4}}>
              <span style={{fontSize:18,color:C.ink,fontWeight:700}}>$</span>
              <input
                type="number"
                value={newPrice}
                onChange={(e)=>setNewPrice(e.target.value)}
                autoFocus
                step="0.01"
                style={{flex:1,fontSize:18,fontWeight:700,padding:'6px 8px',border:'1.5px solid '+C.tealMid,borderRadius:8,outline:'none',background:'#fff',color:C.ink,width:80}}
              />
            </div>
          </div>
        </div>

        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,color:C.inkLight,fontWeight:600,marginBottom:4,display:'block'}}>Reason (optional)</label>
          <select value={reason} onChange={(e)=>setReason(e.target.value)}
            style={{width:'100%',padding:'8px 10px',border:'1px solid '+C.border,borderRadius:8,fontSize:13,background:'#fff',color:reason?C.ink:C.inkLight,outline:'none'}}>
            <option value="">Select reason...</option>
            {REASONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <div style={{marginBottom:18}}>
          <label style={{fontSize:11,color:C.inkLight,fontWeight:600,marginBottom:4,display:'block'}}>Note (optional)</label>
          <input type="text" value={note} onChange={(e)=>setNote(e.target.value)}
            placeholder="Extra detail..."
            style={{width:'100%',padding:'8px 10px',border:'1px solid '+C.border,borderRadius:8,fontSize:13,background:'#fff',color:C.ink,outline:'none',boxSizing:'border-box'}}/>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          <button onClick={()=>save('project')}
            style={{padding:'11px 16px',background:C.bg,border:'1px solid '+C.border,borderRadius:10,fontSize:13,fontWeight:600,color:C.ink,cursor:'pointer',textAlign:'left'}}>
            <div style={{fontWeight:700}}>Save for this project only</div>
            <div style={{fontSize:11,color:C.inkLight,marginTop:2,fontWeight:400}}>Other projects keep using ${material.price || 0}</div>
          </button>
          <button onClick={()=>save('user')}
            style={{padding:'11px 16px',background:C.tealMid,border:'none',borderRadius:10,fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer',textAlign:'left'}}>
            <div>Save globally for all my future projects</div>
            <div style={{fontSize:11,opacity:0.85,marginTop:2,fontWeight:400}}>Builds your personal price book</div>
          </button>
          <button onClick={onClose}
            style={{padding:'9px 16px',background:'transparent',border:'none',fontSize:13,color:C.inkLight,cursor:'pointer',marginTop:4}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Measurement corrections — helpers and modal
//
// Added by patch-corrections-measurement-modal-v1 (Patch 2 of 5).
// PriceEditModal above handles PRICES.
// MeasurementEditModal below handles QUANTITIES and ROOT MEASUREMENTS.
// ============================================================

// Log a measurement correction to the master corrections database.
// Schema v2 — separate from logPriceCorrection so we can mine them differently.
function logMeasurementCorrection(email, projectId, room, fieldKey, aiValue, realValue, reasonGroup, reason, note, upstream) {
  if (!email) return;
  try {
    const key = 'to_corrections_' + email;
    const existing = pbCorrectionsList(email);   // store-v1: tolerate the old object shape
    const entry = {
      type: 'measurement',
      projectId: projectId || null,
      room: room || null,
      fieldKey,
      aiValue: Number(aiValue),
      realValue: Number(realValue),
      reasonGroup,             // 'config' | 'measurement'
      reason,                  // dropdown value
      note: note || '',
      correctedBy: email,
      correctedAt: new Date().toISOString(),
      planFileName: (upstream && upstream.planFileName) || null,
      // For measurement-group corrections that routed upstream:
      routedFromField: (upstream && upstream.routedFromField) || null,
      upstreamFieldKey: (upstream && upstream.upstreamFieldKey) || null,
      upstreamRealValue: (upstream && upstream.upstreamRealValue !== undefined) ? Number(upstream.upstreamRealValue) : null,
    };
    existing.push(entry);
    localStorage.setItem(key, JSON.stringify(existing.slice(-2000)));
    console.log('[corrections] logged measurement:', entry);
    pbQueueCorrection(projectId, entry, email);
  } catch (e) {
    console.warn('[corrections] log failed:', e);
  }
}

// Per-project override store. Used for measurement-group corrections and for
// config-group corrections the user explicitly pinned to this project.
// Schema: { [room]: { [fieldKey]: value } }
function setProjectOverride(projectId, roomName, fieldKey, value) {
  if (!projectId) return;
  try {
    const key = 'project_overrides_' + projectId;
    const data = JSON.parse(localStorage.getItem(key) || '{}');
    if (!data[roomName]) data[roomName] = {};
    data[roomName][fieldKey] = Number(value);
    localStorage.setItem(key, JSON.stringify(data));
    pbTouchLocal(projectId, 'overrides', roomName + PB_SEP + fieldKey);   // store-v1: follows the job to other devices
  } catch (e) {
    console.warn('[corrections] setProjectOverride failed:', e);
  }
}

function getProjectOverride(projectId, roomName, fieldKey) {
  if (!projectId) return null;
  try {
    const key = 'project_overrides_' + projectId;
    const data = JSON.parse(localStorage.getItem(key) || '{}');
    if (data[roomName] && data[roomName][fieldKey] !== undefined) return data[roomName][fieldKey];
    return null;
  } catch (e) {
    return null;
  }
}

// Global per-user override store. Used for config-group corrections that
// represent user habits (sheet width, supplier waste %, etc).
// Schema: { [fieldKey]: value }
function setUserGlobalOverride(email, fieldKey, value) {
  if (!email) return;
  try {
    const key = 'user_global_overrides_' + email;
    const data = JSON.parse(localStorage.getItem(key) || '{}');
    data[fieldKey] = Number(value);
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.warn('[corrections] setUserGlobalOverride failed:', e);
  }
}

function getUserGlobalOverride(email, fieldKey) {
  if (!email) return null;
  try {
    const key = 'user_global_overrides_' + email;
    const data = JSON.parse(localStorage.getItem(key) || '{}');
    return data[fieldKey] !== undefined ? data[fieldKey] : null;
  } catch (e) {
    return null;
  }
}

// ============================================================
// MeasurementEditModal
//
// Smart-split corrections UI. Two dropdown sections (config vs measurement)
// with a visual divider. Config reasons → direct edit. Measurement reasons →
// flip form to ask for the upstream root value.
// ============================================================

function MeasurementEditModal({
  fieldKey,
  fieldLabel,
  fieldUnit,
  currentValue,
  trade,
  roomName,
  projectId,
  currentEmail,
  planFileName,
  onClose,
  onSaved,
}) {
  const [newValue, setNewValue] = useState(currentValue || 0);
  const [reasonValue, setReasonValue] = useState('');
  const [note, setNote] = useState('');
  const [upstreamValue, setUpstreamValue] = useState('');

  if (fieldKey === undefined || fieldKey === null) return null;

  const cfg = getTradeCorrectionsConfig(trade);
  const configReasons = (cfg.reasons && cfg.reasons.config) || [];
  const measurementReasons = (cfg.reasons && cfg.reasons.measurement) || [];

  // Determine which group the selected reason belongs to.
  const selectedConfigReason = configReasons.find(r => r.value === reasonValue);
  const selectedMeasReason = measurementReasons.find(r => r.value === reasonValue);
  const reasonGroup = selectedConfigReason ? 'config' : (selectedMeasReason ? 'measurement' : null);
  const selectedReason = selectedConfigReason || selectedMeasReason;

  // For measurement reasons, look up the upstream field info.
  const upstreamFieldKey = selectedMeasReason && selectedMeasReason.upstreamField;
  const upstreamFieldInfo = upstreamFieldKey
    ? (cfg.rootMeasurements || []).find(m => m.key === upstreamFieldKey)
    : null;

  const save = (scope) => {
    if (!reasonValue) {
      alert('Please pick a reason first.');
      return;
    }

    if (reasonGroup === 'measurement') {
      // Measurement reasons: forced project-scope. If an upstream field exists,
      // we save the upstream value (e.g. user fixes wall m² instead of sheet count).
      if (upstreamFieldKey && upstreamFieldInfo) {
        const upstreamNum = Number(upstreamValue);
        if (isNaN(upstreamNum) || upstreamNum < 0) {
          alert('Please enter a valid ' + upstreamFieldInfo.label + '.');
          return;
        }
        setProjectOverride(projectId, roomName, upstreamFieldKey, upstreamNum);
        logMeasurementCorrection(
          currentEmail, projectId, roomName, upstreamFieldKey,
          null, upstreamNum, // we don't know AI's upstream value at this layer
          'measurement', selectedReason.label, note,
          {
            planFileName,
            routedFromField: fieldKey,
            upstreamFieldKey,
            upstreamRealValue: upstreamNum,
          }
        );
        onSaved && onSaved({
          scope: 'project',
          reasonGroup: 'measurement',
          fieldKey: upstreamFieldKey,
          value: upstreamNum,
          routedFromField: fieldKey,
        });
      } else {
        // Measurement reason with no upstream (e.g. "Scale on plan was wrong",
        // "Plan was unclear"). Save the direct value as a project override.
        const num = Number(newValue);
        if (isNaN(num) || num < 0) {
          alert('Please enter a valid number.');
          return;
        }
        setProjectOverride(projectId, roomName, fieldKey, num);
        logMeasurementCorrection(
          currentEmail, projectId, roomName, fieldKey,
          currentValue, num,
          'measurement', selectedReason.label, note,
          { planFileName }
        );
        onSaved && onSaved({ scope: 'project', reasonGroup: 'measurement', fieldKey, value: num });
      }
    } else {
      // Config reasons: direct edit. Scope honoured (project or global).
      const num = Number(newValue);
      if (isNaN(num) || num < 0) {
        alert('Please enter a valid number.');
        return;
      }
      if (scope === 'project') {
        setProjectOverride(projectId, roomName, fieldKey, num);
      } else {
        setUserGlobalOverride(currentEmail, fieldKey, num);
      }
      logMeasurementCorrection(
        currentEmail, projectId, roomName, fieldKey,
        currentValue, num,
        'config', selectedReason.label, note,
        { planFileName }
      );
      onSaved && onSaved({ scope, reasonGroup: 'config', fieldKey, value: num });
    }

    onClose && onClose();
  };

  // ────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────

  const showUpstreamForm = (reasonGroup === 'measurement') && upstreamFieldKey;

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
      onClick={onClose}>
      <div style={{background:C.card,borderRadius:14,maxWidth:480,width:'100%',padding:24,boxShadow:'0 8px 32px rgba(0,0,0,0.2)',maxHeight:'90vh',overflowY:'auto'}}
        onClick={(e)=>e.stopPropagation()}>
        <div style={{fontSize:11,fontWeight:700,color:C.inkLight,textTransform:'uppercase',letterSpacing:'0.06em',marginBottom:6}}>
          {showUpstreamForm ? 'Fix the actual measurement' : 'Edit ' + (fieldLabel || 'value')}
        </div>
        <div style={{fontSize:16,fontWeight:700,color:C.ink,marginBottom:14}}>
          {showUpstreamForm ? upstreamFieldInfo.label : fieldLabel}
          {roomName ? <span style={{fontSize:12,fontWeight:500,color:C.inkLight,marginLeft:8}}>· {roomName}</span> : null}
        </div>

        {!showUpstreamForm && (
          <div style={{display:'flex',gap:14,alignItems:'center',marginBottom:16}}>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:C.inkLight,marginBottom:4}}>Current (AI)</div>
              <div style={{fontSize:18,color:C.inkMid,textDecoration:'line-through'}}>{currentValue || 0}{fieldUnit ? ' ' + fieldUnit : ''}</div>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:C.inkLight,marginBottom:4}}>New value</div>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <input
                  type="number"
                  value={newValue}
                  onChange={(e)=>setNewValue(e.target.value)}
                  autoFocus
                  step="0.01"
                  style={{flex:1,fontSize:18,fontWeight:700,padding:'6px 8px',border:'1.5px solid '+C.tealMid,borderRadius:8,outline:'none',background:'#fff',color:C.ink,width:80}}
                />
                {fieldUnit ? <span style={{fontSize:14,color:C.inkLight,fontWeight:600}}>{fieldUnit}</span> : null}
              </div>
            </div>
          </div>
        )}

        <div style={{marginBottom:14}}>
          <label style={{fontSize:11,color:C.inkLight,fontWeight:600,marginBottom:4,display:'block'}}>Why are you changing this?</label>
          <select value={reasonValue} onChange={(e)=>setReasonValue(e.target.value)}
            style={{width:'100%',padding:'8px 10px',border:'1px solid '+C.border,borderRadius:8,fontSize:13,background:'#fff',color:reasonValue?C.ink:C.inkLight,outline:'none'}}>
            <option value="">Select reason...</option>
            {configReasons.length > 0 && (
              <optgroup label="── Supplier or spec ──">
                {configReasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </optgroup>
            )}
            {measurementReasons.length > 0 && (
              <optgroup label="── Measurement issue ──">
                {measurementReasons.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </optgroup>
            )}
          </select>
        </div>

        {showUpstreamForm && (
          <div style={{marginBottom:14,padding:'12px 14px',background:C.bg,borderRadius:10,border:'1px solid '+C.border}}>
            <div style={{fontSize:12,color:C.inkLight,marginBottom:8,lineHeight:1.4}}>
              Tell us the real <b>{upstreamFieldInfo.label.toLowerCase()}</b>. Downstream materials will recalculate automatically.
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <input
                type="number"
                value={upstreamValue}
                onChange={(e)=>setUpstreamValue(e.target.value)}
                autoFocus
                step="0.01"
                placeholder={'Real ' + upstreamFieldInfo.label.toLowerCase()}
                style={{flex:1,fontSize:16,fontWeight:700,padding:'8px 10px',border:'1.5px solid '+C.tealMid,borderRadius:8,outline:'none',background:'#fff',color:C.ink}}
              />
              <span style={{fontSize:14,color:C.inkLight,fontWeight:600}}>{upstreamFieldInfo.unit}</span>
            </div>
          </div>
        )}

        <div style={{marginBottom:18}}>
          <label style={{fontSize:11,color:C.inkLight,fontWeight:600,marginBottom:4,display:'block'}}>Note (optional)</label>
          <input type="text" value={note} onChange={(e)=>setNote(e.target.value)}
            placeholder="Extra detail..."
            style={{width:'100%',padding:'8px 10px',border:'1px solid '+C.border,borderRadius:8,fontSize:13,background:'#fff',color:C.ink,outline:'none',boxSizing:'border-box'}}/>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {reasonGroup === 'measurement' ? (
            <button onClick={()=>save('project')}
              style={{padding:'11px 16px',background:C.tealMid,border:'none',borderRadius:10,fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer',textAlign:'left'}}>
              <div>Save correction to this project</div>
              <div style={{fontSize:11,opacity:0.85,marginTop:2,fontWeight:400}}>Measurement fixes don't carry to other plans</div>
            </button>
          ) : (
            <>
              <button onClick={()=>save('global')}
                style={{padding:'11px 16px',background:C.tealMid,border:'none',borderRadius:10,fontSize:13,fontWeight:700,color:'#fff',cursor:'pointer',textAlign:'left'}}>
                <div>Save globally for all my future projects</div>
                <div style={{fontSize:11,opacity:0.85,marginTop:2,fontWeight:400}}>Builds your personal preferences</div>
              </button>
              <button onClick={()=>save('project')}
                style={{padding:'11px 16px',background:C.bg,border:'1px solid '+C.border,borderRadius:10,fontSize:13,fontWeight:600,color:C.ink,cursor:'pointer',textAlign:'left'}}>
                <div style={{fontWeight:700}}>Save for this project only</div>
                <div style={{fontSize:11,color:C.inkLight,marginTop:2,fontWeight:400}}>Other projects keep AI default</div>
              </button>
            </>
          )}
          <button onClick={onClose}
            style={{padding:'9px 16px',background:'transparent',border:'none',fontSize:13,color:C.inkLight,cursor:'pointer',marginTop:4}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── ROOM CARD ────────────────────────────────────────────────────────────────
function RoomCard({room, trade, showM2, showSheets, showPrices, fmt, onEditPrice, onEditMeasurement, projectId, currentEmail, planFileName, measurementUpdateNonce}) {
  // Click-to-edit corrections (Patch 3 of 5).
  // Inline editing replaced by MeasurementEditModal.

  const [showAddEx, setShowAddEx] = useState(false);
  const [newExType, setNewExType] = useState('window');
  const [newExCount, setNewExCount] = useState(1);
  const [newExW, setNewExW] = useState(1.2);
  const [newExH, setNewExH] = useState(1.2);
  const [showAI, setShowAI] = useState(false);
  const [exclusions, setExclusions] = useState(()=>{
    return (room.exclusions||[]).map((e,i)=>({
      id:i, type:typeof e==='object'?(e.type||'window'):'window',
      count:typeof e==='object'?(e.count||1):1,
      width:typeof e==='object'?(e.width||(e.type==='door'?0.9:1.2)):1.2,
      height:typeof e==='object'?(e.height||(e.type==='door'?2.1:1.2)):1.2,
      desc:typeof e==='object'?(e.desc||''):e,
    }));
  });

  // Override lookup — checks project_overrides for this room's field, falls back to AI value.
  // measurementUpdateNonce is included in the dependency so the card rerenders after a save.
  const overrides = useMemo(()=>{
    if (!projectId || !room.room) return {};
    try {
      const data = JSON.parse(localStorage.getItem('project_overrides_'+projectId) || '{}');
      return data[room.room] || {};
    } catch (e) { return {}; }
  }, [projectId, room.room, measurementUpdateNonce]);

  const aiValues = {
    floorM2: room.m2 || 0,
    wallM2: room.wallM2 || room.dryM2 || Math.round((room.m2||0) * 0.65),
    ceilingM2: room.ceilingM2 || Math.round(room.m2||0),
    // stud-through-v1-2026-08-11: vision returns exactly 2.4 for every room
    // when it cannot read one (it did so on all 15 Denby rooms against a
    // true 2.55). A stud the tradie confirmed for this job outranks that,
    // so the chip on the card and the total above it stop disagreeing.
    studHeight: (typeof getConfirmedStud === 'function' &&
                 getConfirmedStud(projectId)) ||
                room.studHeight || 2.4,
    wetM2: room.wetM2 || 0,
  };
  const display = {
    floorM2: overrides.floorM2 !== undefined ? overrides.floorM2 : aiValues.floorM2,
    wallM2: overrides.wallM2 !== undefined ? overrides.wallM2 : aiValues.wallM2,
    ceilingM2: overrides.ceilingM2 !== undefined ? overrides.ceilingM2 : aiValues.ceilingM2,
    studHeight: overrides.studHeight !== undefined ? overrides.studHeight : aiValues.studHeight,
    wetM2: overrides.wetM2 !== undefined ? overrides.wetM2 : aiValues.wetM2,
  };
  const hasOverride = (fk) => overrides[fk] !== undefined;
  const anyOverride = Object.keys(overrides).length > 0;

  const totalExM2 = exclusions.reduce((s,e)=>s+(e.count*e.width*e.height),0);
  const netWallM2 = Math.max(0, display.wallM2 - totalExM2);
  const isWet = room.boardType?.toLowerCase().includes('aqua')||room.room?.toLowerCase().includes('bath')||room.room?.toLowerCase().includes('ensuite')||room.room?.toLowerCase().includes('laundry')||room.room?.toLowerCase().includes('wc')||display.wetM2>0;
  const showWetPill = isWetCapable(room.room) || display.wetM2 > 0;

  // Patch 4: multi-axis recalc engine.
  // For each material, classify it (wall/ceiling/perimeter/sheetDerived/floor)
  // and scale by the ratio of the corrected root vs AI's value.
  // Explicit qty overrides (user clicked the qty cell) take precedence over scaling.
  const calcMaterials = () => {
    // Compute ratios for each scaling axis. Default to 1.0 if no change.
    const wallRatio    = aiValues.wallM2     > 0 ? display.wallM2     / aiValues.wallM2     : 1;
    const ceilingRatio = aiValues.ceilingM2  > 0 ? display.ceilingM2  / aiValues.ceilingM2  : 1;
    const floorRatio   = aiValues.floorM2    > 0 ? display.floorM2    / aiValues.floorM2    : 1;
    // Perimeter scales with the square root of floor area (rough approximation)
    const perimeterRatio = aiValues.floorM2 > 0 ? Math.sqrt(display.floorM2 / aiValues.floorM2) : 1;

    // Stud height also affects wall area indirectly. If wallM2 has its own override,
    // we trust that. If not, but studHeight changed, scale wall by stud ratio.
    let effectiveWallRatio = wallRatio;
    if (overrides.wallM2 === undefined && aiValues.studHeight > 0) {
      const studRatio = display.studHeight / aiValues.studHeight;
      effectiveWallRatio = wallRatio * studRatio;
    }

    // First pass: compute pre-derived qty per material (everything except sheetDerived)
    const firstPass = (room.roomMaterials||[]).map(m => {
      const explicitOverride = overrides[(m.name||'') + '__qty'];
      if (explicitOverride !== undefined) {
        return {...m, qty: explicitOverride, scaled: false, explicitOverride: true};
      }
      const axis = classifyMaterial(m.name, m.cat, m.unit);
      let ratio = 1, axisName = 'floor';
      if (axis === 'wall') { ratio = effectiveWallRatio; axisName = 'wall'; }
      else if (axis === 'ceiling') { ratio = ceilingRatio; axisName = 'ceiling'; }
      else if (axis === 'perimeter') { ratio = perimeterRatio; axisName = 'perimeter'; }
      else if (axis === 'sheetDerived') { ratio = null; axisName = 'sheetDerived'; } // deferred to pass 2
      else { ratio = floorRatio; axisName = 'floor'; }

      if (ratio === null) {
        return {...m, _axis: axisName, _baseQty: m.qty, _ratio: null, scaled: false};
      }
      const isSheet = (m.unit || '').toLowerCase().includes('sheet');
      const newQty = isSheet
        ? Math.max(1, Math.ceil(m.qty * ratio))
        : Math.max(0, Math.round(m.qty * ratio * 10) / 10);
      const wasScaled = Math.abs(ratio - 1) > 0.005;
      return {...m, qty: newQty, scaled: wasScaled, _axis: axisName, _ratio: ratio};
    });

    // Second pass: sheetDerived items (screws, tape, compound) scale with the
    // ratio of new total sheets to original total sheets.
    const origSheets = (room.roomMaterials||[]).filter(m => (m.unit||'').toLowerCase().includes('sheet')).reduce((s,m)=>s+(m.qty||0),0);
    const newSheets = firstPass.filter(m => (m.unit||'').toLowerCase().includes('sheet')).reduce((s,m)=>s+(m.qty||0),0);
    const sheetDerivedRatio = origSheets > 0 ? newSheets / origSheets : 1;

    const final = firstPass.map(m => {
      if (m._axis !== 'sheetDerived') return {...m, total: m.qty * (m.price || 0)};
      const baseQty = m._baseQty;
      const isSheet = (m.unit || '').toLowerCase().includes('sheet');
      const newQty = isSheet
        ? Math.max(1, Math.ceil(baseQty * sheetDerivedRatio))
        : Math.max(0, Math.round(baseQty * sheetDerivedRatio * 10) / 10);
      const wasScaled = Math.abs(sheetDerivedRatio - 1) > 0.005;
      return {...m, qty: newQty, scaled: wasScaled, total: newQty * (m.price || 0)};
    });

    return final;
  };
  const roomMats = calcMaterials();
  const roomTotal = roomMats.reduce((s,m)=>s+(m.price||0)*m.qty,0);

  const addEx=()=>{setExclusions(p=>[...p,{id:Date.now(),type:newExType,count:newExCount,width:newExW,height:newExH,desc:`${newExCount} ${newExType}${newExCount>1?'s':''}`}]);setShowAddEx(false);setNewExCount(1);setNewExW(newExType==='door'?0.9:1.2);setNewExH(newExType==='door'?2.1:1.2);};
  const removeEx=(id)=>setExclusions(p=>p.filter(e=>e.id!==id));

  // Click-to-edit pill styling
  const pill = (over) => ({display:'flex',alignItems:'center',gap:4,background:over?C.amberFaint:C.bg,border:`1px solid ${over?C.amber:C.border}`,borderRadius:8,padding:'4px 8px',cursor:'pointer'});

  // Render a single measurement pill with click-to-edit.
  const renderPill = (icon, fieldKey, fieldLabel, fieldUnit, displayValue, unitSuffix, iconColor) => {
    const over = hasOverride(fieldKey);
    const ai = aiValues[fieldKey];
    return (
      <div style={pill(over)} onClick={()=>onEditMeasurement&&onEditMeasurement({
        fieldKey, fieldLabel, fieldUnit, currentValue: ai, roomName: room.room
      })} title="Click to correct">
        <span style={{fontSize:10,color:iconColor||C.inkLight}}>{icon}</span>
        {over && <span style={{fontSize:11,color:C.inkLight,textDecoration:'line-through',marginRight:2}}>{ai}</span>}
        <span style={{fontSize:13,fontWeight:700,color:over?C.amber:C.ink}}>{displayValue}</span>
        <span style={{fontSize:10,color:iconColor||C.inkLight}}>{unitSuffix}</span>
      </div>
    );
  };

  return (
    <div style={{background:'#FAFCFF',border:`1px solid ${isWet?'#2980B944':anyOverride?C.amber:C.border}`,borderRadius:12,marginBottom:12,overflow:'hidden'}}>
      <div style={{padding:'14px 18px',background:isWet?'#EBF5FB':anyOverride?'#FFFBF0':'#fff'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
          <div style={{flex:1}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,flexWrap:'wrap'}}>
              <span style={{fontSize:15}}>{isWet?'🚿':'🏠'}</span>
              <span style={{fontSize:14,fontWeight:700,color:C.ink}}>{room.room}</span>
              {isWet&&<span style={{fontSize:10,background:'#2980B922',color:'#2980B9',borderRadius:20,padding:'1px 8px',fontWeight:600}}>Wet area</span>}
              {anyOverride&&<span style={{fontSize:10,background:C.amberFaint,color:C.amber,borderRadius:20,padding:'1px 8px',fontWeight:600}}>✏️ Corrected</span>}
            </div>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:6}}>
              {showM2 && renderPill('📐','floorM2','Floor area','m²',display.floorM2,'m² floor')}
              {renderPill('🧱','wallM2','Wall area','m²',display.wallM2,'m² wall')}
              {renderPill('⬜','ceilingM2','Ceiling area','m²',display.ceilingM2,'m² ceil')}
              {renderPill('↕','studHeight','Stud height','m',display.studHeight,'m stud')}
              {showWetPill && renderPill('💧','wetM2','Wet area','m²',display.wetM2,'m² wet','#2980B9')}
            </div>
            <div style={{fontSize:11,color:C.inkMid,marginBottom:4}}>
              Net wall: <strong>{(Math.round(netWallM2*100)/100).toString()} m²</strong>{totalExM2>0&&<span style={{color:C.amber}}> (−{totalExM2.toFixed(1)} m² excluded)</span>}{' · '}Ceiling: <strong>{display.ceilingM2} m²</strong>{' · '}Coving: <strong>{room.perimeterLm||Math.round(Math.sqrt(display.floorM2)*4)} lm</strong>
            </div>
          </div>
          <div style={{textAlign:'right',flexShrink:0}}>
            <div style={{fontSize:20,fontWeight:800,color:trade.color}}>{display.floorM2}<span style={{fontSize:11,fontWeight:400,color:C.inkLight}}> m²</span></div>
            {showPrices&&roomTotal>0&&fmt&&<div style={{fontSize:13,fontWeight:700,color:trade.color,marginTop:2}}>{fmt(roomTotal)}</div>}
            {showPrices&&roomTotal>0&&fmt&&<div style={{fontSize:10,color:C.inkLight}}>excl. GST</div>}
            <button onClick={()=>setShowAI(v=>!v)} style={{fontSize:10,color:C.inkLight,background:'none',border:`1px solid ${C.border}`,borderRadius:6,padding:'2px 6px',cursor:'pointer',marginTop:4}}>{showAI?'Hide AI':'AI values'}</button>
          </div>
        </div>
        {showAI&&<div style={{background:'rgba(99,102,241,0.06)',border:'1px solid rgba(99,102,241,0.2)',borderRadius:8,padding:'8px 12px',marginTop:8,fontSize:11}}><div style={{fontWeight:700,color:'#6366f1',marginBottom:4}}>🤖 AI originally read:</div><div style={{display:'flex',gap:12,flexWrap:'wrap',color:C.inkMid}}><span>Floor: <strong>{aiValues.floorM2} m²</strong></span><span>Wall: <strong>{aiValues.wallM2} m²</strong></span><span>Ceiling: <strong>{aiValues.ceilingM2} m²</strong></span><span>Stud: <strong>{aiValues.studHeight} m</strong></span>{showWetPill&&<span>Wet: <strong>{aiValues.wetM2} m²</strong></span>}</div></div>}
        {room.notes&&<div style={{fontSize:11,color:C.inkMid,marginTop:6,fontStyle:'italic'}}>{room.notes}</div>}
      </div>
      <div style={{borderTop:`1px solid ${C.border}44`}}>
        <div style={{padding:'10px 18px',borderBottom:`1px solid ${C.border}44`,background:'#FFFBF5'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:C.amber,textTransform:'uppercase',letterSpacing:'0.05em'}}>✕ Excluded from measurement</div>
            <button onClick={()=>setShowAddEx(v=>!v)} style={{fontSize:11,color:C.amber,background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:6,padding:'3px 8px',cursor:'pointer'}}>{showAddEx?'Cancel':'+ Add'}</button>
          </div>
          {showAddEx&&<div style={{background:'#fff',border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 12px',marginBottom:10}}>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center',marginBottom:8}}>
              <select value={newExType} onChange={e=>{setNewExType(e.target.value);setNewExW(e.target.value==='door'?0.9:1.2);setNewExH(e.target.value==='door'?2.1:1.2);}} style={{background:'#f8f8f8',border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 8px',fontSize:12,outline:'none'}}><option value="window">🪟 Window</option><option value="door">🚪 Door</option><option value="opening">⬜ Opening</option></select>
              <div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontSize:11,color:C.inkLight}}>Count:</span><input type="number" value={newExCount} min={1} max={20} onChange={e=>setNewExCount(Number(e.target.value))} style={{width:40,border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 6px',fontSize:12,outline:'none',textAlign:'center'}}/></div>
              <div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontSize:11,color:C.inkLight}}>W:</span><input type="number" value={newExW} min={0.3} max={5} step={0.1} onChange={e=>setNewExW(Number(e.target.value))} style={{width:44,border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 6px',fontSize:12,outline:'none',textAlign:'center'}}/><span style={{fontSize:11,color:C.inkLight}}>× H:</span><input type="number" value={newExH} min={0.3} max={3} step={0.1} onChange={e=>setNewExH(Number(e.target.value))} style={{width:44,border:`1px solid ${C.border}`,borderRadius:6,padding:'4px 6px',fontSize:12,outline:'none',textAlign:'center'}}/><span style={{fontSize:11,color:C.inkLight}}>m</span></div>
            </div>
            <div style={{fontSize:11,color:C.inkMid,marginBottom:8}}>= {(newExCount*newExW*newExH).toFixed(2)} m² will be deducted</div>
            <button onClick={addEx} style={{background:C.amber,color:'#fff',border:'none',borderRadius:6,padding:'6px 14px',fontSize:12,fontWeight:700,cursor:'pointer',width:'100%'}}>Add exclusion</button>
          </div>}
          {exclusions.length===0&&!showAddEx&&<div style={{fontSize:11,color:C.inkLight,fontStyle:'italic'}}>No exclusions — click + Add to deduct windows or doors</div>}
          {exclusions.map((ex,j)=>{
            const area=ex.count*ex.width*ex.height, icon=ex.type==='window'?'🪟':ex.type==='door'?'🚪':'⬜';
            return <div key={ex.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'6px 0',borderBottom:j<exclusions.length-1?`1px solid ${C.border}33`:'none'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,flex:1}}><span style={{fontSize:13}}>{icon}</span><div><div style={{fontSize:12,fontWeight:600,color:C.ink}}>{ex.count} {ex.type}{ex.count>1?'s':''} ({ex.width}×{ex.height}m)</div><div style={{fontSize:11,color:C.inkLight}}>{ex.type==='door'?'Door — wall area deducted':'Window — wall area deducted'}</div></div></div>
              <div style={{display:'flex',alignItems:'center',gap:8}}><span style={{fontSize:11,fontWeight:700,color:C.amber,background:C.amberFaint,borderRadius:6,padding:'1px 6px',whiteSpace:'nowrap'}}>{area.toFixed(1)} m²</span><button onClick={()=>removeEx(ex.id)} style={{background:'none',border:'none',cursor:'pointer',color:C.inkLight,fontSize:14,padding:0,lineHeight:1}}>✕</button></div>
            </div>;
          })}
          {exclusions.length>0&&<div style={{marginTop:8,padding:'5px 10px',background:C.amberFaint,borderRadius:6,fontSize:11,color:C.amber,fontWeight:600}}>Total excluded: {totalExM2.toFixed(1)} m²</div>}
        </div>
        {roomMats.length>0&&<div style={{padding:'10px 18px'}}>
          <div style={{fontSize:11,fontWeight:700,color:C.inkLight,textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:8}}>Materials for this room <span style={{fontSize:10,fontWeight:400,textTransform:'none',marginLeft:4}}>— from measurements above</span></div>
          <div className="mu-materials-table" style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,overflow:'hidden'}}>
            <div style={{display:'grid',gridTemplateColumns:showPrices?'1fr 80px 80px 90px':'1fr 80px',padding:'6px 14px',background:C.bg,borderBottom:`1px solid ${C.border}`}}>
              <span style={{fontSize:10,fontWeight:700,color:C.inkLight,textTransform:'uppercase'}}>Item</span>
              <span style={{fontSize:10,fontWeight:700,color:C.inkLight,textTransform:'uppercase',textAlign:'right'}}>Qty</span>
              {showPrices&&<span style={{fontSize:10,fontWeight:700,color:C.inkLight,textTransform:'uppercase',textAlign:'right'}}>Unit $</span>}
              {showPrices&&<span style={{fontSize:10,fontWeight:700,color:C.inkLight,textTransform:'uppercase',textAlign:'right'}}>Total</span>}
            </div>
            {roomMats.map((m,j)=>{
              const qtyKey = (m.name||'') + '__qty';
              const qtyOver = overrides[qtyKey] !== undefined;
              return (<div key={j} style={{display:'grid',gridTemplateColumns:showPrices?'1fr 80px 80px 90px':'1fr 80px',padding:'8px 14px',borderBottom:j<roomMats.length-1?`1px solid ${C.border}44`:'none',alignItems:'center'}}>
                <span style={{fontSize:12,color:C.ink}}>{m.name}</span>
                {onEditMeasurement
                  ? <button onClick={()=>onEditMeasurement({
                      fieldKey: qtyKey,
                      fieldLabel: m.name + ' qty',
                      fieldUnit: m.unit,
                      currentValue: m.qty,
                      roomName: room.room
                    })} title="Click to correct quantity"
                      style={{fontSize:12,fontWeight:700,color:qtyOver?C.amber:(m.scaled?'#6366f1':trade.color),textAlign:'right',background:'transparent',border:'none',cursor:'pointer',padding:'2px 6px',borderRadius:4,textDecoration:'underline dotted',textUnderlineOffset:3}}>{m.qty} <span style={{fontSize:10,color:C.inkLight,fontWeight:400}}>{m.unit}</span>{m.scaled && !qtyOver && <span style={{fontSize:9,color:'#6366f1',marginLeft:4,fontWeight:600}}>(adj)</span>}</button>
                  : <span style={{fontSize:12,fontWeight:700,color:trade.color,textAlign:'right'}}>{m.qty} <span style={{fontSize:10,color:C.inkLight,fontWeight:400}}>{m.unit}</span></span>}
                {showPrices&&(onEditPrice
                  ? <button onClick={()=>onEditPrice(m)} title="Click to update price"
                      style={{fontSize:12,color:C.inkMid,textAlign:'right',background:'transparent',border:'none',cursor:'pointer',padding:'2px 6px',borderRadius:4,textDecoration:'underline dotted',textUnderlineOffset:3}}>{fmt&&m.price>0?fmt(m.price):'—'}</button>
                  : <span style={{fontSize:12,color:C.inkMid,textAlign:'right'}}>{fmt&&m.price>0?fmt(m.price):'—'}</span>
                )}
                {showPrices&&<span style={{fontSize:12,fontWeight:600,color:C.ink,textAlign:'right'}}>{fmt&&m.price>0?fmt(m.qty*(m.price||0)):'—'}</span>}
              </div>);
            })}
          </div>
          {showPrices&&roomTotal>0&&fmt&&<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:trade.light,borderRadius:'0 0 10px 10px',marginTop:-1}}>
            <span style={{fontSize:12,fontWeight:700,color:trade.color}}>Room subtotal excl. GST</span>
            <div style={{textAlign:'right'}}><div style={{fontSize:15,fontWeight:800,color:trade.color}}>{fmt(roomTotal)}</div><div style={{fontSize:11,color:trade.color,opacity:0.7}}>incl. GST: {fmt(roomTotal*1.15)}</div></div>
          </div>}
        </div>}
      </div>
    </div>
  );
}


// ─── stud-confirm-v1-2026-07-26 ──────────────────────────────────────────────
// A plan does not always print its stud height (only 2 of 6 test plans did).
// Rather than silently assuming 2.4 m — which understates South Road by 7%
// and Denby by 6% — the engine flags it and we ask the tradie once.
const studKey = (projectId) => 'stud_confirm_' + (projectId || 'current');
const getConfirmedStud = (projectId) => {
  try {
    const v = parseFloat(localStorage.getItem(studKey(projectId)) || '');
    return (v >= 2.1 && v <= 3.6) ? v : null;
  } catch (e) { return null; }
};
const setConfirmedStud = (projectId, v) => {
  try {
    // stud-ask-v2-2026-10-23: null takes the answer back ("change")
    if (v === null) localStorage.removeItem(studKey(projectId));
    else localStorage.setItem(studKey(projectId), String(v));
  } catch (e) {}
  pbTouchLocal(projectId, 'stud');           // store-v1: follows the job to other devices
};
// stud-ask-v2-2026-10-23 (ground-truth run, Denby): the stud question had
// 2.4 m pre-highlighted, so it read like the answer and got picked (walls
// -9.4%). Now nothing is pre-picked, and the AI's own reading off the sections
// is offered as a suggestion. The AI writes 2.4 for a room when it can't read
// the stud, so 2.4 is never offered as its reading; when rooms differ, the
// height on the most wall (perimeter) wins.
const pbAiStud = (project) => {
  const rooms = project && project.aiAnalysis && Array.isArray(project.aiAnalysis.rooms) ? project.aiAnalysis.rooms : [];
  const w = {};
  rooms.forEach(r => {
    const v = Number(r && r.studHeight);
    if (!(v >= 2.1 && v <= 3.6) || Math.abs(v - 2.4) < 0.005) return;
    const k = String(Math.round(v * 1000) / 1000);
    const len = Number(r.perimeterLm);
    w[k] = (w[k] || 0) + (len > 0 ? len : 1);
  });
  let best = null;
  Object.keys(w).forEach(k => { if (best === null || w[k] > w[best]) best = k; });
  return best === null ? null : Number(best);
};
// walls = square stop x stud - openings. Stud is a pure multiplier, so a
// confirmed value re-derives the wall tile with no server round trip.
const wallsFromStud = (geoRec, stud) => {
  if (!geoRec || !stud) return null;
  const sq = Number(geoRec.square_stop_lm_raw);
  const op = Number(geoRec.openings_m2);
  if (!sq || Number.isNaN(sq)) return null;
  return sq * stud - (Number.isNaN(op) ? 0 : op);
};
// ---- stud-through-v1-2026-08-11 ----
// Every stud-dependent figure the engine returns is LINEAR in the stud:
// walls = perimeter x stud, externals = count x stud. So a confirmed stud
// is applied by RATIO to the whole envelope, which reproduces exactly what
// the engine would return if re-run — with no round trip, and without
// imposing our own convention on numbers the engine computed with its.
const pbStudRatio = (geo, stud) => {
  const assumed = Number(geo && (geo.stud_m || (geo.all && geo.all.stud_m)));
  if (!stud || !assumed || !(assumed > 0)) return 1;
  return stud / assumed;
};
// Fields that scale with the stud. Anything not named here — floor,
// ceiling, square stop, footprint — is stud-independent and must NOT move.
const PB_STUD_SCALED = ['walls_m2', 'external_corners_lm', 'external_lm'];
const pbApplyStud = (geoRec, stud) => {
  if (!geoRec || !stud) return geoRec;
  const r = pbStudRatio(geoRec, stud);
  if (!(r > 0) || Math.abs(r - 1) < 1e-9) return geoRec;
  const scale = (o) => {
    if (!o || typeof o !== 'object') return o;
    const out = Array.isArray(o) ? o.slice() : Object.assign({}, o);
    PB_STUD_SCALED.forEach(k => {
      const v = out[k];
      if (typeof v === 'number') out[k] = Math.round(v * r * 100) / 100;
      else if (v && typeof v === 'object' && typeof v.value === 'number') {
        out[k] = Object.assign({}, v, { value: Math.round(v.value * r * 100) / 100 });
      }
    });
    return out;
  };
  const next = scale(geoRec);
  if (next.all) next.all = scale(next.all);
  if (Array.isArray(next.rooms)) next.rooms = next.rooms.map(scale);
  next.stud_m = stud;
  next.stud_source = 'confirmed';
  return next;
};
// ---- end stud-through-v1-2026-08-11 ----
// ─── end stud-confirm-v1 ─────────────────────────────────────────────────────

// ---- openings-net-v1-2026-09-23 ----
// Walls are NET, as Groundplan measures them: gross less the openings the
// drawing prints (engine fields.openings_m2: joinery tags w x h one face,
// door leaf labels x 1.98 m both faces). Denby 635.8 - 142.6 = 493.2
// against Groundplan's 503.54 (-2.0%).
const pbGeoEnv = (project) => project && project.aiAnalysis && project.aiAnalysis.geometry;
const pbOpeningsM2 = (project) => {
  const g = pbGeoEnv(project);
  const f = g && g.fields && g.fields.openings_m2;
  return (f && f.value != null && f.conf !== 'flag') ? (Number(f.value) || 0) : 0;
};
// undefined = no geometry walls (vision path keeps its old line);
// null = geometry could not read the openings, so the walls are GROSS.
const pbOpeningsInfo = (project) => {
  const g = pbGeoEnv(project);
  const a = g && g.reconciled && g.reconciled.all;
  const s = a && a.square_stop_lm;
  if (!s || s.value == null || s.conf === 'flag') return undefined;
  const m2 = pbOpeningsM2(project);
  if (!m2) return null;
  const o = g.openings || {};
  return { m2: m2, nWin: o.n_windows, nLeaves: o.n_leaves };
};
// Raked ceilings: geometry measures plan area; the lined area is that over
// cos(pitch). The plan says WHERE the rake is; only the tradie knows the angle.
const pitchKey = (projectId) => 'pitch_confirm_' + (projectId || 'current');
const getConfirmedPitch = (projectId) => {
  try {
    const s = localStorage.getItem(pitchKey(projectId));
    if (s === null || s === '') return null;
    const v = parseFloat(s);
    return (v >= 0 && v <= 60) ? v : null;
  } catch (e) { return null; }
};
const setConfirmedPitch = (projectId, v) => {
  try {
    if (v === null) localStorage.removeItem(pitchKey(projectId));
    else localStorage.setItem(pitchKey(projectId), String(v));
  } catch (e) {}
  pbTouchLocal(projectId, 'pitch');          // store-v1: follows the job to other devices
};
const pbRakedM2 = (project) => {
  const g = pbGeoEnv(project);
  const r = g && g.ceilings && g.ceilings.raked_piece_areas_m2;
  return Array.isArray(r) ? r.reduce((t, v) => t + (Number(v) || 0), 0) : 0;
};
const pbRakeExtraM2 = (project, pitch) => {
  if (pitch == null || !(pitch > 0) || pitch >= 60) return 0;
  return pbRakedM2(project) * (1 / Math.cos(pitch * Math.PI / 180) - 1);
};
// ---- end openings-net-v1 ----
// ─── Plan verdict panel (plancheck-v1-2026-10-13) ───────────────────────────
// stop: no numbers at all. hold: numbers hidden until "show them anyway",
// then a standing banner. check: the things to check. ok: one quiet line.
function PlanVerdictPanel({verdict, roughShown, onShowAnyway, onNewScan, onDeleteJob, onLookAtPicture}) {
  const [why, setWhy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  if (!verdict) return null;
  const v = verdict.verdict;
  const reasons = Array.isArray(verdict.reasons) ? verdict.reasons : [];
  const main = reasons.filter(r => r && (r.level === 'stop' || r.level === 'hold' || r.level === 'warn'));
  const info = reasons.filter(r => r && r.level === 'info');
  const text = (r) => <div key={r.code + r.text} style={{fontSize:13,lineHeight:1.55,marginBottom:6}}>{pbTxt(r.text, 600)}</div>;
  const btn = (bg, fg, border) => ({padding:"10px 16px",background:bg,color:fg,border:border||"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer"});
  if (v === 'stop') {
    return (
      <div role="alert" data-pb-verdict="stop" style={{background:C.redLight,border:`1.5px solid ${C.red}66`,borderRadius:14,padding:"18px 20px",marginBottom:18,color:"#7F1D1D"}}>
        <div style={{fontSize:17,fontWeight:800,marginBottom:8}}>⛔ {pbTxt(verdict.headline, 120) || "We couldn't read this plan"}</div>
        {main.map(text)}
        <div style={{fontSize:13,lineHeight:1.55,margin:"10px 0 14px"}}>No numbers are shown for this job, so nothing here can end up in a quote.{pbFilingOn ? "" : " This scan isn't counted against your plan on this device."}</div>
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          {onNewScan&&<button onClick={onNewScan} style={btn(C.red,"#fff")}>Scan a different file</button>}
          {onDeleteJob&&(confirmDel
            ?<button onClick={onDeleteJob} style={btn("#fff",C.red,`1.5px solid ${C.red}`)}>Tap again to delete</button>
            :<button onClick={()=>setConfirmDel(true)} style={btn("transparent","#7F1D1D",`1px solid ${C.red}55`)}>Delete this job</button>)}
        </div>
      </div>
    );
  }
  if (v === 'hold') {
    const partial = verdict.kind === 'partial';
    const levels = verdict.kind === 'levels';      // every-storey round 2
    // alter-v1-2026-10-23: an alteration; measured = the whole building
    const alteration = verdict.kind === 'alteration';
    const wholeBldg = alteration && verdict.measured === true;
    if (!roughShown) {
      return (
        <div role="alert" data-pb-verdict="hold" style={{background:C.amberFaint,border:`1.5px solid ${C.amber}88`,borderRadius:14,padding:"18px 20px",marginBottom:18,color:"#7C2D12"}}>
          <div style={{fontSize:17,fontWeight:800,marginBottom:8}}>⚠ {pbTxt(verdict.headline, 120) || "Couldn't measure this plan from the drawing"}</div>
          {main.map(text)}
          <div style={{fontSize:13,lineHeight:1.55,margin:"10px 0 14px",fontWeight:600}}>
            {partial ? "The numbers are hidden so a short take-off can't slip into a quote." : levels ? "The numbers are hidden until you've looked at each floor's plan — a floor counted twice would make every number too big." : wholeBldg ? "The numbers are hidden because they're the whole building, and only part of it is new work." : alteration ? "The numbers are hidden until you choose to see them, so a by-eye read of the whole building can't slip into a quote." : "The numbers are hidden until you choose to see them, so a by-eye read can't slip into a quote as if it were measured."}
          </div>
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {(partial||levels)&&onLookAtPicture&&<button onClick={onLookAtPicture} style={btn(C.tealMid,"#fff")}>See what was measured</button>}
            <button onClick={onShowAnyway} style={btn(C.amber,"#fff")}>{partial||levels ? "Show the numbers anyway" : wholeBldg ? "Show the whole-building numbers" : "Show the rough numbers"}</button>
            {onNewScan&&<button onClick={onNewScan} style={btn("transparent","#7C2D12",`1px solid ${C.amber}66`)}>Scan a different file</button>}
          </div>
        </div>
      );
    }
    return (
      <div role="status" data-pb-verdict="hold-shown" style={{background:C.amberFaint,border:`1px solid ${C.amber}66`,borderRadius:10,padding:"10px 14px",marginBottom:14,fontSize:13,color:"#7C2D12"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <strong>⚠ {partial ? "Part of this house wasn't measured — these numbers are short." : levels ? "More floors were measured than the drawings show — check each floor before you quote." : wholeBldg ? "Alteration — these are whole-building numbers. Take off just the rooms being worked on." : alteration ? "Rough numbers for an alteration — not measured, and they may include rooms nobody is touching." : "Rough numbers — not measured from the drawing. Check them against the plan before you quote."}</strong>
          <span style={{display:"flex",gap:10}}>
            {(partial||levels)&&onLookAtPicture&&<button onClick={onLookAtPicture} style={{background:"none",border:"none",color:C.tealMid,fontWeight:700,cursor:"pointer",fontSize:12,padding:0}}>See what was measured</button>}
            <button onClick={()=>setWhy(w=>!w)} style={{background:"none",border:"none",color:"#7C2D12",fontWeight:700,cursor:"pointer",fontSize:12,padding:0,textDecoration:"underline"}}>{why?"Hide why":"Why?"}</button>
          </span>
        </div>
        {why&&<div style={{marginTop:8}}>{main.map(text)}</div>}
      </div>
    );
  }
  if (v === 'check') {
    const warns = main.filter(r => r.level === 'warn');
    return (
      <div role="status" data-pb-verdict="check" style={{background:C.amberFaint,border:`1px solid ${C.amber}55`,borderRadius:12,padding:"12px 16px",marginBottom:14,color:"#7C2D12"}}>
        <div style={{fontSize:14,fontWeight:800,marginBottom:6}}>✓ Measured from the drawing — check {warns.length===1?"this":"these"} before you quote</div>
        <ul style={{margin:"0 0 0 18px",padding:0}}>{warns.map(r=><li key={r.code} style={{fontSize:13,lineHeight:1.5,marginBottom:4}}>{pbTxt(r.text, 600)}</li>)}</ul>
        {info.length>0&&<div style={{fontSize:11,color:C.inkMid,marginTop:6,lineHeight:1.5}}>{info.map(r=>pbTxt(r.text, 400)).join(" ")}</div>}
      </div>
    );
  }
  if (v === 'ok') {
    return (
      <div data-pb-verdict="ok" style={{fontSize:12,color:"#15803D",marginBottom:12,lineHeight:1.5}}>
        <strong>✓ Measured from the drawing.</strong>{info.length>0&&<span style={{color:C.inkMid}}> {info.map(r=>pbTxt(r.text, 400)).join(" ")}</span>}
      </div>
    );
  }
  return null;
}

// ─── External-corner check (plancheck-v1-2026-10-13) ────────────────────────
// Grant, 13 Oct 2026: "one step before it all comes up is to have an image of
// the floor plan with the external corners marked that the AI has done, and
// then the human can just check and double-check."
// The engine draws the plan around the building and gives every corner it
// counted a spot on that picture. Tap a spot to take it off; tap Add then the
// plan for one it missed. The count agreed here is what the External tile uses.
const PB_CC_ZOOMS = [1, 1.5, 2, 3, 4];
// every-storey-v1-2026-10-14: a multi-storey job checks one floor at a time
// (a picture per floor), then saves one check with every floor in it.
function CornerCheckFlow({pics, project, stud, studConfirmed, studAssumed, initial, readOnly, onSave, onSkip}) {
  const list = Array.isArray(pics) ? pics.filter(pbOkPic) : [];
  const [i, setI] = useState(0);
  const [done, setDone] = useState([]);
  useLayoutEffect(() => {
    try { const se = document.scrollingElement || document.documentElement; if (se) se.scrollTop = 0; } catch (e) {}
  }, [i]);
  if (!list.length) return null;
  const n = list.length;
  if (n === 1 && !list[0].level) {
    return <CornerCheckScreen img={list[0]} project={project} stud={stud} studAssumed={studAssumed} initial={initial}
      readOnly={readOnly} onSave={onSave} onSkip={onSkip}/>;
  }
  const at = Math.min(i, n - 1);
  const pic = list[at];
  const floors = pbFloors(project);
  const fl = floors.find(f => f.key && f.key === pic.level) || null;
  // a confirmed stud is for the floors whose stud the plans don't state
  const allAssumed = floors.length > 0 && floors.every(f => f.studAssumed);
  const floorStud = studConfirmed && (!fl || fl.studAssumed || allAssumed) ? stud : ((fl && fl.stud) || stud);
  // every-storey round 2: a measured floor with no picture can't be checked
  // here — say so, with PlanBoss's count for it (review, 14 Oct)
  const missing = floors.filter(f => f.key && !list.some(p => p.level === f.key));
  const prev = initial && Array.isArray(initial.levels) ? initial.levels.find(l => l && l.key === pic.level) || null : null;
  const name = pbTxt(pic.level_name, 40) || (fl && fl.name) || ("Floor " + (at + 1));
  const last = at + 1 >= n;
  const next = (res) => {
    const acc = done.concat(res ? [Object.assign({}, res, { key: pic.level || null, name, stud: floorStud })] : []);
    if (!last) { setDone(acc); setI(at + 1); return; }
    if (readOnly) { onSkip(); return; }
    const levels = acc.map(l => ({ key: l.key, name: l.name, ai: l.ai, count: l.count, removed: l.removed, added: l.added,
      img: l.img, stud: l.stud }));
    onSave({ v: 2, ai: levels.reduce((t, l) => t + (l.ai || 0), 0), count: levels.reduce((t, l) => t + (l.count || 0), 0),
      levels, engine: (acc[0] && acc[0].engine) || null, at: Date.now() });
  };
  return <CornerCheckScreen key={"floor" + at} img={pic} project={project} stud={floorStud}
    studAssumed={!studConfirmed && !!(fl ? fl.studAssumed : studAssumed)} initial={prev} readOnly={readOnly}
    floor={{ name, i: at, n, missing }} onSave={(res) => next(res)} onSkip={readOnly && !last ? () => next(null) : onSkip}/>;
}

function CornerCheckScreen({img, project, stud, studAssumed, initial, readOnly, onSave, onSkip, floor}) {
  const sites = Array.isArray(img && img.sites) ? img.sites : [];
  const W = Number(img && img.w) || 1, H = Number(img && img.h) || 1;
  const sameImg = initial && initial.img && initial.img.w === img.w && initial.img.h === img.h;
  const [removed, setRemoved] = useState(() => new Set((initial && Array.isArray(initial.removed)) ? initial.removed : []));
  const [added, setAdded] = useState(() => (sameImg && Array.isArray(initial.added)) ? initial.added.filter(a => a && typeof a.ix === "number") : []);
  const [adding, setAdding] = useState(false);
  const [zi, setZi] = useState(0);
  const [outline, setOutline] = useState(true);
  const boxRef = useRef(null);
  const pending = useRef(null);
  // How big the picture is on screen decides how big the markers are: a whole
  // house on a phone at 1x is a few hundred pixels across, where full-size
  // numbered rings would sit on top of each other.
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const upd = () => setBoxW(el.clientWidth || 0);
    upd();
    window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);
  const ai = sites.reduce((t, x) => t + (x.count === 2 ? 2 : 1), 0);
  const count = sites.filter(x => !removed.has(x.id)).reduce((t, x) => t + (x.count === 2 ? 2 : 1), 0)
    + added.reduce((t, a) => t + (a.c === 2 ? 2 : 1), 0);
  const changed = removed.size > 0 || added.length > 0;
  const unmeasured = Array.isArray(img && img.unmeasured) ? img.unmeasured : [];
  const rings = Array.isArray(img && img.outline) ? img.outline : [];
  const st = Number(stud) || null;
  const zoom = PB_CC_ZOOMS[zi];
  const shownW = boxW * zoom;                              // 0 = not laid out (tests): full size
  const dense = shownW > 0 && shownW < 640;               // whole house in < 640 px: dots, no numbers
  const mk = shownW === 0 || shownW >= 1000 ? 30 : dense ? (shownW < 480 ? 14 : 18) : 24;   // marker size, px
  // keep the middle of the view in the middle when zooming
  const setZoomKeep = (nz) => {
    const el = boxRef.current;
    if (el) pending.current = { cx: (el.scrollLeft + el.clientWidth / 2) / Math.max(1, el.scrollWidth), cy: (el.scrollTop + el.clientHeight / 2) / Math.max(1, el.scrollHeight) };
    setZi(nz);
  };
  useEffect(() => {
    const el = boxRef.current, p = pending.current;
    if (!el || !p) return;
    pending.current = null;
    el.scrollLeft = Math.max(0, p.cx * el.scrollWidth - el.clientWidth / 2);
    el.scrollTop = Math.max(0, p.cy * el.scrollHeight - el.clientHeight / 2);
  }, [zi]);
  const toggle = (id) => { if (readOnly) return; setRemoved(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const cycleAdded = (i) => { if (readOnly) return; setAdded(list => list.flatMap((a, j) => j !== i ? [a] : (a.c === 2 ? [] : [{ ...a, c: 2 }]))); };
  const tapPlan = (e) => {
    if (!adding || readOnly) return;
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const ix = Math.round((e.clientX - r.left) / r.width * W * 10) / 10;
    const iy = Math.round((e.clientY - r.top) / r.height * H * 10) / 10;
    const m = Array.isArray(img.px_to_page) && img.px_to_page.length === 6 ? img.px_to_page.map(Number) : null;
    const x = m ? Math.round((ix * m[0] + iy * m[2] + m[4]) * 10) / 10 : null;
    const y = m ? Math.round((ix * m[1] + iy * m[3] + m[5]) * 10) / 10 : null;
    setAdded(list => list.concat([{ ix, iy, x, y, c: 1 }]).slice(0, 200));
  };
  const save = () => onSave({ v: 1, ai, count, removed: Array.from(removed).sort((a, b) => a - b), added,
    img: { w: W, h: H }, engine: (img && img.version) || null, at: Date.now() });
  const ring = (colour, dashed) => ({position:"absolute",width:mk,height:mk,borderRadius:mk/2,border:`${mk<20?2:3}px ${dashed?"dashed":"solid"} ${colour}`,
    background:"transparent",transform:"translate(-50%,-50%)",padding:0,cursor:readOnly?"default":"pointer",boxShadow:"0 0 0 1px rgba(255,255,255,0.9)",boxSizing:"border-box"});
  const badge = (bg) => ({position:"absolute",left:Math.round(mk*0.6),top:-12,minWidth:16,height:16,borderRadius:8,background:bg,color:"#fff",fontSize:10,fontWeight:800,
    lineHeight:"16px",padding:"0 4px",whiteSpace:"nowrap",pointerEvents:"none",display:dense?"none":"block"});
  return (
    <div className="pb-cc" style={{padding:24,maxWidth:980,margin:"0 auto"}}>
      <h1 style={{fontSize:22,fontWeight:800,color:C.ink,margin:"0 0 6px"}}>{readOnly ? "What was measured" : "Check the external corners"}</h1>
      {floor&&<div data-pb-floor={floor.i+1} style={{fontSize:14,fontWeight:800,color:C.tealMid,margin:"-2px 0 8px"}}>{floor.name} · floor {floor.i+1} of {floor.n}</div>}
      <div style={{fontSize:13,color:C.inkMid,lineHeight:1.55,marginBottom:12}}>
        {readOnly
          ? <>The green line is the building PlanBoss measured{unmeasured.length?<>; the <strong style={{color:C.red}}>red</strong> boxes are walls on the plan it didn't take in</>:null}.</>
          : <>PlanBoss counted <strong>{ai}</strong> external corner{ai===1?"":"s"} at <strong>{sites.length}</strong> spot{sites.length===1?"":"s"} on {floor ? (/^level\s*\d/i.test(floor.name) ? <><strong>{floor.name}</strong> of </> : <>the <strong>{floor.name.toLowerCase()}</strong> of </>) : null}<strong>{pbTxt(project && project.name, 80) || "this plan"}</strong> (a wall end counts 2). Tap a spot to take it off. Missed one? Tap <strong>Add</strong>, then tap the plan where it is.{st&&studAssumed?<> The stud height ({st} m) is assumed — you can set it on the next screen.</>:null}</>}
      </div>
      {!readOnly&&pbCornersSuspect(project)&&(
        <div data-pb-cc-suspect="1" style={{background:C.amberFaint,border:`1px solid ${C.amber}55`,color:"#7C2D12",borderRadius:10,padding:"8px 12px",fontSize:12,lineHeight:1.5,marginBottom:12}}>
          ⚠ PlanBoss isn't confident in this count ({pbTxt(pbCornersSuspect(project), 160).replace(/\.$/, "")}). Check every marked spot — take off any that aren't external corners and add any it missed — before you save.
        </div>
      )}
      {floor&&Array.isArray(floor.missing)&&floor.missing.length>0&&(
        <div data-pb-nopic="1" style={{background:C.amberFaint,border:`1px solid ${C.amber}55`,color:"#7C2D12",borderRadius:10,padding:"8px 12px",fontSize:12,lineHeight:1.5,marginBottom:12}}>
          No check picture for {floor.missing.map(f => pbFloorRef(f.name)).join(" or ")} — {floor.missing.map(f => (f.count == null ? "an unknown number of" : f.count) + " corner" + (f.count === 1 ? "" : "s") + " on " + pbFloorRef(f.name)).join(", ")} {floor.missing.length > 1 ? "are PlanBoss's counts" : "is PlanBoss's count"}, not checked by you.
        </div>
      )}
      {unmeasured.length>0&&(
        <div role="alert" style={{background:C.redLight,border:`1px solid ${C.red}55`,color:"#7F1D1D",borderRadius:10,padding:"10px 14px",fontSize:13,lineHeight:1.5,marginBottom:12}}>
          <strong>Part of this house wasn't measured</strong> (the red area, and anything outside the green line). Its corners aren't counted here, and its walls and ceilings are missing from the numbers.
        </div>
      )}
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap",marginBottom:8}}>
        <button aria-label="Zoom out" onClick={()=>setZoomKeep(Math.max(0, zi-1))} disabled={zi===0} style={{width:40,height:36,borderRadius:8,border:`1px solid ${C.border}`,background:"#fff",fontSize:18,fontWeight:700,cursor:zi===0?"default":"pointer",opacity:zi===0?0.4:1}}>−</button>
        <span style={{fontSize:12,color:C.inkMid,minWidth:34,textAlign:"center"}}>{zoom}×</span>
        <button aria-label="Zoom in" onClick={()=>setZoomKeep(Math.min(PB_CC_ZOOMS.length-1, zi+1))} disabled={zi===PB_CC_ZOOMS.length-1} style={{width:40,height:36,borderRadius:8,border:`1px solid ${C.border}`,background:"#fff",fontSize:18,fontWeight:700,cursor:"pointer",opacity:zi===PB_CC_ZOOMS.length-1?0.4:1}}>+</button>
        {rings.length>0&&<label style={{fontSize:12,color:C.inkMid,display:"flex",alignItems:"center",gap:5,marginLeft:6}}><input type="checkbox" checked={outline} onChange={e=>setOutline(e.target.checked)}/> Measured outline</label>}
        {dense&&<span style={{fontSize:12,color:C.amber,fontWeight:600}}>Zoom in (＋) to see each corner's number and tap it.</span>}
        {!readOnly&&<button onClick={()=>setAdding(a=>!a)} aria-pressed={adding} style={{marginLeft:"auto",padding:"8px 14px",borderRadius:8,border:`1.5px solid ${adding?"#15803D":C.border}`,background:adding?"#DCFCE7":"#fff",color:adding?"#15803D":C.ink,fontSize:13,fontWeight:700,cursor:"pointer"}}>{adding?"Adding — tap the plan (tap here when done)":"＋ Add a missed corner"}</button>}
      </div>
      <div ref={boxRef} className="pb-cc-pic" style={{overflow:"auto",maxHeight:"64vh",border:`1px solid ${C.border}`,borderRadius:12,background:"#fff",WebkitOverflowScrolling:"touch"}}>
        <div onClick={tapPlan} style={{position:"relative",width:(zoom*100)+"%",cursor:adding?"crosshair":"default"}}>
          <img src={"data:" + ((img && img.mime) || "image/png") + ";base64," + ((img && img.b64) || "")} alt="The floor plan with the external corners PlanBoss counted" draggable={false}
            style={{display:"block",width:"100%",height:"auto",userSelect:"none"}}/>
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{position:"absolute",inset:0,width:"100%",height:"100%",pointerEvents:"none"}}>
            {outline&&rings.map((r,i)=>Array.isArray(r)&&r.length>2?<polygon key={"o"+i} points={r.map(p=>p[0]+","+p[1]).join(" ")} fill="rgba(22,163,74,0.08)" stroke="#16A34A" strokeWidth="2" vectorEffect="non-scaling-stroke"/>:null)}
            {unmeasured.map((b,i)=>Array.isArray(b)&&b.length===4?<rect key={"u"+i} x={b[0]} y={b[1]} width={Math.max(0,b[2]-b[0])} height={Math.max(0,b[3]-b[1])} fill="rgba(220,38,38,0.14)" stroke="#DC2626" strokeWidth="2" strokeDasharray="6 4" vectorEffect="non-scaling-stroke"/>:null)}
          </svg>
          {sites.map(x=>{ const off = removed.has(x.id); const two = x.count === 2;
            return (
              <button key={"s"+x.id} data-pb-site={x.id} onClick={(e)=>{e.stopPropagation();toggle(x.id);}}
                aria-label={"Corner "+x.id+(two?" (wall end, counts 2)":"")+(off?" — taken off, tap to put back":" — tap to take off")}
                style={{...ring(off?"#9CA3AF":two?"#DC2626":"#EA580C", off),left:(x.ix/W*100)+"%",top:(x.iy/H*100)+"%"}}>
                <span style={badge(off?"#6B7280":two?"#DC2626":"#EA580C")}>{off?"✕":(x.id+(two?" ×2":""))}</span>
              </button>
            ); })}
          {added.map((a,i)=>(
            <button key={"a"+i} data-pb-added={i} onClick={(e)=>{e.stopPropagation();cycleAdded(i);}}
              aria-label={"Corner you added"+(a.c===2?" (counts 2)":"")+" — tap to make it count 2, tap again to remove"}
              style={{...ring("#16A34A"),left:(a.ix/W*100)+"%",top:(a.iy/H*100)+"%"}}>
              <span style={badge("#16A34A")}>{a.c===2?"+2":"+1"}</span>
            </button>
          ))}
        </div>
      </div>
      <div style={{fontSize:11,color:C.inkLight,margin:"8px 0 12px",lineHeight:1.5}}>
        <span style={{color:"#EA580C",fontWeight:700}}>◯</span> corner, counts 1 · <span style={{color:"#DC2626",fontWeight:700}}>◯ ×2</span> wall end, counts 2 · <span style={{color:"#16A34A",fontWeight:700}}>◯ +1</span> one you added (tap it again to make it 2, a third time to remove)
        {rings.length>0&&<> · <span style={{color:"#16A34A",fontWeight:700}}>green line</span> = what was measured</>}
      </div>
      {/* On a phone this bar is pinned above the bottom nav in ONE row (mobile.css .pb-cc-*): it must not cover the plan. */}
      <div className="pb-cc-bar" style={{background:"#fff",borderTop:`1px solid ${C.border}`,padding:"12px 0",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div className="pb-cc-sum" style={{flex:"1 1 180px"}}>
          <div className="pb-cc-count" style={{fontSize:20,fontWeight:800,color:C.ink}}>{count} external corner{count===1?"":"s"}{changed&&!readOnly?<span style={{fontSize:12,fontWeight:600,color:C.amber}}> (PlanBoss said {ai})</span>:null}</div>
          {st&&<div className="pb-cc-stud" style={{fontSize:12,color:C.inkMid}}>× {st} m stud{studAssumed?" (assumed)":""} = <strong>{(Math.round(count*st*100)/100).toLocaleString("en-NZ")} lm</strong></div>}
        </div>
        {readOnly
          ?<button className="pb-cc-btn" onClick={onSkip} style={{padding:"12px 20px",background:C.tealMid,color:"#fff",border:"none",borderRadius:10,fontSize:15,fontWeight:700,cursor:"pointer"}}>{floor && floor.i + 1 < floor.n ? "Next floor →" : "Done"}</button>
          :<>
            <button className="pb-cc-btn" onClick={onSkip} style={{padding:"12px 16px",background:"transparent",color:C.inkMid,border:`1px solid ${C.border}`,borderRadius:10,fontSize:14,fontWeight:600,cursor:"pointer"}}>Skip for now</button>
            <button className="pb-cc-btn" onClick={save} style={{padding:"12px 20px",background:changed?C.orange:"#15803D",color:"#fff",border:"none",borderRadius:10,fontSize:15,fontWeight:800,cursor:"pointer"}}>{(changed ? "Save — " + count + " corner" + (count===1?"":"s") : unmeasured.length ? "These corners look right ✓" : "Looks right ✓") + (floor && floor.i + 1 < floor.n ? " → next floor" : "")}</button>
          </>}
      </div>
    </div>
  );
}

function TradeMetricsDisplay({trade, project, prefs, rough, partial, extraFloors, wholeBldg, onCheckCorners, canCheckCorners}) {
  const tm = TRADE_METRICS[trade.id];
  // stud-confirm-v1: hooks must run before any early return.
  const _geoRecTop = project.aiAnalysis && project.aiAnalysis.geometry && project.aiAnalysis.geometry.reconciled;
  const [studConfirmed, setStudConfirmed] = useState(() => getConfirmedStud(project.id));
  const [studDraft, setStudDraft] = useState('');
  const [pitchConfirmed, setPitchConfirmed] = useState(() => getConfirmedPitch(project.id));
  const [pitchDraft, setPitchDraft] = useState('');
  if(!tm) return null;
  // plancheck-v1-2026-10-13: the external-corner check, if the tradie did it
  const _cc = pbGetCornerCheck(project.id);
  const _ccSites = pbCornerSites(project);
  const _ccEngine = _ccSites ? _ccSites.reduce((t, x) => t + (x && x.count === 2 ? 2 : 1), 0) : null;
  // ---- floorsource-v1-2026-07-27 — floor area from the wall model ----
  // Vision stores the printed over-slab figure as project.area (Denby:
  // 264.12 against a lined ~231) and it is not repeatable — 145, 256.29,
  // 264.12 across three runs of the same plan. Geometry sums the enclosed
  // room polygons and returned 229.66 every time. A typed override still
  // beats both: the tradie is standing in the building.
  const _pbGeoFloor = (p) => { const f = p && p.aiAnalysis && p.aiAnalysis.geometry && p.aiAnalysis.geometry.reconciled && p.aiAnalysis.geometry.reconciled.all && p.aiAnalysis.geometry.reconciled.all.floor_m2; return (f && f.value != null && f.conf !== 'flag') ? Math.round(f.value * 100) / 100 : null; };
  // plancheck-v1-2026-10-13: no made-up 145 m2 when no floor area was read.
  const floorArea = project.tuning?.floorAreaOverride || _pbGeoFloor(project) || project.area || null;
  const metricPrefs = prefs?.tradeMetrics?.[trade.id] || {};

  // (The old "simulate derived metrics from floor area" block is gone: its
  // rules of thumb - walls = floor x 2.8 - were never shown after 12 Oct and
  // only invited a guess back in.)

  // Real plan totals from the AI analysis (summed across rooms); null -> "—"
  // for anything the scan didn't read.
  const _aiRooms = Array.isArray(project.aiAnalysis?.rooms) ? project.aiAnalysis.rooms : null;
  const _sum = (key) => {
    if (!_aiRooms) return null;
    if (!_aiRooms.some(r => r && r[key] !== undefined && r[key] !== null)) return null;
    return _aiRooms.reduce((s, r) => s + (Number(r[key]) || 0), 0);
  };
  const _realByMetric = {
    floor_area:      _sum('floorM2'),
    wall_area:       _sum('wallM2Net'),
    ceiling_area:    _sum('ceilingM2'),
    square_stop:     _sum('perimeterLm'),
    external_corner: _sum('convexCornerLm'),
    internal_corner: _sum('concaveCornerLm'),
  };
  const metricVal = (id) => {
    // ---- wirefloor-v1-2026-07-27 -----------------------------------------
    // Floor comes from the wall model, not from vision.
    //
    // Vision reads the over-slab area printed on the drawing and reports it
    // as floor area (Denby: 264.12 against a lined ~231). It is also not
    // repeatable — 145, then 256.29, then 264.12 on three runs of the same
    // plan. Geometry sums the enclosed room polygons and returned the same
    // figure every time.
    //
    // Amber is accepted here on purpose. Amber means "measured, with a
    // stated reason to check the calibration" — still strictly better than
    // an unmeasured guess. Only `flag` (the engine could not do it) falls
    // back to vision.
    const _pbGeo = project.aiAnalysis && project.aiAnalysis.geometry &&
      project.aiAnalysis.geometry.reconciled && project.aiAnalysis.geometry.reconciled.all;
    if (_pbGeo && id === "floor_area" && _pbGeo.floor_m2 &&
        _pbGeo.floor_m2.value != null && _pbGeo.floor_m2.conf !== "flag") {
      return Math.round(_pbGeo.floor_m2.value);
    }
    // ---- end wirefloor-v1-2026-07-27 -------------------------------------
    // ---- stopping-tiles-v1-2026-09-23 ----
    // Square stop, walls and externals from the door-true segmentation
    // (engine metrics-v2-2026-09-23). Denby -0.91% / Longview +0.78% on square
    // stop, externals 16/16 and 7/7 against the Groundplan take-offs.
    // The v2 envelope keeps the stud under calibration.stud_m, not at the top
    // level, so reconciled.stud_m is null and the stud ratio silently came out
    // as 1. Read it from where the engine actually puts it.
    if (trade.id === 'plasterer' && _pbGeo) {
      const _ok = (f) => f && f.value != null && f.conf !== 'flag';
      const _env = project.aiAnalysis && project.aiAnalysis.geometry;
      const _cal = _env && _env.calibration && _env.calibration.stud_m;
      const _need = _geoRecTop && Array.isArray(_geoRecTop.needs_confirmation)
        ? _geoRecTop.needs_confirmation.find(n => n && n.field === 'stud_m') : null;
      const _assumed = Number(_geoRecTop && _geoRecTop.stud_m) || Number(_cal && _cal.value) ||
        Number(_need && _need.value) || null;
      const _stud = Number(studConfirmed) || _assumed;
      if (id === 'square_stop' && _ok(_pbGeo.square_stop_lm)) {
        return Math.round(_pbGeo.square_stop_lm.value);
      }
      // every-storey round 2: a multi-storey job's walls and corners floor
      // by floor (each floor's own stud, its own checked corners)
      const _ft = (id === 'wall_area' || id === 'external_corner') && _ok(_pbGeo.square_stop_lm)
        ? pbFloorTotals(project, Number(studConfirmed) || null, _cc) : null;
      if (_ft) {
        const v = id === 'wall_area' ? _ft.walls : _ft.extLm;
        return v == null ? null : Math.round(v);
      }
      // Walls and externals are square stop / corner count x stud. When the
      // stud is only assumed the engine grades them `flag`, but the prompt
      // above the tiles already says so; showing them at the assumed stud
      // beats falling back to vision's unrelated figures.
      // every-storey-v1-2026-10-14: floors with different stud heights are
      // worked out floor by floor by the engine (each floor's square stop x
      // its own stud); a stud the tradie confirms applies to every floor.
      const _floorsDiffer = pbStudsDiffer(project);
      if (id === 'wall_area' && _ok(_pbGeo.square_stop_lm) && _stud) {
        if (_floorsDiffer && !Number(studConfirmed) && _pbGeo.walls_m2 && _pbGeo.walls_m2.value != null) {
          return Math.round(_pbGeo.walls_m2.value);
        }
        // openings-net-v1: NET, the Groundplan convention.
        return Math.round(_pbGeo.square_stop_lm.value * _stud - pbOpeningsM2(project));
      }
      // Ceiling: geometry's free-space ceiling (flat). Vision's ceilingPlaneM2
      // read 258 on Denby against Groundplan 239.40 (+7.8%); geometry is 235.07
      // flat (-1.8%), and the 20 deg rake on the living piece is the rest.
      if (id === 'ceiling_area' && _ok(_pbGeo.ceiling_m2)) {
        // pitch-confirm-v1: raked area x (1/cos(pitch) - 1) once answered.
        return Math.round(_pbGeo.ceiling_m2.value + pbRakeExtraM2(project, pitchConfirmed));
      }
      const _ext = _pbGeo.external_corners_lm;
      // plancheck-v1-2026-10-13: the count the tradie agreed on the corner
      // check picture beats the engine's.
      if (id === 'external_corner' && _cc && _ok(_pbGeo.square_stop_lm) && _stud) {
        const _lm = pbCornerLm(_cc, Number(studConfirmed) || null, _stud);
        return _lm == null ? null : Math.round(_lm);
      }
      if (id === 'external_corner' && _ok(_pbGeo.square_stop_lm) && _ext && _ext.value != null && _assumed && _stud) {
        const _extF = _env && _env.fields && _env.fields.external_corners_lm;
        if (_floorsDiffer && !Number(studConfirmed)) return Math.round(_ext.value);
        if (_floorsDiffer && _extF && typeof _extF.count === 'number') return Math.round(_extF.count * _stud);
        return Math.round(_ext.value / _assumed * _stud);
      }
    }
    // ---- end stopping-tiles-v1 ----
    // __ceilingPlaneOverride: the PLASTERER ceiling tile reflects the continuous
    // ceiling plane (over partitions) the AI emits as top-level ceilingPlaneM2,
    // not the per-room ceilingM2 sum (which omits the over-partition strips).
    // Gated to plasterer; other trades (e.g. insulation) keep the room-sum lens.
    // Falls back to the room-sum if ceilingPlaneM2 is absent (old projects).
    if (id === 'ceiling_area' && trade.id === 'plasterer') {
      const plane = Number(project.aiAnalysis && project.aiAnalysis.ceilingPlaneM2);
      if (!Number.isNaN(plane) && plane > 0) return Math.round(plane);
    }
    // tiles-geo-v1-2026-07-06: geometry-reconciled values (RECONCILIATION_CONTRACT_v1).
    // Server sets reconciled.* to null unless that metric graded GREEN, so a
    // null-check here IS the contract check. Anything else falls through to
    // today's vision path untouched.
    const _geoRec = project.aiAnalysis && project.aiAnalysis.geometry && project.aiAnalysis.geometry.reconciled;
    if (_geoRec) {
      // stud-through-v1-2026-08-11: a confirmed stud is applied to the
      // WHOLE envelope by ratio, so walls and externals move together and
      // stay in the engine's own GROSS convention. The old path recomputed
      // `square stop x stud - openings`, which silently turned the tile net
      // the moment the tradie answered — the number moved for a reason that
      // had nothing to do with the stud.
      const _geoStud = studConfirmed ? pbApplyStud(_geoRec, studConfirmed) : _geoRec;
      if (id === 'wall_area' && _geoStud && _geoStud.walls_m2 != null) {
        return Math.round(_geoStud.walls_m2);
      }
      if (id === 'external_corner' && _geoStud && _geoStud.external_corners_lm != null) {
        return Math.round(_geoStud.external_corners_lm);
      }
      if (id === 'wall_area' && _geoRec.walls_m2 != null) return Math.round(_geoRec.walls_m2);
      if (id === 'square_stop' && _geoRec.square_stop_lm != null) return Math.round(_geoRec.square_stop_lm);
    }
    const r = _realByMetric[id];
    if (r !== null && r !== undefined && !Number.isNaN(r)) return Math.round(r);
    // security-v1-2026-10-12: no simulated fallback. The `derived` figures
    // above are rules of thumb off the floor area (walls = floor x 2.8), and
    // showing them in a tile made a guess look like a measurement. A metric
    // the scan didn't measure now shows "—".
    return null;
  };

  // Filter out metrics the user has unticked in preferences
  const hiddenMetrics = metricPrefs.hiddenMetrics || [];
  const visibleMetrics = tm.metrics.filter(m => !hiddenMetrics.includes(m.id));
  if(visibleMetrics.length === 0) return null;

  return (
    <div style={{background:`${trade.color}08`,border:`1.5px solid ${trade.color}33`,borderRadius:14,padding:"16px 20px",marginBottom:20}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:16}}>{trade.icon}</span>
          <span style={{fontSize:13,fontWeight:700,color:trade.color}}>Key measurements</span>
        </div>
        <span style={{fontSize:10,color:rough||partial||extraFloors||wholeBldg?C.amber:C.inkLight,fontStyle:"italic",fontWeight:rough||partial||extraFloors||wholeBldg?700:400}}>{rough ? "Rough AI read — not measured" : partial ? "Part of the house only — these are short" : extraFloors ? "More floors than the drawings show — check them" : wholeBldg ? "Whole building — this plan is an alteration" : "Check against the plan before quoting"}</span>
      </div>
      {(() => {
        // stud-confirm-v1: shown only when the plan itself does not state a
        // stud height. stud-ask-v2-2026-10-23: a question with nothing
        // pre-picked, the AI's own read offered, and once answered a line
        // saying so with "change".
        const needs = _geoRecTop && Array.isArray(_geoRecTop.needs_confirmation)
          ? _geoRecTop.needs_confirmation.find(n => n && n.field === 'stud_m') : null;
        // plancheck-v1: a rough (by-eye) read doesn't use the stud, so don't ask
        if (rough) return null;
        if (!needs) {
          // stud-ask-v2: when the plan states the stud, say so (Grant, 21 Oct:
          // "it didn't ask me for the stud height" - South Road prints 2.57)
          const cal = project.aiAnalysis && project.aiAnalysis.geometry && project.aiAnalysis.geometry.calibration &&
            project.aiAnalysis.geometry.calibration.stud_m;
          const read = cal && cal.source === 'read' ? Number(cal.value) : null;
          if (!(read > 0) || studConfirmed || pbFloors(project).length >= 2) return null;
          return <div style={{fontSize:10,color:C.inkMid,marginBottom:8}}>Walls at a {read} m stud — stated on the plan</div>;
        }
        const assumed = Number(_geoRecTop.stud_m) || Number(needs.value) || 2.4;
        const accept = (v) => { setConfirmedStud(project.id, v); setStudConfirmed(v); setStudDraft(''); };
        // every-storey round 2: when the plans state some floors' studs but
        // not others', the answer is for the floors without one only
        const fls = Array.isArray(needs.levels) ? needs.levels.map(x => pbTxt(x, 40)).filter(Boolean) : [];
        const some = pbFloors(project).length >= 2 && fls.length > 0 && needs.every_level === false;
        if (studConfirmed) return (
          <div style={{fontSize:10,color:C.inkMid,marginBottom:8}}>
            Walls at a {studConfirmed} m stud{some ? ` on the ${fls.join(" and ").toLowerCase()}` : ""} — your answer
            {' · '}<span onClick={()=>{ setConfirmedStud(project.id, null); setStudConfirmed(null); }}
              style={{textDecoration:"underline",cursor:"pointer"}}>change</span>
          </div>
        );
        const ai = pbAiStud(project);
        const studBtn = {fontSize:12,fontWeight:700,padding:"6px 12px",borderRadius:8,border:"1px solid #fcd34d",background:"#fff",color:"#92400e",cursor:"pointer"};
        return (
          <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:10,padding:"10px 12px",marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:2}}>What's the stud height?</div>
            <div style={{fontSize:11,color:"#92400e",marginBottom:8,lineHeight:1.4}}>
              {some
                ? <>The plan doesn't state a stud height for the <strong>{fls.join(" and ").toLowerCase()}</strong>, and wall area scales directly with it. Until you answer, {fls.length > 1 ? "those floors are" : "that floor is"} worked out at {assumed} m. Your answer is used for {fls.length > 1 ? "those floors" : "that floor"} only — the other floors keep the height the plan states.</>
                : <>The plan doesn't state one, and wall area scales directly with it. Until you answer, walls are worked out at {assumed} m.{pbFloors(project).length >= 2 ? " Your answer is used for every floor." : ""}</>}
              {ai != null ? <> The AI read <strong>{ai} m</strong> off the drawings — check it against the sections.</> : null}
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
              {ai != null && [2.4,2.55,2.7].indexOf(ai) === -1 ? (
                <button onClick={()=>accept(ai)} style={studBtn}>{ai} m (AI's read)</button>
              ) : null}
              {[2.4,2.55,2.7].map(v => (
                <button key={v} onClick={()=>accept(v)} style={studBtn}>
                  {v} m{ai === v ? " (AI's read)" : ""}
                </button>
              ))}
              <input value={studDraft} onChange={e=>setStudDraft(e.target.value)}
                placeholder="other (m)" inputMode="decimal"
                style={{fontSize:12,padding:"6px 8px",borderRadius:8,border:"1px solid #fcd34d",width:86}} />
              <button onClick={()=>{const v=parseFloat(studDraft); if(v>=2.1&&v<=3.6) accept(v);}}
                style={{fontSize:12,fontWeight:700,padding:"6px 12px",borderRadius:8,border:"none",background:"#92400e",color:"#fff",cursor:"pointer"}}>
                Use
              </button>
            </div>
          </div>
        );
      })()}
      {(() => {
        // pitch-confirm-v1: the plan shows a raked ceiling but not its angle.
        if (trade.id !== 'plasterer') return null;
        const needs = _geoRecTop && Array.isArray(_geoRecTop.needs_confirmation)
          ? _geoRecTop.needs_confirmation.find(n => n && n.field === 'ceiling_pitch_deg') : null;
        if (!needs || pitchConfirmed != null || rough) return null;
        const raked = Math.round(pbRakedM2(project) * 10) / 10;
        const accept = (v) => { setConfirmedPitch(project.id, v); setPitchConfirmed(v); };
        const btn = {fontSize:12,fontWeight:700,padding:"6px 12px",borderRadius:8,border:"1px solid #fcd34d",background:"#fff",color:"#92400e",cursor:"pointer"};
        return (
          <div style={{background:"#fffbeb",border:"1px solid #fcd34d",borderRadius:10,padding:"10px 12px",marginBottom:10}}>
            <div style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:2}}>Raked ceiling — what's the pitch?</div>
            <div style={{fontSize:11,color:"#92400e",marginBottom:8,lineHeight:1.4}}>
              The plan shows a raked ceiling over <strong>{raked} m²</strong>. Ceiling area counts it flat until you set the pitch.
            </div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
              {[15,20,25,30].map(v => (
                <button key={v} onClick={()=>accept(v)} style={btn}>{v}°</button>
              ))}
              <button onClick={()=>accept(0)} style={btn}>Flat</button>
              <input value={pitchDraft} onChange={e=>setPitchDraft(e.target.value)}
                placeholder="other (°)" inputMode="decimal"
                style={{fontSize:12,padding:"6px 8px",borderRadius:8,border:"1px solid #fcd34d",width:86}} />
              <button onClick={()=>{const v=parseFloat(pitchDraft); if(v>=0&&v<60) accept(v);}}
                style={{fontSize:12,fontWeight:700,padding:"6px 12px",borderRadius:8,border:"none",background:"#92400e",color:"#fff",cursor:"pointer"}}>
                Use
              </button>
            </div>
          </div>
        );
      })()}
      <div className="mu-key-metrics" style={{display:"grid",gridTemplateColumns:`repeat(auto-fit,minmax(96px,1fr))`,gap:10,marginBottom:10}}>
        {trade.id !== "plasterer" && /* plasterer-nofloor-v1-2026-09-23 */ (
        <div key="floor_area_tile" style={{background:"rgba(255,255,255,0.7)",borderRadius:8,padding:"10px 12px",textAlign:"center"}}>
          <div style={{fontSize:22,fontWeight:800,color:rough?C.inkMid:trade.color,letterSpacing:"-0.5px"}}>{floorArea == null ? "—" : (rough ? "~" : "") + floorArea.toLocaleString("en-NZ")}</div>
          <div style={{fontSize:11,fontWeight:600,color:C.inkMid,marginTop:2}}>m²</div>
          <div style={{fontSize:10,color:C.inkLight,marginTop:1,lineHeight:1.3}}>Floor{_pbGeoFloor(project) != null ? <span title="Measured by the geometry engine" style={{display:"inline-block",width:6,height:6,borderRadius:3,background:"#16a34a",marginLeft:4,verticalAlign:"middle"}}></span> : null}</div>
        </div>
        )}
        {visibleMetrics.map(m=>(
          <div key={m.id} style={{background:"rgba(255,255,255,0.7)",borderRadius:8,padding:"10px 12px",textAlign:"center"}}>
            <div style={{fontSize:22,fontWeight:800,color:rough?C.inkMid:trade.color,letterSpacing:"-0.5px"}}>{(() => { const v = metricVal(m.id); return v === null ? '—' : (rough ? "~" : "") + v.toLocaleString("en-NZ"); })()}</div>
            <div style={{fontSize:11,fontWeight:600,color:C.inkMid,marginTop:2}}>{m.unit}</div>
            <div style={{fontSize:10,color:C.inkLight,marginTop:1,lineHeight:1.3}}>{m.label}{(() => { const g = project.aiAnalysis && project.aiAnalysis.geometry && project.aiAnalysis.geometry.reconciled; const a = g && g.all; const okf = (f) => f && f.value != null && f.conf !== 'flag'; const geo = trade.id === 'plasterer' && a && okf(a.square_stop_lm) && (m.id === 'square_stop' || m.id === 'wall_area' || (m.id === 'ceiling_area' && okf(a.ceiling_m2)) || (m.id === 'external_corner' && a.external_corners_lm && a.external_corners_lm.value != null && !pbCornersSuspect(project))); const hit = geo || (g && ((m.id === 'wall_area' && (g.walls_m2 != null || (studConfirmed && g.square_stop_lm_raw != null))) || (m.id === 'square_stop' && g.square_stop_lm != null))); return hit ? <span title="Cross-checked by geometry engine" style={{display:"inline-block",width:6,height:6,borderRadius:3,background:"#16a34a",marginLeft:4,verticalAlign:"middle"}}></span> : null; })()}</div>
          </div>
        ))}
      </div>
      {trade.id === 'plasterer' && _ccSites && !rough && (() => {
        // plancheck-v1-2026-10-13: has a person checked the externals?
        // every-storey round 2: on a multi-storey job, floors the check
        // didn't cover (no picture) are counted and said so.
        const ft = pbFloorTotals(project, Number(studConfirmed) || null, _cc);
        const unchecked = ft && _cc ? ft.rows.filter(r => !r.checked) : [];
        const n = ft && ft.extCount != null ? ft.extCount : (_cc ? _cc.count : _ccEngine);
        const partly = _cc && unchecked.length > 0;
        return (
          <div data-pb-corners={_cc ? (partly ? "partly" : "checked") : "unchecked"} style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",fontSize:11,marginTop:2,marginBottom:6,color:_cc&&!partly?"#15803D":C.amber}}>
            <span>{_cc
              ? (partly
                ? <>⚠ {n} external corner{n===1?"":"s"} — {_cc.count} checked by you; {unchecked.map(r => (r.engineCount == null ? "?" : r.engineCount) + " on " + pbFloorRef(r.name)).join(", ")} not checked (no picture)</>
                : <>✓ {n} external corner{n===1?"":"s"} — checked by you{_cc.ai!=null&&_cc.ai!==_cc.count?<> (PlanBoss counted {_cc.ai})</>:null}</>)
              : <>⚠ {n} external corner{n===1?"":"s"} — not checked on the plan yet</>}</span>
            {canCheckCorners && onCheckCorners
              ? <button onClick={onCheckCorners} style={{fontSize:11,fontWeight:700,color:C.tealMid,background:"#fff",border:`1px solid ${C.tealMid}55`,borderRadius:6,padding:"3px 10px",cursor:"pointer"}}>{_cc ? "Re-check on the plan" : "Check them on the plan"}</button>
              : (!_cc && <span style={{color:C.inkLight}}>(the check picture isn't on this device — scan the plan again to check them)</span>)}
          </div>
        );
      })()}
      {trade.id === 'plasterer' && !rough && (() => {
        // every-storey-v1-2026-10-14: what each floor came to
        const g = project.aiAnalysis && project.aiAnalysis.geometry;
        const lr = g && Array.isArray(g.level_results) ? g.level_results.filter(x => x && typeof x === 'object') : [];
        if (lr.length < 2) return null;
        const val = (x, k) => { const f = x.fields && x.fields[k]; return f && f.value != null && isFinite(Number(f.value)) ? Math.round(Number(f.value)) : null; };
        const nMeasured = lr.filter(x => x.measured).length;
        // the stud and corners each floor is worked out with (the tradie's
        // answers, not just the engine's)
        const ftRows = (pbFloorTotals(project, Number(studConfirmed) || null, _cc) || {}).rows || [];
        const rowOf = (x) => ftRows.find(r => r.key && r.key === x.key) || null;
        return (
          <div data-pb-floors={lr.length} style={{fontSize:11,color:C.inkMid,background:"rgba(255,255,255,0.6)",borderRadius:8,padding:"8px 10px",marginBottom:6,lineHeight:1.6}}>
            <div style={{fontWeight:700,color:C.ink,marginBottom:2}}>{nMeasured === lr.length ? `All ${lr.length} floors measured` : `${nMeasured} of ${lr.length} floors measured`}</div>
            {lr.slice(0, 8).map((x, k) => (
              <div key={k}>
                <strong>{pbTxt(x.name, 40) || "Floor " + (k + 1)}</strong>{x.measured
                  ? (() => { const r = rowOf(x); const cn = r ? r.count : (typeof x.external_count === 'number' ? x.external_count : null);
                      const st = r ? r.stud : (x.stud_m && x.stud_m.value);
                      return <> — ceiling {val(x, 'ceiling_m2') ?? "—"} m² · square stop {val(x, 'square_stop_lm') ?? "—"} m · {cn == null ? "—" : cn} corners{r && r.checked ? " (checked)" : ""}{st ? <> · stud {pbTxt(st, 6)} m{r && r.studAssumed ? " (assumed)" : ""}</> : null}</>; })()
                  : <span style={{color:C.red}}> — not measured</span>}
              </div>
            ))}
          </div>
        );
      })()}
      {(() => {
        // openings-net-v1: say what was deducted, or say the walls are gross.
        const note = {fontSize:10,borderRadius:6,padding:"4px 10px",display:"inline-block",marginTop:8};
        const o = trade.id === 'plasterer' ? pbOpeningsInfo(project) : undefined;
        if (o === undefined) return tm.deductions.length>0 ? (
          <div style={{...note,color:C.teal,background:C.tealFaint}}>✓ Windows & doors auto-deducted from wall area</div>
        ) : null;
        if (o) return (
          <div style={{...note,color:C.teal,background:C.tealFaint}}>
            ✓ Openings deducted from walls: {Math.round(o.m2)} m²{o.nWin != null ? ` (${o.nWin} windows & ext doors, ${o.nLeaves} door leaves both sides)` : ''}
          </div>
        );
        return (
          <div style={{...note,color:"#92400e",background:"#fffbeb"}}>
            Openings couldn't be read off this plan — wall area is GROSS. Deduct windows & doors yourself.
          </div>
        );
      })()}
      {trade.id === 'plasterer' && pitchConfirmed != null && pbRakedM2(project) > 0 && (
        <div style={{fontSize:10,color:C.inkMid,marginTop:6}}>
          {pitchConfirmed > 0
            ? `Ceiling includes a ${pitchConfirmed}° rake over ${Math.round(pbRakedM2(project))} m² (+${Math.round(pbRakeExtraM2(project, pitchConfirmed) * 10) / 10} m²)`
            : 'Raked area counted flat'}
          {' · '}<span onClick={()=>{ setConfirmedPitch(project.id, null); setPitchConfirmed(null); }}
            style={{textDecoration:"underline",cursor:"pointer"}}>change</span>
        </div>
      )}
    </div>
  );
}

// verify-admin-2026-10-20 (Grant): an unverified email can't subscribe, and an
// email on the admin list only counts once it's verified, but there was no way
// to get the verification link again, and a verification only counted after
// signing out and back in. "I've clicked it" asks Google again and re-checks
// with the server (onCheck), which switches an admin to the admin dashboard.
function PbVerifyEmail({ email, onCheck, checkLabel, notYet }) {
  const label = checkLabel || "I've clicked it";
  const [busy, setBusy] = useState(null);           // "send" | "check"
  const [msg, setMsg] = useState(null);             // { text, good }
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const done = (m) => { if (live.current) { setMsg(m); setBusy(null); } };
  const send = async () => {
    setBusy("send"); setMsg(null);
    try {
      await fbResendVerification();
      done({ good: true, text: "Sent to " + email + ". Click the link in it (check junk too), then tap “" + label + "”." });
    } catch (e) { done({ text: fbErrorMessage(e) }); }
  };
  const check = async () => {
    setBusy("check"); setMsg(null);
    try {
      const me = await onCheck();
      if (me && me.promoted) return;                // on to the admin dashboard
      done(me && me.emailVerified ? { good: true, text: "Email verified ✓" }
        : me ? { text: notYet || ("Not verified yet. Click the link in the email first (check junk too), then tap “" + label + "” again.") }
        : { text: "Couldn't reach PlanBoss just now. Check your connection and try again." });
    } catch (e) { done({ text: fbErrorMessage(e) }); }
  };
  return (
    <div style={{marginTop:10}}>
      <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
        <Btn small onClick={check} disabled={!!busy}>{busy==="check"?"Checking…":label}</Btn>
        <Btn small variant="outline" onClick={send} disabled={!!busy}>{busy==="send"?"Sending…":"Send the link again"}</Btn>
      </div>
      {msg&&<div role="status" style={{fontSize:12,marginTop:8,lineHeight:1.5,color:msg.good?C.tealMid:C.red}}>{msg.text}</div>}
    </div>
  );
}

// admin-uid-2026-10-21 (Grant): the verification email can fail to arrive
// (business mail often blocks firebaseapp.com), so an admin can skip it: this
// account ID goes on Render as PB_ADMIN_UIDS. Only an admin-listed account
// waiting on verification is shown its ID (on the card at the top).
function PbCopyId({ id }) {
  const [copied, setCopied] = useState(null);       // true, false (couldn't), or null
  const copy = async () => {
    try { await navigator.clipboard.writeText(id); setCopied(true); }
    catch (e) { setCopied(false); }
  };
  return (
    <div style={{marginTop:12}}>
      <div style={{fontSize:11,fontWeight:700,color:C.inkMid,textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:4}}>Your account ID</div>
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <code data-pb-account-id="1" style={{fontFamily:"ui-monospace,SFMono-Regular,Menlo,Consolas,monospace",fontSize:13,background:"#fff",border:`1px solid ${C.amberLight}`,borderRadius:8,padding:"6px 10px",userSelect:"all",WebkitUserSelect:"all",wordBreak:"break-all",color:C.ink}}>{id}</code>
        <Btn small variant="outline" onClick={copy}>{copied?"Copied ✓":"Copy ID"}</Btn>
      </div>
      {copied===false&&<div style={{fontSize:12,color:C.inkMid,marginTop:6}}>Couldn't copy it automatically. Press and hold the ID to copy it.</div>}
    </div>
  );
}

function AccountScreen({user,prefs,account,onPrefsChange,onUpgrade,onLogout,onManageSubscription,onDeleteAccount,onCheckVerified}) {
  const plan=PLANS[user.plan]||PLANS.pro;
  // store-v1-2026-10-12: the server's view of the account (plan, scans).
  const ent=account&&account.plan?account.plan:null;
  // stripe-live-2 (review): an account with no plan says so (it used to show
  // Pro, auto-renewing). With filing off the device's own plan decides.
  const hasPlan=ent?!!ent.plan:!!PLANS[user.plan];
  const noPlanWhy=hasPlan?"":(ent?pbNoPlanWhy(account):"offer");
  const priceText=ent&&ent.period==="annual"?`${fmt(Math.round(plan.price*0.8*12))}/year + GST`:`${fmt(plan.price)}/month + GST`;
  const [delStep,setDelStep]=useState(0);          // 0 closed, 1 confirm, 2 deleting
  const [delText,setDelText]=useState("");
  const [delPw,setDelPw]=useState("");
  const [delErr,setDelErr]=useState("");
  const delReady=delText.trim()==="DELETE"&&delPw.length>0;
  const doDelete=async()=>{
    if(!delReady) return;
    setDelErr("");setDelStep(2);
    try{ await onDeleteAccount(delPw); }
    catch(e){ setDelErr((e&&e.message)||"Couldn't delete the account. Check your connection and try again."); setDelStep(1); }
  };
  const filingOn=!!(account&&account.filing);
  const [saved,setSaved]=useState(false);
  const [localPrefs,setLocalPrefs]=useState({...prefs});
  const update=(key,val)=>{
    const updated={...localPrefs,[key]:val};
    setLocalPrefs(updated);
    onPrefsChange(updated);
  };
  const save=()=>{onPrefsChange(localPrefs);setSaved(true);setTimeout(()=>setSaved(false),2000);};

  return (
    <div style={{padding:32,maxWidth:640,margin:"0 auto"}}>
      <h1 style={{fontSize:26,fontWeight:700,color:C.ink,fontFamily:"Inter,system-ui,-apple-system,sans-serif",marginBottom:28}}>Account</h1>

      {/* Profile */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:600,color:C.inkMid,marginBottom:12,textTransform:"uppercase",letterSpacing:"0.05em"}}>Profile</div>
        <div style={{fontSize:15,fontWeight:700,color:C.ink}}>{user.email}</div>
        {account&&account.createdAt&&<div style={{fontSize:13,color:C.inkLight,marginTop:2}}>Member since {pbDate(account.createdAt,{month:"long",year:"numeric"})}</div>}
        {account&&account.emailVerified===false&&<>
          <div style={{fontSize:12,color:C.amber,marginTop:6}}>Email not verified yet — check your inbox (and spam) for the PlanBoss verification email. You'll need it to subscribe.</div>
          {/* verify-admin-2026-10-20: an admin email has these buttons on the card at the top instead */}
          {onCheckVerified&&!account.adminWaiting&&<PbVerifyEmail email={user.email} onCheck={onCheckVerified}/>}
        </>}
      </div>

      {/* store-v1: this month's scans, straight from the server */}
      {ent&&(
        <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:16}}>
          <div style={{fontSize:13,fontWeight:600,color:C.inkMid,marginBottom:12,textTransform:"uppercase",letterSpacing:"0.05em"}}>This month</div>
          {ent.limit===null
            ?<div style={{fontSize:14,color:C.ink}}>Admin account — no scan limit. {ent.used} scan{ent.used===1?"":"s"} this month.</div>
            :ent.plan===null
              ?(noPlanWhy!=="offer"
                ?<div>
                  <div style={{fontSize:14,fontWeight:700,color:C.red,marginBottom:8}}>{{trial:"Your free trial has ended.",plan:"Your plan has ended.",
                    payment:"Your free trial has ended, and the first payment didn't go through.",
                    refused:"This card or email has already had a PlanBoss free trial."}[noPlanWhy]||"No plan at the moment."}</div>
                  {noPlanWhy==="payment"
                    ?<Btn small onClick={()=>pbOpenBillingPortal()}>Update your card →</Btn>
                    :<Btn small onClick={()=>onUpgrade()}>Choose a plan →</Btn>}
                </div>
                :<div>
                  <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:4}}>Start scanning with Pro, free for 7 days.</div>
                  <div style={{fontSize:12,color:C.inkMid,marginBottom:10}}>Your card is taken at checkout; cancel within the 7 days and you pay nothing.</div>
                  <Btn small onClick={()=>onUpgrade()}>Start your free trial →</Btn>
                </div>)
              :<>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:8}}>
                  <div style={{fontSize:22,fontWeight:800,color:C.ink}}>{ent.used} <span style={{fontSize:14,fontWeight:500,color:C.inkLight}}>of {ent.limit} {ent.plan==="trial"||ent.trialing?"free-trial scans":"scans"}</span></div>
                  <div style={{fontSize:12,color:C.inkLight}}>{ent.plan==="trial"||ent.trialing?(ent.trialEndsAt?"trial until "+pbDate(ent.trialEndsAt,{day:"numeric",month:"short"}):""):"resets "+pbDay(ent.resetsOn)}</div>
                </div>
                <div style={{background:C.border,borderRadius:999,height:8,overflow:"hidden"}}>
                  <div style={{width:`${Math.min(100,Math.round(ent.used/Math.max(1,ent.limit)*100))}%`,height:"100%",background:ent.used>=ent.limit?C.red:ent.used>=ent.limit*0.8?C.amber:C.tealMid,borderRadius:999}}/>
                </div>
                <div style={{fontSize:12,color:C.inkLight,marginTop:8}}>
                  {pbPlanLabel(ent.plan)}
                  {/* stripe-live-2: a card trial says when the first payment is */}
                  {ent.trialing
                    ?<> · free trial · {ent.cancelAtPeriodEnd?"ends "+pbDate(ent.trialEndsAt,{day:"numeric",month:"short"})+", nothing charged":"first payment "+pbDate(ent.trialEndsAt,{day:"numeric",month:"short"})}</>
                    :<>
                      {ent.source==="stripe"&&ent.status&&ent.status!=="active"?" · payment "+ent.status.replace("_"," "):""}
                      {ent.source==="stripe"&&ent.renews?(ent.cancelAtPeriodEnd?" · ends ":" · renews ")+pbDate(ent.renews,{day:"numeric",month:"short"}):""}
                    </>}
                </div>
              </>}
        </div>
      )}

      {/* stripe-live-2 (review): no plan yet — not a Pro plan that auto-renews */}
      {!hasPlan&&(
        <div data-pb-no-plan style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:16}}>
          <div style={{fontSize:11,fontWeight:600,color:C.inkMid,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Current plan</div>
          <div style={{fontSize:20,fontWeight:700,color:C.ink}}>No plan yet</div>
          <div style={{fontSize:13,color:C.inkMid,marginTop:4,marginBottom:12}}>{noPlanWhy==="offer"?`Pro is free for 7 days, with ${PB_TRIAL.scans} scans. Your card is taken when you start, and nothing is charged until the 7 days are up.`:"Pro is NZ$50 a month + GST. Pro+ is NZ$100."}</div>
          <Btn small onClick={()=>onUpgrade()}>See plans →</Btn>
        </div>
      )}
      {/* Current plan summary — click to manage */}
      {hasPlan&&<div style={{background:plan.light,border:`1.5px solid ${plan.color}44`,borderRadius:16,padding:24,marginBottom:16,cursor:"pointer"}} onClick={onManageSubscription}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{fontSize:11,fontWeight:600,color:plan.color,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:4}}>Current plan</div>
            <div style={{fontSize:22,fontWeight:700,color:plan.color}}>{plan.name}</div>
            <div style={{fontSize:13,color:C.inkMid,marginTop:2}}>{ent&&ent.source==="invite"?"Beta tester · no charge":ent&&ent.trialing?`Free until ${pbDate(ent.trialEndsAt,{day:"numeric",month:"short"})}, then ${priceText}`:plan.price===0?"Free 7-day trial":priceText}</div>
            {!(ent&&ent.source==="invite")&&<div style={{fontSize:11,color:C.inkLight,marginTop:3}}>{ent&&ent.trialing&&ent.cancelAtPeriodEnd?"Cancelled · nothing will be charged":ent&&ent.period==="annual"?"Auto-renews yearly · Cancel any time":"Auto-renews monthly · Cancel any time"}</div>}
          </div>
          <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
            <button onClick={e=>{e.stopPropagation();onManageSubscription();}} style={{padding:"8px 16px",background:plan.color,color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:700}}>
              {plan.id==="business"?"Manage subscription":"Upgrade plan →"}
            </button>
            <div style={{fontSize:11,color:plan.color}}>View all plans →</div>
          </div>
        </div>
        <div style={{marginTop:14,borderTop:`1px solid ${plan.color}22`,paddingTop:12,display:"flex",flexWrap:"wrap",gap:8}}>
          {plan.features.slice(0,4).map(f=>(
            <span key={f} style={{fontSize:11,color:plan.color,background:`${plan.color}18`,borderRadius:20,padding:"2px 10px"}}>✓ {pbFeatureText(f)}</span>
          ))}
          {plan.features.length>4&&<span style={{fontSize:11,color:plan.color}}>+{plan.features.length-4} more</span>}
        </div>
      </div>}

      {/* Preferences */}
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:600,color:C.inkMid,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"}}>Preferences</div>
        <div style={{fontSize:12,color:C.inkLight,marginBottom:18}}>Pre-fills every new project automatically.</div>

        {/* Region */}
        <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:10,textTransform:"uppercase",letterSpacing:"0.05em"}}>Default region</div>
        <div style={{marginBottom:24}}>
          <select value={localPrefs.region||"auckland_council"} onChange={e=>update("region",e.target.value)}
            style={{width:"100%",padding:"9px 12px",border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.ink,background:C.bg,outline:"none",cursor:"pointer",marginBottom:8}}>
            {COUNCIL_REGIONS.map(region=>(
              <optgroup key={region} label={region}>
                {NZ_COUNCILS.filter(c=>c.region===region).map(c=>(
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {(()=>{const c=NZ_COUNCILS.find(x=>x.id===(localPrefs.region||"auckland_council"));if(!c)return null;return(
            <div style={{fontSize:11,color:C.inkMid,background:C.bg,borderRadius:8,padding:"8px 12px"}}>
              <strong>Climate Zone {c.climateZone}</strong> · Cost index {Math.round(c.costMult*100)}% of NZ average
              {c.notes&&<div style={{marginTop:4,color:C.amber}}>📍 {c.notes}</div>}
            </div>
          );})()}
        </div>

        {/* Wastage allowance */}
        <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"}}>Wastage allowance</div>
        <div style={{fontSize:11,color:C.inkLight,marginBottom:10}}>Adds a buffer to sheet quantities for cuts and offcuts. Industry standard is 10%. Affects sheet materials only (boards, GIB, plasterboard).</div>
        <div style={{marginBottom:24,padding:"14px 16px",background:C.bg,borderRadius:12,border:`1px solid ${C.border}`}}>
          <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
            <input
              type="range"
              min="0"
              max="25"
              step="1"
              value={localPrefs.wastagePercent ?? 10}
              onChange={e=>update("wastagePercent",Number(e.target.value))}
              style={{flex:1,accentColor:C.tealMid,cursor:"pointer"}}
            />
            <div style={{display:"flex",alignItems:"center",gap:4,minWidth:60,justifyContent:"flex-end"}}>
              <input
                type="number"
                min="0"
                max="25"
                value={localPrefs.wastagePercent ?? 10}
                onChange={e=>update("wastagePercent",Math.max(0,Math.min(25,Number(e.target.value)||0)))}
                style={{width:42,padding:"4px 6px",border:`1px solid ${C.border}`,borderRadius:6,fontSize:13,fontWeight:700,color:C.ink,textAlign:"right",background:"#fff"}}
              />
              <span style={{fontSize:13,fontWeight:700,color:C.inkMid}}>%</span>
            </div>
          </div>
          <div style={{fontSize:10,color:C.inkLight,display:"flex",justifyContent:"space-between"}}>
            <span>0% (raw measurements)</span>
            <span>10% standard</span>
            <span>25% complex jobs</span>
          </div>
        </div>

        {/* Trade preferences — each expands inline */}
        <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"}}>Your trade</div>
        <div style={{fontSize:11,color:C.inkLight,marginBottom:10}}>Select your trade and configure your preferred metrics and rates — these pre-populate on every new project.</div>

        {isAdminUser(user) && (()=>{const sel=localPrefs.preferredTrade==="fulljob";const locked=user.plan!=="business";return(
          <div style={{marginBottom:8}}>
            <button onClick={()=>!locked&&update("preferredTrade","fulljob")} style={{width:"100%",padding:"12px 16px",border:`2px solid ${sel?C.purple:C.border}`,borderRadius:12,background:sel?C.purpleLight:C.bg,cursor:locked?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:10,opacity:locked?0.5:1,position:"relative"}}>
              {locked&&<div style={{position:"absolute",top:6,right:10,fontSize:9,color:C.purple,fontWeight:700}}>Business</div>}
              <span style={{fontSize:22}}>👷</span>
              <div style={{flex:1,textAlign:"left"}}>
                <div style={{fontSize:13,fontWeight:sel?700:500,color:sel?C.purple:C.inkMid}}>Full job quote</div>
                <div style={{fontSize:10,color:C.inkLight,marginTop:1}}>{locked?"🔒 upgrade to Business":"All trades — project manager view"}</div>
              </div>
              {sel&&<span style={{fontSize:11,color:C.purple,fontWeight:700}}>★ default</span>}
            </button>
          </div>
        );})()}

        {(()=>{ const _u = LS.get("to_user", null); return TRADES.filter(t=>isTradeLive(t.id,_u)); })().map(t=>{
          const sel=localPrefs.preferredTrade===t.id;
          const tm=TRADE_METRICS[t.id];
          return (
            <div key={t.id} style={{marginBottom:8,border:`2px solid ${sel?t.color:C.border}`,borderRadius:12,background:sel?t.light:C.bg,overflow:"hidden"}}>
              <button onClick={()=>update("preferredTrade",t.id)} style={{width:"100%",padding:"12px 16px",border:"none",background:"transparent",cursor:"pointer",display:"flex",alignItems:"center",gap:10,textAlign:"left"}}>
                <span style={{fontSize:22}}>{t.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:sel?700:500,color:sel?t.color:C.inkMid}}>{t.label}</div>
                  <div style={{fontSize:10,color:C.inkLight,marginTop:1}}>
                    {sel?"Tap again to collapse · your metrics and rates below":"Click to select and configure"}
                  </div>
                </div>
                {sel&&<span style={{fontSize:11,color:t.color,fontWeight:700}}>★ your trade</span>}
              </button>
              {sel&&tm&&(
                <div style={{borderTop:`1px solid ${t.color}22`,padding:"14px 16px",background:"rgba(255,255,255,0.5)"}}>
                  <TradeMetricsPrefsPanel
                    prefs={localPrefs}
                    inlineTrade={t.id}
                    planId={user.plan}
                    onUpdate={(tradeId,metricPrefs)=>{
                      const updated={...localPrefs,tradeMetrics:{...localPrefs.tradeMetrics,[tradeId]:metricPrefs}};
                      setLocalPrefs(updated);
                      onPrefsChange(updated);
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}

        {/* Preferred supplier — sits below trades */}
        <div style={{fontSize:12,fontWeight:700,color:C.ink,marginBottom:10,marginTop:24,textTransform:"uppercase",letterSpacing:"0.05em"}}>Preferred supplier</div>
        <div style={{fontSize:11,color:C.inkLight,marginBottom:12}}>Your preferred supplier is shown first and highlighted in the price comparison.</div>
        <div className="mu-supplier-picker" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:20}}>
          {SUPPLIERS.map(s=>{const sel=localPrefs.preferredSupplier===s.id;return(
            <button key={s.id} onClick={()=>update("preferredSupplier",s.id)} style={{padding:"12px 10px",border:`2px solid ${sel?s.color:C.border}`,borderRadius:12,background:sel?"#fff":C.bg,cursor:"pointer",textAlign:"center"}}>
              <div style={{width:12,height:12,borderRadius:3,background:s.color,display:"inline-block",marginBottom:4}}/>
              <div style={{fontSize:13,fontWeight:sel?700:500,color:sel?s.color:C.inkMid}}>{s.name}</div>
              {sel&&<div style={{fontSize:10,color:s.color,marginTop:2}}>♥ preferred</div>}
            </button>
          );})}
        </div>

        <div style={{display:"flex",alignItems:"center",gap:12,marginTop:4}}>
          <Btn onClick={save} color={C.tealMid} small>Save preferences</Btn>
          {saved&&<span style={{fontSize:13,color:C.teal,fontWeight:600}}>✓ Saved!</span>}
        </div>
      </div>
      {/* Subscription management (stripe-live-2 review: only with a
          subscription — or one whose first payment failed, to fix or cancel) */}
      {((hasPlan&&!(ent&&(ent.source==="invite"||ent.source==="admin")))||noPlanWhy==="payment")&&
      <div style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:16,padding:24,marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:700,color:C.ink,marginBottom:4,textTransform:"uppercase",letterSpacing:"0.05em"}}>Subscription</div>
        <div style={{fontSize:12,color:C.inkMid,marginBottom:14,lineHeight:1.7}}>
          {noPlanWhy==="payment"
            ?<>Your free trial of Pro has ended, and the first payment didn't go through. <strong>Update your card</strong> on the billing page to keep scanning, or cancel there.</>
            :ent&&ent.trialing
            ?<>Your free trial of <strong style={{color:plan.color}}>{plan.name}</strong> runs until <strong>{pbDate(ent.trialEndsAt,{day:"numeric",month:"long"})}</strong>. {ent.cancelAtPeriodEnd
              ?<>It's cancelled, so nothing will be charged.</>
              :<>Then it's <strong>${plan.price}/month + GST</strong>{ent.period==="annual"?<> (billed yearly, ${Math.round(plan.price*0.8*12)} + GST)</>:null}, unless you cancel before then — cancel and you pay nothing.</>}</>
            :<>Your <strong style={{color:plan.color}}>{plan.name}</strong> plan at <strong>{priceText}</strong> auto-renews each {ent&&ent.period==="annual"?"year":"month"} on your billing date.
          You can upgrade, downgrade or cancel any time — changes take effect at the end of your current billing period.</>}
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:14}}>
          {noPlanWhy==="payment"
            ?<button onClick={()=>pbOpenBillingPortal()} style={{padding:"10px 16px",background:plan.light,border:`1px solid ${plan.color}44`,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,color:plan.color,textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>💳 Update your card</span><span>→</span>
            </button>
            :<button onClick={onManageSubscription} style={{padding:"10px 16px",background:plan.light,border:`1px solid ${plan.color}44`,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,color:plan.color,textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span>📋 View or change plan</span><span>→</span>
            </button>}
          <button onClick={()=>{
            // planboss-v1-2026-10-12: this used to say "Cancellation noted"
            // and do nothing — the subscription kept billing. It now opens
            // Stripe's own billing page, where the cancel actually happens.
            pbOpenBillingPortal();
          }} style={{padding:"10px 16px",background:"transparent",border:`1px solid ${C.red}33`,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,color:C.red,textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>✕ Cancel subscription</span><span style={{fontSize:11,color:C.inkLight,fontWeight:400}}>Access continues until period end</span>
          </button>
        </div>
        <div style={{padding:"10px 14px",background:C.bg,borderRadius:8,fontSize:11,color:C.inkLight,lineHeight:1.7,borderLeft:`3px solid ${C.borderMid}`}}>
          <strong>Cancellation policy</strong> — Cancel any time before your next renewal date and you will not be charged again. You keep full access until the end of your paid period. No cancellation fees, no lock-in. For help contact <strong>{PB_SUPPORT_EMAIL}</strong>
        </div>
      </div>}

      <button onClick={onLogout} style={{display:"block",width:"100%",maxWidth:280,margin:"32px auto 0 auto",padding:"14px 24px",background:"#fff",border:`2px solid ${C.red}`,borderRadius:10,color:C.red,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"Inter,system-ui,-apple-system,sans-serif"}}>Sign out</button>

      {/* store-v1: delete the account from inside the app (Apple 5.1.1(v)
          and Google Play both require it). Server deletes every job, plan
          PDF, correction and the sign-in itself. */}
      {onDeleteAccount&&!user.isAdminTest&&(
        <div style={{marginTop:36,padding:20,border:`1px solid ${C.red}33`,borderRadius:14,background:"#fff"}}>
          <div style={{fontSize:13,fontWeight:700,color:C.red,marginBottom:6}}>Delete account</div>
          {delStep===0&&<>
            <div style={{fontSize:12,color:C.inkMid,lineHeight:1.6,marginBottom:12}}>
              Permanently deletes your PlanBoss account and your sign-in, and cancels your PlanBoss subscription straight away (no further charges).{" "}
              {filingOn ? "Every job and plan PDF saved to your account goes too." : "The jobs saved on this device are cleared from it."} This can't be undone.
            </div>
            <button onClick={()=>{setDelStep(1);setDelText("");setDelPw("");setDelErr("");}} style={{padding:"8px 14px",background:"transparent",border:`1px solid ${C.red}66`,borderRadius:8,color:C.red,fontSize:13,fontWeight:700,cursor:"pointer"}}>Delete my account…</button>
          </>}
          {delStep>=1&&<>
            <div style={{fontSize:12,color:C.inkMid,lineHeight:1.6,marginBottom:10}}>Type <strong>DELETE</strong> and your password to confirm. Everything is removed straight away.</div>
            <input value={delText} onChange={e=>setDelText(e.target.value)} placeholder="DELETE" autoCapitalize="characters" disabled={delStep===2} aria-label="Type DELETE to confirm"
              style={{display:"block",width:"100%",maxWidth:220,padding:"9px 12px",border:`1.5px solid ${C.red}66`,borderRadius:8,fontSize:14,marginBottom:8,boxSizing:"border-box"}}/>
            <input type="password" value={delPw} onChange={e=>setDelPw(e.target.value)} placeholder="Your password" autoComplete="current-password" disabled={delStep===2} aria-label="Your password"
              style={{display:"block",width:"100%",maxWidth:220,padding:"9px 12px",border:`1.5px solid ${C.red}66`,borderRadius:8,fontSize:14,marginBottom:10,boxSizing:"border-box"}}/>
            {delErr&&<div role="alert" style={{fontSize:12,color:C.red,marginBottom:10}}>{delErr}</div>}
            <div style={{display:"flex",gap:8}}>
              <button onClick={doDelete} disabled={!delReady||delStep===2} style={{padding:"9px 16px",background:delReady?C.red:C.border,border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:delReady&&delStep!==2?"pointer":"not-allowed"}}>{delStep===2?"Deleting…":"Delete everything"}</button>
              <button onClick={()=>setDelStep(0)} disabled={delStep===2} style={{padding:"9px 14px",background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,color:C.inkMid,fontSize:13,cursor:"pointer"}}>Cancel</button>
            </div>
          </>}
        </div>
      )}
    </div>
  );
}

// ─── SUBSCRIPTIONS PAGE ───────────────────────────────────────────────────────
function SubscriptionsScreen({user,onSelect,onBack,trialOffer,sellBusiness}) {
  // stripe-live-2 (review): monthly first while the free trial is on offer.
  const [billing,setBilling]=useState(trialOffer?"monthly":"annual");
  const [confirmPlan,setConfirmPlan]=useState(null);
  const plan=PLANS[user.plan]||PLANS.pro;

  // stripe-live-2 (review): scans and trades come from PLANS, so this table
  // can't drift from the plans screen and the server (it said 10 / 25 / 50).
  const tradesText=(p)=>p.trades>=10?"All":String(p.trades);
  const FEATURE_ROWS = [
    {category:"Measurements & analyses",features:[
      {label:"Plan scans per month",            values:{pro:String(PLANS.pro.analyses),proplus:String(PLANS.proplus.analyses),business:String(PLANS.business.analyses)}},
      {label:"Trades per scan (plasterer live now)", values:{pro:tradesText(PLANS.pro),proplus:tradesText(PLANS.proplus),business:tradesText(PLANS.business)}},
      {label:pbFeatureText("Jobs saved to your account"), values:{pro:true,proplus:true,business:true}},
      {label:"Tradie take-off with the key metrics for your trade",             values:{pro:true,proplus:true,business:true}},
      {label:"Auto-deduct windows & doors",     values:{pro:true,proplus:true,business:true}},
    ]},
    {category:"Materials & quoting",features:[
      {label:"Materials list",                  values:{pro:true,proplus:true,business:true}},
      {label:"Supplier pricing comparison",     values:{pro:false,proplus:"soon",business:"soon"}},
      {label:"Quote builder (labour + markup)", values:{pro:false,proplus:true,business:true}},
      {label:"Full job quote (all trades)",     values:{pro:false,proplus:false,business:"soon"}},
      {label:"Send to subie",                   values:{pro:false,proplus:false,business:"soon"}},
    ]},
    {category:"Benchmarks",features:[
      {label:"Sense check vs regional $/m²",    values:{pro:false,proplus:true,business:true}},
      {label:"Regional council notes",          values:{pro:false,proplus:true,business:true}},
    ]},
    {category:"Export & sharing",features:[
      {label:"PDF export",                      values:{pro:"soon",proplus:"soon",business:"soon"}},
      {label:"Email quotes to subies",          values:{pro:false,proplus:false,business:"soon"}},
      {label:"Xero / MYOB export",              values:{pro:false,proplus:false,business:"soon"}},
    ]},
    {category:"Team",features:[
      {label:"Team members",                    values:{pro:"1",proplus:"1",business:"5 (soon)"}},
      {label:"Priority support",                values:{pro:false,proplus:false,business:true}},
    ]},
  ];

  const handleClick=(p)=>{
    if(p.id===user.plan) return;
    if(p.price===0) return;
    if(p.price<(PLANS[user.plan]?.price||0)){setConfirmPlan(p);return;}
    onSelect(p.id, billing);           // (it always went to annual)
  };

  // planboss-v1-2026-10-12: downgrades go through Stripe's billing page. This
  // used to rewrite the plan on this device only, while Stripe kept billing
  // the old price.
  const confirmDowngrade=()=>{
    if(!confirmPlan) return;
    setConfirmPlan(null);
    pbOpenBillingPortal();
  };

  const renderCell=(val,planId)=>{
    if(val==="soon") return <span style={{display:"inline-block",padding:"3px 8px",background:"#FEF3C7",color:"#92400E",borderRadius:10,fontSize:10,fontWeight:700,whiteSpace:"nowrap"}}>Coming soon</span>;
    if(val===true) return <span style={{color:C.teal,fontSize:16,fontWeight:700}}>✓</span>;
    if(val===false) return <span style={{color:C.borderMid,fontSize:14}}>—</span>;
    return <span style={{fontSize:13,fontWeight:600,color:C.ink}}>{val}</span>;
  };

  return (
    <div style={{padding:"32px 24px",maxWidth:900,margin:"0 auto"}}>
      {confirmPlan&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.4)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:C.card,borderRadius:16,padding:28,maxWidth:380,width:"90%",boxShadow:"0 8px 32px rgba(0,0,0,0.18)"}}>
            <div style={{fontSize:15,fontWeight:700,color:C.ink,marginBottom:8}}>Downgrade to {confirmPlan.name}?</div>
            <div style={{fontSize:13,color:C.inkMid,marginBottom:12,lineHeight:1.6}}>You'll lose access to features in your current {plan.name} plan at end of billing period.</div>
            <div style={{background:C.redLight,borderRadius:10,padding:"10px 14px",marginBottom:20,fontSize:12,color:C.red}}>
              Features you'll lose: {plan.features.filter(f=>!confirmPlan.features.includes(f)).join(" · ")||"None"}
            </div>
            <div style={{display:"flex",gap:10}}>
              <button onClick={()=>setConfirmPlan(null)} style={{flex:1,padding:"10px",background:"transparent",border:`1px solid ${C.border}`,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:600,color:C.inkMid}}>Cancel</button>
              <button onClick={confirmDowngrade} style={{flex:1,padding:"10px",background:C.redLight,border:`1px solid ${C.red}44`,borderRadius:8,cursor:"pointer",fontSize:13,fontWeight:700,color:C.red}}>Yes, downgrade</button>
            </div>
          </div>
        </div>
      )}

      <button onClick={onBack} style={{background:"none",border:"none",cursor:"pointer",color:C.inkLight,fontSize:13,marginBottom:20,padding:0}}>← Back to account</button>
      <div style={{textAlign:"center",marginBottom:28}}>
        <h1 style={{fontSize:28,fontWeight:700,color:C.ink,fontFamily:"Inter,system-ui,-apple-system,sans-serif",marginBottom:6}}>Plans & pricing</h1>
        <p style={{fontSize:14,color:C.inkMid,marginBottom:16}}>Cancel any time. New accounts get Pro free for 7 days (card up front).</p>
        <div style={{display:"inline-flex",background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:3,gap:3}}>
          {["monthly","annual"].map(b=>(
            <button key={b} onClick={()=>setBilling(b)} style={{padding:"7px 18px",border:"none",borderRadius:8,cursor:"pointer",background:billing===b?C.tealMid:"transparent",color:billing===b?"#fff":C.inkMid,fontWeight:billing===b?700:400,fontSize:13}}>
              {b==="annual"?"Annual (save 20%)":"Monthly"}
            </button>
          ))}
        </div>
      </div>

      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",minWidth:640}}>
          {/* Plan header row */}
          <thead>
            <tr>
              <th style={{width:"30%",padding:"12px 16px",textAlign:"left",borderBottom:`2px solid ${C.border}`,fontSize:12,color:C.inkLight,fontWeight:600,textTransform:"uppercase"}}></th>
              {planOrder.map(pid=>{
                const p=PLANS[pid];
                const price=billing==="annual"&&p.price>0?Math.round(p.price*0.8):p.price;
                const isCurrent=user.plan===pid;
                return (
                  <th key={pid} style={{padding:"16px 12px",textAlign:"center",borderBottom:`2px solid ${isCurrent?p.color:C.border}`,background:isCurrent?p.light:"transparent",position:"relative"}}>
                    {isCurrent&&<div style={{position:"absolute",top:0,left:0,right:0,height:3,background:p.color,borderRadius:"3px 3px 0 0"}}/>}
                    <div style={{fontSize:14,fontWeight:700,color:isCurrent?p.color:C.ink,marginBottom:2}}>{p.name}</div>
                {p.persona&&<div style={{fontSize:10,color:C.inkLight,marginBottom:4,fontStyle:"italic"}}>{p.persona}</div>}
                    {billing==="annual"&&p.price>0 ? (
                      <>
                        <div style={{fontSize:20,fontWeight:700,color:isCurrent?p.color:C.ink}}>
                          {fmt(price*12)}<span style={{fontSize:11,fontWeight:400,color:C.inkLight}}>/yr</span>
                        </div>
                        <div style={{fontSize:10,color:C.inkLight,marginTop:2}}>{fmt(price)}/mo · <span style={{color:C.teal,fontWeight:600}}>save {fmt(p.price*12-price*12)}</span></div>
                      </>
                    ) : (
                      <div style={{fontSize:20,fontWeight:700,color:isCurrent?p.color:C.ink}}>
                        {p.price===0?"Free":fmt(price)}
                        {p.price>0&&<span style={{fontSize:11,fontWeight:400,color:C.inkLight}}>/mo</span>}
                      </div>
                    )}
                    <div style={{marginTop:10}}>
                      {(() => {
                        const _isComingSoon = !isAdminUser(user) && pid === "business" && !sellBusiness;
                        if (_isComingSoon) return <div style={{padding:"6px 12px",background:C.borderMid,color:"#fff",borderRadius:20,fontSize:11,fontWeight:700,display:"inline-block",opacity:0.95}}>🚧 Coming soon</div>;
                        if (isCurrent) return <div style={{padding:"6px 12px",background:p.color,color:"#fff",borderRadius:20,fontSize:11,fontWeight:700,display:"inline-block"}}>✓ Current plan</div>;
                        if (p.price>0) return (
                          <button onClick={()=>handleClick(p)} style={{padding:"7px 14px",background:(p.price||0)>(PLANS[user.plan]?.price||0)?p.color:"transparent",color:(p.price||0)>(PLANS[user.plan]?.price||0)?"#fff":C.red,border:(p.price||0)<=(PLANS[user.plan]?.price||0)?`1px solid ${C.red}44`:"none",borderRadius:8,cursor:"pointer",fontSize:12,fontWeight:700,whiteSpace:"nowrap"}}>
                            {(p.price||0)>(PLANS[user.plan]?.price||0)?`Upgrade →`:"Downgrade"}
                          </button>
                        );
                        return <div style={{fontSize:11,color:C.inkLight}}>Free trial</div>;
                      })()}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {FEATURE_ROWS.map(({category,features})=>(
              <Fragment key={category}>
                <tr key={`cat-${category}`}>
                  <td colSpan={5} style={{padding:"14px 16px 6px",fontSize:11,fontWeight:700,color:C.inkLight,textTransform:"uppercase",letterSpacing:"0.07em",background:C.bg,borderTop:`1px solid ${C.border}`}}>{category}</td>
                </tr>
                {features.map((row,i)=>(
                  <tr key={row.label} style={{background:i%2===0?C.card:"#fafaf8"}}>
                    <td style={{padding:"11px 16px",fontSize:13,color:C.inkMid,borderBottom:`1px solid ${C.border}`}}>{row.label}</td>
                    {planOrder.map(pid=>{
                      const isCurrent=user.plan===pid;
                      return (
                        <td key={pid} style={{padding:"11px 12px",textAlign:"center",borderBottom:`1px solid ${C.border}`,background:isCurrent?`${PLANS[pid].color}08`:"transparent"}}>
                          {renderCell(row.values[pid],pid)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{textAlign:"center",fontSize:11,color:C.inkLight,marginTop:20}}>All prices in NZD · GST not included · Cancel any time</p>
    </div>
  );
}


// ─── APP SHELL ────────────────────────────────────────────────────────────────
// ─── ADMIN DASHBOARD ──────────────────────────────────────────────────────────
// store-v1-2026-10-12: everything here comes from the server (Firestore via
// /admin/*), for verified emails in PB_ADMIN_EMAILS only. The old Users and
// Stats screens read this browser's localStorage, so they only ever showed
// accounts created on the same browser, and "Change plan" changed nothing
// real. Every job and plan PDF an admin opens is logged on the server.
const PB_ADMIN_PLAN_COLORS = { pro:"#60A5FA", proplus:"#F06400", business:"#A78BFA", trial:"#FBBF24",
  beta:"#34D399", admin:"#F472B6", lapsed:"#94A3B8" };
const pbAdminPlanKey = (r) => r.plan || "lapsed";
const pbNum = (v, d) => (v === null || v === undefined || Number.isNaN(Number(v))) ? "—"
  : Number(v).toLocaleString("en-NZ", { maximumFractionDigits: d === undefined ? 1 : d });
const pbMoney = (v) => "$" + Number(v || 0).toLocaleString("en-NZ", { maximumFractionDigits: 0 });
function PbPlanPill({ planKey }) {
  const col = PB_ADMIN_PLAN_COLORS[planKey] || "#94A3B8";
  return <span style={{fontSize:11,fontWeight:700,color:col,background:col+"22",borderRadius:20,padding:"2px 10px",whiteSpace:"nowrap"}}>
    {planKey === "lapsed" ? "No plan" : pbPlanLabel(planKey)}</span>;
}
function PbUsageBar({ used, limit }) {
  if (limit === null || limit === undefined) return <span style={{fontSize:12,color:"#94A3B8"}}>{used} · no limit</span>;
  const pct = limit > 0 ? Math.min(100, Math.round(used / limit * 100)) : 100;
  const col = used >= limit ? "#F87171" : pct >= 80 ? "#FBBF24" : "#34D399";
  return (
    <div style={{minWidth:90}}>
      <div style={{fontSize:12,color:"#E2E8F0",marginBottom:3}}><strong>{used}</strong> of {limit}</div>
      <div style={{height:5,background:"#334155",borderRadius:3,overflow:"hidden"}}><div style={{width:pct+"%",height:"100%",background:col}}/></div>
    </div>
  );
}
// One line describing a tradie's correction (measurement or price).
function pbCorrectionText(e) {
  if (!e || typeof e !== "object") return "—";
  const t = (v, n) => (typeof v === "string" ? v : (typeof v === "number" ? String(v) : "")).slice(0, n || 120);
  if (e.type === "price") {
    return (t(e.materialName) || "price") + ": $" + pbNum(e.oldPrice, 2) + " → $" + pbNum(e.newPrice, 2) + (t(e.scope) ? " (" + t(e.scope) + ")" : "");
  }
  const ai = Number(e.aiValue), real = Number(e.realValue);
  const pct = ai ? ((real - ai) / ai * 100) : null;
  return (t(e.room) ? t(e.room) + " · " : "") + (t(e.fieldKey) || t(e.label) || "measurement") + ": " + pbNum(ai, 2) + " → " + pbNum(real, 2)
    + (pct !== null && Number.isFinite(pct) ? " (" + (pct > 0 ? "+" : "") + pct.toFixed(1) + "%)" : "");
}

// One odd record (a field that isn't what the screen expects) mustn't blank
// the whole admin panel: the panel shows the error and the rest still works.
class PbErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err: err }; }
  componentDidCatch(err) { console.error("[admin] panel error: " + (err && err.message)); }
  render() {
    if (this.state.err) {
      return (
        <div style={{background:"#1E293B",border:"1px solid #F8717155",borderRadius:12,padding:"18px 20px",color:"#FCA5A5",fontSize:13}}>
          This panel hit a record it couldn't show ({String((this.state.err && this.state.err.message) || "error").slice(0, 160)}).
          <div style={{marginTop:10}}><button onClick={()=>this.setState({err:null})} style={{padding:"6px 12px",borderRadius:8,border:"1px solid #334155",background:"#0F172A",color:"#E2E8F0",cursor:"pointer",fontSize:12}}>Try again</button></div>
        </div>
      );
    }
    return this.props.children;
  }
}
const pbTxt = (v, n) => (v === null || v === undefined) ? "" : (typeof v === "string" ? v : (typeof v === "number" || typeof v === "boolean") ? String(v) : "").slice(0, n || 300);
const pbLocalVal = (l, f) => { const e = l && l[f]; return (e && typeof e === "object" && "v" in e) ? e.v : e; };

function AdminDashboard({adminScreen,setAdminScreen,onLogout}) {
  const [flags,setFlags] = useState(()=>LS.get("mu_flags",DEFAULT_FLAGS));
  const [toast,setToast] = useState(null);
  const [adminTestPlan, setAdminTestPlan] = useState("business");
  const adminEmail = (LS.get("mu_admin",null)||{}).email || "admin";

  const [ov, setOv] = useState(null);              // /admin/overview
  const [ovErr, setOvErr] = useState(null);        // { message, code }
  const [loading, setLoading] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [q, setQ] = useState("");
  const [planFilter, setPlanFilter] = useState("all");
  const [sel, setSel] = useState(null);            // the account opened
  const [jobs, setJobs] = useState(null);
  const [jobsNext, setJobsNext] = useState(null);
  const [jobsErr, setJobsErr] = useState(null);
  const [detail, setDetail] = useState(null);      // { job, corrections }
  const [jobBusy, setJobBusy] = useState(null);
  const [corr, setCorr] = useState(null);
  const [corrErr, setCorrErr] = useState(null);

  const showToast = (msg, bad)=>{setToast({msg,bad:!!bad});setTimeout(()=>setToast(null),3500);};

  const load = async () => {
    setLoading(true); setOvErr(null);
    try {
      const r = await pbFetch("/admin/overview");
      if (!r.ok) { setOvErr(await pbErrorInfo(r, "Couldn't load the dashboard.")); setOv(null); }
      else { setOv(await r.json()); setLoadedAt(Date.now()); }
    } catch (e) {
      setOvErr({ message: (e && e.code === "signed_out") ? e.message : "Couldn't reach the PlanBoss server. It may be waking up — try Refresh in a moment.",
                 code: (e && e.code) || "network" });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadCorrections = async () => {
    setCorrErr(null);
    try {
      const r = await pbFetch("/admin/corrections");
      if (!r.ok) throw new Error(await pbErrorText(r, "Couldn't load corrections."));
      setCorr((await r.json()).corrections || []);
    } catch (e) { setCorrErr((e && e.message) || "Couldn't load corrections."); }
  };
  useEffect(() => { if (adminScreen === "corrections" && corr === null) loadCorrections(); }, [adminScreen]);

  const openAccount = async (row) => {
    setAdminScreen("accounts");
    setSel(row); setJobs(null); setJobsNext(null); setJobsErr(null); setDetail(null);
    try {
      const r = await pbFetch("/admin/accounts/" + encodeURIComponent(row.uid) + "/jobs");
      if (!r.ok) throw new Error(await pbErrorText(r, "Couldn't load this account's jobs."));
      const d = await r.json();
      setJobs(d.jobs || []); setJobsNext(d.next || null);
    } catch (e) { setJobsErr((e && e.message) || "Couldn't load jobs."); }
  };
  const moreJobs = async () => {
    if (!sel || !jobsNext) return;
    try {
      const r = await pbFetch("/admin/accounts/" + encodeURIComponent(sel.uid) + "/jobs?after=" + encodeURIComponent(jobsNext));
      if (!r.ok) throw new Error(await pbErrorText(r, "Couldn't load more jobs."));
      const d = await r.json();
      setJobs(j => (j || []).concat(d.jobs || [])); setJobsNext(d.next || null);
    } catch (e) { showToast((e && e.message) || "Couldn't load more jobs.", true); }
  };
  const openJob = async (j) => {
    if (!sel) return;
    setJobBusy(j.id);
    try {
      const r = await pbFetch("/admin/accounts/" + encodeURIComponent(sel.uid) + "/jobs/" + encodeURIComponent(j.id));
      if (!r.ok) throw new Error(await pbErrorText(r, "Couldn't open the job."));
      setDetail(await r.json());
    } catch (e) { showToast((e && e.message) || "Couldn't open the job.", true); }
    finally { setJobBusy(null); }
  };
  // The window opens on the click itself (so no popup blocker), then gets
  // the signed link once the server has made it.
  const openPlanPdf = async (uid, jobId) => {
    const w = window.open("", "_blank");
    try {
      const r = await pbFetch("/admin/accounts/" + encodeURIComponent(uid) + "/jobs/" + encodeURIComponent(jobId) + "/plan");
      if (!r.ok) throw new Error(await pbErrorText(r, "Couldn't fetch the plan."));
      const { url } = await r.json();
      if (w) { w.opener = null; w.location.href = url; } else window.open(url, "_blank", "noopener");
    } catch (e) { if (w) w.close(); showToast((e && e.message) || "Couldn't fetch the plan.", true); }
  };
  // See the job as the tradie sees it: a view-only copy in the admin's own
  // test session. Nothing is written back to their account.
  const openInApp = () => {
    const jb = detail && detail.job;
    if (!jb || !sel) return;
    if (!jb.aiAnalysis) { showToast("This job's results are missing, so it can't be opened.", true); return; }
    const vid = "view_" + jb.id;
    const key = "to_projects_admin@planboss.co.nz";
    const prior = LS.get(key, []) || [];
    prior.filter(p => p && p.viewOnly).forEach(p => pbForgetJobLocal(p.id));    // one view copy at a time
    const proj = { id: vid, viewOnly: true, viewOf: sel.email || sel.uid, viewUid: sel.uid, viewJobId: jb.id,
      name: jb.name || "Job", trades: Array.isArray(jb.trades) ? jb.trades : [], councilId: jb.councilId || null,
      accuracy: jb.accuracy || null, tuning: jb.tuning || null,
      area: jb.area || (jb.summary && jb.summary.floor_m2) || null, createdAt: jb.createdAt || null,
      date: pbDate(jb.createdAt), status: "complete", file: (jb.file && jb.file.name) || null,
      hasPdf: !!(jb.file && jb.file.stored), aiAnalysis: jb.aiAnalysis };
    try { localStorage.setItem(key, JSON.stringify([proj, ...prior.filter(p => p && !p.viewOnly)])); }
    catch (e) { showToast("This job is too big to open in the app on this browser.", true); return; }
    pbForgetJobLocal(vid);
    pbAdoptLocal(vid, jb.local || {});
    LS.set("to_user", { email: "admin@planboss.co.nz", plan: pbFeaturePlan({ plan: sel.plan }) || "pro",
      trialActive: false, trialStarted: null, isAdminTest: true, adminEmail, joinDate: new Date().toISOString() });
    LS.set("mu_admin", null);
    try { localStorage.setItem("pb_open_after_reload", vid); } catch (e) {}
    window.location.reload();
  };

  const toggleFlag = (key)=>{
    const updated={...flags,[key]:{...flags[key],value:!flags[key].value}};
    setFlags(updated);
    LS.set("mu_flags",updated);
  };

  const navItems = [
    {id:"dashboard",   icon:"📊", label:"Dashboard"},
    {id:"accounts",    icon:"👥", label:"Accounts"},
    {id:"corrections", icon:"✏️", label:"Corrections"},
    {id:"flags",       icon:"🚩", label:"Feature flags"},
    {id:"test",        icon:"🧪", label:"Test as user"},
  ];

  const rows = (ov && ov.accounts) || [];
  const t = (ov && ov.totals) || {};
  const byPlan = t.byPlan || {};
  // stripe-live-2: Pro's card trials count as trials, not paying, until the first charge.
  const trialing = t.trialing || 0;
  const paying = (byPlan.pro || 0) + (byPlan.proplus || 0) + (byPlan.business || 0) - trialing;
  const jobsTotal = rows.reduce((s, r) => s + (r.jobsCount || 0), 0);
  const filtered = rows.filter(r => {
    if (q && !((r.email || "") + " " + r.uid).toLowerCase().includes(q.trim().toLowerCase())) return false;
    const k = pbAdminPlanKey(r);
    if (planFilter === "all") return true;
    if (planFilter === "paying") return r.source === "stripe" && r.status !== "trialing";
    if (planFilter === "trial") return k === "trial" || r.status === "trialing";
    return k === planFilter;
  });
  const nearLimit = rows.filter(r => r.limit && r.used > 0).sort((a, b) => (b.used / b.limit) - (a.used / a.limit)).slice(0, 6);
  const recent = rows.filter(r => r.lastScanAt).slice(0, 8);

  const card = {background:"#1E293B",borderRadius:12,border:"1px solid #334155"};
  const h1 = {fontSize:24,fontWeight:700,color:"#fff",margin:0};
  const sub = {fontSize:12,color:"#64748B"};
  const th = {fontSize:11,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.05em",padding:"10px 12px",textAlign:"left",whiteSpace:"nowrap"};
  const td = {fontSize:13,color:"#E2E8F0",padding:"10px 12px",borderTop:"1px solid #334155",verticalAlign:"middle"};
  const btn = {padding:"7px 14px",borderRadius:8,border:"1px solid #334155",background:"#0F172A",color:"#E2E8F0",cursor:"pointer",fontSize:12,fontWeight:600};

  // Why the server data isn't showing, in words that say what to do.
  const setupCard = () => {
    if (!ovErr) return null;
    const c = ovErr.code;
    return (
      <div style={{...card,padding:"20px 24px",borderColor:"#F0640066",marginBottom:20}}>
        <div style={{fontSize:15,fontWeight:700,color:"#FDBA74",marginBottom:8}}>
          {c==="filing_off"?"The filing system isn't switched on yet":c==="admin_only"?"This sign-in isn't an admin on the server":c==="signed_out"?"Your sign-in has expired":"Couldn't load the dashboard"}
        </div>
        <div style={{fontSize:13,color:"#CBD5E1",lineHeight:1.7}}>
          {c==="filing_off"&&<>Accounts, jobs and plan PDFs are only stored once the server has its Firebase key. On Render → <strong>measureup-app</strong> → Environment, add <code>FIREBASE_SERVICE_ACCOUNT</code> (the service-account key), save, and let it redeploy. Steps are in <code>claude/DEPLOY_2026-10-12-filing.md</code>.</>}
          {c==="admin_only"&&<>The server only treats verified emails listed in <code>PB_ADMIN_EMAILS</code> (Render → measureup-app → Environment) as admins. Check your email is on that list and verified, then sign out and back in.</>}
          {c==="signed_out"&&<>Sign out and sign back in to keep going.</>}
          {!["filing_off","admin_only","signed_out"].includes(c)&&<>{ovErr.message}</>}
        </div>
        <div style={{display:"flex",gap:8,marginTop:14}}>
          <button onClick={load} style={btn}>Try again</button>
          {c==="signed_out"&&<button onClick={onLogout} style={btn}>Sign out</button>}
        </div>
      </div>
    );
  };

  return (
    <div className="pb-admin" style={{minHeight:"100vh",background:"#0F172A",display:"flex",fontFamily:"Inter,system-ui,-apple-system,sans-serif"}}>
      {/* Sidebar */}
      <div className="pb-admin-side" style={{width:220,background:"#1E293B",display:"flex",flexDirection:"column",flexShrink:0}}>
        <div style={{padding:"24px 20px",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
          <div style={{fontSize:11,fontWeight:700,color:C.orange,letterSpacing:"0.1em",marginBottom:4}}>ADMIN PANEL</div>
          <div style={{fontSize:16,fontWeight:700,color:"#fff"}}>PlanBoss</div>
          <div style={{fontSize:11,color:"#64748B",marginTop:2,overflow:"hidden",textOverflow:"ellipsis"}}>{adminEmail}</div>
        </div>
        <nav className="pb-admin-nav" style={{flex:1,padding:"12px 0"}}>
          {navItems.map(n=>(
            <button key={n.id} onClick={()=>setAdminScreen(n.id)}
              style={{width:"100%",padding:"10px 20px",border:"none",background:adminScreen===n.id?"rgba(240,100,0,0.15)":"transparent",
                cursor:"pointer",display:"flex",alignItems:"center",gap:10,textAlign:"left",whiteSpace:"nowrap",
                borderLeft:adminScreen===n.id?`3px solid ${C.orange}`:"3px solid transparent"}}>
              <span style={{fontSize:16}}>{n.icon}</span>
              <span style={{fontSize:13,fontWeight:adminScreen===n.id?700:400,color:adminScreen===n.id?"#fff":"#94A3B8"}}>{n.label}</span>
            </button>
          ))}
        </nav>
        <div className="pb-admin-signout" style={{padding:"16px 20px",borderTop:"1px solid rgba(255,255,255,0.08)"}}>
          <button onClick={onLogout} style={{width:"100%",padding:"8px",background:"transparent",border:"1px solid #334155",
            borderRadius:8,color:"#94A3B8",cursor:"pointer",fontSize:12}}>Sign out</button>
        </div>
      </div>

      {/* Main */}
      <div style={{flex:1,overflow:"auto",minWidth:0}}>
        <div className="pb-admin-main" style={{padding:"32px"}}>

          {/* ── DASHBOARD ── */}
          {adminScreen==="dashboard"&&(
            <PbErrorBoundary>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,gap:12,flexWrap:"wrap"}}>
                <div>
                  <h1 style={h1}>Dashboard</h1>
                  <div style={sub}>{t.month?("Month "+t.month+" (NZ time)"):"Live accounts, plans and scans"}{loadedAt?" · updated "+new Date(loadedAt).toLocaleTimeString("en-NZ",{hour:"numeric",minute:"2-digit"}):""}</div>
                </div>
                <button onClick={load} disabled={loading} style={btn}>{loading?"Loading…":"↻ Refresh"}</button>
              </div>
              {setupCard()}
              {ov&&(
                <>
                  <div className="pb-admin-kpis" style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:14,marginBottom:20}}>
                    {[
                      {label:"Accounts",          value:pbNum(t.accounts,0),           note:(t.newThisWeek||0)+" new this week",            color:"#60A5FA"},
                      {label:"Paying",            value:pbNum(paying,0),               note:pbMoney(t.mrr)+"/mo recurring (excl. GST)",     color:"#34D399"},
                      {label:"On free trial",     value:pbNum((byPlan.trial||0)+trialing,0), note:(byPlan.lapsed||0)+" with no plan",     color:"#FBBF24"},
                      {label:"Beta testers",      value:pbNum(byPlan.beta||0,0),       note:"testers (PB_BETA_EMAILS)",                    color:"#34D399"},
                      {label:"Scans this month",  value:pbNum(t.scansThisMonth,0),     note:(t.activeThisMonth||0)+" accounts scanning",    color:"#F06400"},
                      {label:"Est. AI cost",      value:pbMoney(t.estAiCostThisMonth), note:"this month at $"+pbNum(t.costPerScan,2)+"/scan", color:"#F472B6"},
                      {label:"Jobs saved",        value:pbNum(jobsTotal,0),            note:"with their plan PDFs",                         color:"#A78BFA"},
                      {label:"Scan limits",       value:(ov.limits?ov.limits.pro+" / "+ov.limits.proplus+" / "+ov.limits.business:"—"), note:"Pro / Pro+ / Business a month", color:"#94A3B8"},
                    ].map(s=>(
                      <div key={s.label} style={{...card,padding:"16px 18px"}}>
                        <div style={{fontSize:11,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.05em",marginBottom:6}}>{s.label}</div>
                        <div style={{fontSize:28,fontWeight:800,color:s.color,lineHeight:1.1}}>{s.value}</div>
                        <div style={{fontSize:11,color:"#94A3B8",marginTop:6}}>{s.note}</div>
                      </div>
                    ))}
                  </div>
                  <div className="pb-admin-two" style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)",gap:14}}>
                    <div style={{...card,padding:"18px 20px"}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:14}}>Accounts by plan</div>
                      {["pro","proplus","business","trial","beta","admin","lapsed"].map(k=>{
                        const n=byPlan[k]||0; const pct=t.accounts?Math.round(n/t.accounts*100):0;
                        return (
                          <div key={k} onClick={()=>{setPlanFilter(k);setAdminScreen("accounts");setSel(null);}} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,cursor:"pointer"}}>
                            <div style={{width:96,flexShrink:0}}><PbPlanPill planKey={k}/></div>
                            <div style={{flex:1,height:8,background:"#334155",borderRadius:4,overflow:"hidden"}}><div style={{width:pct+"%",height:"100%",background:PB_ADMIN_PLAN_COLORS[k]}}/></div>
                            <div style={{fontSize:12,fontWeight:700,color:"#fff",width:28,textAlign:"right"}}>{n}</div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{...card,padding:"18px 20px"}}>
                      <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:14}}>Closest to their monthly limit</div>
                      {nearLimit.length===0&&<div style={{fontSize:12,color:"#64748B"}}>No scans yet this month.</div>}
                      {nearLimit.map(r=>(
                        <div key={r.uid} onClick={()=>openAccount(r)} style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,cursor:"pointer"}}>
                          <div style={{flex:1,minWidth:0,fontSize:12,color:"#E2E8F0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pbTxt(r.email,200)||pbTxt(r.uid,80)}</div>
                          <PbPlanPill planKey={pbAdminPlanKey(r)}/>
                          <PbUsageBar used={r.used} limit={r.limit}/>
                        </div>
                      ))}
                      <div style={{fontSize:13,fontWeight:700,color:"#fff",margin:"18px 0 10px"}}>Latest scans</div>
                      {recent.length===0&&<div style={{fontSize:12,color:"#64748B"}}>Nobody has scanned yet.</div>}
                      {recent.map(r=>(
                        <div key={r.uid} onClick={()=>openAccount(r)} style={{display:"flex",justifyContent:"space-between",gap:10,fontSize:12,marginBottom:6,cursor:"pointer"}}>
                          <span style={{color:"#E2E8F0",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pbTxt(r.email,200)||pbTxt(r.uid,80)}</span>
                          <span style={{color:"#64748B",whiteSpace:"nowrap"}}>{pbDate(r.lastScanAt,{day:"numeric",month:"short",hour:"numeric",minute:"2-digit"})}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </PbErrorBoundary>
          )}

          {/* ── ACCOUNTS ── */}
          {adminScreen==="accounts"&&(
            <PbErrorBoundary>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:12,flexWrap:"wrap"}}>
                <div>
                  <h1 style={h1}>{sel?(<span><span onClick={()=>{setSel(null);setDetail(null);}} style={{cursor:"pointer",color:"#94A3B8"}}>Accounts</span> / {pbTxt(sel.email,200)||pbTxt(sel.uid,80)}</span>):("Accounts ("+rows.length+")")}</h1>
                  <div style={sub}>{sel?"Plan, scans and every saved job":"Every account, its plan and this month's scans. Tap one to see its jobs."}</div>
                </div>
                <button onClick={sel?()=>openAccount(sel):load} disabled={loading} style={btn}>{loading?"Loading…":"↻ Refresh"}</button>
              </div>
              {setupCard()}

              {!sel&&ov&&(
                <>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center",marginBottom:14}}>
                    <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search email"
                      style={{flex:"1 1 200px",maxWidth:320,padding:"8px 12px",borderRadius:8,border:"1px solid #334155",background:"#0F172A",color:"#E2E8F0",fontSize:13,outline:"none"}}/>
                    {[["all","All"],["paying","Paying"],["trial","Trial"],["beta","Beta"],["lapsed","No plan"],["pro","Pro"],["proplus","Pro+"],["business","Business"],["admin","Admin"]].map(([k,l])=>(
                      <button key={k} onClick={()=>setPlanFilter(k)} style={{...btn,padding:"6px 12px",background:planFilter===k?"rgba(240,100,0,0.18)":"#0F172A",borderColor:planFilter===k?C.orange:"#334155"}}>{l}</button>
                    ))}
                  </div>
                  <div className="pb-admin-table" style={{...card,overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:720}}>
                      <thead><tr>{["Email","Plan","Allowance used","Jobs","Last scan","Joined"].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {filtered.map(r=>(
                          <tr key={r.uid} onClick={()=>openAccount(r)} style={{cursor:"pointer"}}>
                            <td style={{...td,maxWidth:260,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {pbTxt(r.email,200)||<span style={{color:"#64748B"}}>{pbTxt(r.uid,80)}</span>}
                              {!r.emailVerified&&<span title="Email not verified" style={{marginLeft:6,fontSize:10,color:"#FBBF24"}}>unverified</span>}
                              {r.stripeStale&&<span title="Stripe hasn't confirmed a renewal: treated as lapsed until it does (check the webhook)" style={{marginLeft:6,fontSize:10,color:"#F87171"}}>Stripe stale</span>}
                            </td>
                            <td style={td}><PbPlanPill planKey={pbAdminPlanKey(r)}/>{r.source==="stripe"&&r.status&&r.status!=="active"&&<span style={{marginLeft:6,fontSize:10,color:"#F87171"}}>{r.status}</span>}</td>
                            <td style={td}><PbUsageBar used={r.used} limit={r.limit}/>{r.source==="trial"&&<div style={{fontSize:10,color:"#64748B",marginTop:2}}>whole trial</div>}</td>
                            <td style={td}>{r.jobsCount||0}</td>
                            <td style={{...td,color:"#94A3B8",whiteSpace:"nowrap"}}>{r.lastScanAt?pbDate(r.lastScanAt,{day:"numeric",month:"short"}):"—"}</td>
                            <td style={{...td,color:"#94A3B8",whiteSpace:"nowrap"}}>{pbDate(r.createdAt,{day:"numeric",month:"short",year:"2-digit"})}</td>
                          </tr>
                        ))}
                        {filtered.length===0&&<tr><td colSpan={6} style={{...td,color:"#64748B",textAlign:"center",padding:"28px"}}>{rows.length?"No accounts match.":"No accounts yet — they appear here the first time someone signs in."}</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {sel&&(
                <>
                  <div className="pb-admin-kpis" style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:14,marginBottom:16}}>
                    <div style={{...card,padding:"14px 16px"}}>
                      <div style={{fontSize:11,color:"#64748B",textTransform:"uppercase",marginBottom:6}}>Plan</div>
                      <PbPlanPill planKey={pbAdminPlanKey(sel)}/>
                      <div style={{fontSize:11,color:"#94A3B8",marginTop:8,lineHeight:1.6}}>
                        {sel.source==="stripe"?("Stripe · "+(sel.status||"active")+(sel.period?" · "+sel.period:"")+(sel.renews?" · renews "+pbDate(sel.renews,{day:"numeric",month:"short"}):"")):
                         sel.source==="trial"?("Trial ends "+pbDate(sel.trialEndsAt,{day:"numeric",month:"short"})):
                         sel.source==="invite"?"Invited beta tester":sel.source==="admin"?"Admin (PB_ADMIN_EMAILS)":
                         sel.why&&sel.why!=="free trial ended"?("No plan · "+sel.why):
                         sel.trialEndsAt?("Trial ended "+pbDate(sel.trialEndsAt,{day:"numeric",month:"short"})):"No plan · free trial ended"}
                      </div>
                    </div>
                    <div style={{...card,padding:"14px 16px"}}>
                      <div style={{fontSize:11,color:"#64748B",textTransform:"uppercase",marginBottom:8}}>{sel.source==="trial"?"Trial scans used":"Allowance used this month"}</div>
                      <PbUsageBar used={sel.used} limit={sel.limit}/>
                      <div style={{fontSize:11,color:"#94A3B8",marginTop:8}}>{sel.scansThisMonth||0} this month · {sel.scansTotal||0} all time</div>
                    </div>
                    <div style={{...card,padding:"14px 16px"}}>
                      <div style={{fontSize:11,color:"#64748B",textTransform:"uppercase",marginBottom:6}}>Jobs saved</div>
                      <div style={{fontSize:24,fontWeight:800,color:"#A78BFA"}}>{sel.jobsCount||0}</div>
                    </div>
                    <div style={{...card,padding:"14px 16px"}}>
                      <div style={{fontSize:11,color:"#64748B",textTransform:"uppercase",marginBottom:6}}>Joined</div>
                      <div style={{fontSize:14,fontWeight:700,color:"#E2E8F0"}}>{pbDate(sel.createdAt)}</div>
                      <div style={{fontSize:11,color:"#94A3B8",marginTop:6}}>last seen {pbDate(sel.lastSeenAt,{day:"numeric",month:"short"})}{sel.emailVerified?"":" · email unverified"}</div>
                    </div>
                  </div>

                  {jobsErr&&<div style={{...card,padding:"14px 18px",color:"#FCA5A5",fontSize:13,marginBottom:14}}>{jobsErr}</div>}
                  {!jobs&&!jobsErr&&<div style={{color:"#94A3B8",fontSize:13,padding:"10px 0"}}>Loading jobs…</div>}
                  {jobs&&(
                    <div className="pb-admin-table" style={{...card,overflowX:"auto",marginBottom:16}}>
                      <table style={{width:"100%",borderCollapse:"collapse",minWidth:760}}>
                        <thead><tr>{["Job","Scanned","Plan check","Floor m²","Walls m²","Ceilings m²","Square stop lm","Externals","Engine",""].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {jobs.map(j=>{const s=j.summary||{};return(
                            <tr key={j.id} onClick={()=>openJob(j)} style={{cursor:"pointer",background:detail&&detail.job&&detail.job.id===j.id?"#1a2744":"transparent"}}>
                              <td style={{...td,maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pbTxt(j.name,200)}{j.importedFrom?<span title="Copied up from a device; no PDF" style={{marginLeft:6,fontSize:10,color:"#64748B"}}>imported</span>:null}</td>
                              <td style={{...td,color:"#94A3B8",whiteSpace:"nowrap"}}>{pbDate(j.createdAt,{day:"numeric",month:"short",year:"2-digit"})}</td>
                              {/* plancheck-v1: which scans couldn't be read or measured */}
                              <td style={{...td,whiteSpace:"nowrap",fontSize:11,fontWeight:700,color:s.verdict==="stop"?"#F87171":s.verdict==="hold"?"#FBBF24":s.verdict==="check"?"#FCD34D":s.verdict==="ok"?"#4ADE80":"#64748B"}}>
                                {s.verdict==="stop"?"can't read":s.verdict==="hold"?(s.verdictKind==="partial"?"part measured":s.verdictKind==="levels"?"check floors":s.verdictKind==="alteration"?"alteration":"not measured"):s.verdict==="check"?"check":s.verdict==="ok"?"measured":"—"}</td>
                              <td style={td}>{pbNum(s.floor_m2)}</td>
                              <td style={td}>{pbNum(s.walls_m2)}</td>
                              <td style={td}>{pbNum(s.ceiling_m2)}</td>
                              <td style={td}>{pbNum(s.square_stop_lm)}</td>
                              <td style={td}>{s.external_corners!=null?s.external_corners:"—"}</td>
                              <td style={{...td,fontSize:11,color:"#94A3B8",whiteSpace:"nowrap"}}>{pbTxt(s.engine,80)||"vision only"}</td>
                              <td style={{...td,color:"#F06400",whiteSpace:"nowrap"}}>{jobBusy===j.id?"Opening…":"Open →"}</td>
                            </tr>
                          );})}
                          {jobs.length===0&&<tr><td colSpan={10} style={{...td,color:"#64748B",textAlign:"center",padding:"24px"}}>No jobs saved yet.</td></tr>}
                        </tbody>
                      </table>
                      {jobsNext&&<div style={{padding:"12px",textAlign:"center"}}><button onClick={moreJobs} style={btn}>Load older jobs</button></div>}
                    </div>
                  )}

                  {detail&&detail.job&&(()=>{
                    const jb=detail.job; const s=jb.summary||{}; const l=jb.local||{};
                    const lStud=pbLocalVal(l,"stud"), lPitch=pbLocalVal(l,"pitch"), lOver=pbLocalVal(l,"overrides"), lPrice=pbLocalVal(l,"prices");
                    const lAt=["stud","pitch","overrides","prices"].reduce((m,f)=>Math.max(m,(l[f]&&Number(l[f].at))||0),0);
                    const nOver=lOver&&typeof lOver==="object"?Object.values(lOver).reduce((n,r)=>n+(r&&typeof r==="object"?Object.keys(r).length:0),0):0;
                    const nPrice=lPrice&&typeof lPrice==="object"?Object.keys(lPrice).length:0;
                    return (
                      <div style={{...card,padding:"20px 22px",borderColor:"#F0640055"}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,flexWrap:"wrap",marginBottom:14}}>
                          <div>
                            <div style={{fontSize:17,fontWeight:700,color:"#fff"}}>{pbTxt(jb.name,200)}</div>
                            <div style={sub}>{pbDate(jb.createdAt,{day:"numeric",month:"short",year:"numeric",hour:"numeric",minute:"2-digit"})}{jb.file&&jb.file.name?" · "+pbTxt(jb.file.name,120):""}{jb.file&&jb.file.bytes?" · "+pbNum(jb.file.bytes/1048576,1)+" MB":""}</div>
                          </div>
                          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                            {jb.file&&jb.file.stored&&<button onClick={()=>openPlanPdf(sel.uid,jb.id)} style={btn}>📄 Plan PDF</button>}
                            <button onClick={openInApp} style={{...btn,background:"linear-gradient(135deg,#F06400,#D4530A)",border:"none",color:"#fff"}}>Open in app (view only) →</button>
                          </div>
                        </div>
                        <div className="pb-admin-kpis" style={{display:"grid",gridTemplateColumns:"repeat(4,minmax(0,1fr))",gap:10,marginBottom:14}}>
                          {[["Floor",pbNum(s.floor_m2)+" m²"],["Walls (net)",pbNum(s.walls_m2)+" m²"],["Ceilings",pbNum(s.ceiling_m2)+" m²"],["Square stop",pbNum(s.square_stop_lm)+" lm"],
                            ["Externals",(s.external_corners!=null?s.external_corners:"—")+(s.external_corners_lm!=null?" · "+pbNum(s.external_corners_lm)+" lm":"")],
                            ["Stud",(s.stud_m!=null?pbNum(s.stud_m,2)+" m":"—")+(lStud?" · confirmed "+pbTxt(lStud,10)+" m":"")],
                            ["Rooms",s.rooms!=null?pbNum(s.rooms,0):"—"],["Engine",(pbTxt(s.engine,80)||"vision only")+(s.tier?" · "+pbTxt(s.tier,80):"")]].map(([k,v])=>(
                            <div key={k} style={{background:"#0F172A",borderRadius:8,padding:"10px 12px"}}>
                              <div style={{fontSize:10,color:"#64748B",textTransform:"uppercase",marginBottom:4}}>{k}</div>
                              <div style={{fontSize:13,fontWeight:700,color:"#E2E8F0",wordBreak:"break-word"}}>{v}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{fontSize:12,color:"#94A3B8",marginBottom:12}}>
                          Tradie's settings: {lStud?"stud "+pbTxt(lStud,10)+" m":"stud not confirmed"} · {lPitch!=null&&lPitch!==""?"pitch "+pbTxt(lPitch,10)+"°":"no pitch set"} · {nOver} room correction{nOver===1?"":"s"} · {nPrice} price override{nPrice===1?"":"s"}{lAt?" · last changed "+pbDate(lAt,{day:"numeric",month:"short"}):""}
                        </div>
                        <div style={{fontSize:13,fontWeight:700,color:"#fff",marginBottom:8}}>Corrections on this job ({(detail.corrections||[]).length})</div>
                        {(detail.corrections||[]).length===0&&<div style={{fontSize:12,color:"#64748B"}}>None yet.</div>}
                        {(detail.corrections||[]).map(c=>(
                          <div key={c.id} style={{fontSize:12,color:"#E2E8F0",padding:"6px 0",borderTop:"1px solid #334155"}}>
                            <span style={{color:"#64748B"}}>{pbDate(c.at,{day:"numeric",month:"short"})}</span> · {pbCorrectionText(c.entry)}
                            {c.entry&&c.entry.reason?<span style={{color:"#94A3B8"}}> — {pbTxt(c.entry.reason,200)}</span>:null}
                            {c.entry&&c.entry.note?<span style={{color:"#94A3B8"}}> · “{pbTxt(c.entry.note,200)}”</span>:null}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </>
              )}
            </PbErrorBoundary>
          )}

          {/* ── CORRECTIONS ── */}
          {adminScreen==="corrections"&&(
            <PbErrorBoundary>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,gap:12,flexWrap:"wrap"}}>
                <div>
                  <h1 style={h1}>Corrections</h1>
                  <div style={sub}>Where tradies told PlanBoss a number was wrong — newest first, every account.</div>
                </div>
                <button onClick={loadCorrections} style={btn}>↻ Refresh</button>
              </div>
              {corrErr&&<div style={{...card,padding:"14px 18px",color:"#FCA5A5",fontSize:13,marginBottom:14}}>{corrErr}</div>}
              {corr===null&&!corrErr&&<div style={{color:"#94A3B8",fontSize:13}}>Loading…</div>}
              {corr&&(
                <div className="pb-admin-table" style={{...card,overflowX:"auto"}}>
                  <table style={{width:"100%",borderCollapse:"collapse",minWidth:720}}>
                    <thead><tr>{["When","Account","Job","Correction","Reason"].map(h=><th key={h} style={th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {corr.map(c=>(
                        <tr key={c.id}>
                          <td style={{...td,color:"#94A3B8",whiteSpace:"nowrap"}}>{pbDate(c.at,{day:"numeric",month:"short"})}</td>
                          <td style={{...td,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pbTxt(c.email,200)||pbTxt(c.uid,80)}</td>
                          <td style={{...td,maxWidth:180,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{pbTxt(c.jobName,200)||pbTxt(c.jobId,80)}</td>
                          <td style={td}>{pbCorrectionText(c.entry)}</td>
                          <td style={{...td,color:"#94A3B8"}}>{pbTxt(c.entry&&c.entry.reason,200)||"—"}{c.entry&&c.entry.note?" · “"+pbTxt(c.entry.note,120)+"”":""}</td>
                        </tr>
                      ))}
                      {corr.length===0&&<tr><td colSpan={5} style={{...td,color:"#64748B",textAlign:"center",padding:"24px"}}>No corrections yet. They arrive when tradies fix a measurement or a price on a saved job.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </PbErrorBoundary>
          )}

          {/* ── FEATURE FLAGS ── */}
          {adminScreen==="flags"&&(
            <>
              <h1 style={{...h1,marginBottom:8}}>Feature flags</h1>
              <p style={{fontSize:13,color:"#64748B",marginBottom:24}}>Switches stored in this browser only — they change what this device shows, not what customers see.</p>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                {Object.entries(flags).map(([key,flag])=>(
                  <div key={key} style={{...card,padding:"16px 20px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <div>
                      <div style={{fontSize:14,fontWeight:600,color:"#fff",marginBottom:2}}>{flag.label}</div>
                      <div style={{fontSize:11,color:"#64748B"}}>{key}</div>
                    </div>
                    <button onClick={()=>toggleFlag(key)}
                      style={{width:52,height:28,borderRadius:14,border:"none",cursor:"pointer",
                        background:flag.value?"#16A34A":"#334155",position:"relative",transition:"background 0.2s"}}>
                      <div style={{width:22,height:22,borderRadius:11,background:"#fff",position:"absolute",
                        top:3,left:flag.value?27:3,transition:"left 0.2s"}}/>
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ── TEST AS USER ── */}
          {adminScreen==="test"&&(
            <div style={{maxWidth:540}}>
              <h1 style={{...h1,marginBottom:8}}>Test as user</h1>
              <p style={{fontSize:13,color:"#64748B",marginBottom:24}}>Enter the app on any plan's features — no Stripe needed. Scans you run here count on your own admin account and are saved there.</p>
              <div style={{...card,borderRadius:16,padding:28,marginBottom:20}}>
                <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",marginBottom:14,textTransform:"uppercase",letterSpacing:"0.06em"}}>Select plan to test</div>
                {[{id:"pro",name:"Pro",price:"$50/mo",color:"#1B3A5C"},{id:"proplus",name:"Pro+",price:"$100/mo",color:"#F06400"},{id:"business",name:"Business",price:"$200/mo",color:"#6D28D9"}].map(function(p){
                  var selected=(adminTestPlan||"business")===p.id;
                  return(
                    <div key={p.id} onClick={function(){setAdminTestPlan(p.id);}}
                      style={{display:"flex",alignItems:"center",gap:14,padding:"14px 18px",borderRadius:12,marginBottom:10,cursor:"pointer",
                        border:"2px solid "+(selected?p.color:"#334155"),
                        background:selected?p.color+"18":"#0F172A"}}>
                      <div style={{width:18,height:18,borderRadius:"50%",border:"2px solid "+(selected?p.color:"#475569"),background:selected?p.color:"transparent",flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <span style={{fontWeight:700,color:"#F1F5F9",fontSize:15,marginRight:10}}>{p.name}</span>
                        <span style={{color:p.color,fontWeight:600,fontSize:13}}>{p.price}</span>
                      </div>
                    </div>
                  );
                })}
                <button
                  onClick={function(){
                    LS.set("to_user",{email:"admin@planboss.co.nz",plan:adminTestPlan||"business",trialActive:false,trialStarted:null,isAdminTest:true,adminEmail:adminEmail,joinDate:new Date().toISOString()});
                    LS.set("mu_admin",null);
                    window.location.reload();
                  }}
                  style={{width:"100%",marginTop:12,padding:"15px 0",borderRadius:12,border:"none",cursor:"pointer",
                    background:"linear-gradient(135deg,#F06400,#D4530A)",color:"#fff",fontWeight:700,fontSize:15}}>
                  Enter App as {({pro:"Pro",proplus:"Pro+",business:"Business"})[adminTestPlan||"business"]} →
                </button>
                <div style={{marginTop:12,padding:"12px",background:"#0F172A",borderRadius:10,fontSize:12,color:"#64748B",lineHeight:1.7}}>
                  Once inside, an orange <strong style={{color:"#F1F5F9"}}>Admin Test</strong> badge appears top-right. Click it to return here.
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Toast */}
      {toast&&(
        <div style={{position:"fixed",bottom:32,left:"50%",transform:"translateX(-50%)",
          background:toast.bad?"#B91C1C":"#16A34A",color:"#fff",padding:"10px 24px",borderRadius:20,fontSize:13,fontWeight:600,
          boxShadow:"0 4px 20px rgba(0,0,0,0.4)",zIndex:9999,maxWidth:"90vw",textAlign:"center"}}>
          {toast.bad?"":"✓ "}{toast.msg}
        </div>
      )}
    </div>
  );
}

export default function App() {

  // ── PHASE 1: AI CORRECTION STATE ──────────────────────────────
  const [corrections, setCorrections] = useState({});
  const [editingMeasurement, setEditingMeasurement] = useState(null);

  const CORRECTION_REASONS = [
    'AI missed a wall / area',
    'Scale was wrong',
    'Window/door deductions wrong',
    'Different stud height / spec',
    'Plan was unclear',
    'Wrong material / spec assumed',
    'Other',
  ];

  useEffect(() => {
    const user = LS.get('to_user', null);
    if (!user) return;
    try {
      const stored = localStorage.getItem('to_corrections_' + user.email);
      if (stored) setCorrections(JSON.parse(stored));
    } catch (e) {}
  }, []);

  const handleSaveCorrection = ({ projectId, trade, measurementKey, aiValue, unit, label, realValue, reason, note }) => {
    if (!realValue || isNaN(parseFloat(realValue))) { alert('Please enter a valid number.'); return; }
    const user = LS.get('to_user', null);
    const ai = parseFloat(aiValue);
    const real = parseFloat(realValue);
    const diffPct = ai !== 0 ? (((real - ai) / ai) * 100).toFixed(1) + '%' : 'N/A';
    const key = projectId + '__' + trade + '__' + measurementKey;
    const correction = {
      projectId, trade, measurementKey, label, unit,
      aiValue: ai, realValue: real, diffPct, reason, note,
      correctedBy: user ? user.email : 'unknown',
      correctedAt: new Date().toISOString(),
    };
    const updated = { ...corrections, [key]: correction };
    setCorrections(updated);
    if (user) {
      try { localStorage.setItem('to_corrections_' + user.email, JSON.stringify(updated)); }
      catch (e) {}
    }
    setEditingMeasurement(null);
  };

  const getExistingCorrection = (projectId, trade, measurementKey) =>
    corrections[projectId + '__' + trade + '__' + measurementKey] || null;
  // ── END PHASE 1 STATE ──────────────────────────────────────────

  useEffect(()=>{
    document.title="PlanBoss — NZ Trade Intelligence";
    // Clear the old pre-PlanBoss localStorage keys to avoid stale data conflicts
    ["ps_user","ps_projects","ps_prefs","ps_accounts","ps_notify_interest"].forEach(k=>{
      try{localStorage.removeItem(k);}catch(e){}
    });
    // Sanitise stored user plan in case it references a removed plan (e.g. "free")
    // Only sanitise non-null invalid plans. Preserve null/undefined for skip-trial
    // users who have not picked a plan yet — never silently promote them to Pro.
    const stored=LS.get("to_user",null);
    if(stored && stored.plan && !PLANS[stored.plan]){
      // Plan is a truthy string but not in PLANS - legacy data, reset to null.
      const fixed={...stored,plan:null};
      LS.set("to_user",fixed);
      setUser(fixed);
    }
  },[]);
  const [user,    setUser]    = useState(() => LS.get("to_user", null));
  const [screen,  setScreen]  = useState("home");
  const [subScreen,setSubScreen] = useState(null);
  // security-v1-2026-10-12: new users start with no jobs. The three sample
  // jobs that used to appear had no scan behind them, so they showed made-up
  // numbers that looked measured.
  const [projects,setProjectsRaw] = useState(() => LS.get("to_projects_"+getCurrentUserEmail(), []));
  const [activeProject,setActiveProject] = useState(null);
  const [prefs,   setPrefsRaw] = useState(() => LS.get("to_prefs", {...DEFAULT_PREFS}));
  const [upgradeToast, setUpgradeToast] = useState(null);

  // ── Filing system (store-v1-2026-10-12) ──────────────────────────────────
  // account = the server's /me: { filing, plan: { plan, used, limit, resetsOn,
  // trialEndsAt, source }, createdAt, ... } or null when the server can't say.
  const [account, setAccount] = useState(null);
  const [opening, setOpening] = useState(null);        // id of the job being fetched to open
  const [jobNonce, setJobNonce] = useState(0);         // re-mounts the job screen after a pull
  const [filingNote, setFilingNote] = useState(null);  // one-line notice
  // plancheck-v1-2026-10-13: every new screen opens at its top. Nothing reset
  // the scroll before, so after a scan (Analyse sits low on the page) the
  // corner check and the results opened half-way down, below their headline
  // and any "part of this house wasn't measured" warning.
  const contentRef = useRef(null);
  const pbActiveId = activeProject ? activeProject.id : null;
  useLayoutEffect(() => {
    try { const se = document.scrollingElement || document.documentElement; if (se) se.scrollTop = 0; } catch (e) {}
    try { if (document.body) document.body.scrollTop = 0; } catch (e) {}
    try { if (contentRef.current) contentRef.current.scrollTop = 0; } catch (e) {}
  }, [screen, subScreen, pbActiveId]);
  const [cornerCheck, setCornerCheck] = useState(null); // plancheck-v1: { pid, img, readOnly } on the corner check screen
  const projectsRef = useRef(projects);
  projectsRef.current = projects;
  const activeRef = useRef(null);
  activeRef.current = activeProject;
  const listAtRef = useRef(0);
  const refreshingRef = useRef(null);
  const userEmail = user ? user.email : null;
  const filingOn = !!(account && account.filing);

  const pbNote = (msg) => { setFilingNote(msg); setTimeout(() => setFilingNote(null), 7000); };
  // The job list on this device. Filed jobs are kept without their results
  // (those live on the server, with recent ones in the device cache), which
  // keeps the list small enough for a phone.
  const pbPersistProjects = (list, email) => {
    const lean = list.map(p => (p && p.cloud && p.aiAnalysis) ? { ...p, aiAnalysis: undefined } : p);
    try { localStorage.setItem("to_projects_" + (email || getCurrentUserEmail()), JSON.stringify(lean)); }
    catch (e) { console.error("Save projects failed:", e); }
  };
  // Internal list updates (from the server, imports): nothing sent anywhere.
  const setProjectsQuiet = (next, email) => {
    projectsRef.current = next;
    setProjectsRaw(next);
    pbPersistProjects(next, email);
  };
  const pbGetJSON = async (path) => {
    const r = await pbFetch(path);
    if (!r.ok) { const e = new Error(path + " -> " + r.status); e.status = r.status; throw e; }
    return r.json();
  };
  const pbRefreshMe = async (isCancelled) => {
    let me = null;
    try { me = await pbGetJSON("/me"); }
    catch (e) {
      // Deleted (from another device, or by us): nothing here is theirs any more.
      if (e && e.status === 410 && !(isCancelled && isCancelled())) {
        // If a deletion stopped part-way (the sign-in wasn't removed), this
        // finishes it; otherwise it's harmless.
        pbFetch("/account", { method: "DELETE", body: JSON.stringify({ confirm: "DELETE" }) }).catch(() => {});
        window.alert("This PlanBoss account has been deleted.");
        logout();
      }
      return null;
    }
    if (isCancelled && isCancelled()) return null;
    // verify-admin-2026-10-20: the server says this sign-in is an admin (its
    // email is on PB_ADMIN_EMAILS and verified): on to the admin dashboard.
    if (me && me.admin && pbBecomeAdmin(me.email)) return Object.assign({}, me, { promoted: true });
    pbFilingOn = !!(me && me.filing);
    setAccount(me);
    return me;
  };
  // verify-admin-2026-10-20 (Grant): signing in asked the server "admin?" only
  // once and gave up after 8 seconds, and an email verified after signing in
  // only counted after signing out and back in. Now, whenever the server says
  // admin, the app goes to the admin dashboard, the same as an admin sign-in.
  // "Test as user" sessions (an admin trying the app as a tradie) stay put.
  const pbBecomeAdmin = (email) => {
    const cur = LS.get("to_user", null);
    if (!email || (cur && cur.isAdminTest)) return false;
    const a = { email, role: "admin", loginTime: new Date().toISOString() };
    setUser(null); LS.set("to_user", null);
    setScreen("home"); setActiveProject(null);
    setAccount(null); setCornerCheck(null);
    pbFilingOn = false;
    LS.set("mu_admin", a); setAdminUser(a);
    return true;
  };
  // "I've clicked it": ask Google again (a fresh token says "verified"), then
  // the server, which may now say admin.
  const pbCheckVerified = async () => {
    await fbRefreshSignIn();
    return pbRefreshMe();
  };
  // Renames wait here until the server has them, so one made with no signal
  // isn't undone by the next refresh.
  const pbPendingKey = (email) => "pb_pending_names_" + (email || getCurrentUserEmail());
  // Each rename carries the time it was made; the server keeps the newest, so
  // a rename re-sent late (its first reply was lost) can't undo a newer one.
  const pbSendRename = (id, name, email, nameAt) => {
    const key = pbPendingKey(email);
    const at = nameAt || Date.now();
    const pend = LS.get(key, {}) || {};
    pend[id] = { name, at };
    LS.set(key, pend);
    const clear = () => { const now = LS.get(key, {}) || {}; if (now[id] && now[id].at === at) { delete now[id]; LS.set(key, now); } };
    pbFetch("/jobs/" + encodeURIComponent(id), { method: "PATCH", body: JSON.stringify({ name, nameAt: at }) })
      .then(async r => {
        if (r.status === 404) { clear(); return; }
        if (!r.ok) { console.warn("[filing] rename not saved yet (" + r.status + ")"); return; }
        const d = await r.json().catch(() => null);
        clear();
        if (d && d.name && d.name !== name) {        // a newer rename won: show that one
          setProjectsQuiet(projectsRef.current.map(p => (p && p.id === id) ? { ...p, name: d.name } : p), email);
        }
      })
      .catch(e => console.warn("[filing] rename not saved yet: " + (e && e.message)));
  };
  const pbDeleteCloudJob = async (p) => {
    try {
      pbDeletedIds.add(p.id);
      const r = await pbFetch("/jobs/" + encodeURIComponent(p.id), { method: "DELETE" });
      if (!r.ok && r.status !== 404) throw new Error("status " + r.status);
      pbCloudIds.delete(p.id);
      pbForgetJobLocal(p.id);
    } catch (e) {
      // Still on the server: put it back rather than pretend it's gone.
      pbDeletedIds.delete(p.id);
      if (!projectsRef.current.some(x => x && x.id === p.id)) setProjectsQuiet([p, ...projectsRef.current]);
      window.alert("Couldn't delete \"" + p.name + "\" — check your connection and try again.");
    }
  };
  // Every user action on the list comes through here; renames and deletes
  // of filed jobs go to the server too.
  const setProjects = v => {
    const prev = projectsRef.current;
    const next = typeof v === "function" ? v(prev) : v;
    setProjectsQuiet(next);
    const nextById = new Map(next.map(p => [p && p.id, p]));
    prev.forEach(p => {
      if (!p) return;
      const n = nextById.get(p.id);
      if (!n) { if (p.cloud) pbDeleteCloudJob(p); else pbForgetJobLocal(p.id); return; }
      const name = (n.name || "").trim();
      if (p.cloud && name && n.name !== p.name) pbSendRename(p.id, name);
    });
  };

  // Jobs scanned before filing was switched on live only in this browser.
  // Copy them to the account: results and settings (there's no PDF to send).
  const pbImportLocalJobs = async (email, isCancelled) => {
    const candidates = projectsRef.current.filter(p => p && !p.cloud && !p.viewOnly && p.aiAnalysis && !p.importFailed);
    if (!candidates.length) return [];
    const importAt = Date.now();
    const mapped = [], refused = new Set();
    let batch = [], size = 0;
    const send = async () => {
      if (!batch.length) return;
      const ids = batch.map(j => j.id);
      try {
        const r = await pbFetch("/jobs/import", { method: "POST", body: JSON.stringify({ jobs: batch }) });
        if (r.ok) {
          const d = await r.json();
          const got = new Set();
          (d.imported || []).forEach(m => { if (m && m.localId && m.id) { mapped.push(m); got.add(m.localId); } });
          ids.forEach(id => { if (!got.has(id)) refused.add(id); });   // the server turned it down
        }
      } catch (e) { /* no signal: tried again next time */ }
      batch = []; size = 0;
    };
    for (const p of candidates) {
      if (isCancelled()) return [];
      const job = { id: p.id, name: p.name, councilId: p.councilId || null, trades: p.trades || [],
        accuracy: p.accuracy || null, tuning: p.tuning || null, file: typeof p.file === "string" ? p.file : null,
        aiAnalysis: p.aiAnalysis, createdAt: p.createdAt || Date.parse(p.date) || null,
        local: (() => { const l = pbReadJobLocal(p.id); PB_FIELDS.forEach(f => { l[f].at = importAt; }); return l; })() };
      let n = 0;
      try { n = JSON.stringify(job).length; } catch (e) { refused.add(p.id); continue; }
      if (n > 3000000) { refused.add(p.id); continue; }     // too big to send; stays on this device
      if (batch.length && (size + n > 6000000 || batch.length >= 10)) await send();
      batch.push(job); size += n;
    }
    await send();
    mapped.forEach(m => {
      pbMoveJobLocal(m.localId, m.id);
      const atMap = {}; PB_FIELDS.forEach(f => { atMap[f] = importAt; });
      pbSetAtMap(m.id, atMap);
      pbSetDirty(m.id, false);
      pbCloudIds.add(m.id);
      const src = candidates.find(p => p.id === m.localId);
      if (src && src.aiAnalysis) pbCachePut(m.id, src.aiAnalysis);
    });
    if (refused.size && !isCancelled()) {
      setProjectsQuiet(projectsRef.current.map(p => (p && refused.has(p.id)) ? { ...p, importFailed: true } : p), email);
    }
    return mapped;
  };

  // The account's jobs from the server, merged with this device's: filed jobs
  // come from the server; jobs only on this device (not imported yet, or an
  // admin's view-only copy) stay as they are.
  // Every page of the account's jobs (newest first).
  const pbLoadAllJobs = async () => {
    let rows = [], after = null;
    for (let i = 0; i < 25; i++) {
      const d = await pbGetJSON("/jobs" + (after ? "?after=" + encodeURIComponent(after) : ""));
      rows = rows.concat(Array.isArray(d.jobs) ? d.jobs : []);
      if (!d.next) return { rows, complete: true };
      after = d.next;
    }
    return { rows, complete: false };
  };
  const pbRefreshJobs = async (email, isCancelled) => {
    if (refreshingRef.current === email) return;        // one at a time per account
    refreshingRef.current = email;
    try { await pbRefreshJobsNow(email, isCancelled); }
    finally { if (refreshingRef.current === email) refreshingRef.current = null; }
  };
  const pbRefreshJobsNow = async (email, isCancelled) => {
    listAtRef.current = Date.now();
    let rows, complete;
    try { ({ rows, complete } = await pbLoadAllJobs()); }
    catch (e) { console.warn("[filing] couldn't load jobs: " + e.message); return; }
    if (isCancelled()) return;
    const mapped = await pbImportLocalJobs(email, isCancelled);
    if (isCancelled()) return;
    if (mapped.length) {
      try { ({ rows, complete } = await pbLoadAllJobs()); } catch (e) {}
      if (isCancelled()) return;
      pbNote(mapped.length === 1 ? "1 job from this device is now saved to your account."
        : mapped.length + " jobs from this device are now saved to your account.");
    }
    rows = rows.filter(r => r && !pbDeletedIds.has(r.id));
    rows.forEach(r => pbCloudIds.add(r.id));
    const latest = projectsRef.current;
    const prevById = new Map(latest.map(p => [p && p.id, p]));
    const imported = new Set(mapped.map(m => m.localId));
    const serverIds = new Set(rows.map(r => r.id));
    const young = (p) => p && p.createdAt && Date.now() - p.createdAt < 120000;
    const keepLocal = latest.filter(p => p && !p.cloud && !imported.has(p.id));
    // a scan filed while the list was loading may not be in it yet; and if
    // the list was too long to load in full, nothing beyond it is dropped
    const fresh = latest.filter(p => p && p.cloud && !serverIds.has(p.id) && (young(p) || !complete) && !pbDeletedIds.has(p.id));
    const pend = LS.get(pbPendingKey(email), {}) || {};
    const shells = rows.map(r => {
      const sh = pbShellFromRow(r, prevById.get(r.id));
      const pn = pend[r.id] && (typeof pend[r.id] === "string" ? { name: pend[r.id], at: 0 } : pend[r.id]);
      if (pn && pn.name !== r.name) { sh.name = pn.name; pbSendRename(r.id, pn.name, email, pn.at || 1); }
      else if (pn) { const n = LS.get(pbPendingKey(email), {}) || {}; delete n[r.id]; LS.set(pbPendingKey(email), n); }
      return sh;
    });
    // settings changed on this device while it had no signal: send them now
    rows.forEach(r => { if (pbIsDirty(r.id)) pbQueueJobSync(r.id, 800); });
    setProjectsQuiet([...keepLocal, ...fresh, ...shells], email);
    // the job open on screen was deleted on another device
    const act = activeRef.current;
    if (complete && act && act.cloud && !serverIds.has(act.id) && !young(act)) { setActiveProject(null); setScreen("home"); }
  };

  // The server's plan (trial, beta, Stripe or none) decides what this device
  // shows. Before locking anyone out, Stripe is asked directly, in case a
  // payment hasn't reached the account yet.
  const pbApplyServerPlan = async (me, email, isCancelled) => {
    let ent = me && me.plan;
    if (!ent) return;                        // filing off: the device's own record stands
    let fp = pbFeaturePlan(ent);
    // Not paying as far as the server knows: ask Stripe directly (verified
    // emails only). The server files whatever Stripe says as it answers, so a
    // payment the webhook never delivered is picked up here.
    if (ent.source !== "stripe" && ent.source !== "admin" && me.emailVerified) {
      let paid = null;
      try { const s = await pbGetJSON("/subscription-status"); if (s && s.plan && PLANS[s.plan]) paid = s.plan; } catch (e) {}
      if (isCancelled()) return;
      if (paid) {
        fp = paid;
        const me2 = await pbRefreshMe(isCancelled);     // the plan's real allowance, now it's filed
        if (isCancelled()) return;
        if (me2 && me2.plan && me2.plan.source === "stripe") { ent = me2.plan; fp = pbFeaturePlan(ent) || paid; }
        else ent = Object.assign({}, ent, { source: "stripe", plan: paid });
      }
    }
    const cur = LS.get("to_user", null);
    if (!cur || cur.email !== email || cur.isAdminTest) return;
    // stripe-live-2: Pro's free trial is a Stripe subscription that hasn't
    // charged yet ("trialing", card on file); the old card-free trial was
    // source "trial". Both show the trial's end; only the card one is paid for.
    const cardTrial = ent.source === "stripe" && ent.status === "trialing";
    const trial = (ent.source === "trial" || cardTrial) && fp !== null;
    const end = trial ? (ent.trialEndsAt || null) : null;
    const next = { ...cur, plan: fp, trialActive: trial, trialCard: cardTrial, trialEndsAt: end,
      trialCancelled: !!(cardTrial && ent.cancelAtPeriodEnd),
      trialStarted: end ? new Date(end - 7 * 86400000).toISOString() : (trial ? cur.trialStarted : null) };
    setUser(next); LS.set("to_user", next);
    const accounts = LS.get("to_accounts", {});
    // No plan: the plans screen, locked (a new account starts Pro's free
    // trial there). Not for an admin email still waiting on verification:
    // the home screen's card says how to finish that.
    const lock = fp === null && !me.adminWaiting;
    accounts[email] = { ...(accounts[email] || {}), plan: fp, trialActive: trial,
      trialExpired: lock, paidPlan: (fp && (!trial || cardTrial)) ? fp : null };
    LS.set("to_accounts", accounts);
    if (lock) setSubScreen("pricing");
  };

  // Signed in, or a different account: that account's jobs, plan and scans.
  useEffect(() => {
    const email = userEmail;
    pbCloudIds.clear();
    setAccount(null);
    const cached = email ? LS.get("to_projects_" + email, []) : [];
    const list = Array.isArray(cached) ? cached : [];
    projectsRef.current = list;
    setProjectsRaw(list);
    if (!email) return undefined;
    let cancelled = false;
    const isCancelled = () => cancelled;
    (async () => {
      const me = await pbRefreshMe(isCancelled);
      if (cancelled || !me || me.promoted) return;      // promoted: now on the admin dashboard
      await pbApplyServerPlan(me, email, isCancelled);
      if (cancelled || !me.filing) return;
      list.forEach(p => { if (p && p.cloud) pbCloudIds.add(p.id); });
      await pbRefreshJobs(email, isCancelled);
      if (!cancelled) pbFlushCorrections(email);
    })();
    return () => { cancelled = true; };
  }, [userEmail]);

  // Back to the app after a while (phone unlocked, tab switched): pick up
  // jobs scanned on another device.
  useEffect(() => {
    if (!filingOn || !userEmail) return undefined;
    const isCancelled = () => (LS.get("to_user", null) || {}).email !== userEmail;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - listAtRef.current < 120000) return;
      pbRefreshMe(isCancelled).then(me => { if (me && !isCancelled()) pbApplyServerPlan(me, userEmail, isCancelled); });
      pbRefreshJobs(userEmail, isCancelled);
      pbFlushCorrections(userEmail);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [filingOn, userEmail]);

  // Device vs server copy of a job's settings, field by field: newest wins.
  // Anything changed here and not yet sent goes up (the server merges too).
  const pbReconcile = (pid, srvLocal) => {
    const pulled = pbAdoptLocal(pid, srvLocal);
    if (pbIsDirty(pid)) pbQueueJobSync(pid, 0);
    return pulled ? "pulled" : "same";
  };
  const pbForgetMissingJob = (pid) => {
    pbCloudIds.delete(pid);
    pbForgetJobLocal(pid);
    setProjectsQuiet(projectsRef.current.filter(p => p && p.id !== pid));
    if (activeRef.current && activeRef.current.id === pid) { setActiveProject(null); setScreen("home"); }
  };
  // Settings only (no results download): did another device change them?
  const pbPullJob = async (pid) => {
    try {
      const job = (await pbGetJSON("/jobs/" + encodeURIComponent(pid) + "?results=0")).job;
      if (job && pbReconcile(pid, job.local) === "pulled" && activeRef.current && activeRef.current.id === pid) {
        setJobNonce(n => n + 1);
      }
    } catch (e) {
      if (e && e.status === 404) pbForgetMissingJob(pid);
    }
  };
  const pbPlanUrl = async (p) => {
    const path = p.viewOnly && p.viewUid
      ? "/admin/accounts/" + encodeURIComponent(p.viewUid) + "/jobs/" + encodeURIComponent(p.viewJobId) + "/plan"
      : "/jobs/" + encodeURIComponent(p.id) + "/plan";
    const r = await pbFetch(path);
    if (!r.ok) throw new Error(await pbErrorText(r, "Couldn't fetch the plan PDF."));
    return (await r.json()).url || null;
  };

  // Apple 5.1.1(v) / Google Play: delete the account from inside the app.
  const deleteAccount = async (password) => {
    const email = user.email;
    // plancheck-v1-2026-10-13: the password first. It proves it's them (not
    // someone holding an unlocked phone), and Firebase only lets a sign-in be
    // deleted soon after one, so nothing can stop half-way for that reason.
    try { await fbReauthenticate(password || ""); }
    catch (e) {
      const c = e && e.code;
      if (c === "auth/wrong-password" || c === "auth/invalid-credential" || c === "auth/missing-password") throw new Error("That password isn't right.");
      throw new Error(fbErrorMessage(e));
    }
    // The server stops any PlanBoss subscription FIRST (a deleted account
    // must never be billed again). With filing on it then deletes every job,
    // plan PDF, correction and the sign-in; with filing off it holds nothing
    // else, and the sign-in is deleted here.
    let r;
    try { r = await pbFetch("/account", { method: "DELETE", body: JSON.stringify({ confirm: "DELETE" }) }); }
    catch (e) {
      throw new Error((e && e.code === "signed_out") ? e.message : "Couldn't reach the PlanBoss server — check your connection and try again.");
    }
    if (!r.ok) throw new Error((await pbErrorInfo(r, "Couldn't delete the account.")).message);
    const d = await r.json().catch(() => ({}));
    if (!d || d.deleted !== true) {
      try { await fbDeleteUser(); }
      catch (e) {
        if (e && e.code === "auth/requires-recent-login") throw new Error("Your subscription has been cancelled, but the sign-in needs one more step: sign out, sign back in, then delete the account straight away.");
        throw new Error("Your subscription has been cancelled, but the sign-in couldn't be deleted. Email " + PB_SUPPORT_EMAIL + " and we'll finish it for you.");
      }
    }
    // Clear everything this device kept for the account.
    pbCheckImgClearAll();
    (projectsRef.current || []).forEach(p => { if (p) pbForgetJobLocal(p.id); });
    ["to_projects_" + email, "to_corrections_" + email, "user_price_overrides_" + email,
     "user_global_overrides_" + email, "pb_corr_outbox_" + email, "pb_pending_names_" + email,
     "pb_open_after_reload"].forEach(k => { try { localStorage.removeItem(k); } catch (e) {} });
    const accounts = LS.get("to_accounts", {});
    delete accounts[email];
    LS.set("to_accounts", accounts);
    logout();
    window.alert("Your PlanBoss account and everything saved in it has been deleted.");
  };

  // A save's reply brought newer settings from another device for the job on
  // screen: re-draw it with them.
  useEffect(() => {
    const on = (e) => {
      const id = e && e.detail && e.detail.id;
      if (id && activeRef.current && activeRef.current.id === id) setJobNonce(n => n + 1);
    };
    window.addEventListener("pb-job-settings", on);
    return () => window.removeEventListener("pb-job-settings", on);
  }, []);

  // An admin's "Open in app" lands here after the reload.
  useEffect(() => {
    let want = null;
    try { want = localStorage.getItem("pb_open_after_reload"); localStorage.removeItem("pb_open_after_reload"); } catch (e) {}
    if (!want) return;
    const p = (LS.get("to_projects_" + getCurrentUserEmail(), []) || []).find(x => x && x.id === want);
    if (p) { setActiveProject(p); setScreen("materials"); }
  }, []);

  // ── Detect Stripe redirect back to app after payment ──────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentStatus = params.get("payment");
    const planId = params.get("plan");

    if (paymentStatus === "success" && planId && PLANS[planId]) {
      // security-v1-2026-10-12: the address bar is not proof of payment.
      // Typing ?payment=success&plan=business used to unlock Business for
      // free. Now the server reads Stripe and the plan comes from there.
      const currentUser = LS.get("to_user", null);
      window.history.replaceState({}, "", window.location.pathname);
      setSubScreen(null);
      setScreen("home");
      if (currentUser) {
        (async () => {
          let paid = null, unverified = false, trialEnd = null;
          for (let i = 0; i < 4 && !paid && !unverified; i++) {   // Stripe can take a few seconds
            try {
              const r = await pbFetch("/subscription-status");
              const s = r.ok ? await r.json() : null;
              if (s && s.plan && PLANS[s.plan]) { paid = s.plan; trialEnd = (s.status === "trialing" && s.trialEnd) || null; }
              if (s && s.status === "email_not_verified") unverified = true;
            } catch (e) {}
            if (!paid && !unverified) await new Promise(res => setTimeout(res, 3000));
          }
          if (unverified) {
            alert("Payment received — one more step. Verify your email (check your inbox and spam for the PlanBoss verification email), then sign out and sign back in to unlock your plan.");
            return;
          }
          if (!paid) {
            alert("We couldn't confirm your payment with Stripe yet. If you were charged, wait a minute, then sign out and sign back in.");
            return;
          }
          // stripe-live-2: Pro's free trial (card on file, first charge at trialEnd).
          const upgraded = { ...currentUser, plan: paid, trialActive: !!trialEnd, trialCard: !!trialEnd,
            trialEndsAt: trialEnd, trialStarted: trialEnd ? new Date(trialEnd - 7 * 86400000).toISOString() : null };
          setUser(upgraded);
          LS.set("to_user", upgraded);
          const accounts = LS.get("to_accounts", {});
          if (accounts[currentUser.email]) {
            // paidPlan + cleared trial flags, or an expired-trial user who then
            // paid would be sent back to the pricing screen at the next login.
            accounts[currentUser.email] = { ...accounts[currentUser.email], plan: paid,
              paidPlan: paid, trialActive: false, trialExpired: false };
            LS.set("to_accounts", accounts);
          }
          setUpgradeToast(trialEnd ? { plan: paid, trialEnd } : paid);
          setTimeout(() => setUpgradeToast(null), 5000);
          pbRefreshMe();      // store-v1: the new plan's scan allowance
        })();
      }
    }

    if (paymentStatus === "cancelled") {
      window.history.replaceState({}, "", window.location.pathname);
      setSubScreen(null);
    }
  }, []);

  // Auto-save prefs to localStorage (setProjects is with the filing code above)
  const setPrefs    = p => { setPrefsRaw(p);    LS.set("to_prefs",    p); };

  const login = u => {
    // Always load the latest plan from accounts store (in case upgraded)
    const accounts = LS.get("to_accounts", {});
    const stored = accounts[u.email];
    // Resolve the plan, preserving null for skip-trial users who haven't picked yet.
    // Priority: stored account plan > value passed in by login flow > null (no plan).
    const rawPlan = (stored && stored.plan !== undefined) ? stored.plan : (u.plan !== undefined ? u.plan : null);
    // Sanitise: if a non-null plan exists but is invalid (legacy "free", typo, etc.), reset to null.
    const planToUse = (rawPlan === null || rawPlan === undefined) ? null : (PLANS[rawPlan] ? rawPlan : null);
    const userWithPlan = { ...u, plan: planToUse };
    setUser(userWithPlan); LS.set("to_user", userWithPlan);
    setScreen("home");
    // Trial expiry lockout — if stored.trialExpired is set, force pricing screen with no escape.
    if(stored && stored.trialExpired && !["pro","proplus","business"].includes(stored.paidPlan)) {
      setSubScreen("pricing");
    }
    // stripe-live-2: a new account goes straight to the plans (Pro's free
    // trial starts there, with the card, once the email is verified).
    else if(u.signupChoice === "skip" || u.signupChoice === "plans") {
      setSubScreen("pricing");
    }
  };
  const logout = () => {
    // security-v1: end the Firebase session too, or the next person on this
    // phone could still scan as the last one.
    fbSignOut().catch(() => {});
    setUser(null); LS.set("to_user", null);
    setScreen("home"); setActiveProject(null);
    setAccount(null);
    setCornerCheck(null);
    pbFilingOn = false;
  };

  // store-v1: a filed job's results come from the device cache or the
  // server; its settings are checked against the server either way.
  const openProject = async (p) => {
    if (!p || opening) return;
    if (!p.cloud || p.aiAnalysis) {
      setActiveProject(p); setScreen("materials");
      if (p.cloud) pbPullJob(p.id);
      return;
    }
    const cached = pbCacheGet(p.id);
    if (cached) {
      setActiveProject({ ...p, aiAnalysis: cached }); setScreen("materials");
      pbPullJob(p.id);
      return;
    }
    setOpening(p.id);
    try {
      const r = await pbFetch("/jobs/" + encodeURIComponent(p.id));
      if (r.status === 404) {
        pbForgetMissingJob(p.id);
        window.alert("\"" + p.name + "\" is no longer in your account (it may have been deleted on another device).");
        return;
      }
      if (!r.ok) throw new Error(await pbErrorText(r, "Couldn't open this job."));
      const job = (await r.json()).job || {};
      if (!job.aiAnalysis) throw new Error("This job's results couldn't be loaded. Email " + PB_SUPPORT_EMAIL + " and we'll look into it.");
      pbCachePut(p.id, job.aiAnalysis);
      pbReconcile(p.id, job.local);
      setActiveProject({ ...p, aiAnalysis: job.aiAnalysis });
      setScreen("materials");
    } catch (e) {
      window.alert((e && e.message && e.message !== "Failed to fetch") ? e.message : "Couldn't open this job — check your connection and try again.");
    } finally {
      setOpening(null);
    }
  };

  const completeNewProject = ({name,trades,file,councilId,accuracy,tuning,aiAnalysis,jobId,clientId,checkImage,checkImages}) => {
    // store-v1: with filing on, the server has already filed this scan (PDF
    // and results) and sent back its id; without one it stays on this device
    // and is copied up the next time filing is on.
    const now = Date.now();
    const newP = {
      id: jobId || clientId || uid(), cloud: !!jobId, hasPdf: !!jobId, name, trades, file, councilId, accuracy, tuning, aiAnalysis,
      date: new Date(now).toLocaleDateString("en-NZ",{day:"numeric",month:"short",year:"numeric"}),
      createdAt: now, status: "complete",
      // no made-up fallback: the list shows "—" when no floor area was read
      area: aiAnalysis?.floorArea || tuning?.floorAreaOverride || null,
    };
    if (jobId) { pbCloudIds.add(jobId); pbCachePut(jobId, aiAnalysis); pbSetAtMap(jobId, {}); pbSetDirty(jobId, false); }
    setProjectsQuiet([newP, ...projectsRef.current.filter(p => p && p.id !== newP.id)]);
    setActiveProject(newP);
    // plancheck-v1-2026-10-13: for a plasterer, the external corners are
    // checked on the marked-up plan BEFORE the numbers come up (Grant's
    // "one step before it all comes up"). Not for a plan that couldn't be read
    // or wasn't measured: there are no corners to check.
    const v = pbVerdictFor(newP);
    // every-storey-v1-2026-10-14: one picture per measured floor
    const pics = pbPicList({ levels: checkImages || [] }) || pbPicList(checkImage);
    if (pics) pbCheckImgPut(newP.id, pics.length > 1 || pics[0].level ? pics : pics[0]);
    if (pics && (trades || []).includes("plasterer") && v && v.verdict !== "stop" && !(v.verdict === "hold" && v.kind !== "partial" && v.kind !== "levels")) {
      setCornerCheck({ pid: newP.id, pics, readOnly: false });
      setScreen("cornercheck");
    } else setScreen("materials");
    pbRefreshMe();     // the server has counted this scan
  };
  // The stud the corner count is multiplied by: the tradie's, else the plan's.
  const pbStudFor = (p) => {
    const c = Number(getConfirmedStud(p && p.id));
    if (c) return c;
    const g = p && p.aiAnalysis && p.aiAnalysis.geometry;
    const v = Number(g && g.calibration && g.calibration.stud_m && g.calibration.stud_m.value);
    return v || null;
  };
  const pbStudAssumed = (p) => {
    if (Number(getConfirmedStud(p && p.id))) return false;
    const g = p && p.aiAnalysis && p.aiAnalysis.geometry;
    return !!(g && g.calibration && g.calibration.stud_m && g.calibration.stud_m.source === "assumed");
  };
  const saveCornerCheck = (p, res) => {
    if (!p || !res || p.viewOnly) return;
    const prev = pbGetCornerCheck(p.id);
    pbSetCornerCheck(p.id, res);
    const parts = Array.isArray(res.levels) ? res.levels : [res];
    const moved = res.count !== res.ai || parts.some(l => l && ((l.removed || []).length > 0 || (l.added || []).length > 0));
    const same = prev && JSON.stringify(Array.isArray(prev.levels) ? prev.levels.map(l => [l.removed, l.added]) : [prev.removed, prev.added]) ===
      JSON.stringify(Array.isArray(res.levels) ? res.levels.map(l => [l.removed, l.added]) : [res.removed, res.added]);
    if (moved && !same) logCornerCorrection(user && user.email, p, res);
  };
  const openCornerCheck = async (p, readOnly) => {
    if (!p) return;
    const pics = pbPicList(await pbCheckImgGet(p.id));
    if (!pics) { window.alert("The check picture for this job isn't on this device any more. Scan the plan again to check its corners."); return; }
    setCornerCheck({ pid: p.id, pics, readOnly: !!readOnly || !!p.viewOnly });
    setScreen("cornercheck");
  };

  // ── Admin route ──────────────────────────────────────────────────────────────
  const [adminUser, setAdminUser] = useState(()=>LS.get("mu_admin",null));
  const [adminScreen, setAdminScreen] = useState("dashboard");
  // auth-action-2026-10-21: a link from a PlanBoss email (verify email, reset
  // password) comes before everything else, signed in or not.
  const [authAction, setAuthAction] = useState(() => pbReadAuthAction());

  if(authAction) return <PbAuthAction action={authAction} onDone={async (verified) => {
    try { window.history.replaceState({}, "", window.location.pathname); } catch (e) {}
    setAuthAction(null);
    // Signed in on this device too? Pick up "verified" (and admin) straight away.
    if (verified && userEmail) { try { if (await fbRefreshSignIn()) pbRefreshMe(); } catch (e) {} }
  }}/>;

  if(adminUser) return <AdminDashboard
    adminScreen={adminScreen} setAdminScreen={setAdminScreen}
    onLogout={()=>{fbSignOut().catch(()=>{});setAdminUser(null);LS.set("mu_admin",null);}}/>;

  if(!user) return <LoginScreen
    onLogin={login} onSignup={login}
    onAdminLogin={(a)=>{setAdminUser(a);LS.set("mu_admin",a);}}/>;
  // store-v1: someone already paying changes plan on Stripe's billing page;
  // a second Checkout would start a second subscription.
  const hasStripeSub = !!(account && account.plan && account.plan.source === "stripe");
  const choosePlan = (pid, billing) => { if (hasStripeSub) pbOpenBillingPortal(); else setSubScreen(`checkout:${pid}:${billing||"annual"}`); };
  // stripe-live-2: may this account still start Pro's free trial? Not with a
  // paid (Stripe) plan, nor once the server says it's had one; unknown
  // (filing off) shows the offer and Stripe's page has the final word. (A
  // tester or admin who buys Pro gets the trial too, as the server decides.)
  const pbTrialOffer = (() => {
    const ent = account && account.plan;
    if (ent && ent.plan && ent.source === "stripe") return false;
    if (account && account.trialOffer && account.trialOffer.eligible === false) return false;
    if (!ent && user.plan && !user.trialActive) return false;
    return true;
  })();
  const pbSellBusiness = !!(account && account.sellBusiness);
  if(subScreen==="pricing") {
    const stored = LS.get("to_accounts",{})[user.email];
    const isLocked = !!(stored && stored.trialExpired && !["pro","proplus","business"].includes(stored.paidPlan));
    return <PricingScreen currentPlan={user.plan} lockout={isLocked} onSelect={choosePlan} onBack={isLocked?undefined:()=>setSubScreen(null)}
      onSignOut={logout} onAccount={()=>{setSubScreen(null);setScreen("account");}} trialOffer={pbTrialOffer} sellBusiness={pbSellBusiness}
      why={pbNoPlanWhy(account, !!(stored && stored.trialExpired && !(account && account.plan)))}/>;
  }
  if(subScreen==="subscriptions") return <SubscriptionsScreen user={user} onSelect={choosePlan} onBack={()=>setSubScreen(null)} trialOffer={pbTrialOffer} sellBusiness={pbSellBusiness}/>;
  if(subScreen?.startsWith("checkout:")) {
    const parts = subScreen.split(":");
    const pid = parts[1];
    const period = parts[2] || "annual"; // default annual for backwards-compat with old routes
    return <CheckoutScreen plan={pid} period={period} onComplete={pid=>{setUser(u=>({...u,plan:pid}));LS.set("to_user",{...user,plan:pid});setSubScreen(null);}} onBack={()=>setSubScreen("pricing")}
      trialOffer={pbTrialOffer} emailVerified={account?account.emailVerified:undefined} email={user.email} onCheckVerified={pbCheckVerified}/>;
  }

  const plan=PLANS[user.plan]||PLANS.pro;
  const nav=[
    {id:"home",    icon:"🏠", label:"Home"},
    {id:"new",     icon:"➕", label:"New project"},
    ...(activeProject&&screen==="materials"?[{id:"materials",icon:"📋",label:"Materials & quotes"}]:[]),
    {id:"account", icon:"👤", label:"Account"},
  ];


  // ── PHASE 1: CORRECTION MODAL ─────────────────────────────────
  const CorrectionEditModal = () => {
    if (!editingMeasurement) return null;
    const { projectId, trade, measurementKey, aiValue, unit, label } = editingMeasurement;
    const existing = getExistingCorrection(projectId, trade, measurementKey);
    const [realValue, setRealValue] = useState(existing ? String(existing.realValue) : '');
    const [reason, setReason] = useState(existing ? existing.reason : '');
    const [note, setNote] = useState(existing ? existing.note : '');

    const diffPreview = () => {
      const ai = parseFloat(aiValue), real = parseFloat(realValue);
      if (!realValue || isNaN(real) || ai === 0) return null;
      const pct = (((real - ai) / ai) * 100).toFixed(1);
      const col = Math.abs(pct) > 15 ? '#ff6b6b' : Math.abs(pct) > 5 ? '#ffa94d' : '#69db7c';
      return <span style={{color:col,fontWeight:700,marginLeft:8}}>{pct>0?'+':''}{pct}%</span>;
    };

    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',alignItems:'flex-end',justifyContent:'center'}}
        onClick={()=>setEditingMeasurement(null)}>
        <div style={{background:'#1a1a2e',borderRadius:'16px 16px 0 0',padding:'24px 20px 40px',width:'100%',maxWidth:480,boxShadow:'0 -4px 32px rgba(0,0,0,0.5)'}}
          onClick={e=>e.stopPropagation()}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
            <div>
              <div style={{color:'#f97316',fontWeight:700,fontSize:15}}>Correct AI Measurement</div>
              <div style={{color:'#9ca3af',fontSize:13,marginTop:2}}>{label}</div>
            </div>
            <button onClick={()=>setEditingMeasurement(null)}
              style={{background:'none',border:'none',color:'#9ca3af',fontSize:24,cursor:'pointer',lineHeight:1,padding:'0 4px'}}>×</button>
          </div>
          <div style={{background:'#0f0f1a',borderRadius:10,padding:'10px 14px',marginBottom:14,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{color:'#9ca3af',fontSize:13}}>AI estimate</span>
            <span style={{color:'#e5e7eb',fontWeight:600,fontSize:15}}>{aiValue} {unit}</span>
          </div>
          <div style={{marginBottom:14}}>
            <label style={{color:'#9ca3af',fontSize:12,display:'block',marginBottom:6}}>Your real measurement{unit?' ('+unit+')':''}</label>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <input type="number" value={realValue} onChange={e=>setRealValue(e.target.value)}
                placeholder="Enter real number" autoFocus
                style={{flex:1,background:'#0f0f1a',border:'2px solid #f97316',borderRadius:8,color:'#fff',padding:'10px 12px',fontSize:16,outline:'none'}}/>
              {diffPreview()}
            </div>
          </div>
          <div style={{marginBottom:14}}>
            <label style={{color:'#9ca3af',fontSize:12,display:'block',marginBottom:6}}>Why was it off? (helps improve the AI)</label>
            <select value={reason} onChange={e=>setReason(e.target.value)}
              style={{width:'100%',background:'#0f0f1a',border:'1px solid #374151',borderRadius:8,color:reason?'#e5e7eb':'#6b7280',padding:'10px 12px',fontSize:14,outline:'none'}}>
              <option value="">Select a reason...</option>
              {CORRECTION_REASONS.map(r=><option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{marginBottom:20}}>
            <label style={{color:'#9ca3af',fontSize:12,display:'block',marginBottom:6}}>Additional note (optional)</label>
            <textarea value={note} onChange={e=>setNote(e.target.value)}
              placeholder="e.g. North wall wasn't on the schedule" rows={2}
              style={{width:'100%',background:'#0f0f1a',border:'1px solid #374151',borderRadius:8,color:'#e5e7eb',padding:'10px 12px',fontSize:14,outline:'none',resize:'vertical',boxSizing:'border-box'}}/>
          </div>
          <button onClick={()=>handleSaveCorrection({projectId,trade,measurementKey,aiValue,unit,label,realValue,reason,note})}
            style={{width:'100%',background:'#f97316',color:'#fff',border:'none',borderRadius:10,padding:'14px',fontSize:16,fontWeight:700,cursor:'pointer'}}>
            Save Correction ✓
          </button>
          <div style={{textAlign:'center',color:'#4b5563',fontSize:11,marginTop:10}}>
            Your correction helps improve PlanBoss for all tradies 🎯
          </div>
        </div>
      </div>
    );
  };

  // ── PHASE 1: MEASUREMENT ROW WRAPPER ──────────────────────────
  const MeasurementRow = ({ projectId, trade, measurementKey, aiValue, unit, label, children }) => {
    const existing = getExistingCorrection(projectId, trade, measurementKey);
    return (
      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <div style={{flex:1}}>{children}</div>
        {existing && (
          <div style={{fontSize:11,color:'#69db7c',background:'rgba(105,219,124,0.1)',borderRadius:6,padding:'2px 8px',whiteSpace:'nowrap'}}>
            ✓ {existing.realValue} {unit} ({existing.diffPct})
          </div>
        )}
        <button title="Correct this measurement"
          onClick={()=>setEditingMeasurement({projectId,trade,measurementKey,aiValue,unit,label})}
          style={{background:existing?'rgba(249,115,22,0.15)':'rgba(255,255,255,0.07)',border:'none',borderRadius:6,
            padding:'4px 8px',cursor:'pointer',fontSize:14,lineHeight:1,
            color:existing?'#f97316':'#6b7280',flexShrink:0}}>
          ✏️
        </button>
      </div>
    );
  };
  // ── END PHASE 1 COMPONENTS ─────────────────────────────────────

  return (
    <div className="mu-app" style={{display:"flex",minHeight:"100vh",background:C.bg}}>

      {/* ── Upgrade success toast ── */}
      {upgradeToast&&(()=>{
        // stripe-live-2: { plan, trialEnd } after starting Pro's free trial.
        const tp = typeof upgradeToast === "string" ? upgradeToast : upgradeToast.plan;
        const tEnd = typeof upgradeToast === "string" ? null : upgradeToast.trialEnd;
        return (
        <div role="status" data-pb-upgrade-toast style={{position:"fixed",top:20,left:"50%",transform:"translateX(-50%)",
          background:PLANS[tp]?.color||C.tealMid,color:"#fff",
          borderRadius:12,padding:"14px 24px",fontSize:14,fontWeight:700,
          boxShadow:"0 4px 24px rgba(0,0,0,0.18)",zIndex:2000,maxWidth:"92vw",
          display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20}}>🎉</span>
          {tEnd
            ?<span>Your free trial of {PLANS[tp]?.name} has started. Nothing is charged until {pbDate(tEnd,{day:"numeric",month:"short"})}.</span>
            :<span>Welcome to {PLANS[tp]?.name}! Your plan has been upgraded.</span>}
          <button onClick={()=>setUpgradeToast(null)} style={{background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:16,marginLeft:8}}>✕</button>
        </div>);
      })()}

      {/* Sidebar */}
      <div className="mu-sidebar" style={{width:220,background:"#111827",borderRight:"none",display:"flex",flexDirection:"column",flexShrink:0}}>
        <div style={{padding:"20px 18px 16px",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
          {/* logo-signout-2026-10-20 (Grant): the logo signs out and goes back
              to the sign-in screen, as it used to — now ending the Firebase
              session too (logout), so the next person on the device can't
              scan as the last one. */}
          <Logo onClick={logout} title="Sign out" dark/>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:10}}>
            <div style={{fontSize:12,color:"#9CA3AF",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:120}}>{user.email}</div>
            <Pill label={plan.name} color={plan.color} bg={plan.light} small/>
          </div>
        </div>
        <div style={{padding:"12px 10px",flex:1}}>
          {nav.map((n,i)=>(
            <button key={i} onClick={()=>setScreen(n.id)}
              style={{display:"flex",alignItems:"center",gap:10,width:"100%",padding:"10px 12px",border:"none",borderRadius:8,cursor:"pointer",marginBottom:2,
                background:screen===n.id?'rgba(240,100,0,0.12)':"transparent",
                color:screen===n.id?'#F06400':'#9CA3AF',
                fontWeight:screen===n.id?700:400,fontSize:14,textAlign:"left"}}>
              <span style={{fontSize:16}}>{n.icon}</span>{n.label}
            </button>
          ))}
        </div>
        {plan.id!=="business"&&(
          <div style={{padding:"12px",borderTop:"1px solid rgba(255,255,255,0.08)"}}>
            {/* Trial countdown banner */}
            {user.trialActive&&(user.trialEndsAt||user.trialStarted)&&(()=>{
              // store-v1: the server's trial end date when there is one
              const trialEnd=user.trialEndsAt||(new Date(user.trialStarted).getTime()+7*24*60*60*1000);
              const daysLeft=Math.max(0,Math.ceil((trialEnd-Date.now())/(1000*60*60*24)));
              const isUrgent=daysLeft<=3;
              return (
                <div style={{background:isUrgent?"rgba(220,38,38,0.15)":"rgba(240,100,0,0.12)",border:`1px solid ${isUrgent?"rgba(220,38,38,0.3)":"rgba(240,100,0,0.3)"}`,borderRadius:8,padding:"10px 12px",marginBottom:10,textAlign:"center"}}>
                  <div style={{fontSize:11,fontWeight:700,color:isUrgent?"#FCA5A5":"#F06400",marginBottom:3}}>
                    {user.trialCard
                      ?(daysLeft===0?"Free trial ends today":daysLeft===1?"1 day left in your free trial":`${daysLeft} days left in your free trial`)
                      :(daysLeft===0?"Trial expires today!":daysLeft===1?"1 day left in trial":`${daysLeft} days left in trial`)}
                  </div>
                  <div style={{fontSize:10,color:"#9CA3AF",marginBottom:8}}>{user.trialCard
                    ?(user.trialCancelled
                      ?<>Cancelled: ends {new Date(trialEnd).toLocaleDateString("en-NZ",{day:"numeric",month:"short"})}, nothing charged</>
                      :<>First payment {new Date(trialEnd).toLocaleDateString("en-NZ",{day:"numeric",month:"short"})}, unless you cancel</>)
                    :<>Pro features until {new Date(trialEnd).toLocaleDateString("en-NZ",{day:"numeric",month:"short"})}</>}</div>
                  {/* stripe-live-2: a card trial is already a subscription: manage or cancel it on Stripe's page */}
                  <button onClick={()=>user.trialCard?pbOpenBillingPortal():setSubScreen("pricing")} style={{width:"100%",padding:"6px",background:C.orange,color:"#fff",border:"none",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:700}}>
                    {user.trialCard?"Manage or cancel →":"Subscribe now →"}
                  </button>
                </div>
              );
            })()}
            <button onClick={()=>setSubScreen("pricing")} style={{width:"100%",padding:"10px",background:C.orange,color:"#fff",border:"none",borderRadius:10,fontSize:13,fontWeight:700,cursor:"pointer"}}>
              {user.trialActive&&!user.trialCard?"Choose a plan →":"Upgrade plan →"}
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div ref={contentRef} style={{flex:1,overflow:"auto"}}>
        {(()=>{const u=LS.get("to_user",null);return u&&u.isAdminTest?(<div onClick={()=>{LS.set("to_user",null);LS.set("mu_admin",{email:u.adminEmail||"admin",role:"admin",loginTime:new Date().toISOString()});window.location.reload();}} style={{position:"fixed",top:16,right:16,zIndex:9999,background:"linear-gradient(135deg,#F06400,#D4530A)",color:"#fff",fontWeight:700,fontSize:12,padding:"9px 16px",borderRadius:20,cursor:"pointer",boxShadow:"0 4px 18px rgba(240,100,0,0.55)",display:"flex",alignItems:"center",gap:7,border:"2px solid rgba(255,255,255,0.2)"}}>Admin Test · Return to Admin</div>):null;})()}
        {filingNote&&<div role="status" style={{position:"fixed",bottom:84,left:"50%",transform:"translateX(-50%)",background:C.tealMid,color:"#fff",padding:"10px 18px",borderRadius:20,fontSize:13,fontWeight:600,boxShadow:"0 4px 20px rgba(0,0,0,0.2)",zIndex:2000,maxWidth:"90vw",textAlign:"center"}}>☁ {filingNote}</div>}
        {/* verify-admin-2026-10-20: this email is on the admin list but isn't verified yet */}
        {account&&account.adminWaiting&&(
          <div data-pb-admin-waiting="1" style={{margin:"16px 16px 0",background:C.amberFaint,border:`1px solid ${C.amberLight}`,borderRadius:12,padding:"14px 16px"}}>
            <div style={{fontSize:14,fontWeight:700,color:C.ink,marginBottom:4}}>Admin access is waiting on your email</div>
            <div style={{fontSize:13,color:C.inkMid,lineHeight:1.5}}>{user.email} is set up as a PlanBoss admin, but it isn't verified yet. Click the link in the PlanBoss verification email (check junk too). Or skip the email: on Render, add this account ID to <strong>PB_ADMIN_UIDS</strong> (measureup-app → Environment) and let it redeploy. Then tap “Check again”.</div>
            {account.uid&&<PbCopyId id={account.uid}/>}
            <PbVerifyEmail email={user.email} onCheck={pbCheckVerified} checkLabel="Check again"
              notYet="Not yet. Click the link in the email, or add your account ID to PB_ADMIN_UIDS on Render and wait a few minutes for it to redeploy. Then tap “Check again”."/>
          </div>
        )}
        {screen==="home"&&<HomeScreen projects={projects} setProjects={setProjects} onOpen={openProject} onNewProject={()=>setScreen("new")} opening={opening} filing={filingOn}/>}
        {screen==="new"&&<NewProjectScreen user={user} prefs={prefs} account={account} onComplete={completeNewProject} onUpgrade={()=>setSubScreen("pricing")}/>}
        {screen==="materials"&&activeProject&&<MaterialsScreen key={activeProject.id+":"+jobNonce} user={user} prefs={prefs} project={activeProject} onBack={()=>setScreen("home")} onUpgrade={()=>setSubScreen("pricing")} onPlanUrl={pbPlanUrl} onRenameProject={(id,name)=>{setProjects(ps=>ps.map(p=>p.id===id?{...p,name}:p));setActiveProject(a=>({...a,name}));}}
          onNewScan={()=>setScreen("new")} onCheckCorners={openCornerCheck}
          onDeleteJob={activeProject.viewOnly?undefined:()=>{const id=activeProject.id;setProjects(ps=>ps.filter(x=>x&&x.id!==id));setActiveProject(null);setScreen("home");}}/>}
        {screen==="cornercheck"&&cornerCheck&&activeProject&&activeProject.id===cornerCheck.pid&&<CornerCheckFlow key={cornerCheck.pid+(cornerCheck.readOnly?":ro":":rw")}
          pics={cornerCheck.pics} project={activeProject} stud={pbStudFor(activeProject)} studConfirmed={!!Number(getConfirmedStud(activeProject.id))} studAssumed={pbStudAssumed(activeProject)} initial={pbGetCornerCheck(cornerCheck.pid)} readOnly={cornerCheck.readOnly}
          onSave={(res)=>{saveCornerCheck(activeProject,res);setCornerCheck(null);setJobNonce(n=>n+1);setScreen("materials");}}
          onSkip={()=>{setCornerCheck(null);setScreen("materials");}}/>}
        {screen==="account"&&<AccountScreen user={user} prefs={prefs} account={account} onPrefsChange={setPrefs} onUpgrade={(pid)=>pid?choosePlan(pid):setSubScreen("pricing")} onManageSubscription={()=>setSubScreen("subscriptions")} onLogout={logout} onDeleteAccount={deleteAccount} onCheckVerified={pbCheckVerified}/>}
      </div>

      {/* Mobile bottom nav */}
      {user && user.trialActive && (user.trialEndsAt || user.trialStarted) && (() => {
        const stored = LS.get("to_accounts",{})[user.email];
        if(stored && stored.trialExpired) return null;
        const trialEnd = user.trialEndsAt || (new Date(user.trialStarted).getTime() + 7*24*60*60*1000);
        const daysLeft = Math.max(0, Math.ceil((trialEnd - Date.now())/(1000*60*60*24)));
        const urgent = daysLeft <= 2;
        return (
          <div className="mu-trial-chip" style={{background:urgent?"#FEF3C7":"#ECFDF5",borderTop:`1px solid ${urgent?"#F59E0B":"#A7F3D0"}`,padding:"8px 14px",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,fontSize:12,color:urgent?"#92400E":"#065F46"}}>
            <span style={{fontWeight:600}}>
              {user.trialCard
                ?(daysLeft===0?"⏱ Free trial ends today":daysLeft===1?"⏱ 1 day left in your free trial":`⏱ ${daysLeft} days left in your free trial`)
                :(daysLeft===0?"⏱ Trial ends today":daysLeft===1?"⏱ 1 day left in trial":`⏱ ${daysLeft} days left in trial`)}
              {user.trialCard&&user.trialCancelled?" · cancelled, nothing charged":""}
            </span>
            <button onClick={()=>user.trialCard?pbOpenBillingPortal():setSubScreen("pricing")} style={{background:urgent?"#F59E0B":"#0A7B5A",color:"#fff",border:"none",borderRadius:8,padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
              {user.trialCard?"Manage":"Pick a plan"}
            </button>
          </div>
        );
      })()}
      <div className="mu-bottom-nav">
        <button className={screen==="home"?"active":""} onClick={()=>setScreen("home")}>
          <span className="icon">🏠</span>
          <span className="label">Home</span>
        </button>
        <button className={screen==="new"?"active":""} onClick={()=>setScreen("new")}>
          <span className="icon">➕</span>
          <span className="label">New</span>
        </button>
        {activeProject&&screen==="materials"&&(
          <button className="active">
            <span className="icon">📋</span>
            <span className="label">Quote</span>
          </button>
        )}
        <button className={screen==="account"?"active":""} onClick={()=>setScreen("account")}>
          <span className="icon">👤</span>
          <span className="label">Me</span>
        </button>
      </div>
      <CorrectionEditModal />
    </div>
  );
}

// parity:(
